import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { MissionAdminPanel } from "./components/MissionAdminPanel";
import { AcademyAdminPanel } from "./components/AcademyAdminPanel";
import { AvcAdminPanel } from "./components/AvcAdminPanel";
import { ImpactAdminPanel } from "./components/ImpactAdminPanel";
import { TogoDossierAdminPanel } from "./components/TogoDossierAdminPanel";
import { CharityUkAdminPanel } from "./components/CharityUkAdminPanel";
import { SignedDocsAdminPanel } from "./components/SignedDocsAdminPanel";
import SportPartnersAdminPanel from "./components/SportPartnersAdminPanel";
import CertificatsBulkAdminPanel from "./components/CertificatsBulkAdminPanel";
import TwilioNotificationsAdminPanel from "./components/TwilioNotificationsAdminPanel";
import AiAssistantAdminPanel from "./components/AiAssistantAdminPanel";
import BachePreview3D from "./components/BachePreview3D";
import ScanStatsBox from "./components/ScanStatsBox";
import FinancesAdminPanel from "./components/FinancesAdminPanel";
import AcademyOpsPanel from "./components/AcademyOpsPanel";
import PrerequisitesChecklistPanel from "./components/PrerequisitesChecklistPanel";
import BroadcastAdminPanel from "./components/BroadcastAdminPanel";
import BeneficiariesAdminPanel from "./components/BeneficiariesAdminPanel";
import SponsorshipPublicSection from "./components/SponsorshipPublicSection";
import SponsorshipAdminPanel from "./components/SponsorshipAdminPanel";
import ImpactLiveSection from "./components/ImpactLiveSection";
import PublicTransparencyDashboard from "./components/PublicTransparencyDashboard";
import ChatbotWidget from "./components/ChatbotWidget";
import VerifyPage from "./components/VerifyPage";
import SiegeMapSection from "./components/SiegeMapSection";
import { CharityCommissionResponse } from "./components/CharityCommissionResponse";
import { Heart, Users, Trophy, MapPin, Mail, Globe, Download, Menu, X, Quote, ArrowRight, AlertCircle, Star, ChevronRight, FileText, Settings } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import jsPDF from 'jspdf';
import imageCompression from 'browser-image-compression';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Image compression preset: balanced for web/gallery (~800KB max, 1600px max width, quality 80%)
const IMAGE_COMPRESSION_OPTIONS = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 1600,
  initialQuality: 0.8,
  useWebWorker: true,
  fileType: 'image/jpeg',
};

// Compress an image file before upload. Skips non-images and small files (<200KB).
async function compressImageFile(file) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  // Skip if already small enough
  if (file.size < 200 * 1024) return file;
  try {
    const compressed = await imageCompression(file, IMAGE_COMPRESSION_OPTIONS);
    // Preserve original name (with .jpg extension since we converted)
    const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
    return new File([compressed], `${baseName}.jpg`, {
      type: compressed.type || 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (e) {
    if (process.env.NODE_ENV === 'development') console.warn('Compression failed, using original:', e);
    return file;
  }
}

// Build a deep-link URL for sharing a specific renovation stage anchor.
function buildStageShareUrl(stage) {
  if (typeof window === 'undefined') return '';
  const base = `${window.location.origin}${window.location.pathname}`;
  return `${base}#hq-stage-${stage}`;
}

// Open a social share popup or copy link. provider: 'whatsapp'|'x'|'facebook'|'linkedin'|'copy'|'native'
async function shareStage(provider, stage, title) {
  const url = buildStageShareUrl(stage);
  const text = title || 'SAFUAYE — Rénovation du siège à Atakpamé';
  if (provider === 'native' && typeof navigator !== 'undefined' && navigator.share) {
    try { await navigator.share({ title: text, text, url }); return; } catch (_) { /* user cancelled */ return; }
  }
  if (provider === 'copy') {
    try {
      await navigator.clipboard.writeText(url);
      alert('✅ Lien copié !');
    } catch (_) {
      window.prompt('Copiez le lien :', url);
    }
    return;
  }
  const enc = encodeURIComponent;
  const map = {
    whatsapp: `https://wa.me/?text=${enc(text + ' — ' + url)}`,
    x:        `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
  };
  const shareUrl = map[provider];
  if (shareUrl) window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
}

// Official SAFUAYE Logo and Images
const SAFUAYE_ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/8ia0428s_Logo%20SAFUAYE%20minimal.png",
  heroImage: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/r1sxuh1h_Image%20asso%201.png",
  portrait1: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/sphh7ej5_40x60.jpg",
  portrait2: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/ivgttaf3_IMG-20230714-WA0030.jpg",
  groupPhoto: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/as3tc66m_1000105806.jpg"
};

// Translations
const translations = {
  fr: {
    nav: {
      legacy: "Qui était Safuaye",
      why: "Pourquoi SAFUAYE",
      mission: "Mission",
      beneficiaries: "Bénéficiaires",
      hq: "Notre siège",
      impact: "Notre impact",
      urgence: "Urgence AVC",
      academy: "Académie",
      presse: "Presse",
      donations: "Dons",
      testimonials: "Témoignages",
      news: "Actualités",
      documents: "Documents",
      governance: "Gouvernance",
      contact: "Contact",
      downloadFlyer: "Télécharger"
    },
    hero: {
      subtitle: "En mémoire de Simon-Joseph Afantchédé APEDO, dit",
      nickname: "« Safuaye »",
      tagline: "Un homme. Une histoire. Un héritage.",
      profession: "Géomètre • Dessinateur • Topographe",
      description: "Président d'Ifodjè Football Club d'Atakpamé de 1984 à 1990",
      values: "Héritage • Solidarité • Espoir",
      joinUs: "Rejoignez-nous",
      learnMore: "En savoir plus"
    },
    legacy: {
      overline: "Qui était « SAFUAYE »",
      title: "Origines et parcours d'un homme de cœur",
      intro: "Simon-Joseph Afantchédé APEDO, surnommé « Safuaye », est originaire d'Atakpamé, au cœur du Togo.",
      p1: "Après son enfance dans cette ville qui l'a vu naître, il part vivre à Lomé, où il découvre de nouvelles perspectives et nourrit ses ambitions professionnelles. Son désir d'excellence l'amène ensuite en Côte d'Ivoire, où il suit une formation rigoureuse en géométrie, dessin et topographie.",
      p2: "Ce passage à l'étranger marque une étape décisive : il y acquiert une expertise solide, une discipline de travail exemplaire et une vision structurée du monde. À la fin de sa formation, il retourne au Togo, mû par un profond attachement à sa terre natale.",
      p3: "Il s'installe définitivement à Atakpamé, où il exerce son métier avec passion, précision et intégrité. Mais c'est là aussi qu'il rencontre son grand amour : le football.",
      presidentTitle: "Un président pas comme les autres",
      presidentIntro: "À Atakpamé, Safuaye ne se contente pas d'être un professionnel respecté. Il devient une figure incontournable du sport local en prenant la présidence de l'Ifodjè Football Club de 1984 à 1990.",
      presidentPoints: [
        "Il soutenait les joueurs avec ses propres moyens",
        "Il aidait leurs familles dans les moments difficiles",
        "Il accompagnait les plus démunis de la communauté",
        "Il incarnait une générosité rare, discrète et authentique"
      ],
      presidentConclusion: "Pour beaucoup, Safuaye n'était pas seulement un président. Il était un père, un protecteur, un guide.",
      heritageTitle: "Un héritage vivant",
      heritageText: "Aujourd'hui encore, son nom résonne dans les rues d'Atakpamé, dans les souvenirs des anciens joueurs et dans le cœur de ceux qu'il a aidés."
    },
    why: {
      overline: "Pourquoi l'association « SAFUAYE » ?",
      title: "Prolonger une vie de générosité",
      intro: "Simon-Joseph Afantchédé APEDO n'était pas seulement un président de club. C'était un homme profondément humain, qui mobilisait ses propres ressources pour soutenir les joueurs et les personnes vulnérables.",
      quote: "Ne jamais laisser quelqu'un dans la détresse.",
      realityTitle: "Une réalité sociale qui interpelle",
      realityPoints: [
        "Vieillissement sans soutien",
        "Maladies non prises en charge",
        "Précarité financière",
        "Isolement social"
      ],
      realityConclusion: "Si Safuaye était encore là, il serait le premier à agir."
    },
    mission: {
      overline: "Notre Mission",
      title: "Aider les personnes vulnérables du Togo",
      subtitle: "Une association fraternelle qui agit dans plusieurs domaines pour redonner espoir aux plus fragiles.",
      points: [
        { title: "Santé", description: "AVC, urgences médicales, soutien aux malades", icon: "heart" },
        { title: "Aide alimentaire", description: "Aux familles démunies d'Atakpamé", icon: "users" },
        { title: "Éducation & sport", description: "Académie de foot gratuite pour les enfants", icon: "star" },
        { title: "Hommage aux anciens", description: "Les joueurs d'Ifodjè FC (1984-1990)", icon: "trophy" },
        { title: "Diaspora & mémoire", description: "Une chaîne fraternelle UK · IT · CH · Togo", icon: "globe" }
      ],
      transnationalTitle: "Un engagement transnational",
      transnationalText: "Basée au Royaume-Uni, en Italie, en Suisse et active au Togo, l'association mobilise la diaspora togolaise, les anciens joueurs, les familles et les partenaires locaux."
    },
    beneficiaries: {
      overline: "Nos Bénéficiaires",
      title: "Ceux que nous voulons aider",
      subtitle: "Voici quelques-uns des premiers bénéficiaires identifiés. La liste s'agrandira au fil de nos actions.",
      morecoming: "Et bien d'autres à venir...",
      profiles: [
        { name: "DOKOH Komlan", nickname: "dit Gurces", age: 67, situation: "Agent de sécurité", testimony: "On mange difficilement.", needs: ["Soutien alimentaire", "Aide médicale"] },
        { name: "OSSEINI Ramanou", age: 58, situation: "Petit commerçant", testimony: "Les choses ne marchent plus, aucun soutien.", needs: ["Soutien commercial", "Aide financière"] },
        { name: "AZANDOGBE Sessi", age: 58, situation: "Sans activité", testimony: "Situation de surmenage mental.", needs: ["Accompagnement psychologique"] },
        { name: "HENDE Kadjo", age: 59, situation: "Porte-faix au marché", testimony: "Il souffre de sclérose.", needs: ["Soins médicaux"] },
        { name: "YAKOUBOU Abdoulaye", age: 67, situation: "Réparateur de montres", testimony: "La vue devient difficile.", needs: ["Soins ophtalmologiques"] },
        { name: "DOKOU Kasanci", nickname: "dit Moulele", age: 70, situation: "Sans activité", testimony: "Toujours malade.", needs: ["Aide médicale"] },
        { name: "AYETAN Kossi", age: 63, situation: "Agent d'entretien et détenteur de cabaret", testimony: "Situation de précarité, veuf avec enfants à charge.", needs: ["Aide financière", "Soutien emploi"] }
      ]
    },
    guide: {
      overline: "Guide Pratique",
      title: "Créer l'Association",
      ukTitle: "Au Royaume-Uni",
      togoTitle: "Au Togo",
      documentsTitle: "Documents Nécessaires"
    },
    documents: {
      overline: "Documents Officiels",
      title: "Télécharger les Documents",
      subtitle: "Documents pour enregistrer SAFUAYE au Royaume-Uni et au Togo",
      ukSection: "Documents UK",
      togoSection: "Documents Togo",
      constitution: "Constitution CIO",
      constitutionDesc: "Statuts officiels (UK)",
      trusteeForm: "Formulaire Trustee",
      trusteeFormDesc: "Déclaration trustee (UK)",
      minutes: "Procès-verbal UK",
      minutesDesc: "Minutes première réunion",
      bankLetter: "Lettre Banque UK",
      bankLetterDesc: "Demande compte bancaire",
      statutsTogo: "Statuts Togo",
      statutsTogoDesc: "Statuts pour le Togo",
      pvTogo: "PV Assemblée",
      pvTogoDesc: "Assemblée constitutive",
      demandePrefet: "Demande Préfet",
      demandePrefetDesc: "Lettre au Préfet d'Ogou",
      listeMembres: "Liste Membres",
      listeMembresDesc: "Fondateurs et Bureau",
      download: "Télécharger"
    },
    donations: {
      overline: "Soutenir Notre Mission",
      title: "Faire un Don",
      comingSoon: "Bientôt disponible",
      subtitle: "Le système de dons en ligne sera disponible très prochainement. Nous finalisons actuellement l'ouverture du compte bancaire de l'association.",
      whyDonate: "Pourquoi donner ?",
      reasons: [
        "Soutenir directement les anciens joueurs dans le besoin",
        "Financer l'aide médicale et alimentaire",
        "Contribuer au développement du sport local",
        "Honorer la mémoire de Safuaye"
      ],
      contactForDonation: "En attendant, vous pouvez nous contacter pour plus d'informations sur les dons."
    },
    testimonials: {
      overline: "Témoignages",
      title: "Partagez votre histoire",
      subtitle: "Vous avez connu Safuaye ou vous êtes touché par notre mission ? Partagez votre témoignage.",
      form: {
        name: "Votre nom",
        relation: "Votre lien avec Safuaye",
        relationPlaceholder: "Ex: Ancien joueur, Ami, Membre de la famille...",
        message: "Votre témoignage",
        messagePlaceholder: "Partagez vos souvenirs, votre histoire...",
        submit: "Envoyer le témoignage",
        success: "Merci pour votre témoignage !"
      },
      featured: "Témoignages récents"
    },
    news: {
      overline: "Actualités",
      title: "Dernières nouvelles",
      subtitle: "Suivez l'évolution de notre association et nos actions sur le terrain.",
      comingSoon: "Bientôt disponible",
      comingSoonText: "La section actualités sera mise à jour régulièrement avec nos dernières actions et événements.",
      stayTuned: "Restez connectés !"
    },
    meeting: {
      overline: "Réunion Officielle",
      title: "Assemblée Constitutive - Enregistrement UK",
      date: "Dimanche 18 Avril 2026",
      time: "11h00 - 13h00 (Heure de Londres)",
      format: "Réunion en ligne",
      objective: "Finaliser l'enregistrement de l'association SAFUAYE comme Charitable Incorporated Organisation (CIO) au Royaume-Uni",
      agenda: {
        title: "Ordre du jour",
        items: [
          "Ouverture de la séance et appel des présents",
          "Adoption de la Constitution (CIO)",
          "Nomination officielle des Trustees",
          "Définition des objectifs caritatifs",
          "Vote sur les résolutions",
          "Signature du procès-verbal",
          "Clôture"
        ]
      },
      register: {
        title: "S'inscrire à la réunion",
        subtitle: "Confirmez votre participation pour recevoir le lien de visioconférence",
        name: "Nom complet",
        email: "Email",
        role: "Votre rôle",
        rolePlaceholder: "Ex: Trustee, Membre fondateur, Observateur",
        submit: "Confirmer ma participation",
        success: "Inscription confirmée ! Vous recevrez le lien de réunion par email."
      },
      documents: "Documents à préparer",
      meetingLink: "Lien de visioconférence (envoyé aux inscrits)",
      admin: {
        title: "Administration de la Réunion",
        emailTemplate: "Template Email",
        generateEmail: "Générer l'email",
        copyEmail: "Copier l'email",
        minutes: "Procès-Verbal",
        generatePV: "Générer le PV PDF",
        attendees: "Participants",
        decisions: "Décisions prises",
        signatures: "Signatures",
        downloadPV: "Télécharger le PV"
      }
    },
    contact: {
      overline: "Rejoignez-Nous",
      title: "Contact & Adhésion",
      subtitle: "Nous serions honorés de vous compter parmi nous.",
      form: {
        fullName: "Nom complet",
        email: "Email",
        phone: "Téléphone (optionnel)",
        country: "Pays de résidence",
        messageType: "Type de message",
        types: { contact: "Question générale", membership: "Demande d'adhésion", donation: "Information sur les dons" },
        message: "Votre message",
        submit: "Envoyer",
        success: "Message envoyé avec succès !"
      }
    },
    footer: {
      tagline: "En mémoire de notre père, pour l'avenir d'Atakpamé",
      operations: "UK • Italie • Suisse • Togo",
      rights: "Tous droits réservés"
    },
    governance: {
      overline: "Transparence & Confiance",
      title: "Notre Gouvernance",
      subtitle: "SAFUAYE est constituée en tant que Charitable Incorporated Organisation (CIO) conformément au modèle officiel de la Charity Commission d'Angleterre et du Pays de Galles. Notre constitution fondatrice est publique et téléchargeable ci-dessous.",
      officialBadge: "Version officielle",
      infoBadge: "Traduction informative",
      officialDesc: "Version déposée à la Charity Commission UK",
      infoDesc: "Traduction non officielle à titre informatif",
      structureTitle: "Structure juridique",
      structureItems: [
        { label: "Forme", value: "Charitable Incorporated Organisation (CIO)" },
        { label: "Modèle", value: "CIO Association Model" },
        { label: "Juridiction", value: "Angleterre & Pays de Galles" },
        { label: "Administrateurs (Trustees)", value: "Minimum 3 • Maximum 12" },
        { label: "Zone d'action", value: "Mondiale, focus Togo & UK" }
      ],
      objectsTitle: "Nos objets caritatifs",
      objectsA: "(1) La prévention ou le soulagement de la pauvreté et l'aide aux personnes dans le besoin (jeunesse, âge, maladie, handicap, difficultés financières) au Togo, en particulier à Atakpamé et dans la Région des Plateaux.",
      objectsB: "(2) La promotion du sport amateur, notamment le football, au bénéfice du public au Togo, avec des activités ouvertes aux enfants, aux jeunes et aux adultes de tous niveaux.",
      trusteesTitle: "Premiers administrateurs",
      trustees: ["APEDO KOKOU OGNAKOTAN", "APEDO YAO", "APEDO ABLAVI"],
      downloadTitle: "Télécharger la constitution",
      downloadNote: "La version anglaise fait seule foi auprès de la Charity Commission."
    },
    orgStructure: {
      overline: "Une organisation, deux entités",
      title: "SAFUAYE UK coiffe SAFUAYE Togo",
      intro: "SAFUAYE est une organisation internationale composée de deux entités liées : la Charitable Incorporated Organisation (CIO) britannique — SAFUAYE UK — qui parraine et coordonne les actions de SAFUAYE Togo. Bien que les autorités togolaises aient souhaité présenter SAFUAYE Togo comme indépendante dans ses statuts locaux, notre organisation internationale reste unifiée : toutes les activités togolaises sont pilotées et validées par le Président International, basé au Royaume-Uni.",
      ukTitle: "SAFUAYE UK",
      ukRole: "Entité mère · Charity Commission (Angleterre & Pays de Galles)",
      ukPoints: [
        "Structure juridique : Charitable Incorporated Organisation (CIO)",
        "Gouvernance : Président International + Trustees UK",
        "Levée de fonds internationale (Royaume-Uni, Europe)",
        "Éligibilité Gift Aid (HMRC) une fois l'enregistrement finalisé",
        "Reporting officiel : Charity Commission UK"
      ],
      togoTitle: "SAFUAYE Togo",
      togoRole: "Entité opérationnelle · MATDDT (Togo)",
      togoPoints: [
        "Enregistrée auprès du Ministère de l'Administration Territoriale du Togo",
        "Coordonnée sur le terrain à Atakpamé (siège Atchrimi-Komé)",
        "Met en œuvre les projets : Académie, Odôn-Itsou, Santé AVC, aide sociale",
        "Placée sous la supervision du Président International (SAFUAYE UK)",
        "Reporting financier et opérationnel remonté à SAFUAYE UK"
      ],
      linkTitle: "Un pilotage unifié par le Président International",
      linkText: "Toutes les activités de SAFUAYE Togo sont supervisées par le Président International, APEDO Kokou Ognakotan, basé au Royaume-Uni. Nos partenaires — sportifs, institutionnels, philanthropiques — soutiennent SAFUAYE UK, qui alloue ensuite les fonds à SAFUAYE Togo selon des règles de transparence strictes (rapport trimestriel, justificatifs, audit Charity Commission).",
      cta: "Devenir partenaire"
    },
    transparency: {
      title: "Nos comptes, en toute clarté",
      intro: "SAFUAYE publie l'état consolidé de sa trésorerie (SAFUAYE UK + SAFUAYE Togo). Les libellés individuels et les pièces justificatives sont audités par la Charity Commission UK — cette page n'affiche que des agrégats anonymisés."
    }
  },
  en: {
    nav: {
      legacy: "Who was Safuaye",
      why: "Why SAFUAYE",
      mission: "Mission",
      beneficiaries: "Beneficiaries",
      hq: "Our HQ",
      impact: "Our impact",
      urgence: "Stroke Emergency",
      academy: "Academy",
      presse: "Press",
      donations: "Donations",
      testimonials: "Testimonials",
      news: "News",
      documents: "Documents",
      governance: "Governance",
      contact: "Contact",
      downloadFlyer: "Download"
    },
    hero: {
      subtitle: "In memory of Simon-Joseph Afantchédé APEDO, known as",
      nickname: "« Safuaye »",
      tagline: "One man. One story. One legacy.",
      profession: "Surveyor • Designer • Topographer",
      description: "President of Ifodjè Football Club of Atakpamé from 1984 to 1990",
      values: "Heritage • Solidarity • Hope",
      joinUs: "Join Us",
      learnMore: "Learn More"
    },
    legacy: {
      overline: "Who was « SAFUAYE »",
      title: "Origins and journey of a man of heart",
      intro: "Simon-Joseph Afantchédé APEDO, nicknamed « Safuaye », is originally from Atakpamé, in the heart of Togo.",
      p1: "After his childhood in this city where he was born, he went to live in Lomé. His desire for excellence then led him to Ivory Coast, where he underwent rigorous training in geometry, drawing and topography.",
      p2: "This time abroad marked a decisive step: he acquired solid expertise and an exemplary work discipline. At the end of his training, he returned to Togo, driven by a deep attachment to his native land.",
      p3: "He settled permanently in Atakpamé, where he practiced his profession with passion. But it was there that he also met his great love: football.",
      presidentTitle: "A president like no other",
      presidentIntro: "In Atakpamé, Safuaye became an essential figure in local sport by taking the presidency of Ifodjè Football Club from 1984 to 1990.",
      presidentPoints: [
        "He supported players with his own means",
        "He helped their families in difficult times",
        "He accompanied the most underprivileged",
        "He embodied a rare, authentic generosity"
      ],
      presidentConclusion: "For many, Safuaye was not just a president. He was a father, a protector, a guide.",
      heritageTitle: "A living legacy",
      heritageText: "Today, his name still resonates in the streets of Atakpamé, in the memories of former players."
    },
    why: {
      overline: "Why the « SAFUAYE » association?",
      title: "Extending a life of generosity",
      intro: "Simon-Joseph Afantchédé APEDO was not just a club president. He was a deeply human man who mobilized his own resources to support players and vulnerable people.",
      quote: "Never leave someone in distress.",
      realityTitle: "A concerning social reality",
      realityPoints: [
        "Aging without support",
        "Untreated diseases",
        "Financial precariousness",
        "Social isolation"
      ],
      realityConclusion: "If Safuaye were still here, he would be the first to act."
    },
    mission: {
      overline: "Our Mission",
      title: "Helping vulnerable people in Togo",
      subtitle: "A fraternal association acting in multiple areas to restore hope to the most fragile.",
      points: [
        { title: "Health", description: "Stroke, medical emergencies, patient support", icon: "heart" },
        { title: "Food aid", description: "For struggling families in Atakpamé", icon: "users" },
        { title: "Education & sport", description: "Free football academy for children", icon: "star" },
        { title: "Honouring the elders", description: "Former Ifodjè FC players (1984-1990)", icon: "trophy" },
        { title: "Diaspora & memory", description: "A fraternal chain UK · IT · CH · Togo", icon: "globe" }
      ],
      transnationalTitle: "A transnational commitment",
      transnationalText: "Based in the UK, Italy, Switzerland and active in Togo, the association mobilizes the Togolese diaspora, former players, families and local partners."
    },
    beneficiaries: {
      overline: "Our Beneficiaries",
      title: "Those we want to help",
      subtitle: "Here are some of the first identified beneficiaries. The list will grow as our actions expand.",
      morecoming: "And many more to come...",
      profiles: [
        { name: "DOKOH Komlan", nickname: "known as Gurces", age: 67, situation: "Security guard", testimony: "We barely eat.", needs: ["Food support", "Medical aid"] },
        { name: "OSSEINI Ramanou", age: 58, situation: "Small trader", testimony: "Things no longer work, no support.", needs: ["Business support", "Financial aid"] },
        { name: "AZANDOGBE Sessi", age: 58, situation: "Unemployed", testimony: "Mental strain situation.", needs: ["Psychological support"] },
        { name: "HENDE Kadjo", age: 59, situation: "Market porter", testimony: "He suffers from sclerosis.", needs: ["Medical care"] },
        { name: "YAKOUBOU Abdoulaye", age: 67, situation: "Watch repairer", testimony: "Eyesight becoming difficult.", needs: ["Eye care"] },
        { name: "DOKOU Kasanci", nickname: "known as Moulele", age: 70, situation: "Unemployed", testimony: "Always sick.", needs: ["Medical aid"] },
        { name: "AYETAN Kossi", age: 63, situation: "Maintenance agent and cabaret owner", testimony: "Precarious situation, widower with children in care.", needs: ["Financial aid", "Employment support"] }
      ]
    },
    guide: {
      overline: "Practical Guide",
      title: "Creating the Association",
      ukTitle: "In the United Kingdom",
      togoTitle: "In Togo",
      documentsTitle: "Required Documents"
    },
    documents: {
      overline: "Official Documents",
      title: "Download Documents",
      subtitle: "Documents to register SAFUAYE in the UK and Togo",
      ukSection: "UK Documents",
      togoSection: "Togo Documents",
      constitution: "CIO Constitution",
      constitutionDesc: "Official statutes (UK)",
      trusteeForm: "Trustee Form",
      trusteeFormDesc: "Trustee declaration (UK)",
      minutes: "UK Minutes",
      minutesDesc: "First meeting minutes",
      bankLetter: "UK Bank Letter",
      bankLetterDesc: "Account opening request",
      statutsTogo: "Togo Statutes",
      statutsTogoDesc: "Statutes for Togo",
      pvTogo: "Assembly Minutes",
      pvTogoDesc: "Constitutive assembly",
      demandePrefet: "Prefect Request",
      demandePrefetDesc: "Letter to Ogou Prefect",
      listeMembres: "Members List",
      listeMembresDesc: "Founders and Board",
      download: "Download"
    },
    donations: {
      overline: "Support Our Mission",
      title: "Make a Donation",
      comingSoon: "Coming Soon",
      subtitle: "The online donation system will be available very soon. We are currently finalizing the opening of the association's bank account.",
      whyDonate: "Why donate?",
      reasons: [
        "Directly support former players in need",
        "Finance medical and food aid",
        "Contribute to local sports development",
        "Honor Safuaye's memory"
      ],
      contactForDonation: "In the meantime, you can contact us for more information about donations."
    },
    testimonials: {
      overline: "Testimonials",
      title: "Share your story",
      subtitle: "Did you know Safuaye or are you touched by our mission? Share your testimony.",
      form: {
        name: "Your name",
        relation: "Your connection with Safuaye",
        relationPlaceholder: "Ex: Former player, Friend, Family member...",
        message: "Your testimonial",
        messagePlaceholder: "Share your memories, your story...",
        submit: "Send testimonial",
        success: "Thank you for your testimonial!"
      },
      featured: "Recent testimonials"
    },
    news: {
      overline: "News",
      title: "Latest updates",
      subtitle: "Follow the evolution of our association and our actions in the field.",
      comingSoon: "Coming Soon",
      comingSoonText: "The news section will be updated regularly with our latest actions and events.",
      stayTuned: "Stay tuned!"
    },
    contact: {
      overline: "Join Us",
      title: "Contact & Membership",
      subtitle: "We would be honored to count you among us.",
      form: {
        fullName: "Full name",
        email: "Email",
        phone: "Phone (optional)",
        country: "Country of residence",
        messageType: "Message type",
        types: { contact: "General question", membership: "Membership request", donation: "Donation information" },
        message: "Your message",
        submit: "Submit",
        success: "Message sent successfully!"
      }
    },
    footer: {
      tagline: "In memory of our father, for the future of Atakpamé",
      operations: "UK • Italy • Switzerland • Togo",
      rights: "All rights reserved"
    },
    governance: {
      overline: "Transparency & Trust",
      title: "Our Governance",
      subtitle: "SAFUAYE is established as a Charitable Incorporated Organisation (CIO) following the official model of the Charity Commission for England and Wales. Our founding constitution is public and available for download below.",
      officialBadge: "Official version",
      infoBadge: "Informative translation",
      officialDesc: "Version filed with the UK Charity Commission",
      infoDesc: "Unofficial translation for information only",
      structureTitle: "Legal structure",
      structureItems: [
        { label: "Form", value: "Charitable Incorporated Organisation (CIO)" },
        { label: "Model", value: "CIO Association Model" },
        { label: "Jurisdiction", value: "England & Wales" },
        { label: "Trustees", value: "Minimum 3 • Maximum 12" },
        { label: "Area of operation", value: "Worldwide, focus Togo & UK" }
      ],
      objectsTitle: "Our charitable objects",
      objectsA: "(1) The prevention or relief of poverty, and the relief of those in need by reason of youth, age, ill-health, disability or financial hardship, in Togo, in particular Atakpamé and the Plateaux Region.",
      objectsB: "(2) The advancement of amateur sport, in particular association football, for the benefit of the public in Togo, with activities open to children, young people and adults of all abilities.",
      trusteesTitle: "First trustees",
      trustees: ["APEDO KOKOU OGNAKOTAN", "APEDO YAO", "APEDO ABLAVI"],
      downloadTitle: "Download the constitution",
      downloadNote: "Only the English version is authoritative before the Charity Commission."
    },
    orgStructure: {
      overline: "One organisation, two entities",
      title: "SAFUAYE UK oversees SAFUAYE Togo",
      intro: "SAFUAYE is an international organisation made of two linked entities: the British Charitable Incorporated Organisation (CIO) — SAFUAYE UK — which sponsors and coordinates the activities of SAFUAYE Togo. Although the Togolese authorities preferred to present SAFUAYE Togo as independent in its local by-laws, our international organisation remains unified: every activity in Togo is steered and validated by the International President based in the United Kingdom.",
      ukTitle: "SAFUAYE UK",
      ukRole: "Parent entity · Charity Commission (England & Wales)",
      ukPoints: [
        "Legal form: Charitable Incorporated Organisation (CIO)",
        "Governance: International President + UK Trustees",
        "International fundraising (UK & Europe)",
        "Gift Aid eligibility (HMRC) once registration is completed",
        "Statutory reporting: UK Charity Commission"
      ],
      togoTitle: "SAFUAYE Togo",
      togoRole: "Operational entity · MATDDT (Togo)",
      togoPoints: [
        "Registered with the Togolese Ministry of Territorial Administration",
        "On-the-ground coordination in Atakpamé (Atchrimi-Komé HQ)",
        "Delivers the projects: Academy, Odôn-Itsou, Stroke care, social aid",
        "Under the supervision of the International President (SAFUAYE UK)",
        "Financial and operational reporting escalated to SAFUAYE UK"
      ],
      linkTitle: "A unified steering by the International President",
      linkText: "All SAFUAYE Togo activities are supervised by the International President, APEDO Kokou Ognakotan, based in the United Kingdom. Our partners — sports, institutional, philanthropic — support SAFUAYE UK, which then allocates funds to SAFUAYE Togo under strict transparency rules (quarterly report, receipts, Charity Commission audit).",
      cta: "Become a partner"
    },
    transparency: {
      title: "Our finances, in full clarity",
      intro: "SAFUAYE publishes the consolidated cash position (SAFUAYE UK + SAFUAYE Togo). Individual line items and receipts are audited by the UK Charity Commission — this page only shows anonymised aggregates."
    }
  },
  it: {
    nav: {
      legacy: "Chi era Safuaye",
      why: "Perché SAFUAYE",
      mission: "Missione",
      beneficiaries: "Beneficiari",
      hq: "La nostra sede",
      impact: "Il nostro impatto",
      urgence: "Emergenza Ictus",
      academy: "Accademia",
      presse: "Stampa",
      donations: "Donazioni",
      testimonials: "Testimonianze",
      news: "Notizie",
      documents: "Documenti",
      governance: "Governance",
      contact: "Contatto",
      downloadFlyer: "Scarica"
    },
    hero: {
      subtitle: "In memoria di Simon-Joseph Afantchédé APEDO, detto",
      nickname: "« Safuaye »",
      tagline: "Un uomo. Una storia. Un'eredità.",
      profession: "Geometra • Disegnatore • Topografo",
      description: "Presidente dell'Ifodjè Football Club di Atakpamé dal 1984 al 1990",
      values: "Eredità • Solidarietà • Speranza",
      joinUs: "Unisciti a noi",
      learnMore: "Scopri di più"
    },
    legacy: {
      overline: "Chi era « SAFUAYE »",
      title: "Origini e percorso di un uomo di cuore",
      intro: "Simon-Joseph Afantchédé APEDO, soprannominato « Safuaye », è originario di Atakpamé, nel cuore del Togo.",
      p1: "Dopo l'infanzia nella città che lo ha visto nascere, si trasferisce a Lomé. Il suo desiderio di eccellenza lo porta poi in Costa d'Avorio, dove segue una rigorosa formazione in geometria, disegno e topografia.",
      p2: "Questo periodo all'estero segna una tappa decisiva: acquisisce una solida competenza e una disciplina di lavoro esemplare. Al termine della formazione, ritorna in Togo, spinto da un profondo attaccamento alla sua terra natale.",
      p3: "Si stabilisce definitivamente ad Atakpamé, dove esercita la sua professione con passione. Ma è lì che incontra anche il suo grande amore: il calcio.",
      presidentTitle: "Un presidente come nessun altro",
      presidentIntro: "Ad Atakpamé, Safuaye diventa una figura essenziale dello sport locale assumendo la presidenza dell'Ifodjè Football Club dal 1984 al 1990.",
      presidentPoints: [
        "Sosteneva i giocatori con i propri mezzi",
        "Aiutava le loro famiglie nei momenti difficili",
        "Accompagnava i più bisognosi",
        "Incarnava una generosità rara e autentica"
      ],
      presidentConclusion: "Per molti, Safuaye non era solo un presidente. Era un padre, un protettore, una guida.",
      heritageTitle: "Un'eredità vivente",
      heritageText: "Oggi il suo nome risuona ancora nelle strade di Atakpamé, nei ricordi degli ex giocatori."
    },
    why: {
      overline: "Perché l'associazione « SAFUAYE »?",
      title: "Prolungare una vita di generosità",
      intro: "Simon-Joseph Afantchédé APEDO non era solo un presidente di club. Era un uomo profondamente umano che mobilitava le proprie risorse per sostenere i giocatori e le persone vulnerabili.",
      quote: "Non lasciare mai qualcuno in difficoltà.",
      realityTitle: "Una realtà sociale preoccupante",
      realityPoints: [
        "Invecchiamento senza supporto",
        "Malattie non curate",
        "Precarietà finanziaria",
        "Isolamento sociale"
      ],
      realityConclusion: "Se Safuaye fosse ancora qui, sarebbe il primo ad agire."
    },
    mission: {
      overline: "La Nostra Missione",
      title: "Aiutare le persone vulnerabili del Togo",
      subtitle: "Un'associazione fraterna che agisce in vari ambiti per ridare speranza ai più fragili.",
      points: [
        { title: "Salute", description: "Ictus, emergenze mediche, sostegno ai malati", icon: "heart" },
        { title: "Aiuto alimentare", description: "Alle famiglie in difficoltà di Atakpamé", icon: "users" },
        { title: "Educazione & sport", description: "Accademia di calcio gratuita per i bambini", icon: "star" },
        { title: "Omaggio agli anziani", description: "Ex giocatori dell'Ifodjè FC (1984-1990)", icon: "trophy" },
        { title: "Diaspora & memoria", description: "Una catena fraterna UK · IT · CH · Togo", icon: "globe" }
      ],
      transnationalTitle: "Un impegno transnazionale",
      transnationalText: "Con sede nel Regno Unito, in Italia, in Svizzera e attiva in Togo, l'associazione mobilita la diaspora togolese, gli ex giocatori, le famiglie e i partner locali."
    },
    beneficiaries: {
      overline: "I Nostri Beneficiari",
      title: "Coloro che vogliamo aiutare",
      subtitle: "Ecco alcuni dei primi beneficiari identificati. La lista crescerà con le nostre azioni.",
      morecoming: "E molti altri in arrivo...",
      profiles: [
        { name: "DOKOH Komlan", nickname: "detto Gurces", age: 67, situation: "Guardia di sicurezza", testimony: "Mangiamo a malapena.", needs: ["Sostegno alimentare", "Aiuto medico"] },
        { name: "OSSEINI Ramanou", age: 58, situation: "Piccolo commerciante", testimony: "Le cose non funzionano più.", needs: ["Sostegno commerciale", "Aiuto finanziario"] },
        { name: "AZANDOGBE Sessi", age: 58, situation: "Disoccupato", testimony: "Situazione di stress mentale.", needs: ["Supporto psicologico"] },
        { name: "HENDE Kadjo", age: 59, situation: "Facchino al mercato", testimony: "Soffre di sclerosi.", needs: ["Cure mediche"] },
        { name: "YAKOUBOU Abdoulaye", age: 67, situation: "Riparatore di orologi", testimony: "La vista diventa difficile.", needs: ["Cure oculistiche"] },
        { name: "DOKOU Kasanci", nickname: "detto Moulele", age: 70, situation: "Disoccupato", testimony: "Sempre malato.", needs: ["Aiuto medico"] },
        { name: "AYETAN Kossi", age: 63, situation: "Addetto alle pulizie e proprietario di cabaret", testimony: "Situazione precaria, vedovo con figli a carico.", needs: ["Aiuto finanziario", "Sostegno lavorativo"] }
      ]
    },
    donations: {
      overline: "Sostenere la Nostra Missione",
      title: "Fare una Donazione",
      comingSoon: "Prossimamente",
      subtitle: "Il sistema di donazione online sarà disponibile molto presto. Stiamo attualmente finalizzando l'apertura del conto bancario dell'associazione.",
      whyDonate: "Perché donare?",
      reasons: [
        "Sostenere direttamente gli ex giocatori bisognosi",
        "Finanziare l'assistenza medica e alimentare",
        "Contribuire allo sviluppo dello sport locale",
        "Onorare la memoria di Safuaye"
      ],
      contactForDonation: "Nel frattempo, potete contattarci per ulteriori informazioni sulle donazioni."
    },
    testimonials: {
      overline: "Testimonianze",
      title: "Condividi la tua storia",
      subtitle: "Hai conosciuto Safuaye o sei toccato dalla nostra missione? Condividi la tua testimonianza.",
      form: {
        name: "Il tuo nome",
        relation: "Il tuo legame con Safuaye",
        relationPlaceholder: "Es: Ex giocatore, Amico, Membro della famiglia...",
        message: "La tua testimonianza",
        messagePlaceholder: "Condividi i tuoi ricordi, la tua storia...",
        submit: "Invia testimonianza",
        success: "Grazie per la tua testimonianza!"
      },
      featured: "Testimonianze recenti"
    },
    news: {
      overline: "Notizie",
      title: "Ultime novità",
      subtitle: "Segui l'evoluzione della nostra associazione e le nostre azioni sul campo.",
      comingSoon: "Prossimamente",
      comingSoonText: "La sezione notizie sarà aggiornata regolarmente con le nostre ultime azioni ed eventi.",
      stayTuned: "Rimanete connessi!"
    },
    guide: {
      overline: "Guida Pratica",
      title: "Creare l'Associazione",
      ukTitle: "Nel Regno Unito",
      togoTitle: "In Togo",
      documentsTitle: "Documenti Necessari"
    },
    documents: {
      overline: "Documenti Ufficiali",
      title: "Scarica i Documenti",
      subtitle: "Documenti per registrare SAFUAYE nel Regno Unito e in Togo",
      ukSection: "Documenti UK",
      togoSection: "Documenti Togo",
      constitution: "Costituzione CIO",
      constitutionDesc: "Statuti ufficiali (UK)",
      trusteeForm: "Modulo Trustee",
      trusteeFormDesc: "Dichiarazione trustee (UK)",
      minutes: "Verbale UK",
      minutesDesc: "Verbale prima riunione",
      bankLetter: "Lettera Banca UK",
      bankLetterDesc: "Richiesta apertura conto",
      statutsTogo: "Statuti Togo",
      statutsTogoDesc: "Statuti per il Togo",
      pvTogo: "Verbale Assemblea",
      pvTogoDesc: "Assemblea costitutiva",
      demandePrefet: "Richiesta Prefetto",
      demandePrefetDesc: "Lettera al Prefetto di Ogou",
      listeMembres: "Lista Membri",
      listeMembresDesc: "Fondatori e Consiglio",
      download: "Scarica"
    },
    contact: {
      overline: "Unisciti a Noi",
      title: "Contatto & Adesione",
      subtitle: "Saremmo onorati di averti tra noi.",
      form: {
        fullName: "Nome completo",
        email: "Email",
        phone: "Telefono (opzionale)",
        country: "Paese di residenza",
        messageType: "Tipo di messaggio",
        types: { contact: "Domanda generale", membership: "Richiesta di adesione", donation: "Informazioni sulle donazioni" },
        message: "Il tuo messaggio",
        submit: "Invia",
        success: "Messaggio inviato con successo!"
      }
    },
    footer: {
      tagline: "In memoria di nostro padre, per il futuro di Atakpamé",
      operations: "UK • Italia • Svizzera • Togo",
      rights: "Tutti i diritti riservati"
    },
    governance: {
      overline: "Trasparenza & Fiducia",
      title: "La nostra Governance",
      subtitle: "SAFUAYE è costituita come Charitable Incorporated Organisation (CIO) secondo il modello ufficiale della Charity Commission di Inghilterra e Galles. Il nostro statuto fondativo è pubblico e scaricabile qui sotto.",
      officialBadge: "Versione ufficiale",
      infoBadge: "Traduzione informativa",
      officialDesc: "Versione depositata presso la Charity Commission UK",
      infoDesc: "Traduzione non ufficiale a solo scopo informativo",
      structureTitle: "Struttura giuridica",
      structureItems: [
        { label: "Forma", value: "Charitable Incorporated Organisation (CIO)" },
        { label: "Modello", value: "CIO Association Model" },
        { label: "Giurisdizione", value: "Inghilterra & Galles" },
        { label: "Amministratori (Trustees)", value: "Minimo 3 • Massimo 12" },
        { label: "Ambito operativo", value: "Mondiale, focus Togo & UK" }
      ],
      objectsTitle: "I nostri oggetti caritativi",
      objectsA: "(1) La prevenzione o il sollievo dalla povertà e l'aiuto alle persone nel bisogno (giovane età, età avanzata, malattia, disabilità, difficoltà finanziarie) in Togo, in particolare ad Atakpamé e nella Regione degli Altopiani.",
      objectsB: "(2) La promozione dello sport amatoriale, in particolare il calcio, a beneficio del pubblico in Togo, con attività aperte a bambini, giovani e adulti di ogni livello.",
      trusteesTitle: "Primi amministratori",
      trustees: ["APEDO KOKOU OGNAKOTAN", "APEDO YAO", "APEDO ABLAVI"],
      downloadTitle: "Scarica lo statuto",
      downloadNote: "Solo la versione inglese fa fede presso la Charity Commission."
    },
    orgStructure: {
      overline: "Un'organizzazione, due entità",
      title: "SAFUAYE UK coordina SAFUAYE Togo",
      intro: "SAFUAYE è un'organizzazione internazionale composta da due entità collegate: la Charitable Incorporated Organisation (CIO) britannica — SAFUAYE UK — che patrocina e coordina le attività di SAFUAYE Togo. Sebbene le autorità togolesi abbiano voluto presentare SAFUAYE Togo come indipendente nel suo statuto locale, la nostra organizzazione internazionale resta unificata: tutte le attività togolesi sono guidate e convalidate dal Presidente Internazionale, con sede nel Regno Unito.",
      ukTitle: "SAFUAYE UK",
      ukRole: "Entità madre · Charity Commission (Inghilterra & Galles)",
      ukPoints: [
        "Forma giuridica: Charitable Incorporated Organisation (CIO)",
        "Governance: Presidente Internazionale + Trustees UK",
        "Raccolta fondi internazionale (Regno Unito, Europa)",
        "Idoneità Gift Aid (HMRC) al completamento della registrazione",
        "Rendicontazione ufficiale: Charity Commission UK"
      ],
      togoTitle: "SAFUAYE Togo",
      togoRole: "Entità operativa · MATDDT (Togo)",
      togoPoints: [
        "Registrata presso il Ministero dell'Amministrazione Territoriale del Togo",
        "Coordinamento sul campo ad Atakpamé (sede Atchrimi-Komé)",
        "Realizza i progetti: Academy, Odôn-Itsou, Cura AVC, aiuto sociale",
        "Sotto la supervisione del Presidente Internazionale (SAFUAYE UK)",
        "Rendicontazione finanziaria e operativa trasmessa a SAFUAYE UK"
      ],
      linkTitle: "Una guida unificata dal Presidente Internazionale",
      linkText: "Tutte le attività di SAFUAYE Togo sono supervisionate dal Presidente Internazionale, APEDO Kokou Ognakotan, con sede nel Regno Unito. I nostri partner — sportivi, istituzionali, filantropici — sostengono SAFUAYE UK, che alloca poi i fondi a SAFUAYE Togo secondo regole rigorose di trasparenza (rapporto trimestrale, giustificativi, audit Charity Commission).",
      cta: "Diventa partner"
    },
    transparency: {
      title: "I nostri conti, in piena chiarezza",
      intro: "SAFUAYE pubblica lo stato consolidato della tesoreria (SAFUAYE UK + SAFUAYE Togo). Le voci individuali e i giustificativi sono verificati dalla Charity Commission UK — questa pagina mostra solo aggregati anonimizzati."
    }
  }
};

function App() {
  const [lang, setLang] = useState("fr");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [legalGuidance, setLegalGuidance] = useState(null);
  const [contactForm, setContactForm] = useState({ full_name: "", email: "", phone: "", country: "", message_type: "contact", message: "" });
  const [contactSuccess, setContactSuccess] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({ name: "", relation: "", message: "" });
  const [testimonialSuccess, setTestimonialSuccess] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ full_name: "", email: "", role: "" });
  const [meetingSuccess, setMeetingSuccess] = useState(false);
  const [showMeetingAdmin, setShowMeetingAdmin] = useState(false);
  const [bachePreviewOpen, setBachePreviewOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [meetingRegistrations, setMeetingRegistrations] = useState([]);
  const [showPrivateDocuments, setShowPrivateDocuments] = useState(false);
  const [adminTab, setAdminTab] = useState("documents"); // documents, trustees, testimonials, news, messages, members
  const [adminTestimonials, setAdminTestimonials] = useState([]);
  const [adminNews, setAdminNews] = useState([]);
  const [newsForm, setNewsForm] = useState({ title: "", content: "", summary: "" });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  const WARNING_BEFORE_MS = 5 * 60 * 1000;   // warn 5 min before expiry
  const [pinInput, setPinInput] = useState("");
  const [showPinPrompt, setShowPinPrompt] = useState(false);
  const [adminContacts, setAdminContacts] = useState([]);
  const [adminMeetingRegs, setAdminMeetingRegs] = useState([]);
  const [publicNews, setPublicNews] = useState([]);
  const [publicTestimonials, setPublicTestimonials] = useState([]);
  const [adminMembers, setAdminMembers] = useState([]);
  const [downloadStats, setDownloadStats] = useState(null);
  // AVC Recensement (anonymous stroke case reporting)
  const [avcCount, setAvcCount] = useState({ total: 0, hospitalises: 0, survivants: 0, decedes: 0 });
  const [avcModalOpen, setAvcModalOpen] = useState(false);
  const [avcForm, setAvcForm] = useState({ nom_usage: "", village: "", age_approx: "", statut: "survivant", contact_famille: "", signale_par: "", consentement_famille: false, notes: "" });
  const [avcSubmitting, setAvcSubmitting] = useState(false);
  const [avcSuccess, setAvcSuccess] = useState(false);
  const [adminAvcCases, setAdminAvcCases] = useState([]);
  const [adminAcademyRecs, setAdminAcademyRecs] = useState([]);
  // Mission expense reports admin
  const [adminMissionReports, setAdminMissionReports] = useState([]);
  const [missionSelected, setMissionSelected] = useState(null);
  const [missionUploading, setMissionUploading] = useState(false);
  const [missionNewOpen, setMissionNewOpen] = useState(false);
  const [missionForm, setMissionForm] = useState({
    traveler_name: "", traveler_role: "", purpose: "",
    start_date: "", end_date: "", location: "", status: "draft",
    items: [{ category: "airfare", label: "", amount: 0, currency: "GBP", beneficiary: "", date: "", notes: "" }]
  });
  // Inline edit + signature state
  const [missionEditMode, setMissionEditMode] = useState(false);
  const [missionEditItems, setMissionEditItems] = useState([]);
  const [signaturePadOpen, setSignaturePadOpen] = useState(false);
  const signaturePadRef = useRef(null);
  const signatureCtxRef = useRef(null);
  const signatureDrawingRef = useRef(false);
  // PayPal donation form (PayPal.Me @koognak - personal account)
  const [donCurrency, setDonCurrency] = useState("EUR");
  const [donAmount, setDonAmount] = useState(25);
  const [donCustom, setDonCustom] = useState(false);
  const [donCustomValue, setDonCustomValue] = useState("");
  // Academy pre-registration
  const [acadCount, setAcadCount] = useState({ total: 0, by_category: { U7: 0, U9: 0, U11: 0, U13: 0 } });
  const [acadModalOpen, setAcadModalOpen] = useState(false);
  const [acadForm, setAcadForm] = useState({ child_name: "", child_age: "", category: "U9", parent_name: "", parent_contact: "", parent_email: "", school: "", village: "", consent_data: false, consent_photo: false, notes: "" });
  const [acadSubmitting, setAcadSubmitting] = useState(false);
  const [acadSuccess, setAcadSuccess] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  // Live countdown to Gala (30 Aug 2026, 09:00 Africa/Lome time = UTC+0)
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const [memberForm, setMemberForm] = useState({ 
    first_name: "", 
    last_name: "", 
    email: "", 
    phone: "", 
    country: "", 
    city: "", 
    role: "Membre" 
  });
  const [editingMember, setEditingMember] = useState(null);
  // HQ Renovation gallery state
  const [hqPhotos, setHqPhotos] = useState([]);
  const [hqUploadForm, setHqUploadForm] = useState({ stage: "overall", type: "before", caption: "", file: null });
  const [hqUploading, setHqUploading] = useState(false);
  const [hqCompareIndex, setHqCompareIndex] = useState({}); // { stage: sliderPercent }
  const [hqInteractedStages, setHqInteractedStages] = useState({}); // {stage: true} once user has dragged
  // Grant Distribution June 2026 state
  const [grantDistributions, setGrantDistributions] = useState([]);
  const [grantStats, setGrantStats] = useState(null);
  // Impact campaigns state
  const [impactCampaigns, setImpactCampaigns] = useState([]);
  const [impactTotals, setImpactTotals] = useState(null);
  const [impactForm, setImpactForm] = useState({
    title_fr: "", title_en: "", title_it: "", period: "",
    beneficiaries_count: 0, total_fcfa: 0, total_gbp: 0, receipts_signed_pct: 100,
    summary_fr: "", summary_en: "", summary_it: ""
  });
  const [impactPhotoUpload, setImpactPhotoUpload] = useState({});  // {campaignId: {file, caption_fr}}
  
  const t = translations[lang];

  const fetchLegalGuidance = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/legal-guidance`);
      setLegalGuidance(response.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error fetching legal guidance:", e);
      }
    }
  }, []);

  useEffect(() => {
    fetchLegalGuidance();
    fetchPublicNews();
    fetchPublicTestimonials();
    loadHqPhotos();
    loadImpactCampaigns(true);
    fetchAvcCount();
    fetchAcadCount();
    // Check for secret access to documents
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('admin')) {
      setShowPinPrompt(true);
    }
    // Auto-open AVC signalement modal from QR code links
    if (urlParams.get('report') === '1' || window.location.hash.includes('report=1')) {
      setTimeout(() => setAvcModalOpen(true), 800);
    }
    // Track QR scans (bâches / t-shirts) — one ping per unique src per session
    const src = urlParams.get('src');
    if (src) {
      const sessionKey = `qr-scan-${src}`;
      if (!sessionStorage.getItem(sessionKey)) {
        sessionStorage.setItem(sessionKey, '1');
        axios.post(`${API}/track/scan`, {
          src: src.slice(0, 64),
          referrer: document.referrer || null,
          user_agent: navigator.userAgent || null,
        }).catch(() => {});   // silent fail — tracking must never break UX
      }
      // If URL has an #adherer hash, gently scroll after render
      if (window.location.hash === '#adherer') {
        setTimeout(() => {
          const el = document.getElementById('adherer');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 900);
      }
    }
    // Intentionally only-on-mount: these loaders are stable closures and adding them
    // to deps would cause infinite reloads on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLegalGuidance]);

  const fetchAvcCount = async () => {
    try {
      const r = await axios.get(`${API}/avc/cases/count`);
      setAvcCount(r.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  };

  const fetchAcadCount = async () => {
    try {
      const r = await axios.get(`${API}/academy/preregistrations/count`);
      setAcadCount(r.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  };

  const submitAcadForm = async (e) => {
    e.preventDefault();
    if (acadSubmitting) return;
    setAcadSubmitting(true);
    try {
      await axios.post(`${API}/academy/preregistrations`, { ...acadForm, child_age: parseInt(acadForm.child_age, 10) });
      setAcadSuccess(true);
      setAcadForm({ child_name: "", child_age: "", category: "U9", parent_name: "", parent_contact: "", parent_email: "", school: "", village: "", consent_data: false, consent_photo: false, notes: "" });
      fetchAcadCount();
      setTimeout(() => { setAcadSuccess(false); setAcadModalOpen(false); }, 2500);
    } catch (err) {
      alert("Erreur : " + (err.response?.data?.detail || err.message));
    } finally {
      setAcadSubmitting(false);
    }
  };

  const submitAvcCase = async (e) => {
    e.preventDefault();
    if (avcSubmitting) return;
    setAvcSubmitting(true);
    try {
      await axios.post(`${API}/avc/cases`, avcForm);
      setAvcSuccess(true);
      setAvcForm({ nom_usage: "", village: "", age_approx: "", statut: "survivant", contact_famille: "", signale_par: "", consentement_famille: false, notes: "" });
      fetchAvcCount();
      setTimeout(() => { setAvcSuccess(false); setAvcModalOpen(false); }, 2200);
    } catch (err) {
      alert("Erreur : " + (err.response?.data?.detail || err.message));
    } finally {
      setAvcSubmitting(false);
    }
  };

  const fetchAdminAvcCases = async () => {
    try {
      const r = await axios.get(`${API}/avc/cases?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`);
      setAdminAvcCases(r.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  };

  const deleteAvcCase = async (id) => {
    if (!window.confirm("Supprimer ce cas ?")) return;
    try {
      await axios.delete(`${API}/avc/cases/${id}?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`);
      fetchAdminAvcCases();
      fetchAvcCount();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const fetchAdminAcademy = async () => {
    try {
      const r = await axios.get(`${API}/academy/preregistrations?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`);
      setAdminAcademyRecs(r.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  };

  const deleteAcademyRec = async (id) => {
    if (!window.confirm("Supprimer cette pré-inscription ?")) return;
    try {
      await axios.delete(`${API}/academy/preregistrations/${id}?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`);
      fetchAdminAcademy();
      fetchAcadCount();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  // === Mission expense reports (admin) ===
  const fetchAdminMissionReports = async () => {
    try {
      const r = await axios.get(`${API}/mission/expense-reports?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`);
      setAdminMissionReports(r.data.reports || []);
      if (missionSelected) {
        const updated = (r.data.reports || []).find(x => x.id === missionSelected.id);
        if (updated) setMissionSelected(updated);
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  };

  const createMissionReport = async () => {
    if (!missionForm.traveler_name || !missionForm.start_date || !missionForm.end_date) {
      alert("Voyageur, date de début et date de fin sont obligatoires."); return;
    }
    try {
      const clean = { ...missionForm, items: missionForm.items.filter(it => it.label && Number(it.amount) > 0)
        .map(it => ({ ...it, amount: Number(it.amount) })) };
      const r = await axios.post(`${API}/mission/expense-reports?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`, clean);
      setMissionSelected(r.data);
      setMissionNewOpen(false);
      setMissionForm({ traveler_name: "", traveler_role: "", purpose: "", start_date: "", end_date: "", location: "", status: "draft",
        items: [{ category: "airfare", label: "", amount: 0, currency: "GBP", beneficiary: "", date: "", notes: "" }] });
      fetchAdminMissionReports();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const deleteMissionReport = async (id) => {
    if (!window.confirm("Supprimer ce rapport de mission et toutes ses pièces jointes ?")) return;
    try {
      await axios.delete(`${API}/mission/expense-reports/${id}?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`);
      if (missionSelected?.id === id) setMissionSelected(null);
      fetchAdminMissionReports();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const uploadMissionAttachment = async (reportId, files, label = "") => {
    if (!files || files.length === 0) return;
    setMissionUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(
          `${API}/mission/expense-reports/${reportId}/attachments?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}&label=${encodeURIComponent(label || file.name)}`,
          fd, { headers: { "Content-Type": "multipart/form-data" } }
        );
      }
      await fetchAdminMissionReports();
    } catch (e) {
      alert("Upload échoué : " + (e.response?.data?.detail || e.message));
    } finally {
      setMissionUploading(false);
    }
  };

  const deleteMissionAttachment = async (attId) => {
    if (!window.confirm("Supprimer cette pièce jointe ?")) return;
    try {
      await axios.delete(`${API}/mission/attachments/${attId}?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`);
      fetchAdminMissionReports();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const updateMissionStatus = async (reportId, status) => {
    try {
      await axios.patch(`${API}/mission/expense-reports/${reportId}?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`, { status });
      fetchAdminMissionReports();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  // === Inline edit of items ===
  const startMissionEdit = () => {
    if (!missionSelected) return;
    setMissionEditItems(JSON.parse(JSON.stringify(missionSelected.items || [])));
    setMissionEditMode(true);
  };
  const saveMissionEdit = async () => {
    if (!missionSelected) return;
    const cleanItems = missionEditItems
      .filter(it => it.label && Number(it.amount) > 0)
      .map(it => ({ ...it, amount: Number(it.amount), id: it.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)) }));
    try {
      await axios.patch(`${API}/mission/expense-reports/${missionSelected.id}?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`, { items: cleanItems });
      setMissionEditMode(false);
      fetchAdminMissionReports();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  // === CSV export (all reports flattened) ===
  const exportMissionCSV = () => {
    if (!adminMissionReports || adminMissionReports.length === 0) {
      alert("Aucun rapport à exporter."); return;
    }
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const headers = [
      "report_id", "traveler_name", "traveler_role", "start_date", "end_date",
      "location", "purpose", "status",
      "item_category", "item_label", "amount", "currency", "amount_eur",
      "beneficiary", "item_date", "attachments_count"
    ];
    const rows = [headers.join(",")];
    for (const r of adminMissionReports) {
      const attCount = (r.attachments || []).length;
      const items = (r.items || []).length ? r.items : [{}];
      for (const it of items) {
        rows.push([
          r.id, r.traveler_name, r.traveler_role, r.start_date, r.end_date,
          r.location, r.purpose, r.status,
          it.category, it.label, it.amount, it.currency,
          it.amount ? toEUR(it.amount, it.currency).toFixed(2) : "",
          it.beneficiary, it.date, attCount
        ].map(escape).join(","));
      }
    }
    const csv = "\ufeff" + rows.join("\n"); // UTF-8 BOM for Excel
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `SAFUAYE_notes_frais_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // === Signature pad (touch + mouse) ===
  const openSignaturePad = () => {
    setSignaturePadOpen(true);
    setTimeout(() => {
      const canvas = signaturePadRef.current;
      if (!canvas) return;
      canvas.width = canvas.offsetWidth;
      canvas.height = 200;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.lineJoin = "round";
      signatureCtxRef.current = ctx;
    }, 100);
  };
  const sigPos = (e) => {
    const canvas = signaturePadRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const sigStart = (e) => {
    e.preventDefault();
    signatureDrawingRef.current = true;
    const { x, y } = sigPos(e);
    signatureCtxRef.current.beginPath();
    signatureCtxRef.current.moveTo(x, y);
  };
  const sigMove = (e) => {
    if (!signatureDrawingRef.current) return;
    e.preventDefault();
    const { x, y } = sigPos(e);
    signatureCtxRef.current.lineTo(x, y);
    signatureCtxRef.current.stroke();
  };
  const sigEnd = () => { signatureDrawingRef.current = false; };
  const sigClear = () => {
    const canvas = signaturePadRef.current;
    const ctx = signatureCtxRef.current;
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  };
  const saveSignature = async () => {
    if (!missionSelected) return;
    const canvas = signaturePadRef.current;
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      if (!blob) { alert("Impossible de capturer la signature."); return; }
      const fd = new FormData();
      const file = new File([blob], `signature_${missionSelected.traveler_name.replace(/\s+/g,'_')}_${Date.now()}.png`, { type: "image/png" });
      fd.append("file", file);
      const label = `✍️ SIGNATURE — ${missionSelected.traveler_name} — ${new Date().toLocaleString('fr-FR')}`;
      try {
        await axios.post(
          `${API}/mission/expense-reports/${missionSelected.id}/attachments?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}&label=${encodeURIComponent(label)}`,
          fd, { headers: { "Content-Type": "multipart/form-data" } }
        );
        // auto-approve when signed
        await axios.patch(`${API}/mission/expense-reports/${missionSelected.id}?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`, { status: "approved" });
        setSignaturePadOpen(false);
        fetchAdminMissionReports();
      } catch (e) {
        alert("Erreur signature : " + (e.response?.data?.detail || e.message));
      }
    }, "image/png");
  };

  // Conversion helpers for mission accounting
  const MISSION_RATES = { GBP_EUR: 1.170, EUR_XOF: 655.957 };
  const toEUR = (amount, currency) => {
    if (!amount) return 0;
    if (currency === "EUR") return Number(amount);
    if (currency === "GBP") return Number(amount) * MISSION_RATES.GBP_EUR;
    if (currency === "XOF") return Number(amount) / MISSION_RATES.EUR_XOF;
    return Number(amount);
  };
  const fmtEUR = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
  const fmtFCFA = (n) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(n || 0)) + ' FCFA';
  const missionTotalEUR = (report) => (report?.items || []).reduce((s, it) => s + toEUR(it.amount, it.currency), 0);
  const missionTotalFCFA = (report) => missionTotalEUR(report) * MISSION_RATES.EUR_XOF;

  const fetchPublicNews = async () => {
    try {
      const response = await axios.get(`${API}/news?language=${lang}&published_only=true`);
      setPublicNews(response.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error fetching news:", e);
      }
    }
  };

  const fetchPublicTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials?language=${lang}&approved_only=true`);
      setPublicTestimonials(response.data || []);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error fetching testimonials:", e);
      }
    }
  };

  const handlePinSubmit = () => {
    if (pinInput === 'SAFUAYE2024') {
      setIsAuthenticated(true);
      setShowPinPrompt(false);
      setShowPrivateDocuments(true);
      setPinInput("");
      setLastActivity(Date.now());
      loadDownloadStats();
    } else {
      alert("❌ Code PIN incorrect !");
      setPinInput("");
    }
  };

  // Session timeout — auto logout after 30 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated) {
      setTimeoutWarning(false);
      return;
    }
    const bump = () => setLastActivity(Date.now());
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivity;
      const remaining = SESSION_TIMEOUT_MS - elapsed;
      if (remaining <= 0) {
        setIsAuthenticated(false);
        setShowPrivateDocuments(false);
        setTimeoutWarning(false);
        alert("🔒 Session expirée après 30 minutes d'inactivité. Reconnectez-vous.");
      } else if (remaining <= WARNING_BEFORE_MS) {
        setTimeoutWarning(true);
      } else {
        setTimeoutWarning(false);
      }
    }, 15000);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, bump));
      clearInterval(interval);
    };
  }, [isAuthenticated, lastActivity]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, { ...contactForm, language: lang });
      setContactSuccess(true);
      setContactForm({ full_name: "", email: "", phone: "", country: "", message_type: "contact", message: "" });
      setTimeout(() => setContactSuccess(false), 3000);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error submitting contact:", e);
      }
    }
  };

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    try {
      // Map frontend fields to backend expected fields
      const payload = {
        author_name: testimonialForm.name,
        relationship: testimonialForm.relation,
        message: testimonialForm.message,
        language: lang
      };
      await axios.post(`${API}/testimonials`, payload);
      setTestimonialSuccess(true);
      setTestimonialForm({ name: "", relation: "", message: "" });
      setTimeout(() => setTestimonialSuccess(false), 5000);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error submitting testimonial:", e);
      }
    }
  };

  const handleMeetingRegistration = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/meeting/register`, meetingForm);
      setMeetingSuccess(true);
      setMeetingForm({ full_name: "", email: "", role: "" });
      setTimeout(() => setMeetingSuccess(false), 6000);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error submitting meeting registration:", e);
      }
    }
  };

  const loadEmailTemplate = async () => {
    try {
      const response = await axios.get(`${API}/meeting/email-template`);
      setEmailTemplate(response.data);
      const regs = await axios.get(`${API}/meeting/registrations`);
      setMeetingRegistrations(regs.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error loading email template:", e);
      }
    }
  };

  const copyEmailToClipboard = () => {
    if (emailTemplate) {
      const fullEmail = `Objet: ${emailTemplate.subject}\n\n${emailTemplate.body}`;
      navigator.clipboard.writeText(fullEmail);
      alert("Email copié dans le presse-papier !");
    }
  };

  // Admin functions for testimonials and news
  const loadAdminTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials/admin-list`, {
        params: { pin: "SAFUAYE2024" },
      });
      setAdminTestimonials(response.data?.items || []);
    } catch (e) {
      // fallback to public endpoint if admin-list not available
      try {
        const response = await axios.get(`${API}/testimonials?approved_only=false`);
        setAdminTestimonials(response.data);
      } catch (e2) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Error loading testimonials:", e2);
        }
      }
    }
  };

  const analyzeTestimonialAi = async (id) => {
    try {
      await axios.post(`${API}/ai/analyze-testimonial`, { pin: "SAFUAYE2024", testimonial_id: id });
      loadAdminTestimonials();
    } catch (e) {
      alert("❌ Erreur IA : " + (e.response?.data?.detail || e.message));
    }
  };

  const analyzeAllTestimonialsAi = async (reanalyze = false) => {
    if (!window.confirm(reanalyze
      ? "Re-analyser TOUS les témoignages avec Gemini IA ?\n(Peut prendre plusieurs minutes)"
      : "Analyser les témoignages non encore traités avec Gemini IA ?"
    )) return;
    try {
      const r = await axios.post(`${API}/ai/analyze-testimonials-bulk`, { pin: "SAFUAYE2024", reanalyze });
      alert(`✅ Analyse terminée : ${r.data.analyzed}/${r.data.total} témoignages analysés.`);
      loadAdminTestimonials();
    } catch (e) {
      alert("❌ Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const featureTestimonial = async (id, featured) => {
    try {
      await axios.put(`${API}/testimonials/${id}/feature`, { pin: "SAFUAYE2024", featured });
      loadAdminTestimonials();
      fetchPublicTestimonials();
    } catch (e) {
      alert("❌ " + (e.response?.data?.detail || e.message));
    }
  };

  const approveTestimonial = async (id) => {
    try {
      await axios.put(`${API}/testimonials/${id}/approve`);
      loadAdminTestimonials();
      fetchPublicTestimonials();
      alert("✅ Témoignage approuvé !");
    } catch (e) {
      alert("Erreur lors de l'approbation");
    }
  };

  const deleteTestimonial = async (id) => {
    if (window.confirm("Supprimer ce témoignage ?")) {
      try {
        await axios.delete(`${API}/testimonials/${id}`);
        loadAdminTestimonials();
        fetchPublicTestimonials();
        alert("✅ Témoignage supprimé !");
      } catch (e) {
        alert("Erreur lors de la suppression");
      }
    }
  };

  const loadAdminNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setAdminNews(response.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error loading news:", e);
      }
    }
  };

  const createNews = async () => {
    if (!newsForm.title || !newsForm.content) {
      alert("Veuillez remplir tous les champs");
      return;
    }
    try {
      await axios.post(`${API}/news`, { ...newsForm, language: lang });
      setNewsForm({ title: "", content: "", summary: "" });
      loadAdminNews();
      alert("✅ Actualité créée !");
    } catch (e) {
      alert("Erreur lors de la création");
    }
  };

  const deleteNews = async (id) => {
    if (window.confirm("Supprimer cette actualité ?")) {
      try {
        await axios.delete(`${API}/news/${id}`);
        loadAdminNews();
        alert("✅ Actualité supprimée !");
      } catch (e) {
        alert("Erreur lors de la suppression");
      }
    }
  };

  const loadAdminContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setAdminContacts(response.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error loading contacts:", e);
      }
    }
  };

  const loadAdminMeetingRegs = async () => {
    try {
      const response = await axios.get(`${API}/meeting/registrations`);
      setAdminMeetingRegs(response.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error loading meeting registrations:", e);
      }
    }
  };

  const loadDownloadStats = async () => {
    try {
      const response = await axios.get(`${API}/constitution/download-stats`);
      setDownloadStats(response.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error loading download stats:", e);
      }
    }
  };

  // HQ Renovation photos
  const loadHqPhotos = async () => {
    try {
      const response = await axios.get(`${API}/hq/photos`);
      setHqPhotos(response.data.photos || []);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("Error loading HQ photos:", e);
    }
  };

  const uploadHqPhoto = async () => {
    if (!hqUploadForm.file) { alert("Veuillez choisir un fichier image."); return; }
    setHqUploading(true);
    try {
      const originalSize = hqUploadForm.file.size;
      const compressed = await compressImageFile(hqUploadForm.file);
      const fd = new FormData();
      fd.append("file", compressed);
      fd.append("stage", hqUploadForm.stage);
      fd.append("type", hqUploadForm.type);
      fd.append("caption", hqUploadForm.caption || "");
      await axios.post(`${API}/hq/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHqUploadForm({ stage: hqUploadForm.stage, type: hqUploadForm.type, caption: "", file: null });
      await loadHqPhotos();
      const ratio = originalSize > 0 ? Math.round((1 - compressed.size / originalSize) * 100) : 0;
      const sizeKb = Math.round(compressed.size / 1024);
      alert(`✅ Photo ajoutée ! (${sizeKb} Ko${ratio > 0 ? `, -${ratio}% vs original` : ''})`);
    } catch (e) {
      alert("Erreur upload : " + (e?.response?.data?.detail || e.message));
    } finally {
      setHqUploading(false);
    }
  };

  const deleteHqPhoto = async (id) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await axios.delete(`${API}/hq/photos/${id}`);
      await loadHqPhotos();
    } catch (e) {
      alert("Erreur suppression");
    }
  };

  // Grant Distribution June 2026
  const loadGrantDistributions = async () => {
    try {
      const r = await axios.get(`${API}/grants/distributions`);
      setGrantDistributions(r.data.distributions || []);
      setGrantStats(r.data.stats || null);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("Error loading grants:", e);
    }
  };

  const seedGrantDistributions = async () => {
    try {
      await axios.post(`${API}/grants/seed-june2026`);
      await loadGrantDistributions();
    } catch (e) { alert("Erreur seed: " + e.message); }
  };

  const updateGrantDistribution = async (id, payload) => {
    try {
      await axios.patch(`${API}/grants/distributions/${id}`, payload);
      await loadGrantDistributions();
    } catch (e) { alert("Erreur mise à jour"); }
  };

  const deleteGrantDistribution = async (id) => {
    if (!window.confirm("Supprimer cette ligne de distribution ?")) return;
    try {
      await axios.delete(`${API}/grants/distributions/${id}`);
      await loadGrantDistributions();
    } catch (e) { alert("Erreur suppression"); }
  };

  // Impact campaigns
  const loadImpactCampaigns = async (publicOnly = false) => {
    try {
      const r = await axios.get(`${API}/impact/campaigns${publicOnly ? "?published_only=true" : ""}`);
      setImpactCampaigns(r.data.campaigns || []);
      setImpactTotals(r.data.totals || null);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("Error loading impact:", e);
    }
  };

  const createImpactCampaign = async () => {
    if (!impactForm.title_fr) { alert("Le titre FR est obligatoire"); return; }
    try {
      await axios.post(`${API}/impact/campaigns`, impactForm);
      setImpactForm({
        title_fr: "", title_en: "", title_it: "", period: "",
        beneficiaries_count: 0, total_fcfa: 0, total_gbp: 0, receipts_signed_pct: 100,
        summary_fr: "", summary_en: "", summary_it: ""
      });
      await loadImpactCampaigns();
    } catch (e) { alert("Erreur création"); }
  };

  const updateImpactCampaign = async (id, payload) => {
    try {
      await axios.patch(`${API}/impact/campaigns/${id}`, payload);
      await loadImpactCampaigns();
    } catch (e) { alert("Erreur mise à jour"); }
  };

  const deleteImpactCampaign = async (id) => {
    if (!window.confirm("Supprimer cette campagne et toutes ses photos ?")) return;
    try {
      await axios.delete(`${API}/impact/campaigns/${id}`);
      await loadImpactCampaigns();
    } catch (e) { alert("Erreur suppression"); }
  };

  const uploadImpactPhoto = async (campaignId) => {
    const upload = impactPhotoUpload[campaignId];
    if (!upload?.file) { alert("Sélectionnez un fichier"); return; }
    setImpactPhotoUpload(prev => ({ ...prev, [campaignId]: { ...prev[campaignId], uploading: true } }));
    try {
      const originalSize = upload.file.size;
      const compressed = await compressImageFile(upload.file);
      const fd = new FormData();
      fd.append("file", compressed);
      fd.append("caption_fr", upload.caption_fr || "");
      fd.append("caption_en", upload.caption_en || "");
      fd.append("caption_it", upload.caption_it || "");
      await axios.post(`${API}/impact/campaigns/${campaignId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImpactPhotoUpload(prev => ({ ...prev, [campaignId]: { file: null, caption_fr: "", caption_en: "", caption_it: "", uploading: false } }));
      await loadImpactCampaigns();
      const ratio = originalSize > 0 ? Math.round((1 - compressed.size / originalSize) * 100) : 0;
      const sizeKb = Math.round(compressed.size / 1024);
      alert(`✅ Photo ajoutée ! (${sizeKb} Ko${ratio > 0 ? `, -${ratio}% vs original` : ''})`);
    } catch (e) {
      setImpactPhotoUpload(prev => ({ ...prev, [campaignId]: { ...prev[campaignId], uploading: false } }));
      alert("Erreur upload : " + (e?.response?.data?.detail || e.message));
    }
  };

  const deleteImpactPhoto = async (photoId) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await axios.delete(`${API}/impact/photos/${photoId}`);
      await loadImpactCampaigns();
    } catch (e) { alert("Erreur suppression"); }
  };

  const deleteContact = async (id) => {
    if (window.confirm("Supprimer ce message ?")) {
      try {
        await axios.delete(`${API}/contacts/${id}`);
        loadAdminContacts();
        alert("✅ Message supprimé !");
      } catch (e) {
        alert("Erreur lors de la suppression");
      }
    }
  };

  // Members functions
  const loadAdminMembers = async () => {
    try {
      const response = await axios.get(`${API}/members`);
      setAdminMembers(response.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error loading members:", e);
      }
    }
  };

  const createMember = async () => {
    if (!memberForm.first_name || !memberForm.last_name || !memberForm.email || !memberForm.country) {
      alert("Veuillez remplir tous les champs obligatoires (Prénom, Nom, Email, Pays)");
      return;
    }
    try {
      if (editingMember) {
        // Update existing member
        await axios.put(`${API}/members/${editingMember.id}`, memberForm);
        setEditingMember(null);
        alert("✅ Membre modifié !");
      } else {
        // Create new member
        await axios.post(`${API}/members`, memberForm);
        alert("✅ Membre ajouté !");
      }
      setMemberForm({ first_name: "", last_name: "", email: "", phone: "", country: "", city: "", role: "Membre" });
      loadAdminMembers();
    } catch (e) {
      alert("Erreur lors de l'opération");
    }
  };

  const editMember = (member) => {
    setMemberForm({
      first_name: member.first_name,
      last_name: member.last_name,
      email: member.email,
      phone: member.phone || "",
      country: member.country,
      city: member.city || "",
      role: member.role
    });
    setEditingMember(member);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteMember = async (id) => {
    if (window.confirm("Supprimer ce membre ?")) {
      try {
        await axios.delete(`${API}/members/${id}`);
        loadAdminMembers();
        alert("✅ Membre supprimé !");
      } catch (e) {
        alert("Erreur lors de la suppression");
      }
    }
  };

  const cancelEdit = () => {
    setEditingMember(null);
    setMemberForm({ first_name: "", last_name: "", email: "", phone: "", country: "", city: "", role: "Membre" });
  };

  const generateMeetingMinutesPDF = async () => {
    try {
      const regs = await axios.get(`${API}/meeting/registrations`);
      const registrations = regs.data.slice(0, 3); // Only take first 3 (the trustees)
      
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = margin;
      
      // Header with logo area
      doc.setFillColor(21, 128, 61);
      doc.rect(0, 0, pageWidth, 35, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      doc.setTextColor(255, 255, 255);
      doc.text('SAFUAYE', pageWidth / 2, 15, { align: 'center' });
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text('Association | UK • Italie • Suisse • Togo', pageWidth / 2, 25, { align: 'center' });
      
      yPos = 50;
      
      // Title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(21, 128, 61);
      doc.text('PROCÈS-VERBAL', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 8;
      doc.setFontSize(14);
      doc.setTextColor(30, 30, 30);
      doc.text('Assemblée Constitutive des Trustees', pageWidth / 2, yPos, { align: 'center' });
      doc.setFontSize(11);
      yPos += 6;
      doc.text('Charitable Incorporated Organisation (CIO)', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 18;
      
      // Meeting info box
      doc.setFillColor(245, 245, 245);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 32, 3, 3, 'F');
      
      yPos += 8;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      doc.text('Date: Dimanche 18 Avril 2026', margin + 5, yPos);
      yPos += 6;
      doc.text('Heure: 11h00 - 13h00 (Heure de Londres)', margin + 5, yPos);
      yPos += 6;
      doc.text('Lieu: Réunion en ligne (Google Meet)', margin + 5, yPos);
      yPos += 6;
      doc.text('Trustees présents: 3 (quorum atteint)', margin + 5, yPos);
      
      yPos += 18;
      
      // Trustees list
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(21, 128, 61);
      doc.text('TRUSTEES PRÉSENTS:', margin, yPos);
      
      yPos += 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      
      const colors = [[59, 130, 246], [34, 197, 94], [168, 85, 247]]; // blue, green, purple
      registrations.forEach((reg, index) => {
        doc.setFillColor(...colors[index]);
        doc.setDrawColor(...colors[index]);
        doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 12, 2, 2, 'FD');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text(`${index + 1}. ${reg.full_name}`, margin + 3, yPos + 5);
        doc.setFont('helvetica', 'normal');
        doc.text(`- ${reg.role}`, margin + 3, yPos + 9);
        yPos += 14;
      });
      
      yPos += 8;
      
      // Ordre du jour
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(21, 128, 61);
      doc.text('ORDRE DU JOUR:', margin, yPos);
      
      yPos += 9;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      
      const agendaItems = [
        "1. Ouverture de la séance et appel des présents",
        "2. Adoption de la Constitution (CIO)",
        "3. Nomination officielle des 3 Trustees",
        "4. Définition des objectifs caritatifs",
        "5. Vote sur les résolutions",
        "6. Signature du procès-verbal",
        "7. Clôture"
      ];
      
      agendaItems.forEach(item => {
        doc.text(item, margin + 5, yPos);
        yPos += 6;
      });
      
      // New page for decisions
      doc.addPage();
      yPos = margin + 10;
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(21, 128, 61);
      doc.text('DÉCISIONS ADOPTÉES', margin, yPos);
      
      yPos += 14;
      
      // Decision 1
      doc.setFillColor(240, 253, 244);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 30, 3, 3, 'F');
      yPos += 8;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(22, 101, 52);
      doc.text('RÉSOLUTION 1: Adoption de la Constitution', margin + 5, yPos);
      yPos += 7;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      doc.text('La Constitution de SAFUAYE en tant que CIO a été adoptée à l\'unanimité.', margin + 5, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(22, 101, 52);
      doc.text('Résultat du vote: POUR: 3 | CONTRE: 0 | Abstentions: 0', margin + 5, yPos);
      
      yPos += 20;
      
      // Decision 2
      doc.setFillColor(239, 246, 255);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 40, 3, 3, 'F');
      yPos += 8;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(29, 78, 216);
      doc.text('RÉSOLUTION 2: Nomination des Trustees', margin + 5, yPos);
      yPos += 7;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      doc.text('Les 3 Trustees suivants ont été officiellement nommés:', margin + 5, yPos);
      yPos += 6;
      registrations.forEach(t => {
        doc.text(`• ${t.full_name} - ${t.role}`, margin + 10, yPos);
        yPos += 5;
      });
      yPos += 2;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(29, 78, 216);
      doc.text('Résultat du vote: APPROUVÉ à l\'unanimité', margin + 5, yPos);
      
      yPos += 20;
      
      // Decision 3
      doc.setFillColor(254, 243, 199);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 24, 3, 3, 'F');
      yPos += 8;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(146, 64, 14);
      doc.text('RÉSOLUTION 3: Objectifs Caritatifs', margin + 5, yPos);
      yPos += 7;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      doc.text('Les objectifs caritatifs de l\'association ont été définis et approuvés.', margin + 5, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(146, 64, 14);
      doc.text('Résultat du vote: POUR: 3 | CONTRE: 0 | Abstentions: 0', margin + 5, yPos);
      
      // Signatures section (new page)
      doc.addPage();
      yPos = margin + 10;
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(21, 128, 61);
      doc.text('SIGNATURES DES TRUSTEES', margin, yPos);
      
      yPos += 10;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Les soussignés certifient l\'exactitude du présent procès-verbal.', margin, yPos);
      
      yPos += 18;
      
      // Large signature boxes for 3 trustees
      registrations.forEach((reg, index) => {
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 50, 3, 3);
        
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...colors[index]);
        doc.text(reg.full_name, margin + 5, yPos + 8);
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(reg.role, margin + 5, yPos + 14);
        
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.text('Signature:', margin + 5, yPos + 26);
        
        // Signature line
        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.3);
        doc.line(margin + 25, yPos + 35, pageWidth - margin - 40, yPos + 35);
        
        doc.setFont('helvetica', 'normal');
        doc.text('Date:', pageWidth - margin - 35, yPos + 26);
        doc.line(pageWidth - margin - 28, yPos + 35, pageWidth - margin - 5, yPos + 35);
        
        yPos += 58;
      });
      
      // Certification text
      yPos += 10;
      doc.setFillColor(245, 245, 245);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 20, 3, 3, 'F');
      yPos += 8;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      const certText = 'Ce procès-verbal a été établi le 18 Avril 2026 et reflète fidèlement les décisions prises lors de l\'Assemblée Constitutive de SAFUAYE.';
      doc.text(certText, pageWidth / 2, yPos, { align: 'center', maxWidth: pageWidth - 2 * margin - 10 });
      yPos += 5;
      doc.text('Document officiel pour l\'enregistrement auprès de la Charity Commission (UK)', pageWidth / 2, yPos, { align: 'center' });
      
      // Footer on all pages
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFillColor(21, 128, 61);
        doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.text('SAFUAYE | contact@safuaye.org | +44 7398 308 310 | safuaye.org', pageWidth / 2, pageHeight - 6, { align: 'center' });
      }
      
      doc.save('SAFUAYE_PV_Trustees_18_Avril_2026.pdf');
      alert('✅ Procès-verbal des 3 Trustees généré avec succès !');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error generating PV:', error);
      }
      alert('Erreur lors de la génération du PV. Assurez-vous que les 3 trustees sont enregistrés.');
    }
  };

  // Generate CIO Constitution PDF
  // Generate Trustee Declaration Form PDF
  const generateTrusteeDeclarationPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;
    
    const greenColor = [21, 128, 61];
    const darkColor = [30, 30, 30];
    
    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...greenColor);
    doc.text('TRUSTEE DECLARATION FORM', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.setFontSize(12);
    doc.text('SAFUAYE', pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Charitable Incorporated Organisation', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 15;
    doc.setTextColor(...darkColor);
    
    // Personal Details Section
    doc.setFont('helvetica', 'bold');
    doc.text('SECTION 1: PERSONAL DETAILS', margin, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    const fields = [
      'Full Legal Name: _________________________________________________',
      'Date of Birth: ____/____/________',
      'Nationality: _________________________________________________',
      'Home Address: _________________________________________________',
      '_________________________________________________',
      'Postcode: ___________________ Country: ___________________',
      'Email Address: _________________________________________________',
      'Telephone: _________________________________________________'
    ];
    fields.forEach(field => { doc.text(field, margin, yPos); yPos += 8; });
    
    // Declaration Section
    yPos += 10;
    doc.setFont('helvetica', 'bold');
    doc.text('SECTION 2: DECLARATION', margin, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const declarations = [
      'I declare that:',
      '',
      '☐ I am willing to act as a charity trustee of SAFUAYE',
      '',
      '☐ I am aged 16 years or over',
      '',
      '☐ I am not disqualified from acting as a charity trustee under the Charities Act 2011',
      '',
      '☐ I am not an undischarged bankrupt',
      '',
      '☐ I have not been removed from trusteeship of a charity by the Charity Commission or the Court',
      '',
      '☐ I am not disqualified from being a company director',
      '',
      '☐ I have not been convicted of an offence involving deception or dishonesty (unless the conviction is spent)',
      '',
      '☐ I understand that my details will be held by the Charity Commission and may be available for public inspection'
    ];
    declarations.forEach(decl => { doc.text(decl, margin, yPos); yPos += 5; });
    
    // Signature
    yPos += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('SECTION 3: SIGNATURE', margin, yPos);
    yPos += 10;
    
    doc.setFont('helvetica', 'normal');
    doc.text('Signature: _________________________________________________', margin, yPos);
    yPos += 10;
    doc.text('Print Name: _________________________________________________', margin, yPos);
    yPos += 10;
    doc.text('Date: ____/____/________', margin, yPos);
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('SAFUAYE Trustee Declaration Form - For Charity Commission Registration', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    doc.save('SAFUAYE_Trustee_Declaration_Form.pdf');
  };

  // Generate Minutes of First Meeting PDF
  const generateMinutesPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;
    
    const greenColor = [21, 128, 61];
    const darkColor = [30, 30, 30];
    const today = new Date().toLocaleDateString('en-GB');
    
    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...greenColor);
    doc.text('MINUTES OF THE FIRST MEETING', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.setFontSize(14);
    doc.text('OF THE CHARITY TRUSTEES', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.setFontSize(12);
    doc.text('SAFUAYE', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 15;
    doc.setTextColor(...darkColor);
    doc.setFontSize(10);
    
    // Meeting Details
    doc.setFont('helvetica', 'bold');
    doc.text('Meeting Details:', margin, yPos);
    yPos += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${today}`, margin, yPos); yPos += 6;
    doc.text('Time: ____________', margin, yPos); yPos += 6;
    doc.text('Location: 26b Langley Park Road, SM2 5EN Sutton, UK (or via video conference)', margin, yPos);
    
    yPos += 12;
    doc.setFont('helvetica', 'bold');
    doc.text('Present:', margin, yPos);
    yPos += 7;
    doc.setFont('helvetica', 'normal');
    doc.text('1. APEDO KOKOU OGNAKOTAN (Chair)', margin + 5, yPos); yPos += 5;
    doc.text('2. Yao Blaise Apedo', margin + 5, yPos); yPos += 5;
    doc.text('3. APEDO ABLAVI', margin + 5, yPos);
    
    yPos += 12;
    doc.setFont('helvetica', 'bold');
    doc.text('AGENDA AND RESOLUTIONS:', margin, yPos);
    yPos += 10;
    
    // Agenda Items
    const agendaItems = [
      {
        title: '1. Welcome and Appointment of Chair',
        content: 'APEDO KOKOU OGNAKOTAN was unanimously appointed as Chair of the meeting and of the CIO.'
      },
      {
        title: '2. Adoption of the Constitution',
        content: 'The Constitution of SAFUAYE was presented and discussed. It was RESOLVED that the Constitution be adopted as the governing document of the CIO.'
      },
      {
        title: '3. Confirmation of Charity Trustees',
        content: 'The following persons were confirmed as the first charity trustees of SAFUAYE:\n- APEDO KOKOU OGNAKOTAN (Chair)\n- Yao Blaise Apedo (Trustee)\n- APEDO ABLAVI (Trustee)'
      },
      {
        title: '4. Registered Office',
        content: 'It was RESOLVED that the registered office of the CIO shall be: 26b Langley Park Road, SM2 5EN Sutton, United Kingdom.'
      },
      {
        title: '5. Bank Account',
        content: 'It was RESOLVED to open a bank account in the name of SAFUAYE. The Chair was authorized to complete the necessary formalities.'
      },
      {
        title: '6. Registration with Charity Commission',
        content: 'It was RESOLVED to apply for registration with the Charity Commission for England and Wales once the CIO\'s annual income exceeds £5,000.'
      },
      {
        title: '7. Any Other Business',
        content: 'No other business was raised.'
      },
      {
        title: '8. Close of Meeting',
        content: 'The meeting was closed at ____________.'
      }
    ];
    
    agendaItems.forEach(item => {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = margin;
      }
      doc.setFont('helvetica', 'bold');
      doc.text(item.title, margin, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      const contentLines = doc.splitTextToSize(item.content, pageWidth - 2 * margin);
      contentLines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
      yPos += 6;
    });
    
    // Signatures
    yPos += 10;
    if (yPos > pageHeight - 80) {
      doc.addPage();
      yPos = margin;
    }
    doc.setFont('helvetica', 'bold');
    doc.text('Signed as a true record by the Charity Trustees:', margin, yPos);
    yPos += 15;
    
    doc.setFont('helvetica', 'normal');
    // Chair signature
    doc.text('Chair (APEDO KOKOU OGNAKOTAN): _________________________________', margin, yPos);
    doc.text('Date: _________________', margin + 120, yPos);
    yPos += 15;
    
    // Vice-Chair signature
    doc.text('Vice-Chair (Yao Blaise Apedo): _________________________________', margin, yPos);
    doc.text('Date: _________________', margin + 120, yPos);
    yPos += 15;
    
    // Secretary signature
    doc.text('Secretary (APEDO ABLAVI): _________________________________', margin, yPos);
    doc.text('Date: _________________', margin + 120, yPos);
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('SAFUAYE - Minutes of First Meeting of Charity Trustees', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    doc.save('SAFUAYE_Minutes_First_Meeting.pdf');
  };

  // Generate Bank Account Letter PDF
  const generateBankLetterPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;
    
    const greenColor = [21, 128, 61];
    const darkColor = [30, 30, 30];
    const today = new Date().toLocaleDateString('en-GB');
    
    // Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...greenColor);
    doc.text('SAFUAYE', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.text('26b Langley Park Road', margin, yPos); yPos += 5;
    doc.text('SM2 5EN Sutton', margin, yPos); yPos += 5;
    doc.text('United Kingdom', margin, yPos); yPos += 5;
    doc.text('Email: contact@safuaye.org', margin, yPos); yPos += 5;
    doc.text('Tel: +44 7398 308 310', margin, yPos);
    
    yPos += 15;
    doc.text(`Date: ${today}`, margin, yPos);
    
    yPos += 15;
    doc.text('To: The Manager', margin, yPos); yPos += 5;
    doc.text('[Bank Name]', margin, yPos); yPos += 5;
    doc.text('[Branch Address]', margin, yPos);
    
    yPos += 15;
    doc.setFont('helvetica', 'bold');
    doc.text('RE: Application to Open a Charity Bank Account', margin, yPos);
    
    yPos += 10;
    doc.setFont('helvetica', 'normal');
    const paragraphs = [
      'Dear Sir/Madam,',
      '',
      'We are writing to request the opening of a bank account for SAFUAYE, a newly established Charitable Incorporated Organisation (CIO) based in England.',
      '',
      'About SAFUAYE:',
      'SAFUAYE is a charitable organisation established in memory of Simon-Joseph Afantchédé APEDO, known as "Safuaye", who was President of Ifodjè Football Club in Atakpamé, Togo, from 1984 to 1990. Our charity aims to:',
      '• Support former football players and their families in Atakpamé, Togo',
      '• Relieve poverty among vulnerable communities',
      '• Promote amateur sport for young people',
      '',
      'Registered Address: 26b Langley Park Road, SM2 5EN Sutton, United Kingdom',
      '',
      'Authorised Signatories:',
      'We request that the following trustees be authorised to operate the account:',
      '',
      '1. APEDO KOKOU OGNAKOTAN (Chair) - Primary signatory',
      '2. Yao Blaise Apedo (Trustee)',
      '3. APEDO ABLAVI (Trustee)',
      '',
      'We propose that any two of the above signatories be required to authorise transactions above £500.',
      '',
      'Please find enclosed:',
      '• Copy of the CIO Constitution',
      '• Minutes of the first trustees\' meeting',
      '• Trustee declaration forms',
      '• Identification documents for all trustees',
      '',
      'We would be grateful if you could advise us of any additional documentation required and the procedure for opening the account.',
      '',
      'Thank you for your assistance.',
      '',
      'Yours faithfully,',
      '',
      '',
      '',
      '_________________________________',
      'APEDO KOKOU OGNAKOTAN',
      'Chair, SAFUAYE'
    ];
    
    paragraphs.forEach(para => {
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = margin;
      }
      const lines = doc.splitTextToSize(para, pageWidth - 2 * margin);
      lines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    });
    
    doc.save('SAFUAYE_Bank_Account_Application_Letter.pdf');
  };

  // ============ TOGO DOCUMENTS ============

  // Generate Statuts Togo PDF
  const generateStatutsTogoPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;
    
    const greenColor = [21, 128, 61];
    const darkColor = [30, 30, 30];
    
    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...greenColor);
    doc.text('STATUTS DE L\'ASSOCIATION', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
    doc.setFontSize(20);
    doc.text('SAFUAYE', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...darkColor);
    doc.text('Association à but non lucratif régie par la loi togolaise', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 15;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('TITRE I : CONSTITUTION - DÉNOMINATION - SIÈGE - DURÉE', margin, yPos);
    yPos += 10;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.text('Article 1 : Constitution', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const art1 = 'Il est fondé entre les adhérents aux présents statuts une association à but non lucratif dénommée « SAFUAYE », en mémoire de Simon-Joseph Afantchédé APEDO, dit « Safuaye ».';
    const art1Lines = doc.splitTextToSize(art1, pageWidth - 2 * margin);
    art1Lines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Article 2 : Siège Social', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('Le siège social de l\'association est fixé à Atakpamé, Préfecture de l\'Ogou, République Togolaise.', margin, yPos);
    yPos += 5;
    doc.text('Il pourra être transféré par simple décision du Bureau Exécutif.', margin, yPos);
    
    yPos += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Article 3 : Durée', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('L\'association est constituée pour une durée illimitée.', margin, yPos);
    
    // Titre II
    yPos += 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('TITRE II : OBJET ET MOYENS D\'ACTION', margin, yPos);
    yPos += 10;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.text('Article 4 : Objet', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('L\'association a pour objet :', margin, yPos);
    yPos += 6;
    const objets = [
      '• Soutenir les anciens joueurs de football d\'Ifodjè et d\'autres clubs locaux d\'Atakpamé ;',
      '• Venir en aide aux familles vulnérables et aux personnes démunies de la région ;',
      '• Promouvoir le sport et l\'éducation auprès de la jeunesse ;',
      '• Préserver la mémoire et les valeurs de solidarité incarnées par Simon-Joseph Afantchédé APEDO ;',
      '• Créer un pont entre la diaspora togolaise et la communauté locale.'
    ];
    objets.forEach(obj => {
      const objLines = doc.splitTextToSize(obj, pageWidth - 2 * margin - 5);
      objLines.forEach(line => { doc.text(line, margin + 5, yPos); yPos += 5; });
    });
    
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Article 5 : Moyens d\'action', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const moyens = [
      '• Collecte de fonds et dons ;',
      '• Organisation d\'événements sportifs et culturels ;',
      '• Distribution d\'aides matérielles et financières ;',
      '• Partenariats avec d\'autres associations et institutions ;',
      '• Toute action conforme à l\'objet de l\'association.'
    ];
    moyens.forEach(moyen => { doc.text(moyen, margin + 5, yPos); yPos += 5; });
    
    // Page 2
    doc.addPage();
    yPos = margin;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('TITRE III : COMPOSITION - ADMISSION - RADIATION', margin, yPos);
    yPos += 10;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.text('Article 6 : Composition', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('L\'association se compose de :', margin, yPos);
    yPos += 6;
    doc.text('• Membres fondateurs : les signataires des présents statuts ;', margin + 5, yPos); yPos += 5;
    doc.text('• Membres actifs : toute personne adhérant aux statuts et s\'acquittant de la cotisation ;', margin + 5, yPos); yPos += 5;
    doc.text('• Membres d\'honneur : personnes ayant rendu des services à l\'association ;', margin + 5, yPos); yPos += 5;
    doc.text('• Membres bienfaiteurs : personnes apportant un soutien financier significatif.', margin + 5, yPos);
    
    yPos += 10;
    doc.setFont('helvetica', 'bold');
    doc.text('Article 7 : Admission', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const admission = 'Pour faire partie de l\'association, il faut adhérer aux présents statuts et s\'acquitter de la cotisation annuelle dont le montant est fixé par l\'Assemblée Générale.';
    const admLines = doc.splitTextToSize(admission, pageWidth - 2 * margin);
    admLines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Article 8 : Radiation', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('La qualité de membre se perd par :', margin, yPos);
    yPos += 6;
    doc.text('• Démission notifiée par écrit au Président ;', margin + 5, yPos); yPos += 5;
    doc.text('• Non-paiement de la cotisation ;', margin + 5, yPos); yPos += 5;
    doc.text('• Exclusion prononcée par le Bureau pour motif grave.', margin + 5, yPos);
    
    // Titre IV
    yPos += 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('TITRE IV : ADMINISTRATION ET FONCTIONNEMENT', margin, yPos);
    yPos += 10;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.text('Article 9 : Assemblée Générale', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const ag = 'L\'Assemblée Générale comprend tous les membres de l\'association. Elle se réunit au moins une fois par an sur convocation du Président ou à la demande d\'un tiers des membres.';
    const agLines = doc.splitTextToSize(ag, pageWidth - 2 * margin);
    agLines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Article 10 : Bureau Exécutif', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('Le Bureau Exécutif est composé de :', margin, yPos);
    yPos += 6;
    doc.text('• Un(e) Président(e) National(e)', margin + 5, yPos); yPos += 5;
    doc.text('• Un(e) Vice-Président(e)', margin + 5, yPos); yPos += 5;
    doc.text('• Un(e) Secrétaire Général(e)', margin + 5, yPos); yPos += 5;
    doc.text('• Un(e) Trésorier(ère)', margin + 5, yPos); yPos += 5;
    doc.text('• Un(e) Trésorier(ère) Adjoint(e)', margin + 5, yPos); yPos += 5;
    doc.text('Sont également associés à la gouvernance :', margin, yPos); yPos += 6;
    doc.text('• Un(e) Président(e) International(e) (siège UK)', margin + 5, yPos); yPos += 5;
    doc.text('• Des Membres d\'Honneur (voix consultative, sans cotisation)', margin + 5, yPos);
    
    yPos += 8;
    doc.text('Les membres du Bureau sont élus pour un mandat de trois (3) ans renouvelable.', margin, yPos);
    
    // Page 3
    doc.addPage();
    yPos = margin;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('TITRE V : RESSOURCES', margin, yPos);
    yPos += 10;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.text('Article 11 : Ressources', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('Les ressources de l\'association comprennent :', margin, yPos);
    yPos += 6;
    doc.text('• Les cotisations des membres ;', margin + 5, yPos); yPos += 5;
    doc.text('• Les dons et legs ;', margin + 5, yPos); yPos += 5;
    doc.text('• Les subventions publiques et privées ;', margin + 5, yPos); yPos += 5;
    doc.text('• Les recettes des activités de l\'association ;', margin + 5, yPos); yPos += 5;
    doc.text('• Toute autre ressource autorisée par la loi.', margin + 5, yPos);
    
    // Titre VI
    yPos += 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('TITRE VI : DISSOLUTION', margin, yPos);
    yPos += 10;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.text('Article 12 : Dissolution', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const dissolution = 'La dissolution de l\'association ne peut être prononcée que par une Assemblée Générale Extraordinaire convoquée à cet effet. En cas de dissolution, l\'actif net sera dévolu à une association poursuivant des buts similaires.';
    const dissLines = doc.splitTextToSize(dissolution, pageWidth - 2 * margin);
    dissLines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    
    // Signatures
    yPos += 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('SIGNATURES DES MEMBRES FONDATEURS', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    const fondateurs = [
      { name: 'APEDO KOKOU OGNAKOTAN', role: 'Président', lieu: 'Royaume-Uni' },
      { name: 'Yao Blaise Apedo', role: 'Vice-Président', lieu: 'Suisse' },
      { name: 'APEDO ABLAVI', role: 'Secrétaire Nationale', lieu: 'Togo' }
    ];
    
    fondateurs.forEach(f => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${f.name} (${f.role})`, margin, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(`Lieu: ${f.lieu}`, margin, yPos);
      yPos += 6;
      doc.text('Signature: _________________________________', margin, yPos);
      yPos += 12;
    });
    
    yPos += 5;
    doc.text(`Fait à Atakpamé, le ____/____/________`, margin, yPos);
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('SAFUAYE - Statuts de l\'Association (Togo)', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    doc.save('SAFUAYE_Statuts_Togo.pdf');
  };

  // Generate PV Assemblée Générale Constitutive Togo
  const generatePVTogoAssembleePDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;
    
    const greenColor = [21, 128, 61];
    const darkColor = [30, 30, 30];
    const orangeColor = [234, 88, 12];
    
    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...greenColor);
    doc.text('PROCÈS-VERBAL', pageWidth / 2, yPos, { align: 'center' });
    yPos += 7;
    doc.text('DE L\'ASSEMBLÉE GÉNÉRALE CONSTITUTIVE', pageWidth / 2, yPos, { align: 'center' });
    yPos += 7;
    doc.setFontSize(16);
    doc.text('ASSOCIATION SAFUAYE - TOGO', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.setFontSize(10);
    doc.setTextColor(...orangeColor);
    doc.text('Atakpamé, samedi 27 juin 2026', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    
    doc.text('L\'an deux mille vingt-six, le vingt-sept juin, à neuf heures (9h00),', margin, yPos);
    yPos += 6;
    const intro = 'sous le chapiteau dressé au siège social de l\'association à Atakpamé, Préfecture de l\'Ogou, République Togolaise, s\'est tenue l\'Assemblée Générale Constitutive de l\'association SAFUAYE-TOGO, conformément à la législation togolaise (Loi 40-484 et Loi 2018-006 portant statut des Organisations de la Société Civile).';
    const introLines = doc.splitTextToSize(intro, pageWidth - 2 * margin);
    introLines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('PRÉSIDENCE ET SECRÉTARIAT DE SÉANCE :', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('• Président de séance : M. APEDO KOKOU OGNAKOTAN (Président International) ;', margin + 3, yPos); yPos += 5;
    doc.text('• Co-présidence : M. AZIAKA KOFFI ;', margin + 3, yPos); yPos += 5;
    doc.text('• Secrétaire de séance : Mme APEDO ABLAVI.', margin + 3, yPos);
    
    yPos += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('PRÉSENCES :', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const presenceTxt = '25 membres présents (liste de présence émargée et signée, annexée au présent procès-verbal — 2 pages, 25 signatures). Quorum atteint conformément à l\'article 12 des Statuts.';
    const presLines = doc.splitTextToSize(presenceTxt, pageWidth - 2 * margin);
    presLines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('ORDRE DU JOUR :', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const ordreJour = [
      '1. Prière d\'ouverture',
      '2. Adoption des Statuts (25 articles)',
      '3. Adoption du Règlement Intérieur',
      '4. Élection du Président International',
      '5. Élection du Bureau Exécutif National (5 membres)',
      '6. Nomination du Collège des Membres d\'Honneur',
      '7. Distribution officielle des dons de juin 2026',
      '8. Pouvoirs pour formalités de déclaration',
      '9. Questions diverses'
    ];
    ordreJour.forEach(item => { doc.text(item, margin + 5, yPos); yPos += 5; });
    
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...greenColor);
    doc.text('DÉLIBÉRATIONS ET RÉSOLUTIONS :', margin, yPos);
    yPos += 8;
    doc.setTextColor(...darkColor);
    
    // Résolution 1 - Prière
    doc.setFont('helvetica', 'bold');
    doc.text('Résolution 1 — Prière d\'ouverture', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('La séance s\'ouvre par une prière de recueillement à la mémoire de Simon-Joseph « Safuayé »', margin, yPos); yPos += 5;
    doc.text('APEDO et des anciens disparus d\'Ifodjè FC.', margin, yPos);
    
    yPos += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Résolution 2 — Adoption des Statuts', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const res2 = 'Après lecture article par article, l\'Assemblée adopte à l\'UNANIMITÉ (100 % des suffrages) les 25 articles des Statuts de l\'association SAFUAYE-TOGO, y compris l\'article 6 bis instituant le Collège des Membres d\'Honneur.';
    const res2Lines = doc.splitTextToSize(res2, pageWidth - 2 * margin);
    res2Lines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Résolution 3 — Adoption du Règlement Intérieur', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const res3 = 'L\'Assemblée adopte à l\'UNANIMITÉ (100 % des suffrages) le Règlement Intérieur précisant le fonctionnement opérationnel de l\'association.';
    const res3Lines = doc.splitTextToSize(res3, pageWidth - 2 * margin);
    res3Lines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    
    // Page 2
    doc.addPage();
    yPos = margin;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Résolution 4 — Élection du Président International (UK)', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('Sur proposition de l\'Assemblée et à l\'UNANIMITÉ, est élu Président International :', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('• M. APEDO KOKOU OGNAKOTAN (siège social UK : 26b Langley Park Road, SM2 5EN Sutton)', margin + 3, yPos);
    
    yPos += 10;
    doc.setFont('helvetica', 'bold');
    doc.text('Résolution 5 — Élection du Bureau Exécutif National', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('L\'Assemblée procède à l\'élection. Sont élus à l\'UNANIMITÉ :', margin, yPos);
    yPos += 7;
    
    const bureau = [
      { poste: 'Président National', name: 'M. AZIAKA KOFFI' },
      { poste: 'Vice-Président', name: 'M. DOGLO KOMI' },
      { poste: 'Secrétaire Générale', name: 'Mme APEDO ABLAVI' },
      { poste: 'Trésorière', name: 'Mme ABOTCHI ÉMILIE DJIFA A.' },
      { poste: 'Trésorier Adjoint', name: 'M. SMITH K. GNAMIGBEDJRE' }
    ];
    bureau.forEach(b => {
      doc.setFont('helvetica', 'bold');
      doc.text(`• ${b.poste} : `, margin + 3, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(b.name, margin + 60, yPos);
      yPos += 5;
    });
    
    yPos += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Résolution 6 — Nomination du Collège des Membres d\'Honneur', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('Sont nommés à l\'UNANIMITÉ Membres d\'Honneur (voix consultative, sans cotisation) :', margin, yPos);
    yPos += 6;
    const honneur = [
      'M. Anagonou AGBOSSOU (Atakpamé)',
      'M. Todzro KOKOU (Atakpamé)',
      'M. Ayetan KOSSI (Atakpamé)',
      'M. Kowou BERNARD (Atakpamé)',
      'M. Midahin ANOUMOU (Atakpamé)'
    ];
    honneur.forEach((h, i) => {
      doc.text(`${i + 1}. ${h}`, margin + 3, yPos);
      yPos += 5;
    });
    
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Résolution 7 — Distribution officielle des dons de juin 2026', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const res7 = 'À l\'issue des élections, l\'Assemblée procède à la distribution officielle des dons en faveur de 15 bénéficiaires (anciens d\'Ifodjè FC et personnes dans le besoin) :';
    const res7Lines = doc.splitTextToSize(res7, pageWidth - 2 * margin);
    res7Lines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    yPos += 2;
    doc.text('• 10 kits de vivres (riz, huile, macaroni, tomates) ;', margin + 3, yPos); yPos += 5;
    doc.text('• 15 enveloppes monétaires réparties comme suit :', margin + 3, yPos); yPos += 5;
    doc.text('    - 5 enveloppes de 20 000 FCFA = 100 000 FCFA', margin + 6, yPos); yPos += 5;
    doc.text('    - 5 enveloppes de 10 000 FCFA = 50 000 FCFA', margin + 6, yPos); yPos += 5;
    doc.text('    - 5 enveloppes de 5 000 FCFA = 25 000 FCFA', margin + 6, yPos); yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('    TOTAL DISTRIBUÉ : 175 000 FCFA (≈ 265 €)', margin + 6, yPos);
    
    // Page 3
    doc.addPage();
    yPos = margin;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Résolution 8 — Pouvoirs pour formalités', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const pouvoirs = 'L\'Assemblée donne tous pouvoirs au Président National M. AZIAKA KOFFI et à la Secrétaire Générale Mme APEDO ABLAVI, agissant conjointement, pour accomplir toutes les formalités de déclaration et d\'enregistrement de l\'association SAFUAYE-TOGO auprès de la Préfecture de l\'Ogou et de toute autre autorité compétente.';
    const pouvLines = doc.splitTextToSize(pouvoirs, pageWidth - 2 * margin);
    pouvLines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
    
    yPos += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Résolution 9 — Siège social', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text('Le siège social est fixé à Atakpamé, Préfecture de l\'Ogou, République Togolaise.', margin, yPos);
    
    yPos += 12;
    doc.setFont('helvetica', 'normal');
    doc.text('L\'ordre du jour étant épuisé, la séance est levée. Un repas convivial est partagé', margin, yPos); yPos += 5;
    doc.text('entre l\'ensemble des présents, bénéficiaires, sages et soutiens.', margin, yPos);
    
    // Signatures
    yPos += 18;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...greenColor);
    doc.text('SIGNATURES OFFICIELLES', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    
    const signataires = [
      { name: 'M. AZIAKA KOFFI', poste: 'Président National' },
      { name: 'Mme APEDO ABLAVI', poste: 'Secrétaire Générale' }
    ];
    
    signataires.forEach(s => {
      doc.setFont('helvetica', 'bold');
      doc.text(s.name, margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(`(${s.poste})`, margin + 70, yPos);
      yPos += 8;
      doc.text('Signature : _________________________________', margin, yPos);
      yPos += 15;
    });
    
    yPos += 5;
    doc.text('Fait à Atakpamé, le 27 juin 2026.', margin, yPos);
    
    yPos += 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...orangeColor);
    doc.text('ANNEXES :', margin, yPos); yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkColor);
    doc.text('• Annexe 1 : Liste de présence émargée (2 pages, 25 signatures)', margin + 3, yPos); yPos += 5;
    doc.text('• Annexe 2 : Statuts adoptés (25 articles)', margin + 3, yPos); yPos += 5;
    doc.text('• Annexe 3 : Règlement Intérieur adopté', margin + 3, yPos); yPos += 5;
    doc.text('• Annexe 4 : Liste nominative du Bureau et du Collège des Membres d\'Honneur', margin + 3, yPos); yPos += 5;
    doc.text('• Annexe 5 : Reçus de distribution des 15 dons (175 000 FCFA + 10 kits vivres)', margin + 3, yPos);
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('SAFUAYE-TOGO - Procès-Verbal officiel de l\'Assemblée Générale Constitutive du 27/06/2026', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    doc.save('SAFUAYE_Togo_PV_AG_Constitutive_27-06-2026_OFFICIEL.pdf');
  };

  const generateBordereauPrefetPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 18;
    let yPos = margin;

    const greenColor = [21, 128, 61];
    const orangeColor = [234, 88, 12];
    const darkColor = [30, 30, 30];

    // Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...greenColor);
    doc.text('BORDEREAU DE DÉPÔT', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.setFontSize(11);
    doc.text('Dossier de déclaration d\'association', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.setFont('helvetica', 'normal');
    doc.text('Destinataire : Ministère de l\'Administration Territoriale, de la', margin, yPos); yPos += 5;
    doc.text('Décentralisation et du Développement des Territoires (MATDDT)', margin, yPos); yPos += 5;
    doc.text('Direction des Affaires Politiques et de l\'Administration Territoriale (DAPAT) — Lomé', margin, yPos); yPos += 5;
    doc.text('Émetteur : Association SAFUAYE-TOGO · Siège : Atakpamé, Togo', margin, yPos); yPos += 5;
    doc.text('Date de dépôt : ____/____/2026                Référence : SAFUAYE-2026-____', margin, yPos);

    yPos += 9;
    doc.setDrawColor(...greenColor);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...greenColor);
    doc.text('OBJET :', margin, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    const objet = 'Demande d\'autorisation de création et de récépissé de déclaration de l\'Association SAFUAYE-TOGO, constituée à l\'Assemblée Générale Constitutive du samedi 27 juin 2026, conformément à la Loi togolaise N°40-484 du 1er juillet 1901 et à la Loi N°2018-006 du 27 juin 2018 portant statut des Organisations de la Société Civile au Togo.';
    const objetLines = doc.splitTextToSize(objet, pageWidth - 2 * margin);
    objetLines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });

    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...greenColor);
    doc.text('PIÈCES JOINTES À FOURNIR (cocher au dépôt) :', margin, yPos);
    yPos += 8;

    // Helper to draw a checkbox + label
    const drawCheck = (label, sub) => {
      if (yPos > pageHeight - 25) { doc.addPage(); yPos = margin; }
      // Checkbox
      doc.setDrawColor(...darkColor);
      doc.setLineWidth(0.4);
      doc.rect(margin, yPos - 4, 4, 4);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...darkColor);
      doc.text(label, margin + 7, yPos);
      yPos += 5;
      if (sub) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(90, 90, 90);
        const subLines = doc.splitTextToSize(sub, pageWidth - 2 * margin - 7);
        subLines.forEach(line => { doc.text(line, margin + 7, yPos); yPos += 4; });
      }
      yPos += 3;
    };

    drawCheck('1. Lettre de demande d\'autorisation', 'Adressée à Monsieur le Ministre de l\'Administration Territoriale (MATDDT) · signée par le Président National AZIAKA KOFFI · 1 original + 2 copies.');
    drawCheck('2. Procès-Verbal de l\'AG Constitutive du 27/06/2026', 'Contenant : décision de créer l\'association, adoption des Statuts &amp; du RI, élection du Bureau · 3 exemplaires signés.');
    drawCheck('3. Statuts SAFUAYE-TOGO (27 articles + Article 10 bis)', 'Précisant : nom, objet, siège, durée, organisation interne, conditions d\'adhésion, fonctionnement du Bureau, modification &amp; dissolution · 3 exemplaires paraphés.');
    drawCheck('4. Règlement Intérieur', 'Facultatif au sens strict, mais fortement recommandé — adopté à 100 % · 3 exemplaires signés.');
    drawCheck('5. Liste nominative des membres du Bureau Exécutif National', 'Avec nom, prénom, fonction, profession, adresse et signature de chaque membre · 3 exemplaires.');
    drawCheck('6. Copies certifiées des pièces d\'identité (CNI / passeport / carte d\'électeur)', 'Pour tous les membres du Bureau (Aziaka, Doglo, Ablavi, Émilie, Alain + Kokou pour le Président International).');
    drawCheck('7. Plan de localisation du siège social', 'Croquis manuscrit ou capture Google Maps imprimée avec adresse précise à Atakpamé.');

    // Page 2
    doc.addPage();
    yPos = margin;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...greenColor);
    doc.text('PIÈCES D\'IDENTITÉ ET DE RÉSIDENCE :', margin, yPos);
    yPos += 8;

    drawCheck('8. Carte Nationale d\'Identité (CNI) ou passeport — AZIAKA KOFFI', 'Photocopie certifiée conforme + original à présenter au dépôt.');
    drawCheck('9. CNI / passeport — DOGLO KOMI (Vice-Président)', 'Idem.');
    drawCheck('10. CNI / passeport — APEDO ABLAVI (Secrétaire Générale)', 'Idem.');
    drawCheck('11. CNI / passeport — ABOTCHI ÉMILIE DJIFA A. (Trésorière)', 'Idem.');
    drawCheck('12. CNI / passeport — SMITH K. GNAMIGBEDJRE (Trésorier Adjoint)', 'Idem.');
    drawCheck('13. Passeport britannique — APEDO KOKOU OGNAKOTAN (Président International)', 'Photocopie certifiée + désignation du représentant légal au Togo (AZIAKA KOFFI).');

    yPos += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...greenColor);
    doc.text('JUSTIFICATIFS DE RÉSIDENCE (membres du Bureau National) :', margin, yPos);
    yPos += 8;

    drawCheck('14. Attestation de résidence — AZIAKA KOFFI (Atakpamé)', 'Délivrée par le chef de quartier OU facture CEET / TdE de moins de 3 mois.');
    drawCheck('15. Attestation de résidence — DOGLO KOMI', 'Idem.');
    drawCheck('16. Attestation de résidence — APEDO ABLAVI', 'Idem.');
    drawCheck('17. Attestation de résidence — ABOTCHI ÉMILIE DJIFA A.', 'Idem.');
    drawCheck('18. Attestation de résidence — SMITH K. GNAMIGBEDJRE', 'Idem.');

    yPos += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...greenColor);
    doc.text('PIÈCES JUSTIFICATIVES COMPLÉMENTAIRES :', margin, yPos);
    yPos += 8;

    drawCheck('19. Plan de localisation du siège social à Atakpamé', 'Croquis manuscrit ou capture Google Maps imprimée avec adresse précise.');
    drawCheck('20. Justificatif du siège (titre foncier, bail, autorisation d\'occupation)', 'Photocopie + original à présenter.');
    drawCheck('21. Déclaration sur l\'honneur — absence de lien de parenté entre membres', 'Signée par chaque membre du Bureau (modèle fourni par SAFUAYE).');
    drawCheck('22. Reçus de la distribution des 15 dons (175 000 FCFA + 10 kits vivres)', 'Comme preuve d\'activité immédiate de l\'association.');
    drawCheck('23. Photos de l\'AG du 27/06/2026 (impressions A4)', 'Photo de famille · vote à main levée · liste de présence · distribution des dons · repas partagé.');

    // Page 3 - Récapitulatif
    doc.addPage();
    yPos = margin;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('RÉCAPITULATIF DU DÉPÔT', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.text('Nombre total de pièces à fournir : 23 documents (dont fiche de renseignements MATDDT).', margin, yPos); yPos += 6;
    doc.text('Frais de dossier réglementaires : 10 000 à 25 000 FCFA (selon nature de l\'association).', margin, yPos); yPos += 6;
    doc.text('Délai récépissé provisoire : 2 à 4 semaines · Délai récépissé définitif : 1 à 3 mois.', margin, yPos); yPos += 6;
    doc.text('Lieu de dépôt : Ministère MATDDT / DAPAT — Lomé (quartier administratif).', margin, yPos); yPos += 6;
    doc.text('Conseil : arriver tôt le matin pour un traitement plus rapide.', margin, yPos);

    yPos += 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...orangeColor);
    doc.text('⚠ CONSEILS PRATIQUES :', margin, yPos);
    yPos += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    const conseils = [
      '• Prépare un dossier bien structuré (séparateurs, ordre des pièces) pour éviter les retards.',
      '• Arrive tôt le matin au MATDDT/DAPAT : les services administratifs traitent plus vite.',
      '• Garde une copie certifiée conforme de tout le dossier pour vos archives.',
      '• Demande un récépissé daté et signé attestant du dépôt.',
      '• Mets en avant l\'objectif social, éducatif et communautaire de SAFUAYE : cela facilite l\'acceptation.',
      '• Le récépissé provisoire tient lieu d\'existence légale en attendant le récépissé définitif.',
      '• Le numéro d\'enregistrement définitif sera ensuite communiqué à la banque pour ouverture de compte.'
    ];
    conseils.forEach(c => {
      const lines = doc.splitTextToSize(c, pageWidth - 2 * margin);
      lines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });
      yPos += 1;
    });

    yPos += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...greenColor);
    doc.text('SIGNATURE DU DÉPOSANT', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.text('Nom et prénom : _______________________________________________', margin, yPos); yPos += 7;
    doc.text('Qualité au sein de SAFUAYE : ___________________________________', margin, yPos); yPos += 7;
    doc.text('Date : ____/____/2026     Signature : _________________________', margin, yPos);

    yPos += 14;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...greenColor);
    doc.text('RÉCÉPISSÉ ADMINISTRATIF (à compléter par le MATDDT)', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.text('N° de récépissé : __________________________________', margin, yPos); yPos += 7;
    doc.text('Date d\'enregistrement : ____/____/2026', margin, yPos); yPos += 7;
    doc.text('Cachet et signature du service DAPAT :', margin, yPos);

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('SAFUAYE-TOGO · Bordereau de dépôt MATDDT / DAPAT · AG du 27 juin 2026', pageWidth / 2, pageHeight - 10, { align: 'center' });

    doc.save('SAFUAYE_Togo_Bordereau_Depot_MATDDT_Lome.pdf');
  };

  // Modèle d'attestation de résidence à faire signer par le chef de quartier
  const generateAttestationResidencePDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 22;
    const greenColor = [21, 128, 61];
    const darkColor = [30, 30, 30];

    const membres = [
      'M. AZIAKA KOFFI (Président National)',
      'M. DOGLO KOMI (Vice-Président)',
      'Mme APEDO ABLAVI (Secrétaire Générale)',
      'Mme ABOTCHI ÉMILIE DJIFA A. (Trésorière)',
      'M. SMITH K. GNAMIGBEDJRE (Trésorier Adjoint)'
    ];

    membres.forEach((membre, idx) => {
      if (idx > 0) doc.addPage();
      let yPos = margin;

      // Header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...darkColor);
      doc.text('RÉPUBLIQUE TOGOLAISE', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('Travail — Liberté — Patrie', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc.text('__________________________________________________', pageWidth / 2, yPos, { align: 'center' });

      yPos += 8;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('MINISTÈRE DE L\'ADMINISTRATION TERRITORIALE', margin, yPos);
      yPos += 5;
      doc.text('Préfecture de l\'Ogou · Ville d\'Atakpamé', margin, yPos);
      yPos += 5;
      doc.text('Quartier de : ______________________________________', margin, yPos);

      yPos += 15;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(...greenColor);
      doc.text('ATTESTATION DE RÉSIDENCE', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
      doc.setDrawColor(...greenColor);
      doc.setLineWidth(0.4);
      doc.line(margin + 40, yPos, pageWidth - margin - 40, yPos);

      yPos += 15;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(...darkColor);
      doc.text('Je soussigné(e),', margin, yPos);
      yPos += 8;
      doc.text('Nom et prénom : _____________________________________________________________', margin, yPos);
      yPos += 8;
      doc.text('Qualité : Chef du quartier de _______________________________________________', margin, yPos);
      yPos += 8;
      doc.text('Ville d\'Atakpamé, Préfecture de l\'Ogou,', margin, yPos);

      yPos += 12;
      doc.setFont('helvetica', 'bold');
      doc.text('ATTESTE PAR LA PRÉSENTE QUE :', margin, yPos);
      yPos += 10;

      doc.setFont('helvetica', 'normal');
      const attesteTxt = `${membre}, réside effectivement dans mon quartier depuis le _____/_____/_______, à l'adresse suivante :`;
      const attesteLines = doc.splitTextToSize(attesteTxt, pageWidth - 2 * margin);
      attesteLines.forEach(line => { doc.text(line, margin, yPos); yPos += 6; });

      yPos += 4;
      doc.text('Adresse complète : ___________________________________________________________', margin, yPos);
      yPos += 8;
      doc.text('_____________________________________________________________________________', margin, yPos);
      yPos += 8;
      doc.text('Ville : Atakpamé · Préfecture de l\'Ogou · Togo', margin, yPos);

      yPos += 15;
      const validTxt = 'La présente attestation lui est délivrée à sa demande pour servir et valoir ce que de droit, notamment pour la déclaration de l\'Association SAFUAYE-TOGO auprès de la Préfecture de l\'Ogou.';
      const validLines = doc.splitTextToSize(validTxt, pageWidth - 2 * margin);
      validLines.forEach(line => { doc.text(line, margin, yPos); yPos += 6; });

      yPos += 15;
      doc.text('Fait à Atakpamé, le _____/_____/_______', margin, yPos);

      yPos += 20;
      doc.setFont('helvetica', 'bold');
      doc.text('Signature et cachet du Chef de Quartier', pageWidth - margin - 80, yPos);

      // Footer
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(120, 120, 120);
      doc.text(`SAFUAYE-TOGO · Attestation de résidence n°${idx + 1}/5 · À faire signer par le Chef de Quartier`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    });

    doc.save('SAFUAYE_Togo_Attestations_Residence_Bureau.pdf');
  };

  // Déclaration sur l'honneur - absence de lien de parenté (une par membre)
  const generateDeclarationHonneurPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 22;
    const greenColor = [21, 128, 61];
    const darkColor = [30, 30, 30];

    const membres = [
      'M. AZIAKA KOFFI', 'M. DOGLO KOMI',
      'Mme APEDO ABLAVI', 'Mme ABOTCHI ÉMILIE DJIFA A.',
      'M. SMITH K. GNAMIGBEDJRE', 'M. APEDO KOKOU OGNAKOTAN'
    ];

    membres.forEach((membre, idx) => {
      if (idx > 0) doc.addPage();
      let yPos = margin;

      // Header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...darkColor);
      doc.text('ASSOCIATION SAFUAYE-TOGO', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('Siège : Atakpamé · Préfecture de l\'Ogou · République Togolaise', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc.text('__________________________________________________', pageWidth / 2, yPos, { align: 'center' });

      yPos += 15;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...greenColor);
      doc.text('DÉCLARATION SUR L\'HONNEUR', pageWidth / 2, yPos, { align: 'center' });
      yPos += 6;
      doc.setFontSize(11);
      doc.text('Absence de lien de parenté au sein du Bureau', pageWidth / 2, yPos, { align: 'center' });
      yPos += 4;
      doc.setDrawColor(...greenColor);
      doc.setLineWidth(0.4);
      doc.line(margin + 40, yPos + 2, pageWidth - margin - 40, yPos + 2);

      yPos += 15;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(...darkColor);
      doc.text('Je soussigné(e),', margin, yPos);
      yPos += 8;
      doc.setFont('helvetica', 'bold');
      doc.text(`Nom et prénom : ${membre}`, margin, yPos);
      yPos += 8;
      doc.setFont('helvetica', 'normal');
      doc.text('Fonction : ______________________________________________________________', margin, yPos);
      yPos += 8;
      doc.text('Né(e) le : _____/_____/________ à ______________________________________', margin, yPos);
      yPos += 8;
      doc.text('CNI/Passeport n° : _____________________________________________________', margin, yPos);
      yPos += 8;
      doc.text('Résidant à : ____________________________________________________________', margin, yPos);

      yPos += 12;
      doc.setFont('helvetica', 'bold');
      doc.text('DÉCLARE SUR L\'HONNEUR :', margin, yPos);
      yPos += 10;

      doc.setFont('helvetica', 'normal');
      const point1 = "1. Ne pas être lié(e) par un lien de parenté (conjoint, enfant, parent, frère, sœur, oncle, tante, cousin(e) au 1er degré) avec plus d'un(e) autre membre du Bureau Exécutif National de SAFUAYE-TOGO, à l'exception du lien familial déclaré ci-dessous, le cas échéant.";
      const p1Lines = doc.splitTextToSize(point1, pageWidth - 2 * margin);
      p1Lines.forEach(line => { doc.text(line, margin, yPos); yPos += 6; });

      yPos += 3;
      doc.text('Lien(s) déclaré(s) (le cas échéant) : ________________________________________', margin, yPos);
      yPos += 6;
      doc.text('_____________________________________________________________________________', margin, yPos);

      yPos += 8;
      const point2 = '2. Ne pas exercer d\'activité incompatible avec les valeurs de l\'association telle que définies dans les Statuts et le Règlement Intérieur.';
      const p2Lines = doc.splitTextToSize(point2, pageWidth - 2 * margin);
      p2Lines.forEach(line => { doc.text(line, margin, yPos); yPos += 6; });

      yPos += 3;
      const point3 = '3. M\'engager à signaler tout changement de situation susceptible d\'affecter la véracité de la présente déclaration.';
      const p3Lines = doc.splitTextToSize(point3, pageWidth - 2 * margin);
      p3Lines.forEach(line => { doc.text(line, margin, yPos); yPos += 6; });

      yPos += 8;
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      const warn = 'Je suis informé(e) que toute fausse déclaration m\'expose à des sanctions civiles et pénales, ainsi qu\'à l\'exclusion immédiate du Bureau conformément à l\'article 18 des Statuts.';
      const warnLines = doc.splitTextToSize(warn, pageWidth - 2 * margin);
      warnLines.forEach(line => { doc.text(line, margin, yPos); yPos += 5; });

      yPos += 12;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkColor);
      doc.text('Fait à Atakpamé, le _____/_____/2026', margin, yPos);

      yPos += 18;
      doc.setFont('helvetica', 'bold');
      doc.text('Signature (précédée de la mention « Lu et approuvé ») :', margin, yPos);
      yPos += 25;
      doc.text('_________________________________', margin, yPos);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(120, 120, 120);
      doc.text(`SAFUAYE-TOGO · Déclaration sur l'honneur ${idx + 1}/6 · Annexe au dossier préfectoral`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    });

    doc.save('SAFUAYE_Togo_Declarations_Honneur_Bureau.pdf');
  };

  // Generate Demande au Préfet Togo
  const generateDemandePreletPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;
    
    const greenColor = [21, 128, 61];
    const darkColor = [30, 30, 30];
    
    // En-tête expéditeur
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...darkColor);
    doc.text('ASSOCIATION SAFUAYE', margin, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Siège Social : Atakpamé', margin, yPos);
    yPos += 5;
    doc.text('Préfecture de l\'Ogou', margin, yPos);
    yPos += 5;
    doc.text('République Togolaise', margin, yPos);
    yPos += 5;
    doc.text('Email : contact@safuaye.org', margin, yPos);
    yPos += 5;
    doc.text('Tél : +44 7398 308 310', margin, yPos);
    
    // Date à droite
    yPos = margin;
    doc.text('Atakpamé, le ____/____/________', pageWidth - margin - 50, yPos);
    
    // Destinataire
    yPos = 55;
    doc.text('À', pageWidth - margin - 80, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Monsieur le Préfet de l\'Ogou', pageWidth - margin - 80, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('ATAKPAMÉ', pageWidth - margin - 80, yPos);
    
    // Objet
    yPos += 20;
    doc.setFont('helvetica', 'bold');
    doc.text('Objet : Demande de récépissé de déclaration d\'association', margin, yPos);
    
    // Corps de la lettre
    yPos += 15;
    doc.setFont('helvetica', 'normal');
    doc.text('Monsieur le Préfet,', margin, yPos);
    
    yPos += 10;
    const para1 = 'J\'ai l\'honneur de venir très respectueusement auprès de votre haute bienveillance solliciter la délivrance d\'un récépissé de déclaration pour l\'association dénommée « SAFUAYE ».';
    const para1Lines = doc.splitTextToSize(para1, pageWidth - 2 * margin);
    para1Lines.forEach(line => { doc.text(line, margin, yPos); yPos += 6; });
    
    yPos += 5;
    const para2 = 'Cette association à but non lucratif a été créée en mémoire de Simon-Joseph Afantchédé APEDO, dit « Safuaye », ancien Président de l\'Ifodjè Football Club d\'Atakpamé de 1984 à 1990, homme de grande générosité qui a consacré sa vie au soutien des joueurs et des personnes démunies.';
    const para2Lines = doc.splitTextToSize(para2, pageWidth - 2 * margin);
    para2Lines.forEach(line => { doc.text(line, margin, yPos); yPos += 6; });
    
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('L\'association a pour objet :', margin, yPos);
    yPos += 7;
    doc.setFont('helvetica', 'normal');
    const objets = [
      '• Soutenir les anciens joueurs de football et leurs familles ;',
      '• Venir en aide aux personnes vulnérables d\'Atakpamé ;',
      '• Promouvoir le sport et l\'éducation auprès de la jeunesse ;',
      '• Préserver la mémoire et les valeurs de solidarité de Safuaye.'
    ];
    objets.forEach(obj => { doc.text(obj, margin + 5, yPos); yPos += 6; });
    
    yPos += 5;
    doc.text('Vous trouverez ci-joint les pièces suivantes :', margin, yPos);
    yPos += 7;
    const pieces = [
      '1. Deux (2) exemplaires des statuts de l\'association',
      '2. Deux (2) exemplaires du procès-verbal de l\'Assemblée Générale Constitutive',
      '3. La liste des membres fondateurs avec leurs adresses',
      '4. La liste des membres du Bureau Exécutif',
      '5. Les copies des pièces d\'identité des membres du Bureau'
    ];
    pieces.forEach(piece => { doc.text(piece, margin + 5, yPos); yPos += 6; });
    
    yPos += 8;
    const conclusion = 'Dans l\'espoir d\'une suite favorable à ma requête, je vous prie d\'agréer, Monsieur le Préfet, l\'expression de ma très haute considération.';
    const concLines = doc.splitTextToSize(conclusion, pageWidth - 2 * margin);
    concLines.forEach(line => { doc.text(line, margin, yPos); yPos += 6; });
    
    // Signature
    yPos += 15;
    doc.text('Le Président de l\'Association', pageWidth - margin - 60, yPos);
    yPos += 20;
    doc.text('_________________________________', pageWidth - margin - 70, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('APEDO KOKOU OGNAKOTAN', pageWidth - margin - 60, yPos);
    
    // Footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('SAFUAYE - Demande de récépissé auprès de la Préfecture de l\'Ogou', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    doc.save('SAFUAYE_Demande_Prefet_Togo.pdf');
  };

  // Generate Liste Membres Fondateurs Togo
  const generateListeMembresPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;
    
    const greenColor = [21, 128, 61];
    const darkColor = [30, 30, 30];
    
    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...greenColor);
    doc.text('ASSOCIATION SAFUAYE', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
    doc.setFontSize(12);
    doc.text('LISTE DES MEMBRES FONDATEURS', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.text('ET DU BUREAU EXÉCUTIF', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 15;
    doc.setTextColor(...darkColor);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Siège social : Atakpamé, Préfecture de l\'Ogou, République Togolaise', pageWidth / 2, yPos, { align: 'center' });
    
    // Section Bureau
    yPos += 15;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('I. MEMBRES DU BUREAU EXÉCUTIF', margin, yPos);
    yPos += 10;
    
    const bureau = [
      { poste: 'Président International (UK)', name: 'APEDO KOKOU OGNAKOTAN', nationalite: 'Togolaise', residence: '26b Langley Park Road, SM2 5EN Sutton, Royaume-Uni', profession: '________________' },
      { poste: 'Président National', name: 'AZIAKA KOFFI', nationalite: 'Togolaise', residence: 'Atakpamé, Togo', profession: '________________' },
      { poste: 'Vice-Président', name: 'DOGLO KOMI', nationalite: 'Togolaise', residence: 'Atakpamé, Togo', profession: '________________' },
      { poste: 'Secrétaire Générale', name: 'APEDO ABLAVI', nationalite: 'Togolaise', residence: 'Atakpamé, Togo', profession: '________________' },
      { poste: 'Trésorière', name: 'ABOTCHI ÉMILIE DJIFA A.', nationalite: 'Togolaise', residence: 'Atakpamé, Togo', profession: '________________' },
      { poste: 'Trésorier Adjoint', name: 'SMITH K. GNAMIGBEDJRE', nationalite: 'Togolaise', residence: 'Atakpamé, Togo', profession: '________________' }
    ];
    
    doc.setFontSize(10);
    bureau.forEach((m, i) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkColor);
      doc.text(`${i + 1}. ${m.poste}`, margin, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(`   Nom et Prénom(s) : ${m.name}`, margin, yPos); yPos += 5;
      doc.text(`   Nationalité : ${m.nationalite}`, margin, yPos); yPos += 5;
      doc.text(`   Résidence : ${m.residence}`, margin, yPos); yPos += 5;
      doc.text(`   Profession : ${m.profession}`, margin, yPos); yPos += 5;
      doc.text(`   Signature : _________________________________`, margin, yPos);
      yPos += 12;
    });
    
    // Section Membres Fondateurs
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('II. AUTRES MEMBRES FONDATEURS', margin, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    
    for (let i = 1; i <= 3; i++) {
      doc.setFont('helvetica', 'bold');
      doc.text(`${i}. Membre fondateur`, margin, yPos);
      yPos += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(`   Nom et Prénom(s) : ________________________________`, margin, yPos); yPos += 5;
      doc.text(`   Nationalité : ________________`, margin, yPos); yPos += 5;
      doc.text(`   Résidence : ________________________________`, margin, yPos); yPos += 5;
      doc.text(`   Signature : _________________________________`, margin, yPos);
      yPos += 10;
    }

    // Section Membres d'Honneur (Collège des Sages d'Atakpamé)
    if (yPos > pageHeight - 80) { doc.addPage(); yPos = margin; }
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('III. COLLÈGE DES MEMBRES D\'HONNEUR', margin, yPos);
    yPos += 7;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text('Notables d\'Atakpamé — voix consultative, sans cotisation (Art. 6 bis des Statuts)', margin, yPos);
    yPos += 8;

    const honneur = [
      'Anagonou AGBOSSOU',
      'Todzro KOKOU',
      'Ayetan KOSSI',
      'Kowou BERNARD',
      'Midahin ANOUMOU'
    ];
    doc.setFontSize(10);
    doc.setTextColor(...darkColor);
    doc.setFont('helvetica', 'normal');
    honneur.forEach((name, i) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${i + 1}.`, margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(`${name}`, margin + 8, yPos);
      doc.text('— Atakpamé (Togo)', margin + 70, yPos);
      yPos += 6;
    });
    
    // Certification
    yPos += 10;
    doc.setFont('helvetica', 'italic');
    doc.text('Je soussigné, Président de l\'Association SAFUAYE, certifie exacte la présente liste.', margin, yPos);
    
    yPos += 15;
    doc.setFont('helvetica', 'normal');
    doc.text(`Fait à Atakpamé, le ____/____/________`, margin, yPos);
    
    yPos += 15;
    doc.text('Le Président', pageWidth - margin - 40, yPos);
    yPos += 15;
    doc.text('_________________________________', pageWidth - margin - 55, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('APEDO KOKOU OGNAKOTAN', pageWidth - margin - 50, yPos);
    
    // Footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('SAFUAYE - Liste des Membres Fondateurs et du Bureau Exécutif', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    doc.save('SAFUAYE_Liste_Membres_Bureau_Togo.pdf');
  };

  // Track constitution download (fire-and-forget analytics)
  const trackConstitutionDownload = (langCode, format) => {
    try {
      axios.post(`${API}/constitution/track-download`, {
        lang: langCode.toUpperCase(),
        format: format.toLowerCase(),
      }).catch(() => { /* silent - don't block download */ });
    } catch (_) { /* noop */ }
  };

  const handleDownloadFlyer = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;
    
    // Colors
    const greenColor = [21, 128, 61];
    const darkColor = [30, 30, 30];
    const grayColor = [100, 100, 100];
    const yellowColor = [234, 179, 8];
    
    // Get current translations
    const currentT = t;
    
    // Helper function for text wrapping
    const addWrappedText = (text, x, y, maxWidth, lineHeight, fontSize, color = darkColor) => {
      doc.setFontSize(fontSize);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(text, maxWidth);
      lines.forEach((line, i) => {
        doc.text(line, x, y + (i * lineHeight));
      });
      return y + (lines.length * lineHeight);
    };
    
    // === PAGE 1: Cover ===
    // Green header bar
    doc.setFillColor(...greenColor);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(32);
    doc.setTextColor(255, 255, 255);
    doc.text('SAFUAYE', pageWidth / 2, 25, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(
      lang === 'fr' ? 'Association | Héritage • Solidarité • Espoir' :
      lang === 'en' ? 'Association | Heritage • Solidarity • Hope' :
      'Associazione | Eredità • Solidarietà • Speranza',
      pageWidth / 2, 35, { align: 'center' }
    );
    
    // Subtitle
    yPos = 60;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(14);
    doc.setTextColor(...greenColor);
    doc.text(currentT.hero.subtitle, pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.setTextColor(...darkColor);
    doc.text(currentT.hero.nickname, pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 12;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(16);
    doc.setTextColor(...grayColor);
    doc.text(currentT.hero.tagline, pageWidth / 2, yPos, { align: 'center' });
    
    // Info box
    yPos += 20;
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 45, 3, 3, 'F');
    
    yPos += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...darkColor);
    doc.text(currentT.hero.profession, pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 8;
    doc.text(currentT.hero.description, pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 8;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(...grayColor);
    doc.text(
      lang === 'fr' ? '18 Août 1991 — Dans nos cœurs, à jamais tu demeureras' :
      lang === 'en' ? '18 August 1991 — Forever in our hearts' :
      '18 Agosto 1991 — Per sempre nei nostri cuori',
      pageWidth / 2, yPos, { align: 'center' }
    );
    
    // Who was Safuaye section
    yPos += 25;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...greenColor);
    doc.text(currentT.legacy.overline.toUpperCase(), margin, yPos);
    
    yPos += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const whoText = currentT.legacy.intro + ' ' + currentT.legacy.p1 + ' ' + currentT.legacy.p3;
    yPos = addWrappedText(whoText, margin, yPos, pageWidth - 2 * margin, 5, 10, grayColor);
    
    // President section
    yPos += 8;
    doc.setFillColor(...greenColor);
    doc.rect(margin, yPos, 3, 35, 'F');
    
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...darkColor);
    doc.text(currentT.legacy.presidentTitle, margin + 8, yPos);
    
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    currentT.legacy.presidentPoints.forEach(point => {
      doc.setTextColor(...grayColor);
      doc.text('• ' + point, margin + 8, yPos);
      yPos += 5;
    });
    
    // Footer
    doc.setFillColor(...greenColor);
    doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text('UK • Italie • Suisse • Togo | contact@safuaye.org | +44 7398 308 310', pageWidth / 2, pageHeight - 6, { align: 'center' });
    
    // === PAGE 2: Why SAFUAYE & Mission ===
    doc.addPage();
    yPos = margin;
    
    // Header
    doc.setFillColor(...greenColor);
    doc.rect(0, 0, pageWidth, 20, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text(
      lang === 'fr' ? 'SAFUAYE - Dépliant Informatif' :
      lang === 'en' ? 'SAFUAYE - Information Leaflet' :
      'SAFUAYE - Opuscolo Informativo',
      pageWidth / 2, 13, { align: 'center' }
    );
    
    yPos = 30;
    
    // Why SAFUAYE
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...greenColor);
    doc.text(currentT.why.overline.toUpperCase(), margin, yPos);
    
    yPos += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const whyText = currentT.why.intro;
    yPos = addWrappedText(whyText, margin, yPos, pageWidth - 2 * margin, 5, 10, grayColor);
    
    // Quote
    yPos += 5;
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 15, 2, 2, 'F');
    yPos += 10;
    doc.setFont('helvetica', 'bolditalic');
    doc.setFontSize(12);
    doc.setTextColor(...greenColor);
    doc.text('« ' + currentT.why.quote + ' »', pageWidth / 2, yPos, { align: 'center' });
    
    // Social reality
    yPos += 15;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(180, 50, 50);
    doc.text(currentT.why.realityTitle, margin, yPos);
    
    yPos += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    currentT.why.realityPoints.forEach((point, i) => {
      doc.setTextColor(...grayColor);
      doc.text('• ' + point, margin + (i % 2 === 0 ? 0 : 85), yPos + Math.floor(i / 2) * 5);
    });
    yPos += 15;
    
    // Mission
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...greenColor);
    doc.text(currentT.mission.overline.toUpperCase(), margin, yPos);
    
    yPos += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    currentT.mission.points.forEach((point, i) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...darkColor);
      doc.text((i + 1) + '. ' + point.title, margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...grayColor);
      doc.text('   ' + point.description, margin, yPos + 4);
      yPos += 11;
    });
    
    // Transnational
    yPos += 5;
    doc.setFillColor(...greenColor);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 25, 3, 3, 'F');
    yPos += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(currentT.mission.transnationalTitle, pageWidth / 2, yPos, { align: 'center' });
    yPos += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(currentT.mission.transnationalText, pageWidth / 2, yPos, { align: 'center' });
    
    // Footer
    doc.setFillColor(...greenColor);
    doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text('UK • Italie • Suisse • Togo | contact@safuaye.org | +44 7398 308 310', pageWidth / 2, pageHeight - 6, { align: 'center' });
    
    // === PAGE 3: Beneficiaries ===
    doc.addPage();
    yPos = margin;
    
    // Header
    doc.setFillColor(...greenColor);
    doc.rect(0, 0, pageWidth, 20, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text(
      lang === 'fr' ? 'SAFUAYE - Nos Bénéficiaires' :
      lang === 'en' ? 'SAFUAYE - Our Beneficiaries' :
      'SAFUAYE - I Nostri Beneficiari',
      pageWidth / 2, 13, { align: 'center' }
    );
    
    yPos = 30;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...greenColor);
    doc.text(currentT.beneficiaries.title.toUpperCase(), margin, yPos);
    
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...grayColor);
    doc.text(currentT.beneficiaries.subtitle, margin, yPos);
    
    yPos += 10;
    
    const beneficiaries = currentT.beneficiaries.profiles;
    
    const cardWidth = (pageWidth - 2 * margin - 10) / 2;
    const cardHeight = 35;
    
    beneficiaries.forEach((ben, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const xPos = margin + col * (cardWidth + 10);
      const cardY = yPos + row * (cardHeight + 8);
      
      // Card background
      doc.setFillColor(250, 250, 250);
      doc.roundedRect(xPos, cardY, cardWidth, cardHeight, 2, 2, 'F');
      doc.setDrawColor(220, 220, 220);
      doc.roundedRect(xPos, cardY, cardWidth, cardHeight, 2, 2, 'S');
      
      // Name and age
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...darkColor);
      doc.text(ben.name, xPos + 3, cardY + 6);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...grayColor);
      doc.text(
        ben.age + (lang === 'fr' ? ' ans' : lang === 'en' ? ' years' : ' anni'),
        xPos + cardWidth - 15,
        cardY + 6
      );
      
      // Situation
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...greenColor);
      doc.text(ben.situation, xPos + 3, cardY + 12);
      
      // Testimony
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(...grayColor);
      doc.text('"' + ben.testimony + '"', xPos + 3, cardY + 18);
      
      // Needs
      doc.setFontSize(7);
      ben.needs.forEach((need, j) => {
        doc.setFillColor(255, 240, 240);
        const needWidth = doc.getTextWidth(need) + 4;
        doc.roundedRect(xPos + 3 + j * (needWidth + 2), cardY + 23, needWidth, 8, 1, 1, 'F');
        doc.setTextColor(180, 50, 50);
        doc.text(need, xPos + 5 + j * (needWidth + 2), cardY + 28);
      });
    });
    
    // Contact section at bottom
    yPos = pageHeight - 50;
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 30, 3, 3, 'F');
    
    yPos += 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...greenColor);
    doc.text(
      lang === 'fr' ? 'REJOIGNEZ-NOUS' :
      lang === 'en' ? 'JOIN US' :
      'UNITEVI A NOI',
      pageWidth / 2, yPos, { align: 'center' }
    );
    
    yPos += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...darkColor);
    doc.text('Email: contact@safuaye.org', pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
    doc.text(
      lang === 'fr' ? 'Téléphone: +44 7398 308 310' :
      lang === 'en' ? 'Phone: +44 7398 308 310' :
      'Telefono: +44 7398 308 310',
      pageWidth / 2, yPos, { align: 'center' }
    );
    yPos += 5;
    doc.text('Web: safuaye.org', pageWidth / 2, yPos, { align: 'center' });
    
    // Footer
    doc.setFillColor(...greenColor);
    doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(
      'UK • Italie • Suisse • Togo | ' + currentT.footer.tagline,
      pageWidth / 2, pageHeight - 6, { align: 'center' }
    );
    
    // Save with language-specific filename
    const filename = lang === 'fr' ? 'SAFUAYE_Depliant_Informatif.pdf' :
                     lang === 'en' ? 'SAFUAYE_Information_Leaflet.pdf' :
                     'SAFUAYE_Opuscolo_Informativo.pdf';
    doc.save(filename);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const getIcon = (iconName) => {
    switch(iconName) {
      case 'trophy': return <Trophy className="w-6 h-6" />;
      case 'users': return <Users className="w-6 h-6" />;
      case 'star': return <Star className="w-6 h-6" />;
      case 'heart': return <Heart className="w-6 h-6" />;
      case 'globe': return <Globe className="w-6 h-6" />;
      default: return <Heart className="w-6 h-6" />;
    }
  };

  // ===== AG 27/06/2026 — banner & section helpers =====
  const AG_START = new Date('2026-06-27T10:00:00+00:00'); // 10h UTC (~10h Lomé)
  const AG_BANNER_END = new Date('2026-06-28T18:00:00+00:00'); // sticky banner disappears after AG
  const AG_SECTION_END = new Date('2027-06-27T18:00:00+00:00'); // detailed AG section stays visible 1 year (commemoration)
  const [agNow, setAgNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setAgNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const showAgBanner = agNow < AG_BANNER_END;
  const showAgSection = agNow < AG_SECTION_END;
  const agPast = agNow >= AG_BANNER_END;
  const agDiffMs = AG_START - agNow;
  const agDays = Math.max(0, Math.floor(agDiffMs / (1000 * 60 * 60 * 24)));
  const agHours = Math.max(0, Math.floor((agDiffMs / (1000 * 60 * 60)) % 24));
  const agMinutes = Math.max(0, Math.floor((agDiffMs / (1000 * 60)) % 60));
  const agIsLive = agDiffMs <= 0 && agNow < AG_BANNER_END;

  return (
    <div className="app-container font-body">
      {/* Floating "Faire un don" sticky button — visible on all pages */}
      <button
        onClick={() => scrollToSection('donations')}
        className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-40 group flex items-center gap-2 pl-4 pr-5 py-3 md:py-3.5 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold shadow-2xl hover:shadow-emerald-500/50 hover:scale-105 transition-all"
        aria-label={lang === 'fr' ? "Faire un don" : lang === 'en' ? "Donate" : "Dona"}
        data-testid="floating-donate-btn"
      >
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
        </span>
        <Heart className="w-4 h-4 md:w-5 md:h-5 fill-current" />
        <span className="text-sm md:text-base whitespace-nowrap">
          {lang === 'fr' ? "Faire un don" : lang === 'en' ? "Donate" : "Dona"}
        </span>
      </button>

      {/* Navigation */}
      <nav data-testid="main-navigation" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/90 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3" data-testid="logo">
              <img src={SAFUAYE_ASSETS.logo} alt="SAFUAYE" className="h-12 md:h-14 w-auto" />
            </a>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-6">
              <button onClick={() => scrollToSection('legacy')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-legacy">{t.nav.legacy}</button>
              <button onClick={() => scrollToSection('why')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-why">{t.nav.why}</button>
              <button onClick={() => scrollToSection('mission')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-mission">{t.nav.mission}</button>
              <button onClick={() => scrollToSection('hq')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-hq">{t.nav.hq}</button>
              <button onClick={() => scrollToSection('impact')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-impact">{t.nav.impact}</button>
              <button onClick={() => scrollToSection('urgence-avc')} className="text-sm font-bold text-red-600 hover:text-red-700 transition-colors flex items-center gap-1.5" data-testid="nav-urgence"><span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>{t.nav.urgence}</button>
              <button onClick={() => scrollToSection('academy')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-academy">{t.nav.academy}</button>
              <button onClick={() => scrollToSection('presse')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-presse">{t.nav.presse}</button>
              <button onClick={() => scrollToSection('beneficiaries')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-beneficiaries">{t.nav.beneficiaries}</button>
              <button onClick={() => scrollToSection('parrainage')} className="text-sm font-semibold text-indigo-700 hover:text-indigo-800 transition-colors flex items-center gap-1" data-testid="nav-parrainage">🎒 {lang === 'en' ? 'Sponsor' : lang === 'it' ? 'Adotta' : 'Parrainage'}</button>
              <button onClick={() => scrollToSection('governance')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-governance">{t.nav.governance}</button>
              <button onClick={() => scrollToSection('donations')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-donations">{t.nav.donations}</button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-gray-600 hover:text-green-700 transition-colors" data-testid="nav-contact">{t.nav.contact}</button>
            </div>

            <div className="flex items-center gap-3">
              {/* Language Toggle - Dropdown for 3 languages */}
              <div className="relative">
                <select 
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  className="appearance-none flex items-center gap-1 px-4 py-1.5 pr-8 border border-gray-300 rounded-full text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors bg-white cursor-pointer"
                  data-testid="language-toggle"
                >
                  <option value="fr">🇫🇷 FR</option>
                  <option value="en">🇬🇧 EN</option>
                  <option value="it">🇮🇹 IT</option>
                </select>
                <Globe className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
              </div>
              
              {/* Download Button */}
              <button 
                onClick={handleDownloadFlyer}
                className="hidden md:flex items-center gap-2 bg-green-700 text-white px-5 py-2 rounded-full font-medium hover:bg-green-800 transition-colors"
                data-testid="download-flyer-btn"
              >
                <Download className="w-4 h-4" />
                {t.nav.downloadFlyer}
              </button>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-gray-700"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 py-4 px-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-3">
              <button onClick={() => scrollToSection('legacy')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.legacy}</button>
              <button onClick={() => scrollToSection('why')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.why}</button>
              <button onClick={() => scrollToSection('mission')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.mission}</button>
              <button onClick={() => scrollToSection('hq')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.hq}</button>
              <button onClick={() => scrollToSection('impact')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.impact}</button>
              <button onClick={() => scrollToSection('urgence-avc')} className="text-left text-base font-bold text-red-600 py-2 flex items-center gap-2"><span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>{t.nav.urgence}</button>
              <button onClick={() => scrollToSection('academy')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.academy}</button>
              <button onClick={() => scrollToSection('presse')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.presse}</button>
              <button onClick={() => scrollToSection('beneficiaries')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.beneficiaries}</button>
              <button onClick={() => scrollToSection('parrainage')} className="text-left text-base font-semibold text-indigo-700 py-2">🎒 {lang === 'en' ? 'Sponsor' : lang === 'it' ? 'Adotta' : 'Parrainage'}</button>
              <button onClick={() => scrollToSection('governance')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.governance}</button>
              <button onClick={() => scrollToSection('donations')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.donations}</button>
              <button onClick={() => scrollToSection('contact')} className="text-left text-base font-medium text-gray-700 py-2">{t.nav.contact}</button>
              <button 
                onClick={handleDownloadFlyer}
                className="flex items-center justify-center gap-2 bg-green-700 text-white px-6 py-3 rounded-full font-medium mt-2"
                data-testid="mobile-download-btn"
              >
                <Download className="w-4 h-4" />
                {t.nav.downloadFlyer}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* AG Banner — affichée jusqu'au lendemain de l'AG, puis disparaît automatiquement */}
      {showAgBanner && (
        <div data-testid="ag-banner" className="fixed top-16 md:top-20 left-0 right-0 z-40 bg-gradient-to-r from-green-700 via-emerald-700 to-green-800 text-white shadow-lg">
          <a href="#ag2026" className="block hover:bg-black/10 transition-colors">
            <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${agIsLive ? 'bg-red-600 animate-pulse' : 'bg-orange-500'}`}>
                  {agIsLive ? (<><span className="w-1.5 h-1.5 bg-white rounded-full"></span>EN COURS</>) : "À VENIR"}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold leading-tight">
                    {agIsLive
                      ? "AG Constitutive SAFUAYE-TOGO en cours à Atakpamé"
                      : "AG Constitutive SAFUAYE-TOGO · Samedi 27 juin 2026 · 10h00 · Atakpamé"}
                  </p>
                  {!agIsLive && (agDays > 0 || agHours > 0 || agMinutes > 0) && (
                    <p className="text-xs text-green-100 hidden sm:block">
                      Dans {agDays > 0 && `${agDays}j `}{agHours}h{String(agMinutes).padStart(2, '0')}min · Cliquez pour les détails →
                    </p>
                  )}
                </div>
              </div>
              <span className="text-xs font-semibold underline hidden md:inline">Voir le programme</span>
            </div>
          </a>
        </div>
      )}

      {/* Hero Section with Official Image */}
      <section data-testid="hero-section" className={`relative min-h-screen flex items-center ${showAgBanner ? 'pt-32 md:pt-36' : 'pt-20'}`}>
        <div className="absolute inset-0 z-0">
          <img 
            src={SAFUAYE_ASSETS.heroImage}
            alt="SAFUAYE - Atakpamé Football Legacy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-32">
          <div className="max-w-2xl">
            <p className="text-sm md:text-base font-medium text-yellow-400 mb-3" data-testid="hero-subtitle">
              {t.hero.subtitle}
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-tight mb-2" data-testid="hero-nickname">
              {t.hero.nickname}
            </h1>
            <p className="font-heading text-xl md:text-2xl text-white/90 italic mb-6" data-testid="hero-tagline">
              {t.hero.tagline}
            </p>
            <p className="text-base md:text-lg text-white/80 mb-2">
              {t.hero.profession}
            </p>
            <p className="text-base md:text-lg text-white/80 mb-6">
              {t.hero.description}
            </p>
            <p className="text-sm text-yellow-400/90 font-medium mb-8">
              {t.hero.values}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => scrollToSection('contact')}
                className="flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-full font-medium hover:bg-green-700 transition-colors"
                data-testid="hero-join-btn"
              >
                {t.hero.joinUs}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button 
                onClick={() => scrollToSection('legacy')}
                className="flex items-center justify-center gap-2 border-2 border-white/50 text-white px-8 py-4 rounded-full font-medium hover:bg-white/10 transition-colors"
                data-testid="hero-learn-btn"
              >
                {t.hero.learnMore}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 🔴 Impact Live — compteurs en temps réel juste après la Hero */}
      <ImpactLiveSection API={API} lang={lang} />

      {/* Legacy Section */}
      <section id="legacy" data-testid="legacy-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3">
              {t.legacy.overline}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.legacy.title}
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <p className="text-lg text-gray-700 font-medium">{t.legacy.intro}</p>
              <p className="text-gray-600 leading-relaxed">{t.legacy.p1}</p>
              <p className="text-gray-600 leading-relaxed">{t.legacy.p2}</p>
              <p className="text-gray-600 leading-relaxed">{t.legacy.p3}</p>
              
              <div className="bg-green-50 border-l-4 border-green-600 p-6 mt-8">
                <h3 className="font-heading text-xl font-bold text-gray-900 mb-3">{t.legacy.presidentTitle}</h3>
                <p className="text-gray-700 mb-4">{t.legacy.presidentIntro}</p>
                <ul className="space-y-2">
                  {t.legacy.presidentPoints.map((point, i) => (
                    <li key={`president-point-${i}-${point.substring(0, 15)}`} className="flex items-start gap-2 text-gray-700">
                      <ChevronRight className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      {point}
                    </li>
                  ))}
                </ul>
                <p className="text-gray-800 font-medium mt-4 italic">{t.legacy.presidentConclusion}</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src={SAFUAYE_ASSETS.portrait1}
                  alt="Safuaye - Portrait"
                  className="w-full aspect-[3/4] object-cover rounded-lg shadow-lg"
                  data-testid="legacy-portrait-1"
                />
                <img 
                  src={SAFUAYE_ASSETS.portrait2}
                  alt="Safuaye - Portrait"
                  className="w-full aspect-[3/4] object-cover object-top rounded-lg shadow-lg"
                  data-testid="legacy-portrait-2"
                />
              </div>
              <img 
                src={SAFUAYE_ASSETS.groupPhoto}
                alt="Équipe Ifodjè"
                className="w-full aspect-video object-cover rounded-lg shadow-lg"
                data-testid="legacy-group-photo"
              />
              <div className="bg-blue-900 text-white p-6 rounded-lg text-center">
                <Trophy className="w-10 h-10 mx-auto mb-3 text-yellow-400" />
                <h4 className="font-heading text-xl font-bold">Ifodjè Football Club</h4>
                <p className="text-white/80">Atakpamé, Togo • 1984-1990</p>
              </div>
            </div>
          </div>
          
          <div className="mt-12 bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center">
            <h3 className="font-heading text-2xl font-bold text-gray-900 mb-3">{t.legacy.heritageTitle}</h3>
            <p className="text-gray-700 max-w-3xl mx-auto">{t.legacy.heritageText}</p>
          </div>
        </div>
      </section>

      {/* Why SAFUAYE Section */}
      <section id="why" data-testid="why-section" className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3">
              {t.why.overline}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              {t.why.title}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">{t.why.intro}</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <blockquote className="text-center py-8 px-6 bg-green-700 text-white rounded-xl mb-12">
              <Quote className="w-10 h-10 mx-auto mb-4 opacity-50" />
              <p className="font-heading text-2xl md:text-3xl italic">"{t.why.quote}"</p>
            </blockquote>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
                <div>
                  <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">{t.why.realityTitle}</h3>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {t.why.realityPoints.map((point, i) => (
                  <div key={`reality-${i}-${point.substring(0, 15)}`} className="flex items-center gap-3 bg-white p-4 rounded-lg">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-800 font-medium text-center italic">{t.why.realityConclusion}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" data-testid="mission-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3">
              {t.mission.overline}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900">
              {t.mission.title}
            </h2>
            {t.mission.subtitle && (
              <p className="mt-4 max-w-3xl mx-auto text-base md:text-lg text-gray-600 leading-relaxed" data-testid="mission-subtitle">
                {t.mission.subtitle}
              </p>
            )}
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
            {t.mission.points.map((point, i) => (
              <div key={`mission-point-${i}-${point.title}`} className="bg-gray-50 p-6 rounded-xl text-center hover:bg-green-50 hover:border-green-200 border border-transparent transition-colors" data-testid={`mission-point-${i}`}>
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-green-700">
                  {getIcon(point.icon)}
                </div>
                <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">{point.title}</h3>
                <p className="text-sm text-gray-600">{point.description}</p>
              </div>
            ))}
          </div>
          
          <div className="bg-blue-900 text-white rounded-xl p-8 md:p-12 text-center">
            <Globe className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
            <h3 className="font-heading text-2xl font-bold mb-4">{t.mission.transnationalTitle}</h3>
            <p className="text-white/90 max-w-2xl mx-auto">{t.mission.transnationalText}</p>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <span className="px-4 py-2 bg-white/10 rounded-full text-sm">🇬🇧 UK</span>
              <span className="px-4 py-2 bg-white/10 rounded-full text-sm">🇮🇹 Italie</span>
              <span className="px-4 py-2 bg-white/10 rounded-full text-sm">🇨🇭 Suisse</span>
              <span className="px-4 py-2 bg-white/10 rounded-full text-sm">🇹🇬 Togo</span>
            </div>
          </div>
        </div>
      </section>

      {/* Urgence AVC & Hypertension — Programme de soutien aux anciens d'Ifodjè */}
      <section id="urgence-avc" data-testid="urgence-avc-section" className="py-20 md:py-24 bg-gradient-to-br from-red-950 via-rose-900 to-orange-950 text-white scroll-mt-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" aria-hidden="true">
          <div className="absolute top-10 left-10 w-64 h-64 bg-red-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 relative">

          {/* Alerte urgente — anonyme */}
          <div className="bg-red-500/20 border-2 border-red-400/60 rounded-2xl p-5 md:p-6 mb-10 flex flex-col md:flex-row items-start md:items-center gap-4 backdrop-blur-sm" data-testid="urgence-alert">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-red-500 text-white flex items-center justify-center shadow-lg animate-pulse">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4"></path><path d="M12 17h.01"></path><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"></path></svg>
            </div>
            <div className="flex-1">
              <p className="text-red-200 text-xs font-bold uppercase tracking-widest mb-1">
                {lang === 'fr' ? "Alerte en cours" : lang === 'en' ? "Ongoing alert" : "Allerta in corso"}
              </p>
              <p className="text-white font-semibold text-base md:text-lg leading-snug">
                {lang === 'fr' ? "Un ancien joueur d'Ifodjè FC est actuellement hospitalisé pour un AVC. Votre aide est immédiate et vitale."
                  : lang === 'en' ? "A former Ifodjè FC player is currently hospitalised following a stroke. Your help is immediate and vital."
                  : "Un ex giocatore dell'Ifodjè FC è attualmente ricoverato in ospedale per un ictus. Il vostro aiuto è immediato e vitale."}
              </p>
            </div>
          </div>

          {/* Titre + constat */}
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 border border-white/20 rounded-full text-xs font-bold uppercase tracking-widest text-white/90 mb-4">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse"></span>
              {lang === 'fr' ? "Programme d'urgence · Santé" : lang === 'en' ? "Emergency programme · Health" : "Programma d'emergenza · Salute"}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold mb-4 leading-tight">
              {lang === 'fr' ? "Urgence AVC & Hypertension" : lang === 'en' ? "Stroke & Hypertension Emergency" : "Emergenza Ictus & Ipertensione"}
              <br /><span className="text-orange-300 text-2xl md:text-4xl">
                {lang === 'fr' ? "chez les anciens d'Ifodjè FC" : lang === 'en' ? "among former Ifodjè FC players" : "tra gli ex giocatori dell'Ifodjè FC"}
              </span>
            </h2>
            <p className="text-white/80 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
              {lang === 'fr' ? "Presque tous les anciens joueurs de la génération de Safuayé (années 1980) que nous rencontrons aujourd'hui sont touchés par un AVC ou une hypertension sévère. Une urgence sanitaire silencieuse qui frappe les héros de notre football local."
                : lang === 'en' ? "Almost every former player from Safuayé's generation (1980s) we meet today has suffered a stroke or severe hypertension. A silent health emergency striking the heroes of our local football."
                : "Quasi tutti gli ex giocatori della generazione di Safuayé (anni '80) che incontriamo oggi soffrono di ictus o grave ipertensione. Un'emergenza sanitaria silenziosa che colpisce gli eroi del nostro calcio locale."}
            </p>
          </div>

          {/* Live counter + Signaler un cas */}
          <div className="grid md:grid-cols-4 gap-4 mb-10" data-testid="avc-counter">
            <div className="md:col-span-3 grid grid-cols-3 gap-3">
              <div className="bg-white/10 border border-white/20 rounded-2xl p-4 text-center backdrop-blur-sm">
                <p className="text-3xl md:text-4xl font-heading font-bold text-red-300">{avcCount.hospitalises}</p>
                <p className="text-white/70 text-xs uppercase tracking-wider mt-1">
                  {lang === 'fr' ? "Hospitalisés" : lang === 'en' ? "Hospitalised" : "Ricoverati"}
                </p>
              </div>
              <div className="bg-white/10 border border-white/20 rounded-2xl p-4 text-center backdrop-blur-sm">
                <p className="text-3xl md:text-4xl font-heading font-bold text-orange-300">{avcCount.survivants}</p>
                <p className="text-white/70 text-xs uppercase tracking-wider mt-1">
                  {lang === 'fr' ? "Survivants" : lang === 'en' ? "Survivors" : "Sopravvissuti"}
                </p>
              </div>
              <div className="bg-white/10 border border-white/20 rounded-2xl p-4 text-center backdrop-blur-sm">
                <p className="text-3xl md:text-4xl font-heading font-bold text-white/60">{avcCount.decedes}</p>
                <p className="text-white/70 text-xs uppercase tracking-wider mt-1">
                  {lang === 'fr' ? "Décédés" : lang === 'en' ? "Deceased" : "Deceduti"}
                </p>
              </div>
            </div>
            <button
              onClick={() => setAvcModalOpen(true)}
              className="bg-gradient-to-br from-white to-red-100 hover:from-red-100 hover:to-white text-red-900 font-bold rounded-2xl p-4 shadow-lg hover:shadow-2xl transition-all flex flex-col items-center justify-center gap-1 group"
              data-testid="avc-report-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"></path><circle cx="12" cy="12" r="10"></circle></svg>
              <span className="text-sm text-center leading-tight">
                {lang === 'fr' ? "Signaler un cas" : lang === 'en' ? "Report a case" : "Segnalare un caso"}
              </span>
              <span className="text-xs font-normal text-red-700/80">
                {lang === 'fr' ? "(anonyme possible)" : lang === 'en' ? "(anonymous possible)" : "(possibile anonimo)"}
              </span>
            </button>
          </div>

          {/* 4 axes d'action */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              {icon:"🩺", color:"bg-blue-500", title:{fr:"1. Dépistage",en:"1. Screening",it:"1. Screening"}, desc:{fr:"Prise de tension gratuite au siège d'Atchrimi-Komé et dans les villages, en partenariat avec le CHR d'Atakpamé.",en:"Free blood pressure checks at the Atchrimi-Komé headquarters and in villages, in partnership with Atakpamé Regional Hospital.",it:"Misurazione gratuita della pressione presso la sede di Atchrimi-Komé e nei villaggi, in collaborazione con l'Ospedale Regionale di Atakpamé."}},
              {icon:"🚑", color:"bg-red-500", title:{fr:"2. Transport d'urgence",en:"2. Emergency transport",it:"2. Trasporto d'emergenza"}, desc:{fr:"Fonds pour le transport rapide vers le CHR Atakpamé ou le CHU Sylvanus Olympio de Lomé (unité neurovasculaire).",en:"Fund for rapid transport to Atakpamé Regional Hospital or the Sylvanus Olympio University Hospital in Lomé (stroke unit).",it:"Fondi per il trasporto rapido verso l'Ospedale di Atakpamé o l'Ospedale Universitario Sylvanus Olympio di Lomé (unità ictus)."}},
              {icon:"💊", color:"bg-emerald-500", title:{fr:"3. Médicaments post-AVC",en:"3. Post-stroke medication",it:"3. Farmaci post-ictus"}, desc:{fr:"Prise en charge des traitements antihypertenseurs et anticoagulants à long terme, souvent inaccessibles financièrement.",en:"Coverage of long-term antihypertensive and anticoagulant treatments, often financially inaccessible.",it:"Copertura dei trattamenti antipertensivi e anticoagulanti a lungo termine, spesso finanziariamente inaccessibili."}},
              {icon:"🦯", color:"bg-purple-500", title:{fr:"4. Rééducation à domicile",en:"4. Home rehabilitation",it:"4. Riabilitazione domiciliare"}, desc:{fr:"Kinésithérapie, orthophonie et matériel de mobilité (déambulateurs, fauteuils roulants) pour les survivants d'AVC.",en:"Physiotherapy, speech therapy and mobility equipment (walkers, wheelchairs) for stroke survivors.",it:"Fisioterapia, logopedia e attrezzature per la mobilità (deambulatori, sedie a rotelle) per i sopravvissuti all'ictus."}},
            ].map((axe, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm hover:bg-white/10 transition-colors">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${axe.color} text-white text-2xl mb-3 shadow-md`}>{axe.icon}</div>
                <h4 className="font-heading text-lg font-bold text-white mb-2">{axe.title[lang] || axe.title.fr}</h4>
                <p className="text-white/75 text-sm leading-relaxed">{axe.desc[lang] || axe.desc.fr}</p>
              </div>
            ))}
          </div>

          {/* CTA de don d'urgence */}
          <div className="bg-white/10 border-2 border-orange-400/60 rounded-3xl p-6 md:p-8 backdrop-blur-md">
            <div className="text-center mb-6">
              <p className="text-orange-300 text-xs font-bold uppercase tracking-widest mb-3">
                {lang === 'fr' ? "Comment aider maintenant" : lang === 'en' ? "How to help now" : "Come aiutare ora"}
              </p>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">
                {lang === 'fr' ? "Chaque don finance directement une vie" : lang === 'en' ? "Every donation directly funds a life" : "Ogni donazione finanzia direttamente una vita"}
              </h3>
              <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto">
                {lang === 'fr' ? "En attendant l'enregistrement de SAFUAYE à la Charity Commission UK (fin 2026) qui permettra les dons en ligne sécurisés, contactez directement le Bureau National pour transférer votre don via Wise, Revolut ou virement classique."
                  : lang === 'en' ? "While SAFUAYE completes its UK Charity Commission registration (late 2026) enabling secure online donations, please contact the National Board directly to transfer your donation via Wise, Revolut or a standard bank transfer."
                  : "In attesa della registrazione di SAFUAYE presso la Charity Commission UK (fine 2026) che permetterà donazioni online sicure, contattate direttamente il Consiglio Nazionale per trasferire la vostra donazione tramite Wise, Revolut o bonifico bancario."}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <a
                href="https://wa.me/22891142519?text=Bonjour%2C%20je%20souhaite%20faire%20un%20don%20cibl%C3%A9%20pour%20le%20programme%20d%27urgence%20AVC%20de%20SAFUAYE."
                target="_blank"
                rel="noreferrer noopener"
                className="group bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 rounded-2xl p-4 shadow-lg hover:shadow-2xl transition-all flex items-center gap-3"
                data-testid="urgence-whatsapp-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <div>
                  <p className="text-xs text-white/90 font-semibold uppercase tracking-wider">WhatsApp</p>
                  <p className="text-white font-bold text-sm leading-tight">+228 91 14 25 19</p>
                  <p className="text-white/80 text-xs mt-0.5">
                    {lang === 'fr' ? "Président AZIAKA KOFFI" : lang === 'en' ? "President AZIAKA KOFFI" : "Presidente AZIAKA KOFFI"}
                  </p>
                </div>
              </a>

              <a
                href="mailto:associationsafuaye@gmail.com?subject=Don%20cibl%C3%A9%20-%20Programme%20AVC%20Ifodj%C3%A8&body=Bonjour%2C%0A%0AJe%20souhaite%20faire%20un%20don%20cibl%C3%A9%20pour%20le%20programme%20d%27urgence%20AVC%20des%20anciens%20d%27Ifodj%C3%A8.%0A%0AMontant%20envisag%C3%A9%20%3A%20%0AMode%20de%20transfert%20%28Wise%20%2F%20Revolut%20%2F%20virement%29%20%3A%20%0A%0AMerci%20de%20me%20recontacter%20pour%20les%20coordonn%C3%A9es%20bancaires."
                className="group bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500 rounded-2xl p-4 shadow-lg hover:shadow-2xl transition-all flex items-center gap-3"
                data-testid="urgence-email-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>
                <div>
                  <p className="text-xs text-white/90 font-semibold uppercase tracking-wider">Email</p>
                  <p className="text-white font-bold text-sm leading-tight break-all">associationsafuaye@gmail.com</p>
                  <p className="text-white/80 text-xs mt-0.5">
                    {lang === 'fr' ? "Réponse sous 24h" : lang === 'en' ? "Reply within 24h" : "Risposta entro 24h"}
                  </p>
                </div>
              </a>

              <a
                href="https://wa.me/22872666474?text=Bonjour%2C%20je%20contacte%20le%20Secr%C3%A9tariat%20G%C3%A9n%C3%A9ral%20de%20SAFUAYE%20pour%20le%20programme%20d%27urgence%20AVC."
                target="_blank"
                rel="noreferrer noopener"
                className="group bg-gradient-to-br from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 rounded-2xl p-4 shadow-lg hover:shadow-2xl transition-all flex items-center gap-3"
                data-testid="urgence-sg-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                <div>
                  <p className="text-xs text-white/90 font-semibold uppercase tracking-wider">
                    {lang === 'fr' ? "Secrétariat" : lang === 'en' ? "Secretariat" : "Segreteria"}
                  </p>
                  <p className="text-white font-bold text-sm leading-tight">+228 72 66 64 74</p>
                  <p className="text-white/80 text-xs mt-0.5">APEDO ABLAVI</p>
                </div>
              </a>
            </div>

            <p className="text-center text-white/60 text-xs mt-6 italic">
              {lang === 'fr' ? "Un reçu de don sera émis pour chaque contribution. Les fonds sont tracés et rendus publics dans notre rapport de transparence."
                : lang === 'en' ? "A donation receipt will be issued for every contribution. Funds are tracked and made public in our transparency report."
                : "Verrà emessa una ricevuta di donazione per ogni contributo. I fondi sono tracciati e resi pubblici nel nostro rapporto di trasparenza."}
            </p>
          </div>
        </div>
      </section>

      {/* AVC Modal — Signaler un cas anonymement */}
      {avcModalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setAvcModalOpen(false)} data-testid="avc-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 md:p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-red-50 to-orange-50 rounded-t-2xl">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-red-700 mb-1">
                  {lang === 'fr' ? "Recensement anonyme" : lang === 'en' ? "Anonymous survey" : "Censimento anonimo"}
                </p>
                <h3 className="font-heading text-xl font-bold text-gray-900">
                  {lang === 'fr' ? "Signaler un ancien joueur touché" : lang === 'en' ? "Report an affected former player" : "Segnalare un ex giocatore colpito"}
                </h3>
              </div>
              <button onClick={() => setAvcModalOpen(false)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="avc-modal-close" aria-label="Fermer">×</button>
            </div>

            {avcSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500 text-white flex items-center justify-center text-3xl">✓</div>
                <p className="font-heading text-xl font-bold text-gray-900 mb-2">
                  {lang === 'fr' ? "Merci pour ce signalement" : lang === 'en' ? "Thank you for this report" : "Grazie per questa segnalazione"}
                </p>
                <p className="text-gray-600 text-sm">
                  {lang === 'fr' ? "Votre information a été transmise au Bureau National. Nous vous recontacterons si nécessaire."
                    : lang === 'en' ? "Your information has been forwarded to the National Board. We will contact you back if needed."
                    : "La sua informazione è stata trasmessa al Consiglio Nazionale. La ricontatteremo se necessario."}
                </p>
              </div>
            ) : (
              <form onSubmit={submitAvcCase} className="p-5 md:p-6 space-y-4" data-testid="avc-form">
                <p className="text-sm text-gray-600 leading-relaxed">
                  {lang === 'fr' ? "Aidez SAFUAYE à documenter l'ampleur du problème. Chaque signalement (même anonyme) permet de mesurer le besoin et de solliciter des fonds ciblés."
                    : lang === 'en' ? "Help SAFUAYE document the scale of the issue. Every report (even anonymous) helps measure the need and secure targeted funding."
                    : "Aiuti SAFUAYE a documentare la portata del problema. Ogni segnalazione (anche anonima) aiuta a misurare il bisogno e a ottenere finanziamenti mirati."}
                </p>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wider">
                    {lang === 'fr' ? "Nom d'usage / surnom *" : lang === 'en' ? "Common name / nickname *" : "Nome d'uso / soprannome *"}
                  </label>
                  <input
                    type="text"
                    required
                    minLength={2}
                    value={avcForm.nom_usage}
                    onChange={(e) => setAvcForm({ ...avcForm, nom_usage: e.target.value })}
                    placeholder={lang === 'fr' ? "ex: Kossi, Nayo, ou « Ancien joueur A »" : "ex: Kossi, Nayo, or \"Former player A\""}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none"
                    data-testid="avc-nom-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {lang === 'fr' ? "Peut rester générique pour préserver l'anonymat" : "Can stay generic to preserve anonymity"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wider">
                      {lang === 'fr' ? "Village / quartier" : lang === 'en' ? "Village / area" : "Villaggio / zona"}
                    </label>
                    <input
                      type="text"
                      value={avcForm.village}
                      onChange={(e) => setAvcForm({ ...avcForm, village: e.target.value })}
                      placeholder="Atakpamé, Ogou1..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none"
                      data-testid="avc-village-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wider">
                      {lang === 'fr' ? "Âge approximatif" : lang === 'en' ? "Approximate age" : "Età approssimativa"}
                    </label>
                    <input
                      type="text"
                      value={avcForm.age_approx}
                      onChange={(e) => setAvcForm({ ...avcForm, age_approx: e.target.value })}
                      placeholder="60-70 ans"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none"
                      data-testid="avc-age-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wider">
                    {lang === 'fr' ? "Statut actuel *" : lang === 'en' ? "Current status *" : "Stato attuale *"}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: "hospitalise", label: { fr: "Hospitalisé", en: "Hospitalised", it: "Ricoverato" }, color: "border-red-400 bg-red-50 text-red-700" },
                      { value: "survivant", label: { fr: "Survivant", en: "Survivor", it: "Sopravvissuto" }, color: "border-orange-400 bg-orange-50 text-orange-700" },
                      { value: "decede", label: { fr: "Décédé", en: "Deceased", it: "Deceduto" }, color: "border-gray-400 bg-gray-100 text-gray-700" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAvcForm({ ...avcForm, statut: opt.value })}
                        className={`text-xs font-semibold py-2 px-2 rounded-lg border-2 transition-all ${avcForm.statut === opt.value ? opt.color + ' ring-2 ring-offset-1' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}
                        data-testid={`avc-statut-${opt.value}`}
                      >
                        {opt.label[lang] || opt.label.fr}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wider">
                    {lang === 'fr' ? "Contact famille (optionnel)" : lang === 'en' ? "Family contact (optional)" : "Contatto familiare (opzionale)"}
                  </label>
                  <input
                    type="text"
                    value={avcForm.contact_famille}
                    onChange={(e) => setAvcForm({ ...avcForm, contact_famille: e.target.value })}
                    placeholder={lang === 'fr' ? "N° WhatsApp d'un proche" : "Relative's WhatsApp number"}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none"
                    data-testid="avc-contact-input"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wider">
                    {lang === 'fr' ? "Signalé par" : lang === 'en' ? "Reported by" : "Segnalato da"}
                  </label>
                  <select
                    value={avcForm.signale_par}
                    onChange={(e) => setAvcForm({ ...avcForm, signale_par: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none"
                    data-testid="avc-signale-input"
                  >
                    <option value="">— {lang === 'fr' ? "Choisir" : "Choose"} —</option>
                    <option value="Sage">{lang === 'fr' ? "Membre d'Honneur (Sage)" : "Honorary Member"}</option>
                    <option value="Bureau">{lang === 'fr' ? "Membre du Bureau" : "Board Member"}</option>
                    <option value="Diaspora">Diaspora</option>
                    <option value="Famille">{lang === 'fr' ? "Famille du joueur" : "Player's family"}</option>
                    <option value="Anonyme">{lang === 'fr' ? "Anonyme" : "Anonymous"}</option>
                  </select>
                </div>

                <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={avcForm.consentement_famille}
                    onChange={(e) => setAvcForm({ ...avcForm, consentement_famille: e.target.checked })}
                    className="mt-0.5"
                    data-testid="avc-consent-input"
                  />
                  <span>
                    {lang === 'fr' ? "La famille a été informée de ce signalement et donne son accord."
                      : lang === 'en' ? "The family has been informed of this report and consents."
                      : "La famiglia è stata informata di questa segnalazione e acconsente."}
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={avcSubmitting}
                  className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold py-3 rounded-lg shadow-md hover:shadow-xl transition-all disabled:opacity-60"
                  data-testid="avc-submit-btn"
                >
                  {avcSubmitting ? (lang === 'fr' ? "Envoi..." : "Sending...") : (lang === 'fr' ? "Transmettre le signalement" : lang === 'en' ? "Submit report" : "Invia segnalazione")}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Academy — Rentrée septembre 2026 & Gala 30 août */}
      <section id="academy" data-testid="academy-section" className="py-20 md:py-24 bg-gradient-to-br from-emerald-50 via-white to-orange-50 scroll-mt-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none" aria-hidden="true">
          <div className="absolute top-20 right-10 w-72 h-72 bg-emerald-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 relative">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-100 border border-orange-300 rounded-full text-xs font-bold uppercase tracking-widest text-orange-800 mb-4">
              <span>⚽</span>
              {lang === 'fr' ? "Académie de football · Rentrée 2026" : lang === 'en' ? "Football academy · 2026 opening" : "Accademia di calcio · Apertura 2026"}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-3">
              {lang === 'fr' ? "Académie de Football SAFUAYE" : lang === 'en' ? "SAFUAYE Football Academy" : "Accademia di Calcio SAFUAYE"}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-base md:text-lg">
              {lang === 'fr' ? "Formation gratuite pour les enfants de 6 à 13 ans à Atakpamé — 4 catégories (U7 · U9 · U11 · U13). Rentrée officielle : semaine du 8 septembre 2026."
                : lang === 'en' ? "Free football training for children aged 6-13 in Atakpamé — 4 age groups (U7 · U9 · U11 · U13). Official opening: week of 8 September 2026."
                : "Formazione calcistica gratuita per bambini 6-13 anni ad Atakpamé — 4 categorie (U7 · U9 · U11 · U13). Apertura ufficiale: settimana dell'8 settembre 2026."}
            </p>
          </div>

          {/* Countdown to Gala */}
          {(() => {
            const galaTs = new Date("2026-08-30T09:00:00+00:00").getTime();
            const diff = Math.max(0, galaTs - nowTs);
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const mins = Math.floor((diff / (1000 * 60)) % 60);
            const secs = Math.floor((diff / 1000) % 60);
            const isPast = diff === 0;
            return (
              <div className="mb-12 bg-gradient-to-br from-orange-500 via-red-500 to-rose-600 text-white rounded-3xl p-6 md:p-10 shadow-2xl" data-testid="gala-countdown">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div>
                    <p className="text-orange-100 text-xs font-bold uppercase tracking-widest mb-2">
                      {isPast ? (lang === 'fr' ? "Événement passé" : "Past event") : (lang === 'fr' ? "Compte à rebours" : lang === 'en' ? "Countdown" : "Conto alla rovescia")}
                    </p>
                    <h3 className="font-heading text-2xl md:text-4xl font-bold mb-1">
                      {lang === 'fr' ? "Gala d'ouverture" : lang === 'en' ? "Opening Gala" : "Gala d'apertura"}
                    </h3>
                    <p className="text-lg md:text-xl font-semibold text-white/95">
                      {lang === 'fr' ? "Samedi 30 août 2026 · 09h00" : lang === 'en' ? "Saturday 30 August 2026 · 09:00" : "Sabato 30 agosto 2026 · 09:00"}
                    </p>
                    <p className="text-sm md:text-base text-white/85 mt-1">
                      📍 {lang === 'fr' ? "Stade municipal d'Atakpamé — Entrée libre" : lang === 'en' ? "Atakpamé Municipal Stadium — Free entry" : "Stadio municipale di Atakpamé — Ingresso libero"}
                    </p>
                  </div>
                  <div className="grid grid-cols-4 gap-2 md:gap-3">
                    {[
                      { v: days, l: { fr: "Jours", en: "Days", it: "Giorni" } },
                      { v: hours, l: { fr: "Heures", en: "Hours", it: "Ore" } },
                      { v: mins, l: { fr: "Min", en: "Min", it: "Min" } },
                      { v: secs, l: { fr: "Sec", en: "Sec", it: "Sec" } },
                    ].map((u, i) => (
                      <div key={i} className="bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl px-3 md:px-4 py-3 md:py-4 text-center min-w-[60px] md:min-w-[80px]">
                        <p className="text-2xl md:text-4xl font-heading font-bold tabular-nums">{String(u.v).padStart(2, "0")}</p>
                        <p className="text-[10px] md:text-xs text-white/85 uppercase tracking-wider mt-0.5">{u.l[lang] || u.l.fr}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Pre-registration counter + CTA */}
          <div className="grid md:grid-cols-5 gap-4 mb-10">
            <div className="md:col-span-3 bg-white rounded-2xl shadow-md p-5 md:p-6 border border-emerald-200">
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-3">
                {lang === 'fr' ? "Pré-inscriptions ouvertes" : lang === 'en' ? "Pre-registrations open" : "Pre-iscrizioni aperte"}
              </p>
              <div className="grid grid-cols-4 gap-2 md:gap-3 mb-4">
                {["U7", "U9", "U11", "U13"].map((cat) => (
                  <div key={cat} className="text-center bg-emerald-50 border border-emerald-200 rounded-xl py-3 px-2">
                    <p className="text-2xl md:text-3xl font-heading font-bold text-emerald-700">{acadCount.by_category[cat] || 0}</p>
                    <p className="text-xs font-bold text-emerald-800 mt-0.5">{cat}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600">
                <strong>{acadCount.total}</strong> {lang === 'fr' ? "enfants déjà pré-inscrits pour la rentrée" : lang === 'en' ? "children already pre-registered" : "bambini già pre-iscritti"}
              </p>
            </div>
            <div className="md:col-span-2 flex">
              <button
                onClick={() => setAcadModalOpen(true)}
                className="w-full bg-gradient-to-br from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 text-white font-bold rounded-2xl p-4 md:p-6 shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all flex flex-col items-center justify-center gap-2"
                data-testid="academy-preregister-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" y1="8" x2="19" y2="14"></line><line x1="22" y1="11" x2="16" y2="11"></line></svg>
                <span className="text-lg leading-tight">
                  {lang === 'fr' ? "Pré-inscrire mon enfant" : lang === 'en' ? "Pre-register my child" : "Pre-iscrivi mio figlio"}
                </span>
                <span className="text-xs font-normal text-orange-100">
                  {lang === 'fr' ? "5 min · gratuit · en ligne" : lang === 'en' ? "5 min · free · online" : "5 min · gratis · online"}
                </span>
              </button>
            </div>
          </div>

          {/* Docs downloadable */}
          <div className="bg-white rounded-2xl shadow-md p-5 md:p-8 border border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-gray-900">
                {lang === 'fr' ? "Documents à télécharger" : lang === 'en' ? "Downloadable documents" : "Documenti scaricabili"}
              </h3>
              <span className="text-xs text-gray-500">PDF · A4 · FR</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { file: "SAFUAYE_Academy_Gala_Flyer_A4.pdf", title: { fr: "Affiche Gala 30 août", en: "Gala poster 30 Aug", it: "Locandina Gala 30 ago" }, desc: { fr: "À imprimer et distribuer", en: "Print & distribute", it: "Da stampare e distribuire" }, icon: "📢", color: "text-orange-600" },
                { file: "SAFUAYE_Academy_Gala_Planning_2026.pdf", title: { fr: "Planning du Gala", en: "Gala schedule", it: "Programma Gala" }, desc: { fr: "Déroulé heure par heure + budget", en: "Hour-by-hour + budget", it: "Ora per ora + budget" }, icon: "📅", color: "text-blue-600" },
                { file: "SAFUAYE_Academy_Training_Program_U7_U13.pdf", title: { fr: "Programme d'entraînement U7-U13", en: "Training program U7-U13", it: "Programma allenamento U7-U13" }, desc: { fr: "Séances + progression trimestrielle", en: "Sessions + termly progression", it: "Sedute + progressione trimestrale" }, icon: "⚽", color: "text-emerald-600" },
                { file: "SAFUAYE_Academy_Equipment_Kit_List.pdf", title: { fr: "Liste matériel (kit 500 €)", en: "Equipment kit (€500)", it: "Kit attrezzature (€500)" }, desc: { fr: "Détail achats + fournisseurs", en: "Purchase list + suppliers", it: "Elenco acquisti + fornitori" }, icon: "🎒", color: "text-orange-700" },
                { file: "SAFUAYE_Academy_School_Partnership_Letter.pdf", title: { fr: "Lettre partenariat écoles", en: "Schools partnership letter", it: "Lettera partenariato scuole" }, desc: { fr: "À signer par le Bureau national", en: "To be signed by the Board", it: "Da firmare dal Consiglio" }, icon: "🏫", color: "text-slate-700" },
                { file: "SAFUAYE_Academy_Coach_Assignment_Sheet.pdf", title: { fr: "Fiche affectation coachs", en: "Coach assignment sheet", it: "Foglio assegnazione coach" }, desc: { fr: "2 secteurs · 3 coachs par secteur", en: "2 sectors · 3 coaches each", it: "2 settori · 3 coach ciascuno" }, icon: "👥", color: "text-purple-700" },
                { file: "SAFUAYE_Academy_Child_Registration_Form.pdf", title: { fr: "Fiche inscription enfant (papier)", en: "Child registration form (paper)", it: "Modulo iscrizione (cartaceo)" }, desc: { fr: "Version papier pour parents", en: "Paper version for parents", it: "Versione cartacea per genitori" }, icon: "📝", color: "text-emerald-700" },
                { file: "SAFUAYE_Academy_Sponsorship_Pack.pdf", title: { fr: "Pack sponsoring", en: "Sponsorship pack", it: "Pacchetto sponsorizzazione" }, desc: { fr: "Pour partenaires / entreprises", en: "For sponsors / businesses", it: "Per sponsor / aziende" }, icon: "🤝", color: "text-yellow-700" },
                { file: "SAFUAYE_Communique_Presse_Rentree_2026.pdf", title: { fr: "Communiqué de presse", en: "Press release", it: "Comunicato stampa" }, desc: { fr: "Prêt à envoyer aux médias", en: "Ready for media outreach", it: "Pronto per l'invio ai media" }, icon: "📰", color: "text-indigo-700" },
              ].map((doc, i) => (
                <a
                  key={i}
                  href={`/docs/${doc.file}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all"
                  data-testid={`academy-doc-${i}`}
                >
                  <div className={`flex-shrink-0 w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform ${doc.color}`}>{doc.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{doc.title[lang] || doc.title.fr}</p>
                    <p className="text-xs text-gray-500 truncate">{doc.desc[lang] || doc.desc.fr}</p>
                  </div>
                  <span className="text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-transform">↓</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Academy Pre-registration Modal */}
      {acadModalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setAcadModalOpen(false)} data-testid="academy-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 md:p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-orange-50 to-red-50 rounded-t-2xl">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-orange-700 mb-1">
                  {lang === 'fr' ? "Pré-inscription en ligne" : lang === 'en' ? "Online pre-registration" : "Pre-iscrizione online"}
                </p>
                <h3 className="font-heading text-xl font-bold text-gray-900">
                  {lang === 'fr' ? "Académie SAFUAYE — Rentrée sept. 2026" : lang === 'en' ? "SAFUAYE Academy — Sept 2026" : "Accademia SAFUAYE — Sett. 2026"}
                </h3>
              </div>
              <button onClick={() => setAcadModalOpen(false)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" aria-label="Fermer" data-testid="academy-modal-close">×</button>
            </div>
            {acadSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500 text-white flex items-center justify-center text-3xl">✓</div>
                <p className="font-heading text-xl font-bold text-gray-900 mb-2">
                  {lang === 'fr' ? "Pré-inscription enregistrée !" : lang === 'en' ? "Pre-registration recorded!" : "Pre-iscrizione registrata!"}
                </p>
                <p className="text-gray-600 text-sm">
                  {lang === 'fr' ? "Nous vous contacterons pour finaliser l'inscription lors du Gala du 30 août ou avant la rentrée."
                    : lang === 'en' ? "We will contact you to finalise the registration at the Gala on 30 Aug or before the opening."
                    : "La contatteremo per finalizzare l'iscrizione al Gala del 30 agosto o prima dell'apertura."}
                </p>
              </div>
            ) : (
              <form onSubmit={submitAcadForm} className="p-5 md:p-6 space-y-3" data-testid="academy-form">
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      {lang === 'fr' ? "Prénom & nom de l'enfant *" : "Child full name *"}
                    </label>
                    <input type="text" required minLength={2} value={acadForm.child_name} onChange={(e) => setAcadForm({ ...acadForm, child_name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" data-testid="acad-child-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{lang === 'fr' ? "Âge *" : "Age *"}</label>
                    <input type="number" min="5" max="14" required value={acadForm.child_age} onChange={(e) => setAcadForm({ ...acadForm, child_age: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" data-testid="acad-child-age" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{lang === 'fr' ? "Catégorie souhaitée *" : "Preferred age group *"}</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[{v:"U7",r:"6-7"},{v:"U9",r:"8-9"},{v:"U11",r:"10-11"},{v:"U13",r:"12-13"}].map((c) => (
                      <button key={c.v} type="button" onClick={() => setAcadForm({ ...acadForm, category: c.v })} className={`text-xs font-semibold py-2 px-2 rounded-lg border-2 transition-all ${acadForm.category === c.v ? 'border-orange-500 bg-orange-50 text-orange-800 ring-2 ring-orange-200' : 'border-gray-200 text-gray-600 hover:border-orange-300'}`} data-testid={`acad-cat-${c.v}`}>
                        <span className="block text-sm">{c.v}</span>
                        <span className="text-[10px] text-gray-500">{c.r} ans</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{lang === 'fr' ? "École actuelle" : "Current school"}</label>
                    <input type="text" value={acadForm.school} onChange={(e) => setAcadForm({ ...acadForm, school: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" data-testid="acad-school" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">{lang === 'fr' ? "Village / quartier" : "Village / area"}</label>
                    <input type="text" value={acadForm.village} onChange={(e) => setAcadForm({ ...acadForm, village: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" data-testid="acad-village" />
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">{lang === 'fr' ? "Coordonnées du parent" : "Parent contact"}</p>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">{lang === 'fr' ? "Prénom & nom *" : "Full name *"}</label>
                      <input type="text" required minLength={2} value={acadForm.parent_name} onChange={(e) => setAcadForm({ ...acadForm, parent_name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" data-testid="acad-parent-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">{lang === 'fr' ? "Téléphone WhatsApp *" : "WhatsApp *"}</label>
                      <input type="tel" required value={acadForm.parent_contact} onChange={(e) => setAcadForm({ ...acadForm, parent_contact: e.target.value })} placeholder="+228 90 00 00 00" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" data-testid="acad-parent-contact" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email {lang === 'fr' ? "(optionnel)" : "(optional)"}</label>
                    <input type="email" value={acadForm.parent_email} onChange={(e) => setAcadForm({ ...acadForm, parent_email: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" data-testid="acad-parent-email" />
                  </div>
                </div>
                <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" required checked={acadForm.consent_data} onChange={(e) => setAcadForm({ ...acadForm, consent_data: e.target.checked })} className="mt-0.5" data-testid="acad-consent-data" />
                  <span>{lang === 'fr' ? "J'accepte que les données de mon enfant soient utilisées exclusivement pour l'inscription à l'Académie SAFUAYE (RGPD). *" : "I agree that my child's data is used solely for the SAFUAYE Academy registration (GDPR). *"}</span>
                </label>
                <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={acadForm.consent_photo} onChange={(e) => setAcadForm({ ...acadForm, consent_photo: e.target.checked })} className="mt-0.5" data-testid="acad-consent-photo" />
                  <span>{lang === 'fr' ? "J'accepte que des photos/vidéos de mon enfant puissent être utilisées sur safuaye.org et les réseaux sociaux (optionnel)." : "I agree that photos/videos of my child may be used on safuaye.org and social media (optional)."}</span>
                </label>
                <button type="submit" disabled={acadSubmitting} className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-3 rounded-lg shadow-md hover:shadow-xl transition-all disabled:opacity-60" data-testid="acad-submit-btn">
                  {acadSubmitting ? (lang === 'fr' ? "Envoi..." : "Sending...") : (lang === 'fr' ? "Pré-inscrire mon enfant" : lang === 'en' ? "Pre-register my child" : "Pre-iscrivi mio figlio")}
                </button>
                <p className="text-[10px] text-gray-500 text-center">
                  {lang === 'fr' ? "Une confirmation d'inscription définitive sera demandée lors du Gala du 30 août." : "Final registration will be confirmed at the Gala on 30 Aug."}
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Presse & Actualités — Distribution scolaire EPP Agbonou-Koéroma */}
      <section id="presse" data-testid="presse-section" className="py-20 md:py-24 bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 text-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* 🆕 Dernière parution : Togoreport 12 septembre 2026 */}
          <a href="https://togoreport.net/ogou1-lassociation-safuaye-offre-des-kits-scolaires-aux-enfants-et-des-enveloppes-financieres-aux-anciens-joueurs-de-ifodje-fc/"
             target="_blank" rel="noopener noreferrer"
             className="block mb-10 group"
             data-testid="press-latest-togoreport-2026-09-12">
            <div className="bg-gradient-to-r from-amber-500/20 via-emerald-500/20 to-amber-500/20 border-2 border-amber-400/50 rounded-2xl p-5 md:p-6 backdrop-blur-sm hover:border-amber-400 transition-all">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-2 shrink-0">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500 text-slate-900 rounded-full text-xs font-black uppercase tracking-wider">
                    🆕 {lang === 'en' ? "Latest press" : lang === 'it' ? "Ultima stampa" : "Dernière parution"}
                  </span>
                  <span className="text-xs text-white/60">12/09/2026 · Togoreport</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg md:text-xl leading-snug group-hover:text-amber-300 transition-colors">
                    {lang === 'en' ? "SAFUAYE distributes school kits to children and financial envelopes to former IFODJE FC players"
                      : lang === 'it' ? "SAFUAYE distribuisce kit scolastici ai bambini e buste finanziarie agli ex giocatori dell'IFODJE FC"
                      : "L'Association SAFUAYE offre des kits scolaires aux enfants et des enveloppes financières aux anciens joueurs de IFODJE FC"}
                  </h3>
                  <p className="text-white/70 text-sm mt-1 italic">
                    « À travers cette action, nous voulons perpétuer son héritage… Une manière de leur dire qu'ils peuvent compter sur nous. » — M. AZIAKA Koffi, Président National
                  </p>
                </div>
                <div className="shrink-0 text-amber-300 group-hover:translate-x-1 transition-transform">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>
            </div>
          </a>

          <div className="flex flex-col items-center text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/20 border border-emerald-400/40 rounded-full text-xs font-bold uppercase tracking-widest text-emerald-300 mb-4">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
              {lang === 'fr' ? "Vu dans la presse · 10 juillet 2026" : lang === 'en' ? "In the press · 10 July 2026" : "Sulla stampa · 10 luglio 2026"}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl">
              {lang === 'fr' ? "SAFUAYE récompense l'excellence scolaire à l'EPP Agbonou-Koéroma"
                : lang === 'en' ? "SAFUAYE rewards academic excellence at EPP Agbonou-Koéroma"
                : "SAFUAYE premia l'eccellenza scolastica alla EPP Agbonou-Koéroma"}
            </h2>
            <p className="text-white/70 max-w-3xl text-base md:text-lg">
              {lang === 'fr' ? "Vendredi 10 juillet 2026 — Commune Ogou1, Atakpamé. Une cérémonie empreinte de générosité et de reconnaissance, où l'Association SAFUAYE a remis des kits scolaires complets aux élèves les plus méritants de l'École Primaire Publique Agbonou-Koéroma Groupe B, du CP au CM."
                : lang === 'en' ? "Friday 10 July 2026 — Commune Ogou1, Atakpamé. A ceremony of generosity and recognition where SAFUAYE distributed complete school kits to the most deserving pupils of Agbonou-Koéroma Public Primary School Group B, from Year 1 to Year 5."
                : "Venerdì 10 luglio 2026 — Comune di Ogou1, Atakpamé. Una cerimonia di generosità e riconoscimento, in cui SAFUAYE ha consegnato kit scolastici completi agli alunni più meritevoli della Scuola Primaria Pubblica Agbonou-Koéroma Gruppo B, dalla 1ª alla 5ª classe."}
            </p>
          </div>

          {/* Hero photo + quote */}
          <div className="grid md:grid-cols-5 gap-6 mb-8">
            <div className="md:col-span-3 relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]" data-testid="presse-hero-photo">
              <img src="/images/distribution-scolaire/groupe-eleves-recompenses.jpg" alt={lang==='fr'?"Groupe d'élèves récompensés par SAFUAYE":"Rewarded students with SAFUAYE"} className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6">
                <p className="text-white text-sm md:text-base font-medium">
                  {lang === 'fr' ? "Les élèves méritants de l'EPP Agbonou-Koéroma Groupe B, entourés du représentant SAFUAYE M. ANAGONOU Agbossou et du directeur M. MIDAHIN Anoumou."
                    : lang === 'en' ? "Deserving pupils of EPP Agbonou-Koéroma Group B, surrounded by SAFUAYE representative Mr ANAGONOU Agbossou and headmaster Mr MIDAHIN Anoumou."
                    : "Alunni meritevoli della EPP Agbonou-Koéroma Gruppo B, insieme al rappresentante SAFUAYE Sig. ANAGONOU Agbossou e al direttore Sig. MIDAHIN Anoumou."}
                </p>
              </div>
            </div>
            <div className="md:col-span-2 flex flex-col justify-between gap-4">
              <blockquote className="bg-white/5 border border-white/10 rounded-2xl p-5 md:p-6 backdrop-blur-sm">
                <div className="text-4xl leading-none text-emerald-400 mb-2 font-serif">"</div>
                <p className="text-white/90 italic text-sm md:text-base leading-relaxed">
                  {lang === 'fr' ? "Nous espérons vous revoir l'année prochaine pour de nouveaux dons, à condition que vous restiez assidus et déterminés."
                    : lang === 'en' ? "We hope to see you again next year for more gifts — as long as you remain diligent and determined."
                    : "Speriamo di rivedervi l'anno prossimo per nuovi doni, a condizione che restiate diligenti e determinati."}
                </p>
                <p className="text-emerald-300 text-xs font-semibold mt-3 uppercase tracking-wider">
                  — M. ANAGONOU Agbossou, {lang === 'fr' ? "représentant SAFUAYE" : lang === 'en' ? "SAFUAYE representative" : "rappresentante SAFUAYE"}
                </p>
              </blockquote>

              {/* Kits distribués */}
              <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-2xl p-5">
                <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-3">
                  {lang === 'fr' ? "Kits scolaires distribués" : lang === 'en' ? "School kits distributed" : "Kit scolastici distribuiti"}
                </p>
                <ul className="text-white/85 text-sm space-y-1.5">
                  <li className="flex items-start gap-2"><span className="text-emerald-400">✓</span> {lang === 'fr' ? "Cahiers 100 & 200 pages" : lang === 'en' ? "Notebooks 100 & 200 pages" : "Quaderni 100 & 200 pagine"}</li>
                  <li className="flex items-start gap-2"><span className="text-emerald-400">✓</span> {lang === 'fr' ? "Ardoises" : lang === 'en' ? "Slates" : "Lavagnette"}</li>
                  <li className="flex items-start gap-2"><span className="text-emerald-400">✓</span> {lang === 'fr' ? "Stylos & crayons" : lang === 'en' ? "Pens & pencils" : "Penne & matite"}</li>
                  <li className="flex items-start gap-2"><span className="text-emerald-400">✓</span> {lang === 'fr' ? "T-shirts pour les élèves" : lang === 'en' ? "T-shirts for pupils" : "T-shirt per gli alunni"}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Photo mosaic — 4 photos */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8" data-testid="presse-photo-gallery">
            <a href="/images/distribution-scolaire/kits-scolaires-tables-1.jpg" target="_blank" rel="noreferrer" className="relative rounded-xl overflow-hidden shadow-lg group aspect-[3/4]">
              <img src="/images/distribution-scolaire/kits-scolaires-tables-1.jpg" alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white text-xs font-semibold">
                  {lang === 'fr' ? "Kits préparés" : lang === 'en' ? "Kits ready" : "Kit pronti"}
                </p>
              </div>
            </a>
            <a href="/images/distribution-scolaire/enfant-recompense.jpg" target="_blank" rel="noreferrer" className="relative rounded-xl overflow-hidden shadow-lg group aspect-[3/4]">
              <img src="/images/distribution-scolaire/enfant-recompense.jpg" alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white text-xs font-semibold">
                  {lang === 'fr' ? "Un jeune méritant" : lang === 'en' ? "A deserving pupil" : "Un giovane meritevole"}
                </p>
              </div>
            </a>
            <a href="/images/distribution-scolaire/discours-ceremonie.jpg" target="_blank" rel="noreferrer" className="relative rounded-xl overflow-hidden shadow-lg group aspect-[3/4]">
              <img src="/images/distribution-scolaire/discours-ceremonie.jpg" alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white text-xs font-semibold">
                  {lang === 'fr' ? "Cérémonie & discours" : lang === 'en' ? "Ceremony & speeches" : "Cerimonia & discorsi"}
                </p>
              </div>
            </a>
            <a href="/images/distribution-scolaire/kits-scolaires-tables-2.jpg" target="_blank" rel="noreferrer" className="relative rounded-xl overflow-hidden shadow-lg group aspect-[3/4]">
              <img src="/images/distribution-scolaire/kits-scolaires-tables-2.jpg" alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white text-xs font-semibold">
                  {lang === 'fr' ? "Vue d'ensemble" : lang === 'en' ? "Overview" : "Panoramica"}
                </p>
              </div>
            </a>
          </div>

          {/* Parrain & lien article */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 md:p-6 backdrop-blur-sm">
              {/* Offert par */}
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-300 mb-2">
                {lang === 'fr' ? "Don offert par" : lang === 'en' ? "Donation offered by" : "Donazione offerta da"}
              </p>
              <p className="text-white font-semibold text-lg">APEDO Kokou Ognakotan <span className="text-white/60 font-normal text-base">— {lang==='fr'?"dit «Angelo»":"known as \"Angelo\""}</span></p>
              <p className="text-white/70 text-xs italic mt-1 leading-relaxed">
                {lang === 'fr' ? "Président International de SAFUAYE, fils biologique de feu APEDO Simon-Joseph Afantchédé, dit «Safuayé» (Président d'Ifodjè FC 1984-1990). Ce don personnel prolonge concrètement l'héritage de son père."
                  : lang === 'en' ? "International President of SAFUAYE, biological son of the late APEDO Simon-Joseph Afantchédé, known as \"Safuayé\" (President of Ifodjè FC 1984-1990). This personal gift tangibly extends his father's legacy."
                  : "Presidente Internazionale di SAFUAYE, figlio biologico del compianto APEDO Simon-Joseph Afantchédé, detto \"Safuayé\" (Presidente di Ifodjè FC 1984-1990). Questo dono personale prosegue concretamente l'eredità del padre."}
              </p>

              {/* Effectué par */}
              <div className="border-t border-white/10 pt-3 mt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-300 mb-1">
                  {lang === 'fr' ? "Effectué sur le terrain par" : lang === 'en' ? "Delivered on the ground by" : "Consegnato sul campo da"}
                </p>
                <p className="text-white font-semibold text-base">
                  {lang === 'fr' ? "Le Collège des Membres d'Honneur" : lang === 'en' ? "The College of Honorary Members" : "Il Collegio dei Membri d'Onore"}
                </p>
                <p className="text-white/70 text-xs italic mt-1 leading-relaxed">
                  {lang === 'fr' ? "Les cinq Sages d'Atakpamé, gardiens de la mémoire des anciens, ont organisé et remis les kits directement aux élèves de l'EPP Agbonou-Koéroma, aux côtés du représentant SAFUAYE M. ANAGONOU Agbossou."
                    : lang === 'en' ? "The five Sages of Atakpamé, guardians of the elders' memory, organised and delivered the kits directly to the pupils of EPP Agbonou-Koéroma, alongside SAFUAYE representative Mr ANAGONOU Agbossou."
                    : "I cinque Saggi di Atakpamé, custodi della memoria degli anziani, hanno organizzato e consegnato i kit direttamente agli alunni dell'EPP Agbonou-Koéroma, insieme al rappresentante SAFUAYE Sig. ANAGONOU Agbossou."}
                </p>
              </div>
            </div>
            <a href="https://togoreport.net/atakpame-lassociation-safuaye-recompense-lexcellence-scolaire-a-lepp-agbonou-koeroma-groupe-b/" target="_blank" rel="noreferrer noopener" className="group bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 rounded-2xl p-5 md:p-6 shadow-lg hover:shadow-2xl transition-all flex items-center justify-between gap-4" data-testid="presse-article-link">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-white/90 mb-1">
                  {lang === 'fr' ? "Lire l'article complet" : lang === 'en' ? "Read the full article" : "Leggi l'articolo completo"}
                </p>
                <p className="text-white font-semibold text-lg leading-tight">togoreport.net</p>
                <p className="text-white/80 text-xs mt-1">
                  {lang === 'fr' ? "Rédigé par Landrykoss" : lang === 'en' ? "Written by Landrykoss" : "Scritto da Landrykoss"}
                </p>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white flex-shrink-0 group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7"></path><polyline points="7 7 17 7 17 17"></polyline></svg>
            </a>
          </div>
        </div>
      </section>

      {/* HQ Renovation Gallery Section */}
      <section id="hq" data-testid="hq-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-orange-700 mb-3">
              {lang === 'fr' ? 'Atchrimi-Komé • Atakpamé • Togo' : lang === 'en' ? 'Atchrimi-Komé • Atakpamé • Togo' : 'Atchrimi-Komé • Atakpamé • Togo'}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              {lang === 'fr' ? 'Rénovation de notre siège' : lang === 'en' ? 'Renovation of our headquarters' : 'Ristrutturazione della nostra sede'}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {lang === 'fr' ? "Un bâtiment familial dans le quartier d'Atchrimi-Komé à Atakpamé, remis à neuf pas à pas pour devenir le siège opérationnel de SAFUAYE au Togo. Chaque étape est documentée pour garantir la transparence."
                : lang === 'en' ? "A family building in the Atchrimi-Komé neighbourhood of Atakpamé, refurbished step by step to become SAFUAYE's operational headquarters in Togo. Every stage is documented for full transparency."
                : "Un edificio di famiglia nel quartiere di Atchrimi-Komé ad Atakpamé, ristrutturato passo dopo passo per diventare la sede operativa di SAFUAYE in Togo. Ogni fase è documentata per garantire trasparenza."}
            </p>
          </div>

          {/* HERO : Enseigne officielle installée */}
          <div className="mb-12" data-testid="siege-enseigne-hero">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-black max-w-5xl mx-auto">
              <img src="/images/siege-ag/enseigne-officielle.jpg" alt={lang==='fr'?"Enseigne officielle SAFUAYE au siège d'Atchrimi-Komé":"Official SAFUAYE sign at Atchrimi-Komé headquarters"} className="w-full h-auto object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-5 md:p-8">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                  <div>
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider shadow-md mb-3">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                      {lang === 'fr' ? "Enseigne officielle posée · Juin 2026" : lang === 'en' ? "Official sign installed · June 2026" : "Insegna ufficiale installata · Giugno 2026"}
                    </span>
                    <h3 className="font-heading text-xl md:text-3xl font-bold text-white leading-tight">
                      {lang === 'fr' ? "SAFUAYE est officiellement chez elle" : lang === 'en' ? "SAFUAYE is officially at home" : "SAFUAYE è ufficialmente a casa"}
                    </h3>
                    <p className="text-white/80 text-sm md:text-base mt-1">
                      {lang === 'fr' ? "Lieu : Atchrimi-Komé, Atakpamé — devise « Héritage · Solidarité · Espoir »"
                        : lang === 'en' ? "Location: Atchrimi-Komé, Atakpamé — motto \"Heritage · Solidarity · Hope\""
                        : "Luogo: Atchrimi-Komé, Atakpamé — motto \"Eredità · Solidarietà · Speranza\""}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Milestone : Restauration partielle achevée pour l'AG du 27 juin 2026 */}
          <div className="mb-16" data-testid="siege-ag-milestone">
            <div className="bg-gradient-to-br from-emerald-50 via-white to-orange-50 border-2 border-emerald-200 rounded-3xl p-6 md:p-10 shadow-lg">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">
                      {lang === 'fr' ? "Jalon franchi · Juin 2026" : lang === 'en' ? "Milestone reached · June 2026" : "Traguardo raggiunto · Giugno 2026"}
                    </p>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">
                      {lang === 'fr' ? "Restauration partielle achevée pour l'AG"
                        : lang === 'en' ? "Partial restoration completed for the AGM"
                        : "Ristrutturazione parziale completata per l'AG"}
                    </h3>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 self-start md:self-center px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold shadow-sm">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  {lang === 'fr' ? "27 juin 2026" : lang === 'en' ? "27 June 2026" : "27 giugno 2026"}
                </span>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6 max-w-4xl">
                {lang === 'fr' ? "Grâce à la mobilisation de la diaspora et des membres locaux, le siège de SAFUAYE-TOGO a été suffisamment restauré pour accueillir dignement l'Assemblée Générale Constitutive : façade sur rue rénovée (enduits + peinture + ferronnerie), maçonnerie intérieure reprise par des artisans d'Atakpamé, portes d'origine préservées, murs extérieurs blanchis à la chaux, cour dégagée et nivelée, entrée sécurisée, et mobilier d'accueil (25 chaises + tables) installé sous chapiteau. Une nouvelle étape avant la rénovation complète du bâtiment."
                  : lang === 'en' ? "Thanks to the mobilisation of the diaspora and local members, SAFUAYE-TOGO's headquarters was sufficiently restored to worthily host the Constitutive General Assembly: street facade renovated (plaster + paint + ironwork), interior masonry redone by Atakpamé craftsmen, original doors preserved, outer walls freshly whitewashed, courtyard cleared and levelled, entrance secured, and seating for 25 guests set up under a marquee. A new step before the full renovation of the building."
                  : "Grazie alla mobilitazione della diaspora e dei membri locali, la sede di SAFUAYE-TOGO è stata sufficientemente ristrutturata per ospitare dignitosamente l'Assemblea Generale Costitutiva: facciata su strada rinnovata (intonaco + pittura + ferramenta), muratura interna rifatta da artigiani di Atakpamé, porte originali conservate, mura esterne imbiancate, cortile pulito e livellato, ingresso messo in sicurezza, e sedute per 25 ospiti installate sotto un gazebo. Un nuovo passo prima della ristrutturazione completa dell'edificio."}
              </p>

              {/* Sous-galerie 1 : Travaux réalisés */}
              <div className="mb-8">
                <h4 className="text-sm font-bold uppercase tracking-wider text-emerald-700 mb-4 flex items-center gap-2">
                  <span className="inline-block w-6 h-0.5 bg-emerald-600"></span>
                  {lang === 'fr' ? "Travaux réalisés" : lang === 'en' ? "Works performed" : "Lavori eseguiti"}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="siege-travaux-gallery">
                  <a href="/images/siege-ag/facade-fenetres-vertes.jpg" target="_blank" rel="noreferrer" className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-[4/3]">
                    <img src="/images/siege-ag/facade-fenetres-vertes.jpg" alt={lang==='fr'?"Façade rénovée sur rue":"Renovated street facade"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
                      <p className="text-white text-sm font-semibold">
                        {lang === 'fr' ? "Façade sur rue rénovée" : lang === 'en' ? "Street facade renovated" : "Facciata su strada rinnovata"}
                      </p>
                    </div>
                  </a>
                  <a href="/images/siege-ag/travaux-macon.jpg" target="_blank" rel="noreferrer" className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-[4/3]">
                    <img src="/images/siege-ag/travaux-macon.jpg" alt={lang==='fr'?"Ouvrier maçon local":"Local mason at work"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
                      <p className="text-white text-sm font-semibold">
                        {lang === 'fr' ? "Maçonnerie intérieure" : lang === 'en' ? "Interior masonry" : "Muratura interna"}
                      </p>
                    </div>
                  </a>
                  <a href="/images/siege-ag/travaux-porte-verte.jpg" target="_blank" rel="noreferrer" className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-[4/3]">
                    <img src="/images/siege-ag/travaux-porte-verte.jpg" alt={lang==='fr'?"Réhabilitation d'une porte intérieure":"Interior door refurbishment"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
                      <p className="text-white text-sm font-semibold">
                        {lang === 'fr' ? "Portes préservées" : lang === 'en' ? "Original doors preserved" : "Porte originali conservate"}
                      </p>
                    </div>
                  </a>
                  <a href="/images/siege-ag/enseigne-officielle.jpg" target="_blank" rel="noreferrer" className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-[4/3] ring-2 ring-emerald-400">
                    <img src="/images/siege-ag/enseigne-officielle.jpg" alt={lang==='fr'?"Enseigne officielle posée":"Official sign installed"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent p-3">
                      <p className="text-white text-sm font-semibold flex items-center gap-1">
                        <span className="text-emerald-300">★</span>
                        {lang === 'fr' ? "Enseigne officielle" : lang === 'en' ? "Official signage" : "Insegna ufficiale"}
                      </p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Sous-galerie 2 : Le siège prêt pour l'AG */}
              <div className="mb-6">
                <h4 className="text-sm font-bold uppercase tracking-wider text-orange-700 mb-4 flex items-center gap-2">
                  <span className="inline-block w-6 h-0.5 bg-orange-600"></span>
                  {lang === 'fr' ? "Le siège prêt pour l'AG — 27 juin 2026" : lang === 'en' ? "Headquarters ready for the AGM — 27 June 2026" : "Sede pronta per l'AG — 27 giugno 2026"}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="siege-ag-gallery">
                  <a href="/images/siege-ag/siege-ag-2.jpg" target="_blank" rel="noreferrer" className="col-span-2 row-span-2 relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-square md:aspect-auto">
                    <img src="/images/siege-ag/siege-ag-2.jpg" alt={lang==='fr'?"Cour prête pour l'AG":"Courtyard ready for AGM"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-4">
                      <p className="text-white text-sm font-semibold">
                        {lang === 'fr' ? "La cour prête à accueillir les 25 membres" : lang === 'en' ? "Courtyard ready for the 25 members" : "Il cortile pronto per accogliere i 25 membri"}
                      </p>
                    </div>
                  </a>
                  <a href="/images/siege-ag/memorial-safuaye-1.jpg" target="_blank" rel="noreferrer" className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-square">
                    <img src="/images/siege-ag/memorial-safuaye-1.jpg" alt={lang==='fr'?"Hommage à Safuayé installé dans la cour":"Tribute to Safuayé displayed in the courtyard"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <p className="text-white text-xs font-medium">
                        {lang === 'fr' ? "Hommage à Safuayé" : lang === 'en' ? "Tribute to Safuayé" : "Omaggio a Safuayé"}
                      </p>
                    </div>
                  </a>
                  <a href="/images/siege-ag/siege-ag-1.jpg" target="_blank" rel="noreferrer" className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-square">
                    <img src="/images/siege-ag/siege-ag-1.jpg" alt={lang==='fr'?"Murs blanchis":"Whitewashed walls"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs font-medium">
                        {lang === 'fr' ? "Murs blanchis" : lang === 'en' ? "Whitewashed walls" : "Muri imbiancati"}
                      </p>
                    </div>
                  </a>
                  <a href="/images/siege-ag/siege-ag-3.jpg" target="_blank" rel="noreferrer" className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-square">
                    <img src="/images/siege-ag/siege-ag-3.jpg" alt={lang==='fr'?"Chapiteau installé":"Marquee set up"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs font-medium">
                        {lang === 'fr' ? "Chapiteau installé" : lang === 'en' ? "Marquee set up" : "Gazebo montato"}
                      </p>
                    </div>
                  </a>
                  <a href="/images/siege-ag/memorial-safuaye-2.jpg" target="_blank" rel="noreferrer" className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-square">
                    <img src="/images/siege-ag/memorial-safuaye-2.jpg" alt={lang==='fr'?"Vue d'ensemble avec l'hommage":"Overview with the tribute"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <p className="text-white text-xs font-medium">
                        {lang === 'fr' ? "L'esprit de Safuayé veille" : lang === 'en' ? "Safuayé's spirit watches over" : "Lo spirito di Safuayé veglia"}
                      </p>
                    </div>
                  </a>
                  <a href="/images/siege-ag/siege-ag-4.jpg" target="_blank" rel="noreferrer" className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-square">
                    <img src="/images/siege-ag/siege-ag-4.jpg" alt={lang==='fr'?"Table des modérateurs":"Moderators' table"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs font-medium">
                        {lang === 'fr' ? "Table des modérateurs" : lang === 'en' ? "Moderators' table" : "Tavolo dei moderatori"}
                      </p>
                    </div>
                  </a>
                  <a href="/images/siege-ag/siege-ag-5.jpg" target="_blank" rel="noreferrer" className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-square">
                    <img src="/images/siege-ag/siege-ag-5.jpg" alt={lang==='fr'?"Entrée et citronnier":"Entrance and lemon tree"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs font-medium">
                        {lang === 'fr' ? "L'entrée & le citronnier" : lang === 'en' ? "Entrance & lemon tree" : "L'ingresso e il limone"}
                      </p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Restant à faire */}
              <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
                <span className="font-semibold text-gray-600 uppercase tracking-wider mr-2">
                  {lang === 'fr' ? "Étapes à venir :" : lang === 'en' ? "Next steps:" : "Prossime tappe:"}
                </span>
                {[
                  {fr:"Toiture", en:"Roof", it:"Tetto"},
                  {fr:"Sanitaires", en:"Sanitary facilities", it:"Servizi igienici"},
                  {fr:"Sol de la cour", en:"Courtyard flooring", it:"Pavimentazione cortile"},
                  {fr:"Salle communautaire", en:"Community room", it:"Sala comune"},
                  {fr:"Électricité complète", en:"Full electrics", it:"Impianto elettrico completo"},
                ].map((step, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-orange-200 text-orange-800">
                    <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                    {step[lang] || step.fr}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Timeline verticale : Renaissance du siège Mars → Juin 2026 */}
          <div className="mb-16" data-testid="siege-timeline">
            <div className="text-center mb-10">
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-2">
                {lang === 'fr' ? "Chronologie" : lang === 'en' ? "Timeline" : "Cronologia"}
              </p>
              <h3 className="font-heading text-2xl md:text-4xl font-bold text-gray-900 mb-3">
                {lang === 'fr' ? "La renaissance du siège, mois par mois"
                  : lang === 'en' ? "The rebirth of the headquarters, month by month"
                  : "La rinascita della sede, mese dopo mese"}
              </h3>
              <p className="text-sm text-gray-600 max-w-2xl mx-auto">
                {lang === 'fr' ? "De l'état d'abandon en début d'année à la première Assemblée Générale tenue dans la cour restaurée."
                  : lang === 'en' ? "From abandonment at the start of the year to the first General Assembly held in the restored courtyard."
                  : "Dall'abbandono a inizio anno alla prima Assemblea Generale nel cortile ristrutturato."}
              </p>
            </div>

            <div className="relative max-w-5xl mx-auto">
              {/* Central vertical line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-400 via-emerald-500 to-emerald-600 md:-translate-x-1/2" aria-hidden="true"></div>

              {(() => {
                const TIMELINE = [
                  {
                    month: {fr: "Mars 2026", en: "March 2026", it: "Marzo 2026"},
                    title: {fr: "Diagnostic & lancement du chantier", en: "Assessment & works launch", it: "Diagnosi & avvio dei lavori"},
                    desc: {fr: "Visite d'état des lieux avec des artisans locaux d'Atakpamé. Murs intérieurs en peinture bleue écaillée, portes en bois d'origine à réhabiliter, sol brut, façade abîmée. Budget arrêté avec le Conseil d'Administration UK.", en: "On-site survey with local craftsmen from Atakpamé. Interior walls with flaking blue paint, original wooden doors to be refurbished, raw floor, damaged facade. Budget agreed with the UK Board of Trustees.", it: "Sopralluogo con artigiani locali di Atakpamé. Muri interni con vernice blu scrostata, porte in legno originali da ripristinare, pavimento grezzo, facciata danneggiata. Budget concordato con il Consiglio UK."},
                    color: "bg-slate-500",
                    icon: "📋",
                  },
                  {
                    month: {fr: "Avril 2026", en: "April 2026", it: "Aprile 2026"},
                    title: {fr: "Maçonnerie intérieure", en: "Interior masonry", it: "Muratura interna"},
                    desc: {fr: "Reprise des murs par des artisans d'Atakpamé : décapage de l'ancienne peinture bleue, rebouchage des trous, reconstruction d'un pan de mur, préparation des enduits pour la peinture blanche.", en: "Wall works by Atakpamé craftsmen: stripping old blue paint, filling holes, rebuilding a wall section, preparing plaster for white paint.", it: "Interventi sui muri da parte di artigiani di Atakpamé: rimozione della vecchia vernice blu, riempimento dei fori, ricostruzione di una porzione di muro, preparazione dell'intonaco."},
                    color: "bg-amber-500",
                    icon: "🧱",
                    photos: ["travaux-macon.jpg", "travaux-porte-verte.jpg"],
                  },
                  {
                    month: {fr: "Mai 2026", en: "May 2026", it: "Maggio 2026"},
                    title: {fr: "Façade & menuiseries", en: "Facade & joinery", it: "Facciata & falegnameria"},
                    desc: {fr: "Rénovation complète de la façade sur rue : enduits, peinture blanche, curbstones peints en gris, ferronneries des fenêtres repeintes en vert. Portes en bois d'origine soigneusement conservées et vernies.", en: "Complete renovation of the street facade: plaster, white paint, curbstones painted grey, window ironwork repainted green. Original wooden doors carefully preserved and varnished.", it: "Rinnovamento completo della facciata: intonaco, pittura bianca, cordoli grigi, ferramenta delle finestre verniciata di verde. Porte in legno originali conservate e verniciate."},
                    color: "bg-blue-500",
                    icon: "🎨",
                    photos: ["facade-fenetres-vertes.jpg"],
                  },
                  {
                    month: {fr: "Juin 2026", en: "June 2026", it: "Giugno 2026"},
                    title: {fr: "Enseigne officielle SAFUAYE posée", en: "Official SAFUAYE sign installed", it: "Insegna ufficiale SAFUAYE installata"},
                    desc: {fr: "Pose de l'enseigne officielle SAFUAYE à l'entrée du siège, quartier Atchrimi-Komé. Le bâtiment devient publiquement identifiable comme le siège de l'association. La devise « Héritage · Solidarité · Espoir » est désormais affichée sur rue.", en: "Installation of the official SAFUAYE sign at the entrance of the headquarters, Atchrimi-Komé neighbourhood. The building is now publicly identifiable as the association's headquarters. The motto \"Heritage · Solidarity · Hope\" is now displayed on the street.", it: "Installazione dell'insegna ufficiale SAFUAYE all'ingresso della sede, quartiere Atchrimi-Komé. L'edificio è ora pubblicamente identificabile come sede dell'associazione. Il motto \"Eredità · Solidarietà · Speranza\" è ora esposto sulla strada."},
                    color: "bg-teal-600",
                    icon: "🪧",
                    photos: ["enseigne-officielle.jpg"],
                  },
                  {
                    month: {fr: "Juin 2026 (semaine avant l'AG)", en: "June 2026 (week before AGM)", it: "Giugno 2026 (settimana prima dell'AG)"},
                    title: {fr: "Blanchiment de la cour & installation", en: "Courtyard whitewashing & setup", it: "Imbiancatura del cortile & allestimento"},
                    desc: {fr: "Sprint final : blanchiment à la chaux des murs de la cour arrière, dégagement et nivellement du sol, installation du chapiteau, montage du portrait mémorial de Safuayé, mise en place de la sono et de la tribune, arrivée des 25 chaises.", en: "Final sprint: whitewashing the courtyard walls, clearing and levelling the ground, marquee installed, memorial banner of Safuayé mounted, PA system and podium set up, 25 chairs delivered.", it: "Sprint finale: imbiancatura dei muri del cortile, sgombero e livellamento del suolo, montaggio del gazebo, banner commemorativo di Safuayé, impianto audio e podio, arrivo delle 25 sedie."},
                    color: "bg-orange-500",
                    icon: "🏗️",
                    photos: ["memorial-safuaye-1.jpg", "memorial-safuaye-2.jpg"],
                  },
                  {
                    month: {fr: "27 juin 2026", en: "27 June 2026", it: "27 giugno 2026"},
                    title: {fr: "L'AG Constitutive dans la cour restaurée", en: "The Constitutive AGM in the restored courtyard", it: "L'AG Costitutiva nel cortile ristrutturato"},
                    desc: {fr: "9h00. Vingt-cinq membres fondateurs s'installent dans la cour blanchie, sous le chapiteau, devant le portrait de Safuayé. L'Assemblée Générale Constitutive de SAFUAYE-TOGO est ouverte. Le siège renaît de ses cendres.", en: "9:00 am. Twenty-five founding members take their seats in the whitewashed courtyard, under the marquee, in front of Safuayé's portrait. The Constitutive General Assembly of SAFUAYE-TOGO is opened. The headquarters is reborn.", it: "9:00. Venticinque membri fondatori si accomodano nel cortile imbiancato, sotto il gazebo, davanti al ritratto di Safuayé. L'Assemblea Generale Costitutiva di SAFUAYE-TOGO è aperta. La sede rinasce."},
                    color: "bg-emerald-600",
                    icon: "🎉",
                    photos: ["siege-ag-2.jpg", "siege-ag-3.jpg", "siege-ag-5.jpg"],
                    highlight: true,
                  },
                ];
                return TIMELINE.map((event, idx) => {
                  const isLeft = idx % 2 === 0;
                  return (
                    <div key={idx} className={`relative mb-10 md:mb-14 ${isLeft ? 'md:pr-1/2' : 'md:pl-1/2'}`}>
                      {/* Dot marker */}
                      <div className={`absolute left-4 md:left-1/2 top-6 w-4 h-4 rounded-full ${event.color} ring-4 ring-white shadow-md md:-translate-x-1/2 z-10 ${event.highlight ? 'w-5 h-5 animate-pulse' : ''}`} aria-hidden="true"></div>

                      {/* Card */}
                      <div className={`ml-12 md:ml-0 ${isLeft ? 'md:mr-[calc(50%+2rem)] md:text-right' : 'md:ml-[calc(50%+2rem)]'}`}>
                        <div className={`inline-block ${event.highlight ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-300' : 'bg-white border-gray-200'} border rounded-2xl p-5 md:p-6 shadow-md hover:shadow-lg transition-shadow max-w-md ${isLeft ? 'md:ml-auto' : ''} text-left`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${event.color} text-white text-sm shadow-sm`}>{event.icon}</span>
                            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{event.month[lang] || event.month.fr}</p>
                          </div>
                          <h4 className="font-heading text-lg md:text-xl font-bold text-gray-900 mb-2">{event.title[lang] || event.title.fr}</h4>
                          <p className="text-sm text-gray-700 leading-relaxed mb-3">{event.desc[lang] || event.desc.fr}</p>
                          {event.photos && event.photos.length > 0 && (
                            <div className={`grid gap-2 mt-3 ${event.photos.length === 1 ? 'grid-cols-1' : event.photos.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                              {event.photos.map((ph, phi) => (
                                <a key={phi} href={`/images/siege-ag/${ph}`} target="_blank" rel="noreferrer" className="relative rounded-lg overflow-hidden group aspect-[4/3]">
                                  <img src={`/images/siege-ag/${ph}`} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* ============================================================ */}
          {/* Milestone Sept 12, 2026 — Distribution officielle au siège    */}
          {/* ============================================================ */}
          <div className="mb-16" data-testid="siege-distribution-12sept">
            <div className="bg-gradient-to-br from-orange-50 via-white to-emerald-50 border-2 border-orange-300 rounded-3xl p-6 md:p-10 shadow-xl">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-orange-600 text-white flex items-center justify-center shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v10H4V12"></path><path d="M2 7h20v5H2z"></path><path d="M12 22V7"></path><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path></svg>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-orange-700 mb-1">
                      {lang === 'fr' ? "Le siège en action · 12 septembre 2026" : lang === 'en' ? "Headquarters in action · 12 September 2026" : "Sede in azione · 12 settembre 2026"}
                    </p>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">
                      {lang === 'fr' ? "Rencontre officielle & distribution aux bénéficiaires"
                        : lang === 'en' ? "Official meeting & distribution to beneficiaries"
                        : "Incontro ufficiale & distribuzione ai beneficiari"}
                    </h3>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 self-start md:self-center px-4 py-2 rounded-full bg-orange-600 text-white text-sm font-semibold shadow-sm">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  {lang === 'fr' ? "12/09/2026 — Atakpamé" : lang === 'en' ? "12/09/2026 — Atakpamé" : "12/09/2026 — Atakpamé"}
                </span>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6 max-w-4xl">
                {lang === 'fr' ? "Trois mois après l'Assemblée Générale Constitutive, le siège d'Atchrimi-Komé a accueilli sa première grande action opérationnelle : la Rencontre Bénéficiaires SAFUAYE-TOGO. Vingt (20) bénéficiaires signataires (orphelins et veuves) ont reçu un don en espèces tiéré (3× 15 000 · 4× 10 000 · 13× 5 000 FCFA, soit 150 000 FCFA au total) ainsi qu'un kit scolaire complet pour leurs 25 enfants à charge. La presse Togoreport a couvert l'événement. Toutes les pièces officielles (émargement, listes de présence, fiches d'enregistrement, listes enfants scolarisés) sont archivées et publiées ci-dessous."
                  : lang === 'en' ? "Three months after the Constitutive General Assembly, the Atchrimi-Komé headquarters hosted its first major operational action: the SAFUAYE-TOGO Beneficiaries Meeting. Twenty (20) signatory beneficiaries (orphans and widows) received tiered cash donations (3× 15,000 · 4× 10,000 · 13× 5,000 FCFA, totaling 150,000 FCFA) plus a complete school kit for their 25 dependent children. The event was covered by Togoreport. All official documents are archived and published below."
                  : "Tre mesi dopo l'Assemblea Generale Costitutiva, la sede di Atchrimi-Komé ha ospitato la sua prima grande azione operativa: l'Incontro Beneficiari SAFUAYE-TOGO. Venti (20) beneficiari firmatari (orfani e vedove) hanno ricevuto donazioni in contanti scaglionate (3× 15.000 · 4× 10.000 · 13× 5.000 FCFA, totale 150.000 FCFA) più un kit scolastico completo per i loro 25 figli a carico. L'evento è stato coperto da Togoreport. Tutti i documenti ufficiali sono archiviati e pubblicati qui sotto."}
              </p>

              {/* KPI stripe */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {[
                  {v: "20", l: {fr: "Bénéficiaires signataires", en: "Signatory beneficiaries", it: "Beneficiari firmatari"}, c: "text-orange-700"},
                  {v: "25", l: {fr: "Enfants équipés (kits)", en: "Children equipped (kits)", it: "Bambini equipaggiati"}, c: "text-emerald-700"},
                  {v: "150 000", l: {fr: "FCFA distribués", en: "FCFA distributed", it: "FCFA distribuiti"}, c: "text-amber-700"},
                  {v: "11", l: {fr: "Pièces officielles archivées", en: "Official documents archived", it: "Documenti ufficiali archiviati"}, c: "text-blue-700"},
                ].map((k, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 text-center shadow-sm">
                    <p className={`text-2xl md:text-3xl font-bold ${k.c}`}>{k.v}</p>
                    <p className="text-xs text-gray-600 mt-1 uppercase tracking-wider">{k.l[lang] || k.l.fr}</p>
                  </div>
                ))}
              </div>

              {/* Photo mosaic — 15 event photos */}
              <div className="mb-8">
                <h4 className="text-sm font-bold uppercase tracking-wider text-orange-700 mb-4 flex items-center gap-2">
                  <span className="inline-block w-6 h-0.5 bg-orange-600"></span>
                  {lang === 'fr' ? "Photos officielles de la rencontre" : lang === 'en' ? "Official meeting photos" : "Foto ufficiali dell'incontro"}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3" data-testid="siege-12sept-gallery">
                  {[
                    "IMG-20260912-WA0008.jpg","IMG-20260912-WA0009.jpg","IMG-20260912-WA0010.jpg",
                    "IMG-20260912-WA0011.jpg","IMG-20260912-WA0012.jpg","IMG-20260912-WA0013.jpg",
                    "IMG-20260912-WA0014.jpg","IMG-20260912-WA0015.jpg","IMG-20260912-WA0016.jpg",
                    "IMG-20260912-WA0017.jpg","IMG-20260912-WA0018.jpg","IMG-20260912-WA0019.jpg",
                    "IMG-20260912-WA0020.jpg","IMG-20260912-WA0021.jpg","IMG-20260912-WA0022.jpg",
                  ].map((f, i) => (
                    <a
                      key={i}
                      href={`${API}/media/${f}`}
                      target="_blank"
                      rel="noreferrer"
                      className="relative rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-shadow group aspect-square bg-gray-100"
                      data-testid={`siege-12sept-photo-${i}`}
                    >
                      <img
                        src={`${API}/media/${f}`}
                        alt={`SAFUAYE-TOGO — Rencontre du 12 septembre 2026 · photo ${i + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              </div>

              {/* Official documents links */}
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider text-emerald-700 mb-4 flex items-center gap-2">
                  <span className="inline-block w-6 h-0.5 bg-emerald-600"></span>
                  {lang === 'fr' ? "Documents officiels de la distribution" : lang === 'en' ? "Official distribution documents" : "Documenti ufficiali della distribuzione"}
                </h4>
                <div className="grid md:grid-cols-3 gap-3">
                  {[
                    {
                      file: "SAFUAYE_Registres_Officiels_12sept2026.pdf",
                      title: {fr: "Registres officiels (11 pièces)", en: "Official registers (11 documents)", it: "Registri ufficiali (11 documenti)"},
                      desc: {fr: "Émargement · listes de présence · fiches d'enregistrement · liste des 25 enfants", en: "Signature list · attendance · registration forms · list of 25 children", it: "Firme · presenze · schede · lista 25 bambini"},
                      icon: "📋",
                    },
                    {
                      file: "SAFUAYE_PV_Remise_Dons_2026-09-12.pdf",
                      title: {fr: "PV de Remise de Dons", en: "Donation Handover Minutes", it: "Verbale di consegna donazioni"},
                      desc: {fr: "20 bénéficiaires, montants tiérés (5-15k FCFA), cosigné", en: "20 beneficiaries, tiered amounts (5-15k FCFA), co-signed", it: "20 beneficiari, importi scaglionati (5-15k FCFA), controfirmato"},
                      icon: "📄",
                    },
                    {
                      file: "SAFUAYE_Album_Rencontre_12sept2026.pdf",
                      title: {fr: "Album photo officiel", en: "Official photo album", it: "Album fotografico ufficiale"},
                      desc: {fr: "Album PDF haute résolution avec légendes officielles", en: "High-resolution PDF album with official captions", it: "Album PDF ad alta risoluzione"},
                      icon: "📸",
                    },
                  ].map((d, i) => (
                    <a
                      key={i}
                      href={`${API}/docs/${d.file}`}
                      target="_blank"
                      rel="noreferrer"
                      className="group bg-white border-2 border-gray-200 hover:border-emerald-500 rounded-2xl p-4 md:p-5 shadow-sm hover:shadow-lg transition-all"
                      data-testid={`siege-12sept-doc-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl md:text-3xl">{d.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-emerald-700 leading-tight">
                            {d.title[lang] || d.title.fr}
                          </p>
                          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                            {d.desc[lang] || d.desc.fr}
                          </p>
                          <p className="text-xs font-semibold text-emerald-600 mt-2 inline-flex items-center gap-1">
                            {lang === 'fr' ? "Consulter" : lang === 'en' ? "Open" : "Aprire"}
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                          </p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {hqPhotos.length === 0 ? (
            <div className="max-w-2xl mx-auto bg-orange-50 border border-orange-200 rounded-2xl p-8 text-center" data-testid="hq-empty">
              <div className="text-5xl mb-3">🏗️</div>
              <p className="text-gray-700 mb-2 font-semibold">
                {lang === 'fr' ? "Prochaines étapes : documentation avant/après détaillée."
                  : lang === 'en' ? "Next up: detailed before/after documentation."
                  : "Prossimo passo: documentazione dettagliata prima/dopo."}
              </p>
              <p className="text-sm text-gray-600">
                {lang === 'fr' ? "Chaque étape suivante (toiture, sanitaires, sol de la cour, salle communautaire, électricité) sera photographiée avant/après et publiée ici pour garantir la transparence des travaux."
                  : lang === 'en' ? "Each upcoming stage (roof, sanitary, courtyard flooring, community room, electrics) will be photographed before/after and published here for full transparency."
                  : "Ogni tappa successiva (tetto, servizi igienici, pavimentazione, sala comune, impianto elettrico) sarà fotografata prima/dopo e pubblicata qui per garantire la trasparenza."}
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {(() => {
                const STAGE_LABEL = {
                  overall:    { fr: "Vue générale",    en: "Overall view",   it: "Vista generale" },
                  demolition: { fr: "Démolition",      en: "Demolition",     it: "Demolizione" },
                  foundations:{ fr: "Fondations",      en: "Foundations",    it: "Fondazioni" },
                  walls:      { fr: "Murs",            en: "Walls",          it: "Muri" },
                  roof:       { fr: "Toiture",         en: "Roof",           it: "Tetto" },
                  electrics:  { fr: "Électricité",     en: "Electrics",      it: "Impianto elettrico" },
                  plumbing:   { fr: "Plomberie",       en: "Plumbing",       it: "Idraulica" },
                  joinery:    { fr: "Menuiserie",      en: "Joinery",        it: "Falegnameria" },
                  painting:   { fr: "Peinture",        en: "Painting",       it: "Pittura" },
                  finishing:  { fr: "Finitions",       en: "Finishing",      it: "Finiture" },
                  furniture:  { fr: "Mobilier",        en: "Furniture",      it: "Arredamento" },
                };
                const grouped = hqPhotos.reduce((acc, p) => {
                  (acc[p.stage] = acc[p.stage] || []).push(p);
                  return acc;
                }, {});
                const stageOrder = ["overall","demolition","foundations","walls","roof","electrics","plumbing","joinery","painting","finishing","furniture"];
                const documentedStages = stageOrder.filter(s => grouped[s]);
                const totalStages = stageOrder.length;
                const documentedCount = documentedStages.length;
                const progressPct = Math.round((documentedCount / totalStages) * 100);
                return (
                  <>
                    {/* Progress counter + bar */}
                    <div data-testid="hq-progress" className="max-w-3xl mx-auto bg-white border border-orange-100 rounded-2xl p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <p className="text-sm font-semibold text-gray-800">
                          {lang === 'fr' ? `${documentedCount} étape${documentedCount > 1 ? 's' : ''} documentée${documentedCount > 1 ? 's' : ''} sur ${totalStages}`
                            : lang === 'en' ? `${documentedCount} stage${documentedCount > 1 ? 's' : ''} documented out of ${totalStages}`
                            : `${documentedCount} fase${documentedCount > 1 ? 'i' : ''} documentat${documentedCount > 1 ? 'e' : 'a'} su ${totalStages}`}
                        </p>
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-700 bg-orange-100 px-2.5 py-1 rounded-full">
                          <span className="w-1.5 h-1.5 bg-orange-600 rounded-full animate-pulse"></span>
                          {progressPct}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-orange-500 to-orange-600 rounded-full transition-all duration-700 ease-out"
                          style={{ width: `${progressPct}%` }}
                          data-testid="hq-progress-bar"
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-3 italic">
                        {lang === 'fr' ? "Glissez les sliders Avant/Après pour explorer chaque étape."
                          : lang === 'en' ? "Drag the Before/After sliders to explore each stage."
                          : "Trascina i cursori Prima/Dopo per esplorare ogni fase."}
                      </p>
                    </div>

                    {documentedStages.map((stage, stageIdx) => {
                  const photos = grouped[stage];
                  const before = photos.find(p => p.type === "before");
                  const after = photos.find(p => p.type === "after");
                  const progress = photos.filter(p => p.type === "progress");
                  const sliderPct = hqCompareIndex[stage] ?? 50;
                  const hasInteracted = !!hqInteractedStages[stage];
                  const stageTitle = STAGE_LABEL[stage]?.[lang] || stage;
                  const shareTitle = lang === 'fr'
                    ? `SAFUAYE — Chantier siège à Atakpamé : ${stageTitle}`
                    : lang === 'en'
                    ? `SAFUAYE — HQ renovation in Atakpamé: ${stageTitle}`
                    : `SAFUAYE — Ristrutturazione sede ad Atakpamé: ${stageTitle}`;
                  return (
                    <div key={stage} id={`hq-stage-${stage}`} data-testid={`hq-stage-${stage}`} className="scroll-mt-24">
                      <h3 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                        <span className="inline-block w-2 h-8 bg-orange-500 rounded"></span>
                        {stageTitle}
                      </h3>

                      {before && after && (
                        <div className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-xl border border-gray-200 select-none" data-testid={`hq-compare-${stage}`}>
                          <div className="relative aspect-[16/10] bg-gray-100">
                            <img
                              src={`${API}/hq/photos/${after.id}/file`}
                              alt={`${stage} after`}
                              className="absolute inset-0 w-full h-full object-cover"
                              draggable="false"
                            />
                            <div
                              className="absolute inset-0 overflow-hidden"
                              style={{ width: `${sliderPct}%` }}
                            >
                              <img
                                src={`${API}/hq/photos/${before.id}/file`}
                                alt={`${stage} before`}
                                className="absolute inset-0 h-full object-cover"
                                style={{ width: `${100 / (sliderPct / 100 || 0.0001)}%`, maxWidth: 'none' }}
                                draggable="false"
                              />
                            </div>
                            {/* Labels */}
                            <span className="absolute top-3 left-3 bg-black/70 text-white text-xs px-3 py-1 rounded-full font-semibold tracking-wide">
                              {lang === 'fr' ? 'AVANT' : lang === 'en' ? 'BEFORE' : 'PRIMA'}
                            </span>
                            <span className="absolute top-3 right-3 bg-orange-600 text-white text-xs px-3 py-1 rounded-full font-semibold tracking-wide">
                              {lang === 'fr' ? 'APRÈS' : lang === 'en' ? 'AFTER' : 'DOPO'}
                            </span>
                            {/* Divider line */}
                            <div className={`absolute top-0 bottom-0 w-1 bg-white shadow-lg pointer-events-none ${!hasInteracted ? 'hq-slider-hint-line' : ''}`} style={{ left: `calc(${sliderPct}% - 2px)` }}>
                              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center ${!hasInteracted ? 'hq-slider-hint-handle' : ''}`}>
                                <span className="text-orange-600 font-bold">‹›</span>
                              </div>
                            </div>
                            {/* First-use hint bubble */}
                            {!hasInteracted && (
                              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-gray-900/85 text-white text-xs px-3 py-1.5 rounded-full pointer-events-none hq-slider-hint-bubble flex items-center gap-1.5">
                                <span>👉</span>
                                <span>{lang === 'fr' ? 'Glissez pour comparer' : lang === 'en' ? 'Drag to compare' : 'Trascina per confrontare'}</span>
                              </div>
                            )}
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={sliderPct}
                              onChange={(e) => {
                                setHqCompareIndex({ ...hqCompareIndex, [stage]: parseInt(e.target.value) });
                                if (!hasInteracted) setHqInteractedStages(prev => ({ ...prev, [stage]: true }));
                              }}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
                              data-testid={`hq-compare-slider-${stage}`}
                              aria-label={`Compare before/after for ${stage}`}
                            />
                          </div>
                          {(before.caption || after.caption) && (
                            <div className="bg-white p-4 text-sm text-gray-700 border-t border-gray-100">
                              {before.caption && <p><strong className="text-gray-900">{lang === 'fr' ? 'Avant :' : lang === 'en' ? 'Before:' : 'Prima:'}</strong> {before.caption}</p>}
                              {after.caption && <p><strong className="text-gray-900">{lang === 'fr' ? 'Après :' : lang === 'en' ? 'After:' : 'Dopo:'}</strong> {after.caption}</p>}
                            </div>
                          )}
                        </div>
                      )}

                      {!before && after && (
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="rounded-xl overflow-hidden shadow-md bg-white">
                            <img src={`${API}/hq/photos/${after.id}/file`} alt={`${stage} after`} className="w-full aspect-[4/3] object-cover" />
                            <div className="p-3 text-sm"><span className="inline-block bg-orange-100 text-orange-700 font-semibold text-xs px-2 py-0.5 rounded mr-2">{lang === 'fr' ? 'APRÈS' : lang === 'en' ? 'AFTER' : 'DOPO'}</span>{after.caption}</div>
                          </div>
                        </div>
                      )}
                      {before && !after && (
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="rounded-xl overflow-hidden shadow-md bg-white">
                            <img src={`${API}/hq/photos/${before.id}/file`} alt={`${stage} before`} className="w-full aspect-[4/3] object-cover" />
                            <div className="p-3 text-sm"><span className="inline-block bg-gray-800 text-white font-semibold text-xs px-2 py-0.5 rounded mr-2">{lang === 'fr' ? 'AVANT' : lang === 'en' ? 'BEFORE' : 'PRIMA'}</span>{before.caption}</div>
                          </div>
                        </div>
                      )}

                      {progress.length > 0 && (
                        <div className="mt-6">
                          <p className="text-sm font-semibold text-gray-700 mb-3">
                            {lang === 'fr' ? 'En cours de travaux' : lang === 'en' ? 'Work in progress' : 'Lavori in corso'}
                          </p>
                          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {progress.map(p => (
                              <div key={p.id} className="rounded-xl overflow-hidden shadow-sm border border-gray-200 bg-white">
                                <img src={`${API}/hq/photos/${p.id}/file`} alt={p.caption || stage} className="w-full aspect-square object-cover" />
                                {p.caption && <div className="p-2 text-xs text-gray-600">{p.caption}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Social share buttons for this stage */}
                      <div className="mt-5 flex items-center flex-wrap gap-2 justify-center md:justify-end" data-testid={`hq-share-${stage}`}>
                        <span className="text-xs text-gray-500 mr-1">
                          {lang === 'fr' ? 'Partager :' : lang === 'en' ? 'Share:' : 'Condividi:'}
                        </span>
                        <button onClick={() => shareStage('whatsapp', stage, shareTitle)} title="WhatsApp" data-testid={`share-whatsapp-${stage}`} className="w-9 h-9 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white flex items-center justify-center transition-transform hover:scale-110 shadow-sm" aria-label="Partager sur WhatsApp">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.595 5.392l-.999 3.648 3.893-1.739zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
                        </button>
                        <button onClick={() => shareStage('x', stage, shareTitle)} title="X / Twitter" data-testid={`share-x-${stage}`} className="w-9 h-9 rounded-full bg-black hover:bg-gray-800 text-white flex items-center justify-center transition-transform hover:scale-110 shadow-sm" aria-label="Partager sur X">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                        </button>
                        <button onClick={() => shareStage('facebook', stage, shareTitle)} title="Facebook" data-testid={`share-facebook-${stage}`} className="w-9 h-9 rounded-full bg-[#1877F2] hover:bg-[#166FE5] text-white flex items-center justify-center transition-transform hover:scale-110 shadow-sm" aria-label="Partager sur Facebook">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"/></svg>
                        </button>
                        <button onClick={() => shareStage('linkedin', stage, shareTitle)} title="LinkedIn" data-testid={`share-linkedin-${stage}`} className="w-9 h-9 rounded-full bg-[#0A66C2] hover:bg-[#004182] text-white flex items-center justify-center transition-transform hover:scale-110 shadow-sm" aria-label="Partager sur LinkedIn">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        </button>
                        <button onClick={() => shareStage('copy', stage, shareTitle)} title={lang === 'fr' ? 'Copier le lien' : lang === 'en' ? 'Copy link' : 'Copia link'} data-testid={`share-copy-${stage}`} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors shadow-sm" aria-label="Copier le lien">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                          {lang === 'fr' ? 'Copier le lien' : lang === 'en' ? 'Copy link' : 'Copia link'}
                        </button>
                      </div>
                    </div>
                  );
                })}
                  </>
                );
              })()}
            </div>
          )}

          <p className="text-center text-xs text-gray-500 mt-10 italic">
            {lang === 'fr' ? "Chantier suivi par le responsable local et validé chaque mois par le Conseil d'Administration (UK)."
              : lang === 'en' ? "Project managed locally and validated monthly by the Board of Trustees (UK)."
              : "Cantiere seguito dal responsabile locale e convalidato ogni mese dal Consiglio di Amministrazione (UK)."}
          </p>
        </div>
      </section>

      {/* Interactive map — locate the SAFUAYE-TOGO headquarters (Atchrimi-Komé) */}
      <SiegeMapSection lang={lang} apiBase={API} />

      {/* Impact Section */}
      <section id="impact" data-testid="impact-section" className="py-20 md:py-28 bg-gradient-to-b from-emerald-50 via-white to-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-emerald-700 mb-3">
              {lang === 'fr' ? 'Transparence • Redevabilité' : lang === 'en' ? 'Transparency • Accountability' : 'Trasparenza • Responsabilità'}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              {lang === 'fr' ? 'Notre impact' : lang === 'en' ? 'Our impact' : 'Il nostro impatto'}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {lang === 'fr' ? "Chaque action, chaque don, chaque vie touchée — documentés et rendus publics pour bâtir la confiance."
                : lang === 'en' ? "Every action, every donation, every life touched — documented and shared publicly to build trust."
                : "Ogni azione, ogni donazione, ogni vita toccata — documentata e resa pubblica per costruire fiducia."}
            </p>
          </div>

          {(!impactCampaigns || impactCampaigns.length === 0) ? (
            <div className="max-w-3xl mx-auto bg-white border-2 border-dashed border-emerald-300 rounded-3xl p-10 text-center" data-testid="impact-empty">
              <div className="text-6xl mb-4">🌱</div>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-3">
                {lang === 'fr' ? 'Notre première action démarre en juin 2026'
                  : lang === 'en' ? 'Our first action starts in June 2026'
                  : 'La nostra prima azione inizia a giugno 2026'}
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {lang === 'fr' ? "Notre toute première distribution officielle d'aide aura lieu en juin 2026 au profit des 7 anciens joueurs et collaborateurs d'Ifodjè FC déjà identifiés à Atakpamé. Nous publierons ici, dès juillet, les chiffres détaillés, les photos (avec consentement) et les enseignements tirés."
                  : lang === 'en' ? "Our first official aid distribution will take place in June 2026, supporting the 7 former Ifodjè FC players and collaborators already identified in Atakpamé. From July onwards, we will publish here detailed figures, photos (with consent) and key learnings."
                  : "La nostra prima distribuzione ufficiale di aiuti si terrà a giugno 2026, a beneficio dei 7 ex giocatori e collaboratori di Ifodjè FC già identificati ad Atakpamé. Da luglio pubblicheremo qui le cifre dettagliate, le foto (con consenso) e gli insegnamenti."}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                  <div className="text-2xl font-bold text-emerald-700">7</div>
                  <div className="text-xs text-emerald-900">{lang === 'fr' ? 'Bénéficiaires programmés' : lang === 'en' ? 'Beneficiaries planned' : 'Beneficiari programmati'}</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                  <div className="text-2xl font-bold text-emerald-700">175k</div>
                  <div className="text-xs text-emerald-900">FCFA {lang === 'en' ? 'planned' : lang === 'it' ? 'previsti' : 'prévus'}</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                  <div className="text-2xl font-bold text-emerald-700">£210</div>
                  <div className="text-xs text-emerald-900">≈ GBP</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                  <div className="text-2xl font-bold text-emerald-700">06/26</div>
                  <div className="text-xs text-emerald-900">{lang === 'fr' ? 'Distribution' : lang === 'en' ? 'Distribution' : 'Distribuzione'}</div>
                </div>
              </div>
              <p className="text-xs text-gray-500 italic mt-6">
                {lang === 'fr' ? "Cette page sera enrichie après chaque action documentée."
                  : lang === 'en' ? "This page will be enriched after each documented action."
                  : "Questa pagina sarà arricchita dopo ogni azione documentata."}
              </p>
            </div>
          ) : (
            <div>
              {/* Aggregate counters */}
              {impactTotals && impactTotals.campaigns_count > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-5xl mx-auto">
                  <div className="bg-white rounded-2xl p-6 shadow-md border border-emerald-100 text-center">
                    <div className="text-4xl font-bold text-emerald-700">{impactTotals.beneficiaries_count}</div>
                    <div className="text-sm text-gray-600 mt-1">{lang === 'fr' ? 'Bénéficiaires aidés' : lang === 'en' ? 'Beneficiaries supported' : 'Beneficiari aiutati'}</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border border-emerald-100 text-center">
                    <div className="text-4xl font-bold text-emerald-700">{impactTotals.total_fcfa.toLocaleString('fr-FR')}</div>
                    <div className="text-sm text-gray-600 mt-1">FCFA {lang === 'en' ? 'distributed' : lang === 'it' ? 'distribuiti' : 'distribués'}</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border border-emerald-100 text-center">
                    <div className="text-4xl font-bold text-emerald-700">£{impactTotals.total_gbp}</div>
                    <div className="text-sm text-gray-600 mt-1">{lang === 'en' ? 'equivalent' : lang === 'it' ? 'equivalenti' : 'équivalent'}</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border border-emerald-100 text-center">
                    <div className="text-4xl font-bold text-emerald-700">{impactTotals.campaigns_count}</div>
                    <div className="text-sm text-gray-600 mt-1">{lang === 'fr' ? 'Actions menées' : lang === 'en' ? 'Actions completed' : 'Azioni completate'}</div>
                  </div>
                </div>
              )}

              {/* Campaigns list */}
              <div className="space-y-12">
                {impactCampaigns.map(c => {
                  const title = (lang === 'en' && c.title_en) ? c.title_en : (lang === 'it' && c.title_it) ? c.title_it : c.title_fr;
                  const summary = (lang === 'en' && c.summary_en) ? c.summary_en : (lang === 'it' && c.summary_it) ? c.summary_it : c.summary_fr;
                  return (
                    <div key={c.id} className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden" data-testid={`impact-campaign-${c.id}`}>
                      <div className="p-6 md:p-8">
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                          <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wide rounded-full">{c.period}</span>
                          <div className="flex gap-4 text-sm">
                            <span><strong className="text-emerald-700">{c.beneficiaries_count}</strong> <span className="text-gray-500">{lang === 'fr' ? 'bénéf.' : lang === 'en' ? 'benef.' : 'benef.'}</span></span>
                            <span><strong className="text-emerald-700">{c.total_fcfa.toLocaleString('fr-FR')} FCFA</strong></span>
                            <span><strong className="text-emerald-700">£{c.total_gbp}</strong></span>
                            {c.receipts_signed_pct > 0 && <span className="text-gray-500">{c.receipts_signed_pct}% {lang === 'fr' ? 'reçus signés' : lang === 'en' ? 'receipts signed' : 'ricevute firmate'}</span>}
                          </div>
                        </div>
                        <h3 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-3">{title}</h3>
                        {summary && <p className="text-gray-700 leading-relaxed whitespace-pre-line">{summary}</p>}

                        {c.photos && c.photos.length > 0 && (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-6">
                            {c.photos.map(p => (
                              <div key={p.id} className="rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                                <img src={`${API}/impact/photos/${p.id}/file`} alt={p.caption_fr || title} className="w-full aspect-square object-cover" />
                                {(p.caption_fr || p.caption_en || p.caption_it) && (
                                  <div className="p-2 text-xs text-gray-700 italic">
                                    {(lang === 'en' && p.caption_en) ? p.caption_en : (lang === 'it' && p.caption_it) ? p.caption_it : p.caption_fr}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Beneficiaries Section */}
      <section id="beneficiaries" data-testid="beneficiaries-section" className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3">
              {t.beneficiaries.overline}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.beneficiaries.title}
            </h2>
            <p className="text-lg text-gray-600">{t.beneficiaries.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.beneficiaries.profiles.map((profile, i) => (
              <div key={`beneficiary-${i}-${profile.name}`} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow" data-testid={`beneficiary-${i}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-lg font-bold text-gray-900">{profile.name}</h3>
                    {profile.nickname && <p className="text-sm text-gray-500">{profile.nickname}</p>}
                  </div>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">{profile.age} ans</span>
                </div>
                <p className="text-sm text-green-700 font-medium mb-3">{profile.situation}</p>
                <p className="text-gray-600 italic mb-4">"{profile.testimony}"</p>
                <div className="flex flex-wrap gap-2">
                  {profile.needs.map((need, j) => (
                    <span key={`need-${i}-${j}-${need.substring(0, 10)}`} className="px-3 py-1 bg-red-50 text-red-700 text-xs rounded-full">{need}</span>
                  ))}
                </div>
              </div>
            ))}
            
            {/* Card for more beneficiaries */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-dashed border-green-300 flex flex-col items-center justify-center text-center" data-testid="beneficiary-more">
              <div className="w-16 h-16 bg-green-200 rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-green-700" />
              </div>
              <h3 className="font-heading text-lg font-bold text-green-800 mb-2">{t.beneficiaries.morecoming}</h3>
              <p className="text-sm text-green-700">{lang === 'fr' ? 'Notre mission continue d\'identifier et d\'aider les personnes dans le besoin à Atakpamé.' : 'Our mission continues to identify and help people in need in Atakpamé.'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Guide Section */}
      <section id="guide" data-testid="guide-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3">
              {t.guide.overline}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900">
              {t.guide.title}
            </h2>
          </div>

          {legalGuidance && (
            <div className="grid lg:grid-cols-2 gap-8">
              {/* UK Guide */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-2xl">🇬🇧</span>
                  <h3 className="font-heading text-xl font-bold text-gray-900">{t.guide.ukTitle}</h3>
                </div>
                <Accordion type="single" collapsible className="space-y-2" data-testid="uk-accordion">
                  {legalGuidance.uk.steps.map((step, index) => (
                    <AccordionItem key={`uk-step-${step.step}`} value={`uk-${index}`} className="border border-gray-200 bg-white rounded-lg">
                      <AccordionTrigger className="px-4 py-3 text-left hover:no-underline" data-testid={`uk-step-${index + 1}`}>
                        <span className="flex items-center gap-3">
                          <span className="w-7 h-7 bg-green-600 text-white text-sm flex items-center justify-center rounded-full font-medium">{step.step}</span>
                          <span className="font-medium text-gray-900 text-sm">{step.title}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4 text-gray-600 text-sm">
                        {step.description}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Togo Guide */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-2xl">🇹🇬</span>
                  <h3 className="font-heading text-xl font-bold text-gray-900">{t.guide.togoTitle}</h3>
                </div>
                <Accordion type="single" collapsible className="space-y-2" data-testid="togo-accordion">
                  {legalGuidance.togo.steps.map((step, index) => (
                    <AccordionItem key={`togo-step-${step.step}`} value={`togo-${index}`} className="border border-gray-200 bg-white rounded-lg">
                      <AccordionTrigger className="px-4 py-3 text-left hover:no-underline" data-testid={`togo-step-${index + 1}`}>
                        <span className="flex items-center gap-3">
                          <span className="w-7 h-7 bg-yellow-500 text-white text-sm flex items-center justify-center rounded-full font-medium">{step.step}</span>
                          <span className="font-medium text-gray-900 text-sm">{step.title}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4 text-gray-600 text-sm">
                        {step.description}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SAFUAYE UK ↔ SAFUAYE Togo — Structure organisationnelle */}
      <section id="org-structure" data-testid="org-structure-section" className="py-20 md:py-28 bg-gradient-to-b from-white via-emerald-50/30 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
          <div className="absolute top-10 left-1/4 w-96 h-96 bg-emerald-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-navy-800 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 relative">
          <div className="text-center mb-12">
            <span className="inline-block text-xs tracking-[0.25em] uppercase text-emerald-700 font-semibold mb-3" data-testid="org-structure-overline">
              {t.orgStructure.overline}
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-5xl font-bold text-gray-900 mb-5" data-testid="org-structure-title">
              {t.orgStructure.title}
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {t.orgStructure.intro}
            </p>
          </div>

          {/* Deux cartes UK / Togo + flèche de parrainage */}
          <div className="grid md:grid-cols-2 gap-6 mb-8 relative">
            {/* SAFUAYE UK — Entité mère */}
            <div className="bg-gradient-to-br from-blue-900 to-navy-800 text-white rounded-2xl p-7 shadow-xl border-2 border-blue-700 relative overflow-hidden" data-testid="org-uk-card">
              <div className="absolute top-3 right-3 text-[10px] font-bold uppercase bg-white text-navy-900 px-2 py-1 rounded">Parent</div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-3xl">🇬🇧</span>
                <div>
                  <h3 className="font-heading text-2xl font-bold">{t.orgStructure.ukTitle}</h3>
                  <p className="text-blue-200 text-xs">{t.orgStructure.ukRole}</p>
                </div>
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {t.orgStructure.ukPoints.map((p, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-emerald-400 mt-0.5">◆</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* SAFUAYE Togo — Entité opérationnelle */}
            <div className="bg-white text-gray-900 rounded-2xl p-7 shadow-lg border-2 border-emerald-300 relative overflow-hidden" data-testid="org-togo-card">
              <div className="absolute top-3 right-3 text-[10px] font-bold uppercase bg-emerald-700 text-white px-2 py-1 rounded">Opérationnel</div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-3xl">🇹🇬</span>
                <div>
                  <h3 className="font-heading text-2xl font-bold">{t.orgStructure.togoTitle}</h3>
                  <p className="text-emerald-700 text-xs font-semibold">{t.orgStructure.togoRole}</p>
                </div>
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {t.orgStructure.togoPoints.map((p, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-emerald-700 mt-0.5">◆</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bloc de liaison — Président International */}
          <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 text-white rounded-2xl p-6 md:p-8 shadow-xl" data-testid="org-link-block">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🤝</span>
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl md:text-2xl font-bold mb-2">{t.orgStructure.linkTitle}</h3>
                <p className="text-emerald-50 text-sm md:text-base leading-relaxed">{t.orgStructure.linkText}</p>
              </div>
              <a href="#contact" data-testid="org-link-cta"
                 className="inline-flex items-center gap-2 bg-white text-emerald-800 font-bold px-5 py-3 rounded-lg hover:bg-emerald-50 transition-colors whitespace-nowrap">
                {t.orgStructure.cta} →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Section - Constitution CIO téléchargeable */}
      <PublicTransparencyDashboard API={API} t={t} />

      {/* Programme Parrainage Scolaire — Public */}
      <SponsorshipPublicSection API={API} lang={lang} />

      <section id="governance" data-testid="governance-section" className="py-20 md:py-28 bg-gradient-to-br from-slate-50 via-blue-50/40 to-slate-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <div className="absolute top-20 right-20 w-96 h-96 bg-blue-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-indigo-600 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 relative">
          <div className="text-center mb-14">
            <span className="inline-block text-xs tracking-[0.25em] uppercase text-blue-700 font-semibold mb-3">
              {t.governance.overline}
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-5xl font-bold text-gray-900 mb-5">
              {t.governance.title}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t.governance.subtitle}
            </p>
          </div>

          {/* Structure juridique + Objets + Trustees */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Structure */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="governance-structure-card">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-blue-700" />
              </div>
              <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">{t.governance.structureTitle}</h3>
              <dl className="space-y-2.5 text-sm">
                {t.governance.structureItems.map((item, i) => (
                  <div key={i} className="flex flex-col gap-0.5 pb-2 border-b border-slate-100 last:border-0">
                    <dt className="text-xs uppercase tracking-wide text-slate-500 font-medium">{item.label}</dt>
                    <dd className="text-slate-800 font-medium">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Objets caritatifs */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="governance-objects-card">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-emerald-700" />
              </div>
              <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">{t.governance.objectsTitle}</h3>
              <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
                <p>{t.governance.objectsA}</p>
                <p>{t.governance.objectsB}</p>
              </div>
            </div>

            {/* Trustees */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="governance-trustees-card">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-indigo-700" />
              </div>
              <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">{t.governance.trusteesTitle}</h3>
              <ul className="space-y-3">
                {t.governance.trustees.map((name, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-800">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                      {name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                    </div>
                    <span className="font-medium">{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Téléchargements */}
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 rounded-3xl p-8 md:p-10 text-white shadow-2xl">
            <div className="text-center mb-8">
              <h3 className="font-heading text-2xl md:text-3xl font-bold mb-2">{t.governance.downloadTitle}</h3>
              <p className="text-blue-100 text-sm">{t.governance.downloadNote}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { code: 'EN', flag: '🇬🇧', name: 'English', official: true },
                { code: 'FR', flag: '🇫🇷', name: 'Français', official: false },
                { code: 'IT', flag: '🇮🇹', name: 'Italiano', official: false }
              ].map(item => (
                <div key={item.code} className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20 hover:bg-white/15 transition-colors" data-testid={`governance-download-${item.code.toLowerCase()}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">{item.flag}</span>
                    <div>
                      <p className="font-bold text-base">{item.name}</p>
                      {item.official ? (
                        <span className="inline-block text-[10px] uppercase tracking-wider bg-emerald-500 text-white px-2 py-0.5 rounded-full font-semibold mt-0.5">
                          {t.governance.officialBadge}
                        </span>
                      ) : (
                        <span className="inline-block text-[10px] uppercase tracking-wider bg-white/20 text-white px-2 py-0.5 rounded-full font-medium mt-0.5">
                          {t.governance.infoBadge}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-blue-100 mb-4 min-h-[32px]">
                    {item.official ? t.governance.officialDesc : t.governance.infoDesc}
                  </p>
                  <div className="flex gap-2">
                    <a
                      href={`/docs/SAFUAYE_Constitution_CIO_${item.code}.pdf`}
                      download
                      onClick={() => trackConstitutionDownload(item.code, 'pdf')}
                      data-testid={`public-download-${item.code.toLowerCase()}-pdf`}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-blue-900 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors"
                    >
                      <Download className="w-4 h-4" /> PDF
                    </a>
                    <a
                      href={`/docs/SAFUAYE_Constitution_CIO_${item.code}.docx`}
                      download
                      onClick={() => trackConstitutionDownload(item.code, 'docx')}
                      data-testid={`public-download-${item.code.toLowerCase()}-word`}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-white/20 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-white/30 transition-colors border border-white/30"
                    >
                      <FileText className="w-4 h-4" /> Word
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Donations Section - Coming Soon */}
      <section id="donations" className="py-20 md:py-28 bg-gradient-to-br from-green-50 to-emerald-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-72 h-72 bg-green-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3">
              {t.donations.overline}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-3">
              {lang === 'fr' ? "Soutenir SAFUAYE" : lang === 'en' ? "Support SAFUAYE" : "Sostenere SAFUAYE"}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {lang === 'fr' ? "Chaque contribution finance directement nos actions : rénovation du siège, kits scolaires, urgence AVC des anciens joueurs, forages Eau & Élan."
                : lang === 'en' ? "Every contribution directly funds our actions: headquarters renovation, school kits, stroke emergency support for former players, Water & Elan boreholes."
                : "Ogni contributo finanzia direttamente le nostre azioni: ristrutturazione della sede, kit scolastici, emergenza ictus per ex giocatori, pozzi Acqua & Élan."}
            </p>
          </div>

          {(() => {
            const CURRENCIES = [
              { code: "EUR", symbol: "€", flag: "🇪🇺", label: "Euro" },
              { code: "USD", symbol: "$", flag: "🇺🇸", label: "US Dollar" },
              { code: "GBP", symbol: "£", flag: "🇬🇧", label: "British Pound" },
            ];
            const PRESETS = [10, 25, 50, 100];
            const active = CURRENCIES.find(c => c.code === donCurrency);
            const finalAmount = donCustom ? parseFloat(donCustomValue) : donAmount;
            const validAmount = !isNaN(finalAmount) && finalAmount > 0;
            const paypalUrl = validAmount ? `https://paypal.me/koognak/${finalAmount}${donCurrency}` : "";
            return (
              <div className="grid md:grid-cols-5 gap-6">

                {/* Left column: PayPal donation form */}
                <div className="md:col-span-3 bg-white rounded-2xl shadow-xl p-6 md:p-8 border-2 border-green-200" data-testid="paypal-donation-card">
                  <div className="flex items-center gap-3 mb-5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-8 h-8 text-[#003087]" fill="currentColor"><path d="M20.067 8.478c.492.315.844.825.983 1.415.28 1.194.006 2.523-.628 3.6-.916 1.556-2.66 2.396-4.62 2.396h-.457c-.238 0-.44.174-.478.41l-.44 2.8-.128.812c-.03.19-.196.328-.386.328h-2.65c-.163 0-.28-.136-.256-.298l.966-6.128c.038-.238.24-.412.48-.412h1.32c2.16 0 3.844-.884 4.353-3.16.056-.253.098-.5.13-.74.13-.902.014-1.535-.19-1.023zM17.71 4.995c-.596-.68-1.657-.972-3.014-.972H9.86c-.24 0-.44.174-.48.41L7.42 17.05c-.023.162.094.298.256.298h2.988l.751-4.766-.023.15c.038-.237.24-.41.48-.41h1.323c2.62 0 4.665-1.064 5.267-4.14.017-.09.033-.18.048-.267.083-.53.083-.936-.038-1.267-.11-.29-.28-.55-.505-.774-.055-.055-.115-.107-.178-.157-.061-.048-.125-.093-.192-.135z"/></svg>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-[#003087]">PayPal</p>
                      <p className="text-sm text-gray-600">
                        {lang === 'fr' ? "Paiement sécurisé multi-devise" : lang === 'en' ? "Secure multi-currency payment" : "Pagamento sicuro multi-valuta"}
                      </p>
                    </div>
                  </div>

                  {/* Currency selector */}
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                    {lang === 'fr' ? "1. Choisir la devise" : lang === 'en' ? "1. Choose currency" : "1. Scegli la valuta"}
                  </p>
                  <div className="grid grid-cols-3 gap-2 mb-6">
                    {CURRENCIES.map(cur => (
                      <button
                        key={cur.code}
                        onClick={() => setDonCurrency(cur.code)}
                        className={`py-3 px-2 rounded-lg font-semibold text-sm border-2 transition-all ${donCurrency === cur.code ? 'border-green-600 bg-green-50 text-green-800 ring-2 ring-green-200' : 'border-gray-200 text-gray-600 hover:border-green-300'}`}
                        data-testid={`don-currency-${cur.code}`}
                      >
                        <span className="text-xl mr-1">{cur.flag}</span> {cur.code}
                      </button>
                    ))}
                  </div>

                  {/* Amount presets */}
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                    {lang === 'fr' ? "2. Montant" : lang === 'en' ? "2. Amount" : "2. Importo"}
                  </p>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {PRESETS.map(amt => (
                      <button
                        key={amt}
                        onClick={() => { setDonAmount(amt); setDonCustom(false); }}
                        className={`py-3 rounded-lg font-bold text-lg border-2 transition-all ${(!donCustom && donAmount === amt) ? 'border-green-600 bg-green-600 text-white shadow-md' : 'border-gray-200 text-gray-700 hover:border-green-300 bg-white'}`}
                        data-testid={`don-preset-${amt}`}
                      >
                        {active.symbol}{amt}
                      </button>
                    ))}
                  </div>
                  <div className="mb-6">
                    <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${donCustom ? 'border-green-600 bg-green-50' : 'border-gray-200 bg-white'}`}>
                      <input type="checkbox" checked={donCustom} onChange={(e) => setDonCustom(e.target.checked)} className="rounded" data-testid="don-custom-toggle" />
                      <span className="text-sm text-gray-700 font-medium">
                        {lang === 'fr' ? "Montant libre" : lang === 'en' ? "Custom amount" : "Importo libero"}
                      </span>
                      {donCustom && (
                        <div className="flex-1 flex items-center gap-2 ml-2">
                          <span className="text-lg font-bold text-gray-600">{active.symbol}</span>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={donCustomValue}
                            onChange={(e) => setDonCustomValue(e.target.value)}
                            placeholder="0"
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-green-400"
                            data-testid="don-custom-input"
                            autoFocus
                          />
                        </div>
                      )}
                    </label>
                  </div>

                  {/* CTA button */}
                  <a
                    href={validAmount ? paypalUrl : "#"}
                    target={validAmount ? "_blank" : undefined}
                    rel="noreferrer noopener"
                    onClick={(e) => { if (!validAmount) e.preventDefault(); }}
                    className={`block w-full text-center py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${validAmount ? 'bg-gradient-to-r from-[#003087] to-[#0070ba] hover:from-[#0070ba] hover:to-[#003087] text-white hover:shadow-2xl' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                    data-testid="paypal-donate-btn"
                  >
                    {validAmount
                      ? (lang === 'fr' ? `Contribuer ${active.symbol}${finalAmount} via PayPal` : lang === 'en' ? `Contribute ${active.symbol}${finalAmount} via PayPal` : `Contribuire ${active.symbol}${finalAmount} via PayPal`)
                      : (lang === 'fr' ? "Saisir un montant" : lang === 'en' ? "Enter an amount" : "Inserire un importo")}
                  </a>
                  <p className="text-xs text-gray-500 text-center mt-3">
                    {lang === 'fr' ? "Vous serez redirigé vers PayPal sur paypal.me/koognak" : lang === 'en' ? "You will be redirected to PayPal at paypal.me/koognak" : "Sarà reindirizzato a PayPal su paypal.me/koognak"}
                  </p>

                  {/* Legal notice */}
                  <div className="mt-5 p-3 bg-amber-50 border-l-4 border-amber-400 rounded text-xs text-amber-900 leading-relaxed">
                    <strong>{lang === 'fr' ? "Mention légale : " : lang === 'en' ? "Legal notice: " : "Nota legale: "}</strong>
                    {lang === 'fr' ? "Contribution volontaire. Le reçu fiscal (déductibilité impôt) sera disponible après l'enregistrement de SAFUAYE à la Charity Commission UK (prévu fin 2026)."
                      : lang === 'en' ? "Voluntary contribution. Tax-deductible receipts will be available after SAFUAYE's registration with the UK Charity Commission (expected late 2026)."
                      : "Contributo volontario. Le ricevute fiscalmente deducibili saranno disponibili dopo la registrazione di SAFUAYE presso la Charity Commission UK (prevista fine 2026)."}
                  </div>
                </div>

                {/* Right column: Alternatives + où va votre don */}
                <div className="md:col-span-2 space-y-4">
                  {/* Où va votre don */}
                  <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white rounded-2xl p-5 md:p-6 shadow-lg">
                    <p className="text-xs font-bold uppercase tracking-widest text-green-100 mb-2">
                      {lang === 'fr' ? "Où va votre contribution" : lang === 'en' ? "Where your gift goes" : "Dove va il tuo contributo"}
                    </p>
                    <ul className="space-y-2.5 text-sm">
                      {[
                        {fr:"Kits scolaires (EPP Agbonou-Koéroma)",en:"School kits (EPP Agbonou-Koéroma)",it:"Kit scolastici (EPP Agbonou-Koéroma)"},
                        {fr:"Urgence AVC : transport CHR, médicaments",en:"Stroke emergency: transport, medication",it:"Emergenza ictus: trasporto, farmaci"},
                        {fr:"Rénovation du siège d'Atchrimi-Komé",en:"Atchrimi-Komé HQ renovation",it:"Ristrutturazione sede Atchrimi-Komé"},
                        {fr:"Forages d'eau (Eau & Élan)",en:"Water boreholes (Eau & Élan)",it:"Pozzi d'acqua (Eau & Élan)"},
                        {fr:"Fonctionnement de l'association",en:"Association running costs",it:"Costi di funzionamento"},
                      ].map((line, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-green-200 mt-0.5">✓</span>
                          <span>{line[lang] || line.fr}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Alternatives */}
                  <div className="bg-white rounded-2xl shadow-md p-5 md:p-6 border border-gray-200">
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-700 mb-3">
                      {lang === 'fr' ? "Autres moyens de contribuer" : lang === 'en' ? "Other ways to contribute" : "Altri modi per contribuire"}
                    </p>
                    <div className="space-y-2 text-sm">
                      <a href="mailto:associationsafuaye@gmail.com?subject=Don%20via%20Wise" className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors group" data-testid="don-wise">
                        <div>
                          <p className="font-semibold text-gray-900">Wise</p>
                          <p className="text-xs text-gray-500">{lang==='fr'?"Virement international faible frais":"Low-fee international transfer"}</p>
                        </div>
                        <span className="text-gray-400 group-hover:text-green-600 group-hover:translate-x-0.5 transition-transform">→</span>
                      </a>
                      <a href="mailto:associationsafuaye@gmail.com?subject=Don%20via%20Revolut" className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors group" data-testid="don-revolut">
                        <div>
                          <p className="font-semibold text-gray-900">Revolut</p>
                          <p className="text-xs text-gray-500">{lang==='fr'?"Compte multi-devise, transfert instantané":"Multi-currency, instant transfer"}</p>
                        </div>
                        <span className="text-gray-400 group-hover:text-green-600 group-hover:translate-x-0.5 transition-transform">→</span>
                      </a>
                      <a href="mailto:associationsafuaye@gmail.com?subject=Don%20par%20virement%20bancaire" className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors group" data-testid="don-virement">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {lang === 'fr' ? "Virement bancaire" : lang === 'en' ? "Bank transfer" : "Bonifico bancario"}
                          </p>
                          <p className="text-xs text-gray-500">{lang==='fr'?"IBAN disponible sur demande":"IBAN available on request"}</p>
                        </div>
                        <span className="text-gray-400 group-hover:text-green-600 group-hover:translate-x-0.5 transition-transform">→</span>
                      </a>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      {lang === 'fr' ? "Écrivez-nous — nous vous transmettrons les coordonnées de manière sécurisée sous 24h." : lang === 'en' ? "Email us — we will share the details securely within 24h." : "Scriveteci — vi trasmetteremo i dettagli in sicurezza entro 24 ore."}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3">
              {t.testimonials.overline}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.testimonials.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.testimonials.subtitle}
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 md:p-10 shadow-lg border border-green-100">
              {testimonialSuccess && (
                <div className="bg-green-100 text-green-800 p-4 rounded-lg mb-6 text-center font-medium flex items-center justify-center gap-2">
                  <Heart className="w-5 h-5" />
                  {t.testimonials.form.success}
                </div>
              )}
              <form onSubmit={handleTestimonialSubmit} className="space-y-6">
                <div>
                  <input
                    type="text"
                    placeholder={t.testimonials.form.name}
                    value={testimonialForm.name}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder={t.testimonials.form.relation}
                    value={testimonialForm.relation}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, relation: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                  />
                </div>
                <div>
                  <textarea
                    placeholder={t.testimonials.form.messagePlaceholder}
                    value={testimonialForm.message}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, message: e.target.value })}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none bg-white"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-green-600 text-white py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  <Quote className="w-5 h-5" />
                  {t.testimonials.form.submit}
                </button>
              </form>
            </div>
          </div>

          {publicTestimonials.length > 0 && (
            <div className="max-w-5xl mx-auto mt-16" data-testid="public-testimonials-list">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
                {t.testimonials.featured || "Témoignages récents"}
              </h3>
              <div className="grid gap-6 md:grid-cols-2">
                {publicTestimonials.slice(0, 6).map((testimonial) => (
                  <div
                    key={testimonial.id}
                    data-testid={`testimonial-card-${testimonial.id}`}
                    className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Quote className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-700 italic mb-4">« {testimonial.message} »</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-900">{testimonial.author_name}</p>
                            {testimonial.relationship && (
                              <p className="text-sm text-gray-500">{testimonial.relationship}</p>
                            )}
                          </div>
                          <Heart className="w-5 h-5 text-green-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Notre Terrain — Odôn-Itsou 2026 */}
      <section id="notre-terrain" className="py-20 md:py-28 bg-gradient-to-b from-amber-50 via-orange-50 to-white" data-testid="odon-itsou-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-amber-700 mb-3">
              📍 Notre terrain — 9 août 2026
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Notre travail à Odôn-Itsou
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-3xl mx-auto italic">
              « Ensemble pour un Grand Ogou uni, résilient et prospère » — 53<sup>ème</sup> édition du festival Odôn-Itsou, Stade Municipal d&apos;Atakpamé.
            </p>
          </div>

          {/* Bloc récit + chiffres clés */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="md:col-span-2 bg-white rounded-2xl shadow-lg border border-amber-200 p-6 md:p-8">
              <h3 className="font-heading text-xl font-bold text-[#0B2545] mb-3">
                Notre première action de terrain
              </h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Dimanche <b>9 août 2026</b>, l&apos;association SAFUAYE-TOGO a mené sa première grande action de sensibilisation à l&apos;occasion du <b>gala de football Odôn-Itsou</b> (10 équipes en compétition). Objectif : gagner en visibilité et faire adhérer un maximum de personnes à notre mission.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Le <b>Président National AZIAKA Koffi</b> et le <b>Chargé de Communication ASSION Folly Kokou</b> ont sensibilisé les visiteurs, enrôlé les nouveaux adhérents et inscrit des enfants dans l&apos;Académie de Foot SAFUAYE (U8-U13). La <b>Secrétaire Générale APEDO Ablavi</b> est venue ponctuellement soutenir l&apos;équipe.
              </p>
              <div className="bg-amber-100 border-l-4 border-amber-500 p-4 rounded">
                <p className="text-sm text-amber-900 italic">
                  « Le travail a payé — l&apos;association est repartie avec plusieurs nouveaux adhérents, et beaucoup de visiteurs ont reçu les dépliants avec la promesse de nous rejoindre. » <b className="not-italic">Akpé kaka.</b>
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { n: "10", label: "équipes au gala", color: "text-amber-700" },
                { n: "3", label: "domaines présentés", color: "text-emerald-700" },
                { n: "1", label: "journée complète", color: "text-[#0B2545]"},
                { n: "16", label: "photos officielles", color: "text-rose-700" },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-xl shadow border border-amber-200 p-4 flex flex-col items-center justify-center text-center" data-testid={`odon-stat-${i}`}>
                  <div className={`text-4xl md:text-5xl font-bold ${s.color}`}>{s.n}</div>
                  <div className="text-xs md:text-sm text-gray-600 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Galerie photos */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="odon-itsou-gallery">
            {[
              { f: "02-bache-installee-mur.jpg", cap: "Bâche officielle installée" },
              { f: "04-dialogue-communaute.jpg", cap: "Dialogue avec la communauté" },
              { f: "08-stand-inscription.jpg", cap: "Séance d'inscription au stand" },
              { f: "11-jhonore-safuaye-jeune.jpg", cap: "Cadre #JHonoreSafuaye" },
              { f: "03-president-et-visiteur.jpg", cap: "Le Président en action" },
              { f: "16-jhonore-safuaye-sbca.jpg", cap: "Bénévole SBCA Academy" },
              { f: "09-jhonore-safuaye-portrait.jpg", cap: "Un aîné d'Atakpamé" },
              { f: "14-visiteurs-cadre.jpg", cap: "L'ambiance de la journée" },
            ].map((p, i) => (
              <a
                key={i}
                href={`/photos/odon-itsou-2026/${p.f}`}
                target="_blank"
                rel="noreferrer"
                className="group relative rounded-xl overflow-hidden shadow hover:shadow-2xl transition-all duration-300 aspect-square bg-gray-100"
                data-testid={`odon-photo-${i}`}
              >
                <img
                  src={`/photos/odon-itsou-2026/${p.f}`}
                  alt={p.cap}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                  <p className="text-white text-xs md:text-sm font-medium">{p.cap}</p>
                </div>
              </a>
            ))}
          </div>

          {/* CTA téléchargement rapport + partage */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <a
              href="/docs/SAFUAYE_Rapport_OdonItsou_2026.pdf"
              download
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0B2545] hover:bg-[#08192F] text-white font-semibold rounded-lg shadow transition"
              data-testid="odon-download-report"
            >
              📄 Télécharger le rapport photo complet (PDF)
            </a>
            <a
              href="/docs/SAFUAYE_FeuilleDeRoute_Academie_2026-2027.pdf"
              download
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-lg shadow transition"
              data-testid="download-feuille-de-route"
              title="Feuille de route stratégique 13 pages pour l'organisation de l'Académie"
            >
              🎓 Feuille de route Académie 2026-2027
            </a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent("Merci Atakpamé 🌿 Découvrez notre travail au festival Odôn-Itsou 2026 : https://safuaye.org/#notre-terrain #JHonoreSafuaye")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow transition"
              data-testid="odon-share-whatsapp"
            >
              📱 Partager sur WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section id="news" className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3">
              {t.news.overline}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.news.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.news.subtitle}
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {publicNews.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center border border-gray-200">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <FileText className="w-10 h-10 text-green-600" />
                </div>
                <div className="inline-flex items-center gap-3 bg-blue-100 text-blue-800 px-6 py-3 rounded-full font-semibold mb-6">
                  <AlertCircle className="w-5 h-5" />
                  <span>{t.news.comingSoon}</span>
                </div>
                <p className="text-lg text-gray-600 mb-4">
                  {t.news.comingSoonText}
                </p>
                <p className="text-xl font-semibold text-green-700">
                  {t.news.stayTuned}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {publicNews.map((article) => (
                  <div key={article.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow">
                    <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4">
                      <p className="text-white text-xs font-semibold uppercase tracking-wide">
                        {new Date(article.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : lang === 'en' ? 'en-GB' : 'it-IT', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-bold text-gray-900 mb-3">
                        {article.title}
                      </h3>
                      {article.summary && (
                        <p className="text-gray-600 mb-4 text-sm font-medium">
                          {article.summary}
                        </p>
                      )}
                      <p className="text-gray-700 leading-relaxed">
                        {article.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Meeting Section - UK Registration */}
      <section id="meeting" className="py-20 md:py-28 bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-700 mb-3">
              {t.meeting?.overline || "Réunion Officielle"}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.meeting?.title || "Assemblée Constitutive - Enregistrement UK"}
            </h2>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Meeting Info Card */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-200 p-8 md:p-10 mb-8">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-blue-600 font-semibold text-sm mb-2">📅 Date</div>
                  <div className="font-bold text-gray-900">{t.meeting?.date || "Dimanche 18 Avril 2026"}</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-green-600 font-semibold text-sm mb-2">🕐 Heure</div>
                  <div className="font-bold text-gray-900">{t.meeting?.time || "11h00 - 13h00 (Londres)"}</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-purple-600 font-semibold text-sm mb-2">💻 Format</div>
                  <div className="font-bold text-gray-900">{t.meeting?.format || "En ligne"}</div>
                </div>
              </div>

              {/* Objective */}
              <div className="bg-gradient-to-r from-blue-100 to-green-100 rounded-xl p-6 mb-8">
                <h3 className="font-heading text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Star className="w-5 h-5 text-blue-600" />
                  {lang === 'fr' ? 'Objectif' : lang === 'en' ? 'Objective' : 'Obiettivo'}
                </h3>
                <p className="text-gray-700">
                  {t.meeting?.objective || "Finaliser l'enregistrement de l'association SAFUAYE comme Charitable Incorporated Organisation (CIO) au Royaume-Uni"}
                </p>
              </div>

              {/* Agenda */}
              <div className="mb-8">
                <h3 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-green-600" />
                  {t.meeting?.agenda?.title || "Ordre du jour"}
                </h3>
                <ol className="space-y-3">
                  {(t.meeting?.agenda?.items || [
                    "Ouverture de la séance et appel des présents",
                    "Adoption de la Constitution (CIO)",
                    "Nomination officielle des Trustees",
                    "Définition des objectifs caritatifs",
                    "Vote sur les résolutions",
                    "Signature du procès-verbal",
                    "Clôture"
                  ]).map((item, index) => (
                    <li key={`agenda-${index}-${item.substring(0, 15)}`} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </span>
                      <span className="text-gray-700 pt-0.5">{item}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Registration Info - Restricted Meeting */}
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10 border border-gray-200">
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Users className="w-7 h-7 text-blue-600" />
                Réunion Restreinte
              </h3>
              <p className="text-gray-600 mb-6">
                Cette réunion est réservée aux <strong>3 Trustees officiels</strong> de l'association SAFUAYE pour finaliser l'enregistrement CIO au Royaume-Uni.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <h4 className="font-semibold text-lg mb-4 text-blue-900">👥 Participants Requis</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Président (Chair)</p>
                      <p className="text-sm text-gray-600">Responsable principal de la CIO</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                    <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Vice-Président (Vice-Chair)</p>
                      <p className="text-sm text-gray-600">Soutien et suppléance du président</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                    <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Secrétaire (Secretary)</p>
                      <p className="text-sm text-gray-600">Gestion administrative et PV</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800 font-medium">
                    ℹ️ Minimum requis par la Charity Commission UK : 3 trustees
                  </p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 text-center">
                  🔐 Les informations des trustees sont gérées via le panneau d'administration
                </p>
              </div>
            </div>

            {/* Admin Panel - Hidden by default */}
            {showPrivateDocuments && (
              <div className="mt-12 max-w-5xl mx-auto">
                <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-2xl p-8 border-2 border-purple-300">
                  <h3 className="font-heading text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Settings className="w-7 h-7 text-purple-600" />
                    Panneau d'Administration SAFUAYE
                  </h3>

                  {/* Admin Tabs */}
                  <div className="flex gap-2 mb-6 flex-wrap">
                    <button
                      onClick={() => setAdminTab("trustees")}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                        adminTab === "trustees" 
                          ? "bg-purple-600 text-white" 
                          : "bg-white text-gray-700 hover:bg-purple-50"
                      }`}
                    >
                      👥 Trustees & Réunion
                    </button>
                    <button
                      onClick={() => { setAdminTab("testimonials"); loadAdminTestimonials(); }}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                        adminTab === "testimonials" 
                          ? "bg-purple-600 text-white" 
                          : "bg-white text-gray-700 hover:bg-purple-50"
                      }`}
                    >
                      💬 Témoignages
                    </button>
                    <button
                      onClick={() => { setAdminTab("news"); loadAdminNews(); }}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                        adminTab === "news" 
                          ? "bg-purple-600 text-white" 
                          : "bg-white text-gray-700 hover:bg-purple-50"
                      }`}
                    >
                      📰 Actualités
                    </button>
                  </div>

                  {/* Trustees Tab */}
                  {adminTab === "trustees" && (
                    <div>
                      {/* Trustees Input Form */}
                      <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                        <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                          <Users className="w-5 h-5 text-purple-600" />
                          Enregistrer les 3 Trustees Officiels
                        </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Saisissez les informations des 3 trustees qui participeront à la réunion
                    </p>
                    
                    <div className="space-y-4">
                      {/* Président */}
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="font-semibold text-blue-900 mb-3">1️⃣ Président (Chair)</p>
                        <div className="grid md:grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="Nom complet"
                            id="chair-name"
                            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            id="chair-email"
                            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      {/* Vice-Président */}
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <p className="font-semibold text-green-900 mb-3">2️⃣ Vice-Président (Vice-Chair)</p>
                        <div className="grid md:grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="Nom complet"
                            id="vice-name"
                            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            id="vice-email"
                            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      </div>

                      {/* Secrétaire */}
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="font-semibold text-purple-900 mb-3">3️⃣ Secrétaire (Secretary)</p>
                        <div className="grid md:grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="Nom complet"
                            id="secretary-name"
                            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            id="secretary-email"
                            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>

                      <button
                        onClick={async () => {
                          const chair = { 
                            full_name: document.getElementById('chair-name').value, 
                            email: document.getElementById('chair-email').value,
                            role: 'Président (Chair)'
                          };
                          const vice = { 
                            full_name: document.getElementById('vice-name').value, 
                            email: document.getElementById('vice-email').value,
                            role: 'Vice-Président (Vice-Chair)'
                          };
                          const secretary = { 
                            full_name: document.getElementById('secretary-name').value, 
                            email: document.getElementById('secretary-email').value,
                            role: 'Secrétaire (Secretary)'
                          };
                          
                          try {
                            await axios.post(`${API}/meeting/register`, chair);
                            await axios.post(`${API}/meeting/register`, vice);
                            await axios.post(`${API}/meeting/register`, secretary);
                            alert('✅ Les 3 trustees ont été enregistrés avec succès !');
                          } catch (e) {
                            if (process.env.NODE_ENV === 'development') {
                              console.error(e);
                            }
                            alert('Erreur lors de l\'enregistrement');
                          }
                        }}
                        className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors font-semibold"
                      >
                        💾 Enregistrer les Trustees
                      </button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Email Template */}
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <Mail className="w-5 h-5 text-blue-600" />
                        Email de Convocation
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Générez l'email à envoyer aux participants inscrits
                      </p>
                      <button
                        onClick={loadEmailTemplate}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold mb-3"
                      >
                        📧 Générer l'Email
                      </button>
                      {emailTemplate && (
                        <button
                          onClick={copyEmailToClipboard}
                          className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                        >
                          📋 Copier l'Email
                        </button>
                      )}
                      {emailTemplate && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs">
                          <p className="font-semibold mb-2">Destinataires: {emailTemplate.count}</p>
                          <p className="text-gray-600 truncate">{emailTemplate.recipients.join(', ')}</p>
                        </div>
                      )}
                    </div>

                    {/* Meeting Minutes PDF */}
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-green-600" />
                        Procès-Verbal
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Générez le PV officiel de la réunion (après la réunion)
                      </p>
                      <button
                        onClick={generateMeetingMinutesPDF}
                        className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold mb-3"
                      >
                        📄 Générer le PV PDF
                      </button>
                      <div className="mt-4 p-3 bg-green-50 rounded-lg text-xs">
                        <p className="font-semibold mb-1">Le PV contiendra:</p>
                        <ul className="text-gray-700 space-y-1">
                          <li>• Liste des participants</li>
                          <li>• Ordre du jour</li>
                          <li>• Décisions votées</li>
                          <li>• Zones de signature</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Email Preview Modal */}
                  {emailTemplate && (
                    <div className="mt-6 bg-white rounded-xl p-6 shadow-lg">
                      <h4 className="font-semibold text-lg mb-4">📧 Aperçu de l'Email</h4>
                      <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                        <p className="font-bold mb-2">Objet: {emailTemplate.subject}</p>
                        <pre className="text-xs whitespace-pre-wrap font-mono text-gray-700">
                          {emailTemplate.body}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Testimonials Tab */}
              {adminTab === "testimonials" && (
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Quote className="w-5 h-5 text-blue-600" />
                    Gérer les Témoignages
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Approuvez ou supprimez les témoignages soumis par les visiteurs.
                  </p>
                  {adminTestimonials.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Aucun témoignage en attente</p>
                  ) : (
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {adminTestimonials.map((testimonial) => (
                        <div key={testimonial.id} className={`p-4 rounded-lg border-2 ${testimonial.approved ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold text-gray-900">{testimonial.author_name}</p>
                              <p className="text-xs text-gray-500">{testimonial.relationship}</p>
                            </div>
                            {testimonial.approved ? (
                              <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">✓ Approuvé</span>
                            ) : (
                              <span className="text-xs bg-yellow-600 text-white px-2 py-1 rounded">⏳ En attente</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mb-3">{testimonial.message}</p>
                          <div className="flex gap-2">
                            {!testimonial.approved && (
                              <button
                                onClick={() => approveTestimonial(testimonial.id)}
                                className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                              >
                                ✓ Approuver
                              </button>
                            )}
                            <button
                              onClick={() => deleteTestimonial(testimonial.id)}
                              className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                            >
                              ✕ Supprimer
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* News Tab */}
              {adminTab === "news" && (
                <div>
                  {/* Create News Form */}
                  <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                    <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      Créer une Actualité
                    </h4>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Titre de l'actualité"
                        value={newsForm.title}
                        onChange={(e) => setNewsForm({...newsForm, title: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                      <input
                        type="text"
                        placeholder="Résumé court"
                        value={newsForm.summary}
                        onChange={(e) => setNewsForm({...newsForm, summary: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                      <textarea
                        placeholder="Contenu complet de l'actualité"
                        value={newsForm.content}
                        onChange={(e) => setNewsForm({...newsForm, content: e.target.value})}
                        rows="4"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                      <button
                        onClick={createNews}
                        className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 font-semibold"
                      >
                        ✓ Publier l'Actualité
                      </button>
                    </div>
                  </div>

                  {/* News List */}
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h4 className="font-semibold text-lg mb-4">📰 Actualités Publiées</h4>
                    {adminNews.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Aucune actualité</p>
                    ) : (
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {adminNews.map((news) => (
                          <div key={news.id} className="p-4 rounded-lg border-2 bg-blue-50 border-blue-200">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold text-gray-900">{news.title}</p>
                                <p className="text-xs text-gray-500">{new Date(news.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{news.summary || news.content.substring(0, 100)}...</p>
                            <button
                              onClick={() => deleteNews(news.id)}
                              className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                            >
                              ✕ Supprimer
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  </section>

      {/* Contact Section */}
      <section id="contact" data-testid="contact-section" className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Membership Form CTA */}
          <div id="adherer" className="mb-12 bg-gradient-to-br from-green-700 to-emerald-800 rounded-3xl p-8 md:p-10 text-white shadow-xl" data-testid="membership-cta">
            <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
              <div className="text-5xl">📝</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold uppercase tracking-wider text-emerald-100 mb-1">
                  {lang === 'fr' ? 'Devenir membre' : lang === 'en' ? 'Become a member' : 'Diventa socio'}
                </p>
                <h3 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                  {lang === 'fr' ? "Rejoignez SAFUAYE — l'adhésion est gratuite"
                    : lang === 'en' ? 'Join SAFUAYE — membership is free'
                    : 'Unisciti a SAFUAYE — l\'adesione è gratuita'}
                </h3>
                <p className="text-emerald-50 mb-5 leading-relaxed">
                  {lang === 'fr' ? "Téléchargez la fiche d'adhésion bilingue (FR + EN), imprimez-la, complétez-la, signez-la, puis renvoyez-la à contact@safuaye.org (scan ou photo lisible) ou par courrier au siège UK. Confirmation sous 7 jours."
                    : lang === 'en' ? 'Download the bilingual (FR + EN) membership form, print it, fill it in, sign it, and return it to contact@safuaye.org (scan or readable photo) or by post to our UK office. Confirmation within 7 days.'
                    : 'Scarica il modulo di adesione bilingue (FR + EN), stampalo, compilalo, firmalo e rinvialo a contact@safuaye.org (scansione o foto leggibile) o per posta alla sede UK. Conferma entro 7 giorni.'}
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="/docs/SAFUAYE_Membership_Form.pdf"
                    download
                    data-testid="download-membership-pdf"
                    className="inline-flex items-center gap-2 bg-white hover:bg-emerald-50 text-green-800 px-5 py-2.5 rounded-full font-semibold transition-colors shadow-md"
                  >
                    <Download className="w-4 h-4" /> PDF
                  </a>
                  <a
                    href="/docs/SAFUAYE_Membership_Form.docx"
                    download
                    data-testid="download-membership-word"
                    className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2.5 rounded-full font-semibold transition-colors shadow-md border border-emerald-400"
                  >
                    <FileText className="w-4 h-4" /> Word
                  </a>
                  <a
                    href="mailto:contact@safuaye.org?subject=Fiche%20d'adh%C3%A9sion%20SAFUAYE"
                    data-testid="email-membership"
                    className="inline-flex items-center gap-2 bg-transparent border-2 border-white/60 hover:bg-white/10 text-white px-5 py-2.5 rounded-full font-semibold transition-colors"
                  >
                    <Mail className="w-4 h-4" /> {lang === 'fr' ? 'Envoyer par email' : lang === 'en' ? 'Send by email' : 'Invia per email'}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3">
                {t.contact.overline}
              </p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                {t.contact.title}
              </h2>
              <p className="text-lg text-gray-600 mb-8">{t.contact.subtitle}</p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-green-700" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">contact@safuaye.org</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{lang === 'fr' ? 'Téléphone' : 'Phone'}</p>
                    <p className="font-medium text-gray-900">+44 7398 308 310</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-green-700" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Operations</p>
                    <p className="font-medium text-gray-900">UK • Italie • Suisse • Togo</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <img src={SAFUAYE_ASSETS.logo} alt="SAFUAYE Logo" className="h-24 w-auto" />
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
              {contactSuccess && (
                <div className="bg-green-100 text-green-800 p-4 rounded-lg mb-6 text-center font-medium" data-testid="contact-success">
                  {t.contact.form.success}
                </div>
              )}
              <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                <input
                  type="text"
                  placeholder={t.contact.form.fullName}
                  value={contactForm.full_name}
                  onChange={(e) => setContactForm({ ...contactForm, full_name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="contact-name-input"
                />
                <input
                  type="email"
                  placeholder={t.contact.form.email}
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="contact-email-input"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="tel"
                    placeholder={t.contact.form.phone}
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    data-testid="contact-phone-input"
                  />
                  <input
                    type="text"
                    placeholder={t.contact.form.country}
                    value={contactForm.country}
                    onChange={(e) => setContactForm({ ...contactForm, country: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    data-testid="contact-country-input"
                  />
                </div>
                <select
                  value={contactForm.message_type}
                  onChange={(e) => setContactForm({ ...contactForm, message_type: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="contact-type-select"
                >
                  <option value="contact">{t.contact.form.types.contact}</option>
                  <option value="membership">{t.contact.form.types.membership}</option>
                  <option value="donation">{t.contact.form.types.donation}</option>
                </select>
                <textarea
                  placeholder={t.contact.form.message}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  data-testid="contact-message-input"
                />
                <button
                  type="submit"
                  className="w-full bg-green-700 text-white px-6 py-4 rounded-lg font-medium hover:bg-green-800 transition-colors"
                  data-testid="contact-submit-btn"
                >
                  {t.contact.form.submit}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ===== AG CONSTITUTIVE SAFUAYE-TOGO — Section publique ===== */}
      {showAgSection && (
        <section id="ag2026" data-testid="ag-section" className="relative py-20 bg-gradient-to-br from-green-50 via-white to-orange-50 scroll-mt-24">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-700 text-white rounded-full text-sm font-bold tracking-wider uppercase mb-4">
                {agIsLive ? "En cours d'assemblée" : (agPast ? "Assemblée tenue · Bureau élu" : "Événement à venir")}
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-3">
                Assemblée Générale Constitutive
              </h2>
              <p className="text-xl text-green-800 font-semibold mb-2">SAFUAYE-TOGO</p>
              <p className="text-gray-600 italic">Samedi 27 juin 2026 · 9h00 (mise en place dès 8h30) · Atakpamé, Préfecture de l'Ogou</p>
              {!agIsLive && (agDays > 0 || agHours > 0 || agMinutes > 0) && (
                <div className="mt-6 inline-flex items-center gap-3 px-6 py-3 bg-white border-2 border-orange-300 rounded-2xl shadow">
                  <span className="text-3xl font-bold text-orange-600 tabular-nums">{agDays}<span className="text-sm text-gray-500 ml-1">j</span></span>
                  <span className="text-gray-300">·</span>
                  <span className="text-3xl font-bold text-orange-600 tabular-nums">{String(agHours).padStart(2, '0')}<span className="text-sm text-gray-500 ml-1">h</span></span>
                  <span className="text-gray-300">·</span>
                  <span className="text-3xl font-bold text-orange-600 tabular-nums">{String(agMinutes).padStart(2, '0')}<span className="text-sm text-gray-500 ml-1">min</span></span>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-green-100">
                <p className="text-xs uppercase tracking-wider text-green-700 font-bold mb-2">📅 Date &amp; heure</p>
                <p className="font-bold text-gray-900 text-lg">Samedi 27 juin 2026</p>
                <p className="text-gray-600">Accueil dès 9h00</p>
                <p className="text-gray-600">Début à 10h00 précises</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-green-100">
                <p className="text-xs uppercase tracking-wider text-green-700 font-bold mb-2">📍 Lieu</p>
                <p className="font-bold text-gray-900 text-lg">Atakpamé</p>
                <p className="text-gray-600">Préfecture de l'Ogou</p>
                <p className="text-gray-600">République Togolaise</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-green-100">
                <p className="text-xs uppercase tracking-wider text-green-700 font-bold mb-2">📞 Contact Président National</p>
                <p className="font-bold text-gray-900 text-lg">AZIAKA KOFFI</p>
                <p className="text-gray-600">Président National</p>
                <a href="tel:+22891142519" className="text-orange-600 font-semibold hover:underline" data-testid="phone-aziaka">+228 91 14 25 19</a>
                <p className="text-xs text-gray-500 mt-1">Représentant légal Togo</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-green-100">
                <p className="text-xs uppercase tracking-wider text-green-700 font-bold mb-2">📞 Secrétariat Général</p>
                <p className="font-bold text-gray-900 text-lg">APEDO ABLAVI</p>
                <p className="text-gray-600">Secrétaire Générale</p>
                <a href="tel:+22872666474" className="text-orange-600 font-semibold hover:underline">+228 72 66 64 74</a>
                <p className="text-xs text-gray-500 mt-1">WhatsApp Secrétariat</p>
              </div>
            </div>

            {/* Numéro dédié dons Yas / Wave / Mixx by Yas */}
            <div className="mb-12 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-400 rounded-2xl p-6 shadow-sm" data-testid="donation-mobile-card">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0 bg-white rounded-xl px-6 py-4 shadow-md border border-amber-300">
                  <p className="text-xs uppercase tracking-wider text-amber-700 font-bold mb-1">💛 Numéro dédié Dons</p>
                  <a href="tel:+22893020384" className="block text-2xl md:text-3xl font-black text-amber-800 tabular-nums" data-testid="phone-dons-yas">+228 93 02 03 84</a>
                  <p className="text-xs text-gray-600 mt-1">Yas · 5G Togo</p>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <p className="font-bold text-gray-900 text-lg mb-1">📱 Faites un don par mobile money</p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Ce numéro <b>Yas</b> est dédié à la réception des dons via <b>Wave</b>, <b>Mixx by Yas</b>, <b>Mobile Money</b> et <b>Flooz</b>.
                    Chaque geste alimente directement les actions SAFUAYE au Togo.
                    Composez <span className="font-mono bg-white px-1.5 py-0.5 rounded border">*145#</span> depuis Yas pour envoyer un don.
                  </p>
                </div>
              </div>
            </div>

            {/* Trombinoscope du Bureau */}
            <div className="mb-12" data-testid="ag-trombinoscope">
              <div className="text-center mb-8">
                <span className="inline-block px-4 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-bold tracking-wider uppercase mb-3">{agPast ? "✅ Bureau élu" : "Bureau pressenti"}</span>
                <h3 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">{agPast ? "Le Bureau élu de l'AG 2026" : "Les visages de l'AG 2026"}</h3>
                <p className="text-gray-600 mt-2 text-sm italic">{agPast ? "Élus à l'unanimité lors de l'AG Constitutive du 27 juin 2026" : "Photos officielles à venir après l'élection du 27 juin"}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                {[
                  { initials: "AKO", name: "APEDO KOKOU OGNAKOTAN", role: "Président International", flag: "🇬🇧", color: "from-blue-600 to-indigo-700" },
                  { initials: "AK", name: "AZIAKA KOFFI", role: "Président National", flag: "🇹🇬", color: "from-green-600 to-green-800" },
                  { initials: "DK", name: "DOGLO KOMI", role: "Vice-Président", flag: "🇹🇬", color: "from-emerald-600 to-teal-700" },
                  { initials: "AA", name: "APEDO ABLAVI", role: "Secrétaire Générale", flag: "🇹🇬", color: "from-orange-500 to-red-600" },
                  { initials: "AE", name: "ABOTCHI ÉMILIE DJIFA A.", role: "Trésorière", flag: "🇹🇬", color: "from-amber-500 to-orange-600" },
                  { initials: "SK", name: "SMITH K. GNAMIGBEDJRE", role: "Trésorier Adjoint", flag: "🇹🇬", color: "from-rose-500 to-pink-600" },
                ].map((m, i) => (
                  <div key={m.initials} data-testid={`bureau-member-${i}`} className="group text-center">
                    <div className="relative mx-auto mb-3 w-24 h-24 md:w-28 md:h-28">
                      <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${m.color} shadow-lg group-hover:shadow-xl transition-all group-hover:scale-105 flex items-center justify-center`}>
                        <span className="text-white font-bold text-2xl md:text-3xl tracking-wider">{m.initials}</span>
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center text-xl border-2 border-white">
                        {m.flag}
                      </div>
                    </div>
                    <p className="font-semibold text-sm text-gray-900 leading-tight">{m.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{m.role}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 max-w-2xl mx-auto text-center">
                <p className="text-xs text-gray-500 italic">
                  🎖️ Les soutiens fidèles seront nommés <strong className="text-orange-700">Membres d'Honneur</strong> de l'association.
                </p>
              </div>
            </div>

            {/* Collège des Membres d'Honneur */}
            <div className="mb-12" data-testid="ag-college-honneur">
              <div className="text-center mb-8">
                <span className="inline-block px-4 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-bold tracking-wider uppercase mb-3">🎖️ Collège des Membres d'Honneur</span>
                <h3 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">Les sages d'Atakpamé</h3>
                <p className="text-gray-600 mt-2 text-sm max-w-2xl mx-auto italic">
                  Cinq notables d'Atakpamé apportent leur sagesse, leur expérience et leur ancrage communautaire au service de SAFUAYE.
                  Voix consultative au sein du Bureau, sans cotisation.
                </p>
              </div>

              {/* Photo officielle du Collège */}
              <figure className="relative group mb-10 overflow-hidden rounded-2xl shadow-xl" data-testid="college-photo-officielle">
                <div className="absolute -inset-1 bg-gradient-to-br from-amber-400 via-orange-500 to-red-600 rounded-3xl blur opacity-30 group-hover:opacity-50 transition-opacity"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/l0r14za9_IMG-20260629-WA0031.jpg"
                  alt="Les 5 Membres d'Honneur de SAFUAYE-TOGO posant ensemble"
                  loading="lazy"
                  className="relative w-full h-auto object-cover rounded-2xl"
                />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white px-4 md:px-6 py-4">
                  <p className="text-sm md:text-base font-semibold">📷 Le Collège officiel des Membres d'Honneur — Atakpamé, 27 juin 2026</p>
                  <p className="text-xs md:text-sm opacity-90 mt-1">Anagonou AGBOSSOU · Todzro KOKOU · Ayetan KOSSI · Kowou BERNARD · Midahin ANOUMOU</p>
                </figcaption>
              </figure>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
                {[
                  { initials: "AA", name: "Anagonou AGBOSSOU", ville: "Atakpamé" },
                  { initials: "TK", name: "Todzro KOKOU", ville: "Atakpamé" },
                  { initials: "AK", name: "Ayetan KOSSI", ville: "Atakpamé" },
                  { initials: "KB", name: "Kowou BERNARD", ville: "Atakpamé" },
                  { initials: "MA", name: "Midahin ANOUMOU", ville: "Atakpamé" },
                ].map((h, i) => (
                  <div key={h.initials} data-testid={`honneur-${i}`} className="group bg-white rounded-2xl p-5 shadow-sm border border-amber-100 hover:border-amber-300 hover:shadow-md transition-all text-center">
                    <div className="relative mx-auto mb-3 w-20 h-20">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-amber-600 shadow-md group-hover:scale-105 transition-transform flex items-center justify-center">
                        <span className="text-white font-bold text-xl tracking-wider">{h.initials}</span>
                      </div>
                      <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-amber-500 text-white shadow-md flex items-center justify-center text-xs font-bold border-2 border-white">
                        ⭐
                      </div>
                    </div>
                    <p className="font-semibold text-sm text-gray-900 leading-tight">{h.name}</p>
                    <p className="text-xs text-amber-700 mt-1 font-medium">Notable d'Atakpamé 🇹🇬</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 max-w-3xl mx-auto bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
                <p className="text-xs text-amber-900">
                  📜 <strong>Statut juridique :</strong> Les Membres d'Honneur sont nommés par l'AG sur proposition du Bureau (Article 6 bis des Statuts).
                  Ils participent aux délibérations avec <strong>voix consultative</strong>, sont dispensés de cotisation et constituent un <strong>collège de sages</strong> dont l'expertise éclaire les grandes orientations de l'association.
                </p>
              </div>
            </div>

            {/* Reportage AG — photos et récit */}
            {agPast && (
              <div className="mb-12" data-testid="ag-reportage">
                <div className="text-center mb-8">
                  <span className="inline-block px-4 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold tracking-wider uppercase mb-3">📸 Reportage officiel</span>
                  <h3 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">L'AG du 27 juin 2026 en images</h3>
                  <p className="text-gray-600 mt-2 text-sm max-w-2xl mx-auto italic">
                    Une matinée historique pour SAFUAYE, à Atakpamé.
                  </p>
                </div>

                {/* Photo de famille — Hero */}
                <figure className="relative group mb-10 overflow-hidden rounded-2xl shadow-2xl" data-testid="ag-photo-famille">
                  <div className="absolute -inset-1 bg-gradient-to-br from-green-600 via-orange-500 to-red-600 rounded-3xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/ymvp91as_IMG-20260629-WA0077.jpg"
                    alt="Photo de famille de l'AG SAFUAYE-TOGO · 27 juin 2026 · Atakpamé"
                    loading="lazy"
                    className="relative w-full h-auto object-cover rounded-2xl"
                  />
                  <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-white px-4 md:px-8 py-5">
                    <p className="text-sm md:text-lg font-bold">👨‍👩‍👧‍👦 La famille SAFUAYE-TOGO réunie — 27 juin 2026</p>
                    <p className="text-xs md:text-sm opacity-90 mt-1 italic">Bureau · Membres d'Honneur · Bénéficiaires · Soutiens — tous sous le même toit, animés par la même cause.</p>
                  </figcaption>
                </figure>

                {/* Décisions officielles - Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                  <div className="bg-gradient-to-br from-green-600 to-green-700 text-white rounded-2xl p-5 text-center shadow-lg" data-testid="stat-statuts">
                    <p className="text-4xl md:text-5xl font-bold tabular-nums">100%</p>
                    <p className="text-xs uppercase tracking-wider opacity-90 mt-1 font-bold">Statuts adoptés</p>
                    <p className="text-xs opacity-80 mt-0.5">25 articles · à l'unanimité</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-2xl p-5 text-center shadow-lg" data-testid="stat-ri">
                    <p className="text-4xl md:text-5xl font-bold tabular-nums">100%</p>
                    <p className="text-xs uppercase tracking-wider opacity-90 mt-1 font-bold">Règlement Intérieur</p>
                    <p className="text-xs opacity-80 mt-0.5">approuvé à l'unanimité</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-500 to-red-600 text-white rounded-2xl p-5 text-center shadow-lg" data-testid="stat-bureau">
                    <p className="text-4xl md:text-5xl font-bold">✓</p>
                    <p className="text-xs uppercase tracking-wider opacity-90 mt-1 font-bold">Bureau élu</p>
                    <p className="text-xs opacity-80 mt-0.5">5 postes · à l'unanimité</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-5 text-center shadow-lg" data-testid="stat-presents">
                    <p className="text-4xl md:text-5xl font-bold tabular-nums">25</p>
                    <p className="text-xs uppercase tracking-wider opacity-90 mt-1 font-bold">Membres présents</p>
                    <p className="text-xs opacity-80 mt-0.5">Liste émargée &amp; signée</p>
                  </div>
                </div>

                {/* Bouton téléchargement PV officiel */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
                  <button
                    onClick={generatePVTogoAssembleePDF}
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-green-700 to-green-800 hover:from-green-800 hover:to-green-900 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg hover:shadow-xl transition-all"
                    data-testid="download-pv-officiel"
                  >
                    📜 Télécharger le PV officiel (PDF)
                  </button>
                  <button
                    onClick={generateBordereauPrefetPDF}
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg hover:shadow-xl transition-all"
                    data-testid="download-bordereau"
                  >
                    🏛️ Bordereau de dépôt MATDDT (Lomé)
                  </button>
                  <button
                    onClick={generateAttestationResidencePDF}
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg hover:shadow-xl transition-all"
                    data-testid="download-attestation"
                  >
                    🏠 Attestations de résidence (5)
                  </button>
                  <button
                    onClick={generateDeclarationHonneurPDF}
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-950 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg hover:shadow-xl transition-all"
                    data-testid="download-declaration"
                  >
                    ✍️ Déclarations sur l'honneur (6)
                  </button>
                  <a
                    href="https://wa.me/22872666474?text=Bonjour%20SAFUAYE%2C%20je%20viens%20de%20d%C3%A9couvrir%20le%20compte-rendu%20de%20l%27AG%20du%2027%2F06%2F2026.%20F%C3%A9licitations%20!"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-800 border border-gray-200 px-6 py-3 rounded-full text-sm font-semibold shadow hover:shadow-md transition-all"
                    data-testid="contact-felicitations"
                  >
                    💬 Féliciter le Bureau
                  </a>
                </div>

                {/* Galerie photos */}
                <div className="grid md:grid-cols-2 gap-5 mb-8">
                  {[
                    {
                      src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/l9l6zvcc_IMG-20260628-WA0052.jpg",
                      alt: "Bureau de l'AG · 8h30 mise en place",
                      caption: "8h30 — Mise en place du Bureau · Photographe officiel en action",
                      span: "md:col-span-2"
                    },
                    {
                      src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/yis3rhry_IMG-20260629-WA0075.jpg",
                      alt: "Prière d'ouverture de l'AG SAFUAYE-TOGO",
                      caption: "🙏 Prière d'ouverture — un instant de recueillement avant les délibérations",
                      span: "md:col-span-2"
                    },
                    {
                      src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/f14qsw4k_IMG-20260628-WA0053.jpg",
                      alt: "Bureau présidentiel : AZIAKA KOFFI, APEDO KOKOU OGNAKOTAN, APEDO ABLAVI",
                      caption: "Le Bureau présidentiel : AZIAKA KOFFI (Président National), APEDO KOKOU OGNAKOTAN (Président International), APEDO ABLAVI (Secrétaire Générale)"
                    },
                    {
                      src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/xcwn4992_IMG-20260628-WA0049.jpg",
                      alt: "AZIAKA KOFFI et DOGLO KOMI au travail sur les Statuts",
                      caption: "Le Président National AZIAKA KOFFI et le Vice-Président DOGLO KOMI au travail"
                    },
                    {
                      src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/tcrrkt8f_IMG-20260629-WA0049.jpg",
                      alt: "Vote à main levée — Approbation à l'unanimité",
                      caption: "✋ Vote à main levée — Statuts &amp; Règlement Intérieur approuvés à l'unanimité",
                      span: "md:col-span-2"
                    },
                    {
                      src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/hgcuu76o_IMG-20260628-WA0071.jpg",
                      alt: "Liste de présence émargée — page 1",
                      caption: "📋 Liste de présence officielle — page 1 (lignes 1-21)"
                    },
                    {
                      src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/yrsscv3r_IMG-20260628-WA0072.jpg",
                      alt: "Liste de présence émargée — page 2",
                      caption: "📋 Liste de présence (suite) — page 2 (lignes 22-25 dont le Bureau élu)"
                    },
                  ].map((p, i) => (
                    <figure key={i} data-testid={`ag-photo-${i}`} className={`group relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all ${p.span || ''}`}>
                      <img
                        src={p.src}
                        alt={p.alt}
                        loading="lazy"
                        className="w-full h-full object-cover aspect-[4/3] group-hover:scale-[1.02] transition-transform duration-500"
                      />
                      <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent text-white text-xs md:text-sm px-4 py-3" dangerouslySetInnerHTML={{ __html: p.caption }} />
                    </figure>
                  ))}
                </div>

                {/* Récit chronologique */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
                  <h4 className="font-heading text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                    <span className="text-2xl">📜</span> Le déroulé minute par minute
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-700 text-white flex items-center justify-center font-bold text-sm">8h30</div>
                      <div>
                        <p className="font-semibold text-gray-900">Tout est en place</p>
                        <p className="text-sm text-gray-600">Sous le chapiteau dressé au siège, table des modérateurs installée. Sono, caméra, tribune et bulletins prêts. Les premiers membres arrivent.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-sm">🙏</div>
                      <div>
                        <p className="font-semibold text-gray-900">Prière d'ouverture</p>
                        <p className="text-sm text-gray-600">L'assemblée se recueille un instant. Les têtes s'inclinent. Une pensée pour les anciens disparus, une supplique pour la réussite de la mission SAFUAYE. La cérémonie peut commencer dans la sérénité.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm">9h00</div>
                      <div>
                        <p className="font-semibold text-gray-900">Ouverture officielle</p>
                        <p className="text-sm text-gray-600">L'Assemblée est ouverte sous la direction conjointe de <strong>APEDO KOKOU OGNAKOTAN</strong> (Président International), <strong>AZIAKA KOFFI</strong> (Président National) et <strong>APEDO ABLAVI</strong> (Secrétaire Générale). Émargement de la liste : <strong>25 membres présents</strong> (Atakpamé, Lomé, Diaspora Paris &amp; UK).</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-600 text-white flex items-center justify-center font-bold text-sm">⚖️</div>
                      <div>
                        <p className="font-semibold text-gray-900">Adoption des Statuts &amp; du Règlement Intérieur</p>
                        <p className="text-sm text-gray-600">Lecture article par article. Vote à main levée. <strong className="text-green-700">Statuts adoptés à 100 %</strong>. <strong className="text-orange-700">Règlement Intérieur approuvé à 100 %</strong>.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-sm">🗳️</div>
                      <div>
                        <p className="font-semibold text-gray-900">Élection du Bureau</p>
                        <p className="text-sm text-gray-600">Le <strong>Bureau Exécutif National (5 membres)</strong> et le <strong>Président International</strong> sont approuvés <strong className="text-red-700">à l'unanimité</strong>. Nomination des 5 Membres d'Honneur d'Atakpamé.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-pink-600 text-white flex items-center justify-center font-bold text-sm">❤️</div>
                      <div>
                        <p className="font-semibold text-gray-900">Distribution officielle des dons</p>
                        <p className="text-sm text-gray-600"><strong>15 bénéficiaires</strong> reçoivent leur soutien : 10 kits de vivres (riz, huile, macaroni, tomates) et 15 enveloppes totalisant <strong>175 000 FCFA</strong> remises par les membres du Bureau dans l'émotion et la reconnaissance.</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 pt-5 border-t border-gray-100 text-center">
                    <p className="text-sm text-gray-700 italic">
                      « Une page d'histoire s'écrit aujourd'hui pour SAFUAYE. »
                    </p>
                    <p className="text-xs text-gray-500 mt-1">— Atakpamé, le 27 juin 2026</p>
                  </div>
                </div>

                {/* Distribution des dons */}
                <div className="mt-12" data-testid="ag-dons">
                  <div className="text-center mb-8">
                    <span className="inline-block px-4 py-1 bg-pink-100 text-pink-800 rounded-full text-xs font-bold tracking-wider uppercase mb-3">❤️ Solidarité en action</span>
                    <h3 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">Distribution officielle des dons</h3>
                    <p className="text-gray-600 mt-2 text-sm max-w-2xl mx-auto italic">
                      Après l'élection du Bureau, place à la solidarité : 15 bénéficiaires soutenus dans la dignité.
                    </p>
                  </div>

                  {/* Stats dons */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    <div className="bg-gradient-to-br from-pink-500 to-rose-600 text-white rounded-2xl p-5 text-center shadow-lg" data-testid="dons-total">
                      <p className="text-4xl md:text-5xl font-bold tabular-nums">15</p>
                      <p className="text-xs uppercase tracking-wider opacity-90 mt-1 font-bold">Dons distribués</p>
                      <p className="text-xs opacity-80 mt-0.5">Bénéficiaires de juin 2026</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl p-5 text-center shadow-lg" data-testid="dons-kits">
                      <p className="text-4xl md:text-5xl font-bold tabular-nums">10</p>
                      <p className="text-xs uppercase tracking-wider opacity-90 mt-1 font-bold">Kits de vivres</p>
                      <p className="text-xs opacity-80 mt-0.5">Riz · huile · macaroni · tomates</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-green-700 text-white rounded-2xl p-5 text-center shadow-lg" data-testid="dons-enveloppes">
                      <p className="text-4xl md:text-5xl font-bold tabular-nums">15</p>
                      <p className="text-xs uppercase tracking-wider opacity-90 mt-1 font-bold">Enveloppes</p>
                      <p className="text-xs opacity-80 mt-0.5">5 × 20k · 5 × 10k · 5 × 5k</p>
                    </div>
                    <div className="bg-gradient-to-br from-violet-600 to-purple-700 text-white rounded-2xl p-5 text-center shadow-lg" data-testid="dons-fcfa">
                      <p className="text-3xl md:text-4xl font-bold tabular-nums">175&nbsp;000</p>
                      <p className="text-xs uppercase tracking-wider opacity-90 mt-1 font-bold">FCFA distribués</p>
                      <p className="text-xs opacity-80 mt-0.5">≈ 265 € · ≈ 230 £</p>
                    </div>
                  </div>

                  {/* Détail enveloppes */}
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl p-5 mb-8 border border-emerald-200">
                    <p className="font-semibold text-emerald-900 mb-3 text-sm uppercase tracking-wider">💰 Répartition des enveloppes</p>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-2xl font-bold text-emerald-700">5×</p>
                        <p className="text-sm font-semibold text-gray-900">20 000 FCFA</p>
                        <p className="text-xs text-gray-500">= 100 000 FCFA</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-2xl font-bold text-emerald-700">5×</p>
                        <p className="text-sm font-semibold text-gray-900">10 000 FCFA</p>
                        <p className="text-xs text-gray-500">= 50 000 FCFA</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <p className="text-2xl font-bold text-emerald-700">5×</p>
                        <p className="text-sm font-semibold text-gray-900">5 000 FCFA</p>
                        <p className="text-xs text-gray-500">= 25 000 FCFA</p>
                      </div>
                    </div>
                  </div>

                  {/* Galerie dons */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {[
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/zc0gmn0o_IMG-20260629-WA0084.jpg",
                        alt: "Le Bureau SAFUAYE-TOGO élu — photo officielle",
                        caption: "📷 Le Bureau SAFUAYE-TOGO élu — photo officielle (Doglo · Émilie · Alain · Ablavi · Aziaka · Kokou)",
                        span: "md:col-span-2 lg:col-span-3"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/wuroio89_IMG-20260629-WA0076.jpg",
                        alt: "Vue d'ensemble de l'Assemblée avant la distribution",
                        caption: "Vue d'ensemble de l'Assemblée avant la distribution"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/m9k4g7nn_IMG-20260629-WA0051.jpg",
                        alt: "Remise d'un don à un bénéficiaire",
                        caption: "Remise d'enveloppe et kit vivres"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/zois5rb7_IMG-20260629-WA0033.jpg",
                        alt: "Un don remis dans la dignité",
                        caption: "Un don remis dans la dignité"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/gizyqrxd_IMG-20260629-WA0034.jpg",
                        alt: "Geste fraternel entre membre du Bureau et bénéficiaire",
                        caption: "Geste fraternel · main dans la main"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/miq5e0z2_IMG-20260629-WA0020.jpg",
                        alt: "Kit de vivres et enveloppe remis avec respect",
                        caption: "Kit de vivres et enveloppe remis avec respect"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/hk540v4i_IMG-20260629-WA0057.jpg",
                        alt: "La Trésorière ABOTCHI ÉMILIE DJIFA A. remet le don à un bénéficiaire",
                        caption: "La Trésorière ABOTCHI ÉMILIE DJIFA A. · don remis avec attention"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/54egwmjl_IMG-20260629-WA0043.jpg",
                        alt: "Hommage à un ancien d'Ifodjè",
                        caption: "Hommage à un ancien d'Ifodjè FC"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/25pan8e0_IMG-20260629-WA0045.jpg",
                        alt: "Poignée de main fraternelle lors de la remise du don",
                        caption: "🤝 Poignée de main fraternelle — l'âme de SAFUAYE"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/jxkcsgwo_IMG-20260629-WA0078.jpg",
                        alt: "Remise d'enveloppe à un bénéficiaire souriant",
                        caption: "Remise d'enveloppe · un sourire vaut mille remerciements"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/mehluaff_IMG-20260629-WA0095.jpg",
                        alt: "Remise d'un don à un ancien d'Ifodjè FC",
                        caption: "Reconnaissance et respect aux anciens d'Ifodjè FC",
                        span: "md:col-span-2 lg:col-span-3"
                      },
                    ].map((p, i) => (
                      <figure key={i} data-testid={`don-photo-${i}`} className={`group relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all ${p.span || ''}`}>
                        <img
                          src={p.src}
                          alt={p.alt}
                          loading="lazy"
                          className="w-full h-full object-cover aspect-[4/3] group-hover:scale-[1.02] transition-transform duration-500"
                        />
                        <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent text-white text-xs md:text-sm px-4 py-3" dangerouslySetInnerHTML={{ __html: p.caption }} />
                      </figure>
                    ))}
                  </div>

                  <p className="text-center text-xs text-gray-500 mt-6 italic max-w-2xl mx-auto">
                    Chaque enveloppe a été remise en main propre par un membre du Bureau ou un Sage du Collège, dans le respect et la dignité de chaque bénéficiaire.
                  </p>
                </div>

                {/* Repas du partage */}
                <div className="mt-12" data-testid="ag-repas">
                  <div className="text-center mb-8">
                    <span className="inline-block px-4 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-bold tracking-wider uppercase mb-3">🍲 Convivialité</span>
                    <h3 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">Le repas du partage</h3>
                    <p className="text-gray-600 mt-2 text-sm max-w-2xl mx-auto italic">
                      Après les votes et les dons, place à la fraternité autour d'un repas préparé avec amour.
                      « En Afrique, ce qui se partage à table se grave dans le cœur. »
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {[
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/m3gew127_IMG-20260629-WA0053.jpg",
                        alt: "Préparation du repas par les femmes du Bureau",
                        caption: "🥘 Les marmites prêtes — préparation soignée par les femmes du Bureau",
                        span: "md:col-span-2 lg:col-span-3"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/g8h04124_IMG-20260629-WA0087.jpg",
                        alt: "Service du repas aux bénéficiaires",
                        caption: "Service du repas — chacun reçoit sa part"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/ogutsyv8_IMG-20260629-WA0068.jpg",
                        alt: "Le Président National savoure le repas",
                        caption: "Un membre du Bureau savoure le repas partagé"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/b9wmi7kv_IMG-20260629-WA0069.jpg",
                        alt: "Trois convives partagent le repas",
                        caption: "Convives en pleine dégustation · l'instant fraternité"
                      },
                      {
                        src: "https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/tyoahmo0_IMG-20260629-WA0054.jpg",
                        alt: "Anciens et soutiens partagent le même repas",
                        caption: "Anciens, dirigeants et soutiens à la même table",
                        span: "md:col-span-2 lg:col-span-3"
                      },
                    ].map((p, i) => (
                      <figure key={i} data-testid={`repas-photo-${i}`} className={`group relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all ${p.span || ''}`}>
                        <img
                          src={p.src}
                          alt={p.alt}
                          loading="lazy"
                          className="w-full h-full object-cover aspect-[4/3] group-hover:scale-[1.02] transition-transform duration-500"
                        />
                        <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent text-white text-xs md:text-sm px-4 py-3" dangerouslySetInnerHTML={{ __html: p.caption }} />
                      </figure>
                    ))}
                  </div>

                  <div className="mt-6 max-w-3xl mx-auto bg-orange-50 border-l-4 border-orange-500 rounded-r-lg p-4 text-center">
                    <p className="text-sm text-orange-900 italic">
                      🌍 « <strong>La table SAFUAYE</strong> n'exclut personne : bénéficiaires, membres du Bureau, Sages d'Atakpamé, soutiens — tous ont mangé ensemble dans la même cour, ce 27 juin 2026. C'est cela, l'âme africaine de notre association. »
                    </p>
                  </div>
                </div>

                {/* IN MEMORIAM — Le fondateur Simon-Joseph "Safuayé" APEDO */}
                <div className="mt-12" data-testid="ag-in-memoriam">
                  <div className="text-center mb-8">
                    <span className="inline-block px-4 py-1 bg-gray-900 text-amber-300 rounded-full text-xs font-bold tracking-wider uppercase mb-3">🕊️ In Memoriam</span>
                    <h3 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">Celui dont nous portons le nom</h3>
                    <p className="text-gray-600 mt-2 text-sm max-w-2xl mx-auto italic">
                      L'association SAFUAYE tire son nom du surnom d'un homme. Voici son histoire.
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 rounded-3xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
                    <div className="grid md:grid-cols-5 gap-0">
                      {/* Photo affiche commémorative */}
                      <div className="md:col-span-2 relative">
                        <img
                          src="https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/kafljng3_IMG-20260629-WA0080.jpg"
                          alt="Affiche commémorative de Simon-Joseph Afantchédé APEDO dit Safuayé"
                          loading="lazy"
                          className="w-full h-full object-cover md:min-h-[500px]"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="inline-block px-3 py-1 bg-amber-500 text-gray-900 rounded-full text-xs font-bold tracking-wider uppercase shadow-md">⚽ Ancien Président d'Ifodjè FC</span>
                        </div>
                      </div>

                      {/* Bio fondateur */}
                      <div className="md:col-span-3 p-7 md:p-10 text-gray-100">
                        <div className="inline-block px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs font-bold tracking-wider uppercase mb-4">
                          Président 1984-1990
                        </div>

                        <h4 className="font-heading text-3xl md:text-4xl font-bold text-white leading-tight">
                          Simon-Joseph Afantchédé <span className="text-amber-300">APEDO</span>
                        </h4>
                        <p className="text-amber-200 mt-1 text-lg italic">dit « Safuayé »</p>

                        <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
                          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <p className="text-amber-300 text-xs uppercase tracking-wider font-bold mb-1">📐 Profession</p>
                            <p className="text-white">Géomètre · Dessinateur · Topographe</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <p className="text-amber-300 text-xs uppercase tracking-wider font-bold mb-1">⚽ Présidence Ifodjè</p>
                            <p className="text-white">1984 → 1990</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <p className="text-amber-300 text-xs uppercase tracking-wider font-bold mb-1">🕊️ Rappelé à Dieu</p>
                            <p className="text-white">18 août 1991 · Atakpamé</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <p className="text-amber-300 text-xs uppercase tracking-wider font-bold mb-1">💚 Héritage</p>
                            <p className="text-white">Le nom <strong>SAFUAYE</strong> est notre serment</p>
                          </div>
                        </div>

                        <blockquote className="mt-6 border-l-4 border-amber-400 pl-4 py-2">
                          <p className="text-base md:text-lg text-amber-50 italic font-light leading-relaxed">
                            « L'amour n'est pas une question de croyance, mais une question de démonstration. »
                          </p>
                          <footer className="text-xs text-amber-300 mt-2">— Simon-Joseph « Safuayé » APEDO</footer>
                        </blockquote>

                        <p className="mt-6 text-sm text-gray-300 leading-relaxed">
                          Élu <strong className="text-white">président d'Ifodjè Football Club d'Atakpamé de 1984 à 1990</strong>, il porta le club pendant six saisons. À sa disparition en août 1991, son surnom <em>« Safuayé »</em> est devenu un héritage. <strong className="text-amber-200">L'association qui aujourd'hui rénove le siège, soutient les anciens, et tient son AG dans la cour qu'il aimait, porte ce surnom comme un drapeau.</strong>
                        </p>

                        <p className="mt-4 text-xs text-amber-300/80 italic">
                          ✨ Chaque action de SAFUAYE est une démonstration. À jamais tu demeures.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mémoires d'Ifodjè — Portraits des anciens */}
                <div className="mt-12" data-testid="ag-memoires">
                  <div className="text-center mb-8">
                    <span className="inline-block px-4 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold tracking-wider uppercase mb-3">⚽ Mémoires d'Atakpamé</span>
                    <h3 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">Les visages d'Ifodjè</h3>
                    <p className="text-gray-600 mt-2 text-sm max-w-2xl mx-auto italic">
                      Derrière chaque don, il y a un homme. Derrière chaque homme, il y a une histoire. Voici les portraits de ceux qui ont porté les couleurs d'<strong>Ifodjè Football Club d'Atakpamé</strong>.
                    </p>
                    <p className="text-xs text-gray-500 max-w-2xl mx-auto mt-3 italic">
                      📝 Les récits ci-dessous s'inspirent de la <strong>mémoire régionale</strong> et des témoignages du football togolais des années 1980-1990. Toute correction, précision ou complément par les familles et proches est chaleureusement bienvenu via WhatsApp.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {/* Carte Assogba NAYO */}
                    <article className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow border border-green-100" data-testid="memoire-assogba">
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img
                          src="https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/64ge8mji_IMG-20260629-WA0023.jpg"
                          alt="Portrait d'Assogba NAYO, ancien d'Ifodjè FC d'Atakpamé"
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="inline-block px-3 py-1 bg-green-700 text-white rounded-full text-xs font-bold tracking-wider uppercase shadow-md">⚽ Ancien d'Ifodjè FC</span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-heading text-xl font-bold text-gray-900">Assogba NAYO</h4>
                        <p className="text-sm text-gray-500 mt-1">Atakpamé · Togo</p>
                        <div className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed">
                          <p className="text-xs font-semibold text-green-700 uppercase tracking-wider">Poste probable · Défenseur central / milieu défensif</p>
                          <p>
                            Joueur emblématique d'Ifodjè, souvent cité parmi les <strong>cadres de l'équipe durant les années de montée en puissance du club</strong>. Réputé pour son engagement, son sens du placement et sa discipline dans le vestiaire.
                          </p>
                          <p>
                            A contribué à la solidité défensive du club durant les années où Ifodjè s'imposait comme une force dans la région de l'Ogou.
                          </p>
                          <p className="italic text-gray-600 border-l-2 border-green-600 pl-3 text-xs">
                            « Bénéficiaire de l'AG du 27/06/2026 — kit de vivres et enveloppe remis en reconnaissance d'une vie de passion footballistique. »
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                          <a
                            href="https://wa.me/22872666474?text=Bonjour%20SAFUAYE%2C%20je%20souhaite%20enrichir%20la%20biographie%20d%27Assogba%20NAYO"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-green-700 hover:text-green-900 hover:underline"
                          >
                            ✍️ Enrichir
                          </a>
                          <span className="text-xs text-gray-400">Profil n°01</span>
                        </div>
                      </div>
                    </article>

                    {/* Carte Laby ANKOU */}
                    <article className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow border border-green-100" data-testid="memoire-laby">
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img
                          src="https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/9bqqwck9_IMG-20260629-WA0025.jpg"
                          alt="Portrait de Laby ANKOU, ancien d'Ifodjè FC d'Atakpamé"
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="inline-block px-3 py-1 bg-green-700 text-white rounded-full text-xs font-bold tracking-wider uppercase shadow-md">⚽ Ancien d'Ifodjè FC</span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-heading text-xl font-bold text-gray-900">Laby ANKOU</h4>
                        <p className="text-sm text-gray-500 mt-1">Atakpamé · Togo</p>
                        <div className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed">
                          <p className="text-xs font-semibold text-green-700 uppercase tracking-wider">Poste probable · Milieu offensif / ailier</p>
                          <p>
                            Un des joueurs les plus <strong>techniques de sa génération à Atakpamé</strong>. Très à l'aise balle au pied, capable de créer des décalages, de casser les lignes et d'apporter de la créativité.
                          </p>
                          <p>
                            Souvent considéré comme un <strong>« moteur » du jeu offensif</strong> d'Ifodjè, il a marqué plusieurs buts décisifs dans les compétitions régionales.
                          </p>
                          <p className="italic text-gray-600 border-l-2 border-green-600 pl-3 text-xs">
                            « Bénéficiaire de l'AG du 27/06/2026 — kit de vivres et enveloppe remis avec gratitude. »
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                          <a
                            href="https://wa.me/22872666474?text=Bonjour%20SAFUAYE%2C%20je%20souhaite%20enrichir%20la%20biographie%20de%20Laby%20ANKOU"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-green-700 hover:text-green-900 hover:underline"
                          >
                            ✍️ Enrichir
                          </a>
                          <span className="text-xs text-gray-400">Profil n°02</span>
                        </div>
                      </div>
                    </article>

                    {/* Carte Ayetan KOSSI — double statut */}
                    <article className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow border border-amber-200" data-testid="memoire-ayetan">
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img
                          src="https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/dh8bsk7b_IMG-20260629-WA0027.jpg"
                          alt="Portrait d'Ayetan KOSSI, ancien d'Ifodjè FC et Sage d'Atakpamé"
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4 right-4 flex flex-wrap gap-2">
                          <span className="inline-block px-3 py-1 bg-green-700 text-white rounded-full text-xs font-bold tracking-wider uppercase shadow-md">⚽ Ancien d'Ifodjè</span>
                          <span className="inline-block px-3 py-1 bg-amber-500 text-white rounded-full text-xs font-bold tracking-wider uppercase shadow-md">🎖️ Sage</span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-heading text-xl font-bold text-gray-900">Ayetan KOSSI</h4>
                        <p className="text-sm text-gray-500 mt-1">Atakpamé · Togo</p>
                        <div className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed">
                          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Poste probable · Milieu relayeur / latéral</p>
                          <p>
                            Joueur <strong>polyvalent, apprécié pour son endurance et son engagement</strong>. Gros volume de jeu, capacité à couvrir beaucoup de terrain — joueur de confiance pour les entraîneurs.
                          </p>
                          <p>
                            Souvent utilisé pour <strong>équilibrer le jeu</strong>, il contribuait aux transitions entre défense et attaque. Honneur particulier : il est aujourd'hui l'un des cinq <strong>Sages d'Atakpamé</strong> nommés Membres d'Honneur par l'AG.
                          </p>
                          <p className="italic text-gray-600 border-l-2 border-amber-500 pl-3 text-xs">
                            « Membre d'Honneur du Collège des Sages · Bénéficiaire de l'AG 27/06/2026 — son expérience éclairera durablement nos décisions. »
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                          <a
                            href="https://wa.me/22872666474?text=Bonjour%20SAFUAYE%2C%20je%20souhaite%20enrichir%20la%20biographie%20d%27Ayetan%20KOSSI"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-amber-700 hover:text-amber-900 hover:underline"
                          >
                            ✍️ Enrichir
                          </a>
                          <span className="text-xs text-gray-400">Profil n°03</span>
                        </div>
                      </div>
                    </article>

                    {/* Carte Dokou KOMLANVI */}
                    <article className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow border border-green-100" data-testid="memoire-dokou">
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img
                          src="https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/84sfh6rm_IMG-20260629-WA0064.jpg"
                          alt="Portrait de Dokou KOMLANVI, ancien d'Ifodjè FC d'Atakpamé"
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="inline-block px-3 py-1 bg-green-700 text-white rounded-full text-xs font-bold tracking-wider uppercase shadow-md">⚽ Ancien d'Ifodjè FC</span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-heading text-xl font-bold text-gray-900">Dokou KOMLANVI</h4>
                        <p className="text-sm text-gray-500 mt-1">Atakpamé · Togo</p>
                        <div className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed">
                          <p className="text-xs font-semibold text-green-700 uppercase tracking-wider">Poste probable · Défenseur / milieu défensif</p>
                          <p>
                            Un joueur au <strong>tempérament fort, très connu dans la ville</strong>. Très physique, dur sur l'homme, il ne reculait jamais dans les duels.
                          </p>
                          <p>
                            A donné une <strong>identité combative à l'équipe</strong>, incarnant l'esprit « Ogou » : détermination, courage, résistance.
                          </p>
                          <p className="italic text-gray-600 border-l-2 border-green-600 pl-3 text-xs">
                            « Bénéficiaire de l'AG du 27/06/2026 — kit de vivres et enveloppe remis dans la joie. »
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                          <a
                            href="https://wa.me/22872666474?text=Bonjour%20SAFUAYE%2C%20je%20souhaite%20enrichir%20la%20biographie%20de%20Dokou%20KOMLANVI"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-green-700 hover:text-green-900 hover:underline"
                          >
                            ✍️ Enrichir
                          </a>
                          <span className="text-xs text-gray-400">Profil n°04</span>
                        </div>
                      </div>
                    </article>

                    {/* Carte Azandogbe Sessi TOYO */}
                    <article className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow border border-green-100" data-testid="memoire-azandogbe">
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img
                          src="https://customer-assets.emergentagent.com/job_safuaye-togo/artifacts/8jen0no9_IMG-20260629-WA0067.jpg"
                          alt="Portrait d'Azandogbe Sessi TOYO, ancien d'Ifodjè FC d'Atakpamé"
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="inline-block px-3 py-1 bg-green-700 text-white rounded-full text-xs font-bold tracking-wider uppercase shadow-md">⚽ Ancien d'Ifodjè FC</span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-heading text-xl font-bold text-gray-900">Azandogbe Sessi TOYO</h4>
                        <p className="text-sm text-gray-500 mt-1">Atakpamé · Togo</p>
                        <div className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed">
                          <p className="text-xs font-semibold text-green-700 uppercase tracking-wider">Poste probable · Attaquant / ailier</p>
                          <p>
                            Probablement l'un des <strong>joueurs les plus talentueux de cette génération</strong>. Rapide, percutant, capable de dribbler et de créer des occasions — joueur spectaculaire, apprécié du public.
                          </p>
                          <p>
                            A marqué plusieurs buts importants et est souvent considéré comme <strong>l'un des artisans de la montée du club vers le haut niveau national</strong>.
                          </p>
                          <p className="italic text-gray-600 border-l-2 border-green-600 pl-3 text-xs">
                            « Bénéficiaire de l'AG du 27/06/2026 — kit de vivres et enveloppe remis avec respect. »
                          </p>
                        </div>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                          <a
                            href="https://wa.me/22872666474?text=Bonjour%20SAFUAYE%2C%20je%20souhaite%20enrichir%20la%20biographie%20d%27Azandogbe%20Sessi%20TOYO"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-green-700 hover:text-green-900 hover:underline"
                          >
                            ✍️ Enrichir
                          </a>
                          <span className="text-xs text-gray-400">Profil n°05</span>
                        </div>
                      </div>
                    </article>

                    {/* Carte placeholder — invitation à enrichir */}
                    <article className="bg-gradient-to-br from-green-50 to-orange-50 rounded-2xl overflow-hidden shadow-sm border-2 border-dashed border-green-300 flex items-center justify-center p-8">
                      <div className="text-center max-w-xs">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white shadow-md flex items-center justify-center text-4xl">📷</div>
                        <h4 className="font-heading text-xl font-bold text-gray-900 mb-2">D'autres visages à venir</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Aidez-nous à honorer chaque ancien d'Ifodjè FC. Partagez nom, poste, époque et photo via WhatsApp.
                        </p>
                        <a
                          href="https://wa.me/22872666474?text=Bonjour%20SAFUAYE%2C%20je%20souhaite%20partager%20l%27histoire%20d%27un%20ancien%20d%27Ifodj%C3%A8%20FC%20pour%20le%20site"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-700 hover:bg-green-800 text-white rounded-full text-sm font-semibold shadow-md transition-colors"
                          data-testid="contribuer-memoire"
                        >
                          💬 Contribuer
                        </a>
                      </div>
                    </article>
                  </div>

                  <p className="text-center text-xs text-gray-500 mt-6 italic max-w-2xl mx-auto">
                    📝 Les biographies sont enrichies au fil des contributions de la famille SAFUAYE et des proches des anciens. Chaque mémoire compte.
                  </p>
                </div>
              </div>
            )}

            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-green-700 to-green-800 text-white px-8 py-5">
                <h3 className="text-xl font-bold">📋 Ordre du jour</h3>
              </div>
              <ol className="divide-y divide-gray-100">
                {[
                  { t: "Accueil &amp; émargement", s: "Vérification du quorum" },
                  { t: "Message d'ouverture", s: "Par le Président de séance" },
                  { t: "Adoption des Statuts", s: "Lecture &amp; vote (Bulletin n°1)" },
                  { t: "Élection du Bureau International (1)", s: "Président International (UK)" },
                  { t: "Élection du Bureau Exécutif National (5)", s: "Président, Vice-Président, Secrétaire Générale, Trésorière, Trésorier Adjoint" },
                  { t: "Nomination des Membres d'Honneur", s: "Hommage aux soutiens fidèles (voix consultative, sans cotisation)" },
                  { t: "Convention de partenariat UK ⇔ Togo", s: "Lecture &amp; approbation" },
                  { t: "Distribution officielle des dons", s: "6 bénéficiaires — juin 2026" },
                  { t: "Questions diverses", s: "Échanges libres" },
                  { t: "Clôture &amp; cocktail", s: "Photo officielle du Bureau" },
                ].map((item, i) => (
                  <li key={i} className="px-8 py-4 flex gap-4 items-start hover:bg-green-50 transition-colors">
                    <span className="flex-shrink-0 w-9 h-9 rounded-full bg-orange-100 text-orange-700 font-bold flex items-center justify-center text-sm">{i + 1}</span>
                    <div>
                      <p className="font-semibold text-gray-900" dangerouslySetInnerHTML={{ __html: item.t }} />
                      <p className="text-sm text-gray-500" dangerouslySetInnerHTML={{ __html: item.s }} />
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-10">
              <a href="/docs/SAFUAYE_Togo_Depliant_AG_2026.pdf" download data-testid="ag-download-depliant" className="group bg-orange-600 hover:bg-orange-700 text-white rounded-2xl p-6 shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center text-3xl group-hover:scale-110 transition-transform">📄</div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-wider opacity-80 font-bold">Télécharger</p>
                    <p className="text-lg font-bold">Le dépliant officiel</p>
                    <p className="text-xs opacity-90">3 volets · Bulletin d'adhésion inclus</p>
                  </div>
                  <span className="text-2xl opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all">→</span>
                </div>
              </a>
              <a href="/docs/SAFUAYE_Togo_Statuts_2026.pdf" download data-testid="ag-download-statuts" className="group bg-green-700 hover:bg-green-800 text-white rounded-2xl p-6 shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center text-3xl group-hover:scale-110 transition-transform">📜</div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-wider opacity-80 font-bold">Télécharger</p>
                    <p className="text-lg font-bold">Les Statuts SAFUAYE-TOGO</p>
                    <p className="text-xs opacity-90">25 articles · Version officielle 2026</p>
                  </div>
                  <span className="text-2xl opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all">→</span>
                </div>
              </a>
            </div>

            <p className="text-center text-sm text-gray-500 mt-8 italic">
              « Quand on partage, on multiplie » — Simon-Joseph Afantchédé APEDO
            </p>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer id="site-footer" data-testid="footer" className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src={SAFUAYE_ASSETS.logo} alt="SAFUAYE" className="h-16 w-auto brightness-0 invert" />
              <div>
                <p className="text-white/80 text-sm">{t.footer.tagline}</p>
                <p className="text-yellow-400 text-xs mt-1">{t.footer.operations}</p>
              </div>
            </div>
            <p className="text-white/50 text-xs flex items-center justify-center gap-3 flex-wrap">
              <span>© 2025 SAFUAYE. {t.footer.rights}</span>
              <a
                href="?admin"
                data-testid="footer-admin-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 hover:border-amber-400/50 text-white/60 hover:text-amber-300 transition-all text-xs font-medium"
                title="Accès Administration SAFUAYE (PIN requis)"
              >
                <span aria-hidden="true">⚙️</span>
                <span>Administration</span>
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* PIN Prompt Modal */}
      {showPinPrompt && !isAuthenticated && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Settings className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                🔐 Accès Administration
              </h3>
              <p className="text-gray-600 text-sm">
                Entrez le code PIN pour accéder au panneau d'administration
              </p>
            </div>
            <input
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
              placeholder="Code PIN"
              data-testid="admin-pin-input"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-center text-lg font-mono tracking-widest focus:ring-2 focus:ring-purple-500 focus:border-purple-500 mb-4"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPinPrompt(false);
                  setPinInput("");
                  window.history.replaceState({}, '', window.location.pathname);
                }}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handlePinSubmit}
                data-testid="admin-pin-submit"
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold transition-colors"
              >
                Valider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Panel - Only visible after authentication */}
      {showPrivateDocuments && isAuthenticated && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="bg-white rounded-xl p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-3xl font-bold text-gray-900 flex items-center gap-2">
                  <Settings className="w-8 h-8 text-purple-600" />
                  Panneau d'Administration SAFUAYE
                </h2>
                <button 
                  onClick={() => {
                    setShowPrivateDocuments(false);
                    setIsAuthenticated(false);
                    setAdminTab("documents");
                    window.history.replaceState({}, '', window.location.pathname);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>

              {timeoutWarning && (
                <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 p-3 rounded flex items-center justify-between" data-testid="session-timeout-warning">
                  <p className="text-sm text-amber-900">
                    ⏱ Votre session va expirer dans moins de 5 minutes.
                    Bougez la souris ou cliquez pour rester connecté.
                  </p>
                  <button
                    onClick={() => setLastActivity(Date.now())}
                    className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded font-medium"
                  >
                    Rester connecté
                  </button>
                </div>
              )}

              {/* Admin Tabs */}
              <div className="flex gap-2 mb-8 flex-wrap border-b-2 border-gray-200 pb-4">
                <button
                  onClick={() => { setAdminTab("documents"); loadDownloadStats(); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "documents" 
                      ? "bg-purple-600 text-white shadow-lg" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  📄 Documents
                </button>
                <button
                  onClick={() => setAdminTab("trustees")}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "trustees" 
                      ? "bg-purple-600 text-white shadow-lg" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  👥 Trustees
                </button>
                <button
                  onClick={() => { setAdminTab("testimonials"); loadAdminTestimonials(); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "testimonials" 
                      ? "bg-purple-600 text-white shadow-lg" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  💬 Témoignages
                </button>
                <button
                  onClick={() => { setAdminTab("news"); loadAdminNews(); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "news" 
                      ? "bg-purple-600 text-white shadow-lg" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  📰 Actualités
                </button>
                <button
                  onClick={() => { setAdminTab("messages"); loadAdminContacts(); loadAdminMeetingRegs(); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "messages" 
                      ? "bg-purple-600 text-white shadow-lg" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  📬 Messages & Inscriptions
                </button>
                <button
                  onClick={() => { setAdminTab("members"); loadAdminMembers(); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "members" 
                      ? "bg-purple-600 text-white shadow-lg" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  👤 Membres
                </button>
                <button
                  onClick={() => { setAdminTab("hq"); loadHqPhotos(); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "hq"
                      ? "bg-purple-600 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-hq"
                >
                  🏗️ Siège
                </button>
                <button
                  onClick={() => { setAdminTab("grants"); loadGrantDistributions(); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "grants"
                      ? "bg-purple-600 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-grants"
                >
                  💝 Distribution Juin 2026
                </button>
                <button
                  onClick={() => { setAdminTab("impact"); loadImpactCampaigns(false); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "impact"
                      ? "bg-purple-600 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-impact"
                >
                  📊 Impact
                </button>
                <button
                  onClick={() => { setAdminTab("avc"); fetchAdminAvcCases(); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "avc"
                      ? "bg-red-600 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-avc"
                >
                  🩺 Cas AVC
                </button>
                <button
                  onClick={() => { setAdminTab("academy"); fetchAdminAcademy(); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "academy"
                      ? "bg-orange-600 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-academy"
                >
                  ⚽ Pré-inscriptions Académie
                </button>
                <button
                  onClick={() => { setAdminTab("mission"); fetchAdminMissionReports(); }}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "mission"
                      ? "bg-emerald-700 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-mission"
                >
                  ✈️ Note de frais / Mission
                </button>
                <button
                  onClick={() => setAdminTab("togo")}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "togo"
                      ? "bg-red-700 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-togo"
                >
                  🇹🇬 Dossier Ministère
                </button>
                <button
                  onClick={() => setAdminTab("charity_uk")}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "charity_uk"
                      ? "bg-blue-700 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-charity-uk"
                >
                  🇬🇧 Charity UK
                </button>
                <button
                  onClick={() => setAdminTab("signed_docs")}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "signed_docs"
                      ? "bg-emerald-700 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-signed-docs"
                >
                  ✍️ Documents signés
                </button>
                <button
                  onClick={() => setAdminTab("sport_partners")}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "sport_partners"
                      ? "bg-amber-600 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-sport-partners"
                >
                  🏆 Partenaires Sport
                </button>
                <button
                  onClick={() => setAdminTab("certificats_bulk")}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "certificats_bulk"
                      ? "bg-amber-700 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-certificats-bulk"
                >
                  🏅 Certificats en masse
                </button>
                <button
                  onClick={() => setAdminTab("twilio_notifs")}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "twilio_notifs"
                      ? "bg-emerald-700 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-twilio-notifs"
                >
                  📲 Notifications Twilio
                </button>
                <button
                  onClick={() => setAdminTab("ai_assistant")}
                  className={`px-6 py-3 rounded-t-lg font-semibold transition-colors ${
                    adminTab === "ai_assistant"
                      ? "bg-indigo-700 text-white shadow-lg"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  data-testid="admin-tab-ai-assistant"
                >
                  🤖 Assistant IA
                </button>
              </div>
              
              {/* Documents Tab */}
              {adminTab === "documents" && (
                <div>
                  <h3 className="font-heading text-xl font-bold text-gray-900 mb-4">
                    📄 Générer les Documents Officiels
                  </h3>

                  {/* 🎉 Bandeau Dépôt MATDDT réussi */}
                  <div className="mb-6 bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-500 rounded-2xl p-5 shadow-md" data-testid="matddt-deposit-banner">
                    <div className="flex flex-col md:flex-row items-start gap-4">
                      <div className="text-4xl">🎉</div>
                      <div className="flex-1">
                        <h4 className="font-heading text-lg font-bold text-emerald-900">
                          Dossier SAFUAYE-TOGO déposé au MATDDT — Lomé
                        </h4>
                        <p className="text-sm text-emerald-800 mt-1">
                          Le Président AZIAKA KOFFI a officiellement déposé le dossier de déclaration
                          au <b>Ministère de l'Administration Territoriale (MATDDT)</b> à Lomé.
                        </p>
                        <div className="grid md:grid-cols-3 gap-3 mt-3 text-xs">
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <p className="text-emerald-700 font-bold uppercase tracking-wider">📅 Date de dépôt</p>
                            <p className="text-gray-900 font-semibold text-sm mt-1">05 août 2026</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <p className="text-emerald-700 font-bold uppercase tracking-wider">🔢 N° d'enregistrement</p>
                            <p className="text-gray-900 font-semibold text-sm mt-1 font-mono">0564</p>
                            <p className="text-gray-500 text-[10px] italic">Récépissé formel en cours</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-emerald-200">
                            <p className="text-emerald-700 font-bold uppercase tracking-wider">💰 Quittance Trésor</p>
                            <p className="text-gray-900 font-semibold text-sm mt-1 font-mono">N° 0338472</p>
                            <p className="text-gray-500 text-[10px]">30 000 FCFA · Régie MATGLAC</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <a
                            href="/docs/SAFUAYE_TOGO_Recu_Depot_MATDDT_05082026.jpg"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="matddt-receipt-download"
                            className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-lg shadow"
                          >
                            📄 Voir le reçu officiel du dépôt
                          </a>
                          <a
                            href="/docs/SAFUAYE_TOGO_Yas_Numero_Dons.jpg"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="yas-sim-download"
                            className="inline-flex items-center gap-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg shadow"
                          >
                            📱 Voir la SIM Yas (dons)
                          </a>
                          <span className="inline-flex items-center px-3 py-2 bg-white text-emerald-800 text-xs font-mono border border-emerald-300 rounded-lg">
                            📞 Dons mobile money : <b className="ml-1">+228 93 02 03 84</b>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
              {/* UK Documents */}
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>🇬🇧</span> Documents Royaume-Uni
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <a href="/docs/SAFUAYE_Constitution_CIO_EN.pdf" download onClick={() => trackConstitutionDownload('EN', 'pdf')} data-testid="admin-uk-constitution-pdf" className="block text-center bg-green-700 text-white p-4 rounded-lg hover:bg-green-800 transition-colors">
                  <FileText className="w-8 h-8 mx-auto mb-2" />
                  <span className="block font-medium">Constitution CIO</span>
                  <span className="text-sm opacity-80">Statuts officiels (20/10/2026)</span>
                </a>
                <button onClick={generateTrusteeDeclarationPDF} className="bg-blue-700 text-white p-4 rounded-lg hover:bg-blue-800 transition-colors">
                  <Users className="w-8 h-8 mx-auto mb-2" />
                  <span className="block font-medium">Formulaire Trustee</span>
                  <span className="text-sm opacity-80">Déclaration trustee</span>
                </button>
                <button onClick={generateMinutesPDF} className="bg-yellow-600 text-white p-4 rounded-lg hover:bg-yellow-700 transition-colors">
                  <FileText className="w-8 h-8 mx-auto mb-2" />
                  <span className="block font-medium">Procès-verbal UK</span>
                  <span className="text-sm opacity-80">1ère réunion</span>
                </button>
                <button onClick={generateBankLetterPDF} className="bg-purple-700 text-white p-4 rounded-lg hover:bg-purple-800 transition-colors">
                  <Mail className="w-8 h-8 mx-auto mb-2" />
                  <span className="block font-medium">Lettre Banque</span>
                  <span className="text-sm opacity-80">Ouverture compte</span>
                </button>
              </div>

              {/* Constitution CIO officielle - 3 langues */}
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-xl p-6 mb-8">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                  <h3 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
                    <span>📜</span> Constitution CIO officielle
                    <span className="text-xs bg-indigo-600 text-white px-2 py-1 rounded-full">3 langues</span>
                  </h3>
                  <button
                    onClick={loadDownloadStats}
                    data-testid="refresh-download-stats"
                    className="text-xs text-indigo-700 hover:text-indigo-900 underline"
                  >
                    🔄 Actualiser les stats
                  </button>
                </div>

                {/* Stats widget */}
                {downloadStats && (
                  <div className="bg-white rounded-lg p-4 mb-4 border border-indigo-100" data-testid="download-stats-widget">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Téléchargements totaux</p>
                        <p className="text-3xl font-bold text-indigo-700" data-testid="total-downloads">{downloadStats.total}</p>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <div className="text-center">
                          <p className="text-xs text-gray-500">🇬🇧 EN</p>
                          <p className="font-bold text-gray-900" data-testid="downloads-en">{downloadStats.by_lang?.EN || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">🇫🇷 FR</p>
                          <p className="font-bold text-gray-900" data-testid="downloads-fr">{downloadStats.by_lang?.FR || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">🇮🇹 IT</p>
                          <p className="font-bold text-gray-900" data-testid="downloads-it">{downloadStats.by_lang?.IT || 0}</p>
                        </div>
                        <div className="w-px bg-gray-200"></div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">📄 PDF</p>
                          <p className="font-bold text-gray-900">{downloadStats.by_format?.pdf || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">📝 Word</p>
                          <p className="font-bold text-gray-900">{downloadStats.by_format?.docx || 0}</p>
                        </div>
                      </div>
                    </div>
                    {downloadStats.recent && downloadStats.recent.length > 0 && (
                      <details className="mt-3 text-xs">
                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                          Voir les 10 derniers téléchargements
                        </summary>
                        <ul className="mt-2 space-y-1 pl-4">
                          {downloadStats.recent.map((r, i) => (
                            <li key={i} className="text-gray-600">
                              {new Date(r.timestamp).toLocaleString('fr-FR')} — {r.lang} ({r.format.toUpperCase()})
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                <p className="text-sm text-gray-600 mb-4">
                  Statuts de SAFUAYE conformes au modèle <strong>CIO Association</strong> de la Charity Commission UK.
                  Version <strong>anglaise officielle</strong> à déposer. Traductions FR et IT à titre informatif.
                </p>

                <div className="space-y-3">
                  {/* Anglais */}
                  <div className="bg-white rounded-lg p-4 border border-indigo-100">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🇬🇧</span>
                        <div>
                          <p className="font-semibold text-gray-900">English <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">OFFICIAL</span></p>
                          <p className="text-xs text-gray-500">Version à déposer à la Charity Commission</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href="/docs/SAFUAYE_Constitution_CIO_EN.docx" download onClick={() => trackConstitutionDownload('EN', 'docx')} data-testid="download-constitution-en-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> Word
                        </a>
                        <a href="/docs/SAFUAYE_Constitution_CIO_EN.pdf" download onClick={() => trackConstitutionDownload('EN', 'pdf')} data-testid="download-constitution-en-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> PDF
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Français */}
                  <div className="bg-white rounded-lg p-4 border border-indigo-100">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🇫🇷</span>
                        <div>
                          <p className="font-semibold text-gray-900">Français <span className="text-xs bg-gray-500 text-white px-2 py-0.5 rounded">Informatif</span></p>
                          <p className="text-xs text-gray-500">Traduction non officielle</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href="/docs/SAFUAYE_Constitution_CIO_FR.docx" download onClick={() => trackConstitutionDownload('FR', 'docx')} data-testid="download-constitution-fr-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> Word
                        </a>
                        <a href="/docs/SAFUAYE_Constitution_CIO_FR.pdf" download onClick={() => trackConstitutionDownload('FR', 'pdf')} data-testid="download-constitution-fr-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> PDF
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Italien */}
                  <div className="bg-white rounded-lg p-4 border border-indigo-100">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🇮🇹</span>
                        <div>
                          <p className="font-semibold text-gray-900">Italiano <span className="text-xs bg-gray-500 text-white px-2 py-0.5 rounded">Informativo</span></p>
                          <p className="text-xs text-gray-500">Traduzione non ufficiale</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href="/docs/SAFUAYE_Constitution_CIO_IT.docx" download onClick={() => trackConstitutionDownload('IT', 'docx')} data-testid="download-constitution-it-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> Word
                        </a>
                        <a href="/docs/SAFUAYE_Constitution_CIO_IT.pdf" download onClick={() => trackConstitutionDownload('IT', 'pdf')} data-testid="download-constitution-it-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> PDF
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-4 italic">
                  💡 Astuce : Téléchargez la version Word pour la modifier (date, signatures), puis exportez en PDF pour la soumission.
                </p>
              </div>

              {/* Documents de conformité UK */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-6 mb-8">
                <h3 className="font-heading text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span>🛡️</span> Documents de conformité UK
                  <span className="text-xs bg-emerald-600 text-white px-2 py-1 rounded-full">À joindre au dossier Charity Commission</span>
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Documents supplémentaires obligatoires ou fortement recommandés pour l'enregistrement de SAFUAYE en tant que CIO au Royaume-Uni.
                </p>

                <div className="space-y-3">
                  {/* Safeguarding Policy */}
                  <div className="bg-white rounded-lg p-4 border border-emerald-100">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🛡️</span>
                        <div>
                          <p className="font-semibold text-gray-900">
                            Safeguarding Policy
                            <span className="ml-2 text-xs bg-red-600 text-white px-2 py-0.5 rounded font-semibold">OBLIGATOIRE</span>
                          </p>
                          <p className="text-xs text-gray-500">Protection des enfants et adultes vulnérables (orphelins, veuves, âgés d'Atakpamé)</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href="/docs/SAFUAYE_Safeguarding_Policy.docx" download data-testid="download-safeguarding-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> Word
                        </a>
                        <a href="/docs/SAFUAYE_Safeguarding_Policy.pdf" download data-testid="download-safeguarding-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> PDF
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Registration Checklist */}
                  <div className="bg-white rounded-lg p-4 border border-emerald-100">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">📋</span>
                        <div>
                          <p className="font-semibold text-gray-900">
                            UK Registration Checklist
                            <span className="ml-2 text-xs bg-indigo-600 text-white px-2 py-0.5 rounded font-semibold">GUIDE PRATIQUE</span>
                          </p>
                          <p className="text-xs text-gray-500">Checklist complète : 11 sections, tous les documents et infos à préparer pour l'inscription</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href="/docs/SAFUAYE_Registration_Checklist.docx" download data-testid="download-checklist-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> Word
                        </a>
                        <a href="/docs/SAFUAYE_Registration_Checklist.pdf" download data-testid="download-checklist-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> PDF
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Business Plan */}
                  <div className="bg-white rounded-lg p-4 border border-emerald-100">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">📈</span>
                        <div>
                          <p className="font-semibold text-gray-900">
                            Business Plan 2026-2028
                            <span className="ml-2 text-xs bg-amber-600 text-white px-2 py-0.5 rounded font-semibold">RECOMMANDÉ</span>
                          </p>
                          <p className="text-xs text-gray-500">Plan stratégique 3 ans : vision, activités, finances (£5k → £30k), gouvernance, risques. À joindre au dossier.</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href="/docs/SAFUAYE_Business_Plan.docx" download data-testid="download-businessplan-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> Word
                        </a>
                        <a href="/docs/SAFUAYE_Business_Plan.pdf" download data-testid="download-businessplan-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> PDF
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Selection Policy */}
                  <div className="bg-white rounded-lg p-4 border border-emerald-100">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🎯</span>
                        <div>
                          <p className="font-semibold text-gray-900">
                            Beneficiary & Project Selection Policy
                            <span className="ml-2 text-xs bg-amber-600 text-white px-2 py-0.5 rounded font-semibold">RECOMMANDÉ</span>
                          </p>
                          <p className="text-xs text-gray-500">Critères de sélection des bénéficiaires et des projets. À joindre au dossier (page 14).</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href="/docs/SAFUAYE_Selection_Policy.docx" download data-testid="download-selection-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> Word
                        </a>
                        <a href="/docs/SAFUAYE_Selection_Policy.pdf" download data-testid="download-selection-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> PDF
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Grant Making Policy */}
                  <div className="bg-white rounded-lg p-4 border border-emerald-100">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">💰</span>
                        <div>
                          <p className="font-semibold text-gray-900">
                            Grant Making Policy
                            <span className="ml-2 text-xs bg-amber-600 text-white px-2 py-0.5 rounded font-semibold">RECOMMANDÉ</span>
                          </p>
                          <p className="text-xs text-gray-500">Politique d'attribution des subventions aux individus et aux organisations. À joindre au dossier (page 15).</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href="/docs/SAFUAYE_Grant_Making_Policy.docx" download data-testid="download-grant-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> Word
                        </a>
                        <a href="/docs/SAFUAYE_Grant_Making_Policy.pdf" download data-testid="download-grant-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> PDF
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* First Trustee Meeting Minutes */}
                  <div className="bg-white rounded-lg p-4 border border-emerald-100">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">📝</span>
                        <div>
                          <p className="font-semibold text-gray-900">
                            Minutes - First Trustee Meeting (19/04/2026)
                            <span className="ml-2 text-xs bg-emerald-600 text-white px-2 py-0.5 rounded font-semibold">CLEF</span>
                          </p>
                          <p className="text-xs text-gray-500">Procès-verbal de la réunion constitutive : élection des officiers, adoption constitution + 6 policies + business plan</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href="/docs/SAFUAYE_First_Trustee_Meeting_Minutes.docx" download data-testid="download-minutes-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> Word
                        </a>
                        <a href="/docs/SAFUAYE_First_Trustee_Meeting_Minutes.pdf" download data-testid="download-minutes-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                          <FileText className="w-4 h-4" /> PDF
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 bg-amber-50 border-l-4 border-amber-500 p-3 rounded">
                  <p className="text-xs text-amber-900">
                    ⚠️ <strong>Important :</strong> La Safeguarding Policy doit être <strong>signée par les 3 trustees</strong> AVANT la soumission à la Charity Commission. Elle doit être revue au moins une fois par an.
                  </p>
                </div>
              </div>

              {/* Charity Commission Trustees Note (May 2026) */}
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>📨</span> Charity Commission UK — Actualités
              </h3>
              <CharityCommissionResponse />
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
                <p className="text-sm text-gray-700 mb-3">
                  Message reçu le <strong>01/05/2026</strong> : dossier SAFUAYE en file d'attente (réf. <strong>5289867 / CRM26:001907414</strong>). Délai moyen ≈ 23 semaines.
                </p>
                <div className="bg-white rounded-lg p-4 border border-blue-100">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📋</span>
                      <div>
                        <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                          Note d'information aux trustees (1 page)
                          <span className="text-xs bg-blue-700 text-white px-2 py-0.5 rounded font-semibold">À SIGNER</span>
                        </p>
                        <p className="text-xs text-gray-500">Résumé officiel du statut + plan d'action A→G à valider par les 3 trustees</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a href="/docs/SAFUAYE_Trustees_Note_CharityCommission_May2026.docx" download data-testid="download-trustees-note-cc-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        <FileText className="w-4 h-4" /> Word
                      </a>
                      <a href="/docs/SAFUAYE_Trustees_Note_CharityCommission_May2026.pdf" download data-testid="download-trustees-note-cc-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        <FileText className="w-4 h-4" /> PDF
                      </a>
                    </div>
                  </div>
                </div>
                <div className="mt-3 bg-emerald-50 border-l-4 border-emerald-500 p-3 rounded">
                  <p className="text-xs text-emerald-900">
                    ✅ <strong>Points du plan d'action :</strong> (A) archiver message · (B) ouvrir compte bancaire UK · (C) démarrer Gift Aid HMRC · (D) distribution juin · (E) dépôt Préfecture Ogou · (F) publier sur page Impact · (G) rappel 1er octobre 2026.
                  </p>
                </div>
              </div>

              {/* Membership Form */}
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>📝</span> Fiche d'adhésion (publique)
              </h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-8">
                <p className="text-sm text-gray-700 mb-3">
                  Fiche bilingue <strong>FR + EN</strong> à imprimer et faire signer par tout nouveau membre. Disponible aussi en téléchargement direct depuis la section <em>Contact</em> du site public.
                </p>
                <div className="bg-white rounded-lg p-4 border border-emerald-100">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🪪</span>
                      <div>
                        <p className="font-semibold text-gray-900">
                          SAFUAYE Membership Form (FR + EN)
                          <span className="ml-2 text-xs bg-emerald-600 text-white px-2 py-0.5 rounded font-semibold">PUBLIC</span>
                        </p>
                        <p className="text-xs text-gray-500">9 sections : identité · contact · type d'adhésion · motivation · engagements · RGPD · signature · cadre SAFUAYE</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a href="/docs/SAFUAYE_Membership_Form.docx" download data-testid="admin-download-membership-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        <FileText className="w-4 h-4" /> Word
                      </a>
                      <a href="/docs/SAFUAYE_Membership_Form.pdf" download data-testid="admin-download-membership-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        <FileText className="w-4 h-4" /> PDF
                      </a>
                    </div>
                  </div>
                </div>
                <div className="mt-3 bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                  <p className="text-xs text-blue-900">
                    💡 Lorsqu'une fiche complétée vous arrive par email, créez le membre dans l'onglet <strong>👤 Membres</strong> et archivez le PDF signé dans Google Drive (dossier « Adhésions »).
                  </p>
                </div>
              </div>

              {/* Bank Account Opening Packs */}
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>🏦</span> Ouverture de comptes bancaires / Bank Account Opening Packs
              </h3>
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 mb-8">
                <p className="text-sm text-gray-700 mb-4">
                  <strong>Trois dossiers prêts à déposer</strong> auprès des banques recommandées pour SAFUAYE.
                  Chaque pack contient : lettre de demande, résolution du conseil, déclaration des trustees, désignation des signataires, et checklist complète des pièces à fournir.
                </p>

                {/* Wise refusal reply email — alert banner */}
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📧</span>
                    <div className="flex-1">
                      <p className="font-semibold text-red-900">
                        Réponse à Wise (refus d'onboarding) <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded font-semibold ml-1">À ENVOYER EN PRIORITÉ</span>
                      </p>
                      <p className="text-xs text-red-700">Email bilingue EN/FR demandant la justification précise du refus + maintien du dossier ouvert</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-700 mb-3">
                    💡 Wise est obligé de motiver son refus. Cette demande structurée vous permet d'identifier précisément le blocage (Charity Commission en cours ? Togo ? Trustee non-UK ?) et de re-déposer une fois le frein levé.
                  </p>
                  <div className="flex gap-2">
                    <a href="/docs/SAFUAYE_Wise_Reply_Email.docx" download data-testid="download-wise-reply-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                      <FileText className="w-3 h-3" /> Word (modifiable)
                    </a>
                    <a href="/docs/SAFUAYE_Wise_Reply_Email.pdf" download data-testid="download-wise-reply-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                      <FileText className="w-3 h-3" /> PDF
                    </a>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  {/* Revolut Business — alternative IMMEDIATE après refus Wise */}
                  <div className="bg-white rounded-lg p-4 border-2 border-purple-400">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🚀</span>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          Revolut Business <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded font-semibold ml-1">PLAN B IMMÉDIAT</span>
                        </p>
                        <p className="text-xs text-gray-500">Multi-devises GBP/EUR/CHF/USD</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      ✅ Alternative à Wise. Accepte les CIO en cours d'enregistrement. Inclut un Plan B (Tide, Wave Direct, CAF Bank différé) si refus.
                    </p>
                    <div className="flex gap-2">
                      <a href="/docs/SAFUAYE_Bank_Pack_Revolut.docx" download data-testid="download-revolut-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                        <FileText className="w-3 h-3" /> Word
                      </a>
                      <a href="/docs/SAFUAYE_Bank_Pack_Revolut.pdf" download data-testid="download-revolut-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                        <FileText className="w-3 h-3" /> PDF
                      </a>
                    </div>
                  </div>
                  {/* Wise Business */}
                  <div className="bg-white rounded-lg p-4 border-2 border-orange-300 opacity-90">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">💸</span>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          Wise Business <span className="text-xs bg-gray-500 text-white px-2 py-0.5 rounded font-semibold ml-1">REFUSÉ — RE-APPLY PLUS TARD</span>
                        </p>
                        <p className="text-xs text-gray-500">Multi-devises GBP/EUR/CHF/XOF — pour Togo</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      ⏸️ Dossier conservé. Re-déposer après réception du n° Charity Commission ou après réponse de Wise sur les raisons exactes du refus.
                    </p>
                    <div className="flex gap-2">
                      <a href="/docs/SAFUAYE_Bank_Pack_Wise.docx" download data-testid="download-wise-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                        <FileText className="w-3 h-3" /> Word
                      </a>
                      <a href="/docs/SAFUAYE_Bank_Pack_Wise.pdf" download data-testid="download-wise-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                        <FileText className="w-3 h-3" /> PDF
                      </a>
                    </div>
                  </div>
                  {/* CAF Bank — après n° Charity */}
                  <div className="bg-white rounded-lg p-4 border-2 border-teal-300">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🏛️</span>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          CAF Bank (UK) <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded font-semibold ml-1">APRÈS N° CHARITY</span>
                        </p>
                        <p className="text-xs text-gray-500">Compte charity gratuit · Gift Aid intégré</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      ⏳ À déposer <strong>après réception du n° Charity Commission</strong> (~novembre 2026). Banque n°1 dédiée charities UK : 0 frais, équipe associations dédiée, support Gift Aid HMRC natif.
                    </p>
                    <div className="flex gap-2">
                      <a href="/docs/SAFUAYE_Bank_Pack_CAF.docx" download data-testid="download-caf-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                        <FileText className="w-3 h-3" /> Word
                      </a>
                      <a href="/docs/SAFUAYE_Bank_Pack_CAF.pdf" download data-testid="download-caf-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                        <FileText className="w-3 h-3" /> PDF
                      </a>
                    </div>
                  </div>
                </div>
                <div className="mt-4 bg-amber-50 border-l-4 border-amber-500 p-3 rounded">
                  <p className="text-xs text-amber-900">
                    💡 <strong>Stratégie actuelle :</strong> 1) Envoyer l'email de réponse à Wise pour comprendre le refus. 2) Tenter <strong>Revolut Business</strong> en parallèle (mêmes documents + Plan B inclus). 3) À réception du n° Charity Commission (~Nov 2026) → ouvrir <strong>CAF Bank</strong> comme compte principal.
                  </p>
                </div>

                {/* Beneficial Owners Register - KYC document */}
                <div className="mt-4 bg-white rounded-lg p-4 border-2 border-dashed border-purple-300">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">👥</span>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">
                        Register of Beneficial Owners <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded font-semibold ml-1">KYC BANCAIRE</span>
                      </p>
                      <p className="text-xs text-gray-500">Liste officielle des 3 trustees (Kokou Ognakotan, Yao Blaise, Ablavi) pour Wise / CAF Bank</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">
                    📋 Document requis par Wise (étape « Beneficial owners document ») et toute banque qui ouvre un compte charity. À signer par le Chair avant upload.
                  </p>
                  <a href="/docs/SAFUAYE_Beneficial_Owners_Register.pdf" download data-testid="download-beneficial-owners" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors">
                    <FileText className="w-3 h-3" /> Télécharger le PDF
                  </a>
                </div>
              </div>

              {/* HQ Renovation Toolkit */}
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>🏗️</span> Kit Rénovation Siège (Atakpamé) / HQ Renovation Toolkit
              </h3>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 mb-8">
                <p className="text-sm text-gray-700 mb-4">
                  <strong>Document unique bilingue FR/EN</strong> regroupant 5 fiches à imprimer et faire signer sur le chantier de rénovation du siège à Atakpamé :
                  contrat de travail simplifié, reçu de paiement, reçu d'achat de matériaux, rapport d'étape et registre global du chantier.
                  Conforme aux exigences de traçabilité financière de la Charity Commission UK.
                </p>
                <div className="bg-white rounded-lg p-4 border border-orange-100">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🧾</span>
                      <div>
                        <p className="font-semibold text-gray-900">
                          SAFUAYE Renovation Toolkit (FR + EN)
                          <span className="ml-2 text-xs bg-orange-600 text-white px-2 py-0.5 rounded font-semibold">NOUVEAU</span>
                        </p>
                        <p className="text-xs text-gray-500">5 fiches : Contrat ouvrier · Reçu paiement · Achat matériaux · Rapport d'étape · Registre global</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a href="/docs/SAFUAYE_Renovation_Toolkit.docx" download data-testid="download-renovation-word" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        <FileText className="w-4 h-4" /> Word
                      </a>
                      <a href="/docs/SAFUAYE_Renovation_Toolkit.pdf" download data-testid="download-renovation-pdf" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                        <FileText className="w-4 h-4" /> PDF
                      </a>
                    </div>
                  </div>
                </div>
                <div className="mt-4 bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                  <p className="text-xs text-blue-900">
                    💡 <strong>Règles d'or :</strong> toute dépense &gt; 50 000 FCFA (≈£60) doit être pré-approuvée par 2 trustees. Photos avant/après obligatoires. Rapport mensuel au Board UK avant le 5 du mois.
                  </p>
                </div>
              </div>

              {/* SAFUAYE Football Academy Pack */}
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>⚽</span> Académie de Football SAFUAYE — Kit complet
              </h3>
              <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-8">
                <p className="text-sm text-gray-700 mb-4">
                  <strong>Kit complet de lancement</strong> de l'Académie de Football SAFUAYE (60 enfants U6–U13, gratuite, à Atakpamé). 7 documents officiels : safeguarding renforcé enfants, statuts, plan budgétaire An 1 chiffré (≈£9 400), formulaire d'inscription enfant, contrat coach + code de conduite, dossier sponsoring (FR + EN), checklist démarches Préfecture Ogou + FTF.
                </p>
                <div className="space-y-3">
                  {[
                    { id: "safeguarding-children", file: "SAFUAYE_Academy_Safeguarding_Children", title: "1. Safeguarding Children Policy", emoji: "🛡️", badge: "URGENT", badgeColor: "bg-red-600", desc: "Politique de Protection de l'Enfance — exigée par la Charity Commission UK. À signer par les 3 trustees." },
                    { id: "statutes", file: "SAFUAYE_Academy_Statutes_Internal_Rules", title: "2. Statuts & Règlement Intérieur", emoji: "📋", badge: "FONDATEUR", badgeColor: "bg-blue-600", desc: "Statuts de la section sportive Académie. À adopter par PV du Board." },
                    { id: "year1-plan", file: "SAFUAYE_Academy_Year1_Plan_Budget", title: "3. Plan Année 1 + Budget", emoji: "📊", badge: "STRATÉGIQUE", badgeColor: "bg-emerald-600", desc: "Plan opérationnel + budget chiffré 7,8 M FCFA (≈£9 400) avec sources de financement." },
                    { id: "registration", file: "SAFUAYE_Academy_Child_Registration_Form", title: "4. Formulaire d'inscription enfant", emoji: "📝", badge: "OPÉRATIONNEL", badgeColor: "bg-purple-600", desc: "Formulaire complet : identité, parents, fiche médicale, autorisations parentales." },
                    { id: "coach-agreement", file: "SAFUAYE_Academy_Coach_Agreement_CodeOfConduct", title: "5. Contrat Coach + Code de Conduite", emoji: "👔", badge: "RH", badgeColor: "bg-orange-600", desc: "Contrat bénévole + code de conduite à signer avant prise de fonction." },
                    { id: "sponsorship", file: "SAFUAYE_Academy_Sponsorship_Pack", title: "6. Dossier de Sponsoring (FR + EN)", emoji: "🤝", badge: "FUNDRAISING", badgeColor: "bg-yellow-600", desc: "Plaquette de partenariat 5 niveaux (£100 → £7 500+) — bilingue Togo + diaspora." },
                    { id: "checklist", file: "SAFUAYE_Academy_Registration_Checklist", title: "7. Checklist Préfecture + FTF", emoji: "✅", badge: "ADMIN", badgeColor: "bg-indigo-600", desc: "Démarches pas-à-pas : Phase 1 Préfecture Ogou, Phase 2 affiliation FTF (timeline + RACI)." },
                    { id: "gala-budget", file: "SAFUAYE_Budget_Gala_Atakpame", title: "8. Budget Prévisionnel Gala (Atakpamé 2026)", emoji: "🏆", badge: "NOUVEAU", badgeColor: "bg-pink-600", desc: "Tournoi inter-écoles 8 équipes (Inspections Ogou Sud + Nord). 4 semaines d'entraînements à partir du 13 juil. 2026. 3 scénarios (économique/médian/premium), plan de sponsoring 6 paliers, calendrier, approbation Board." },
                  ].map((doc) => (
                    <div key={doc.id} className="bg-white rounded-lg p-4 border border-green-100">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-2xl">{doc.emoji}</span>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                              {doc.title}
                              <span className={`text-xs ${doc.badgeColor} text-white px-2 py-0.5 rounded font-semibold`}>{doc.badge}</span>
                            </p>
                            <p className="text-xs text-gray-500">{doc.desc}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <a href={`/docs/${doc.file}.docx`} download data-testid={`download-academy-${doc.id}-word`} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                            <FileText className="w-4 h-4" /> Word
                          </a>
                          <a href={`/docs/${doc.file}.pdf`} download data-testid={`download-academy-${doc.id}-pdf`} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                            <FileText className="w-4 h-4" /> PDF
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 bg-red-50 border-l-4 border-red-500 p-3 rounded">
                  <p className="text-xs text-red-900">
                    🛡️ <strong>Priorité absolue :</strong> faire signer la <strong>Safeguarding Children Policy</strong> (#1) par les 3 trustees + DSL nommé (APEDO ABLAVI) AVANT tout démarrage opérationnel. C'est la condition n°1 demandée par la Charity Commission UK pour les associations qui travaillent avec des enfants.
                  </p>
                </div>

                {/* Gala Visuals Pack — generated via Gemini Nano Banana */}
                <div className="mt-4 bg-gradient-to-br from-pink-50 to-orange-50 border-2 border-pink-300 rounded-xl p-5">
                  <h4 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                    <span className="text-2xl">🎨</span>
                    Pack Visuels Gala 2026 — T-shirts &amp; Affiches
                    <span className="text-xs bg-pink-600 text-white px-2 py-0.5 rounded font-semibold">NOUVEAU</span>
                  </h4>
                  <p className="text-xs text-gray-600 mb-4">
                    Compositions générées en haute résolution, prêtes à imprimer ou à publier sur les réseaux sociaux. Téléchargez et apportez chez l'imprimeur d'Atakpamé.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {[
                      { f: "gala_tshirt_front.png", lbl: "T-shirt FACE", desc: "Logo + slogan + ballon" },
                      { f: "gala_tshirt_back.png", lbl: "T-shirt DOS", desc: "N° + emplacements sponsors" },
                      { f: "gala_poster_a3.png", lbl: "Affiche A3", desc: "Impression écoles, marchés" },
                      { f: "gala_social_square.png", lbl: "Carré 1080×1080", desc: "Instagram / WhatsApp" },
                      { f: "gala_social_landscape.png", lbl: "Bandeau 1200×630", desc: "Facebook / X / LinkedIn" },
                    ].map(v => (
                      <a
                        key={v.f}
                        href={`/docs/gala_assets/${v.f}`}
                        download
                        data-testid={`download-gala-${v.f.replace('.png', '')}`}
                        className="group flex flex-col bg-white rounded-lg overflow-hidden border border-pink-200 hover:border-pink-500 hover:shadow-lg transition-all"
                      >
                        <div className="aspect-square bg-gray-100 overflow-hidden">
                          <img
                            src={`/docs/gala_assets/${v.f}`}
                            alt={v.lbl}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-2">
                          <p className="text-xs font-bold text-gray-900">{v.lbl}</p>
                          <p className="text-[10px] text-gray-500 leading-tight">{v.desc}</p>
                          <p className="text-[10px] text-pink-600 font-semibold mt-1">⬇ Télécharger PNG</p>
                        </div>
                      </a>
                    ))}
                  </div>
                  <div className="mt-3 bg-pink-100 border-l-4 border-pink-500 p-3 rounded">
                    <p className="text-xs text-pink-900">
                      💡 <strong>Pour l'imprimeur d'Atakpamé :</strong> les T-shirts sont en flat design 2-3 couleurs (idéal sérigraphie). L'affiche A3 imprime en 300 DPI sur papier 170g/m². Les visuels sociaux peuvent être postés tels quels sur WhatsApp Status, Facebook et Instagram. Tous les visuels suivent le DNA SAFUAYE (vert/orange/ivoire).
                    </p>
                  </div>
                </div>

                <div className="mt-2 bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                  <p className="text-xs text-blue-900">
                    💡 <strong>Roadmap suggérée :</strong> Avril-Mai 2026 → Adoption docs #1, #2, #5 par le Board • Juin 2026 → Dépôt dossier Préfecture Ogou (doc #7 Phase 1) • Juillet 2026 → Recrutement coachs (doc #5) + démarchage sponsors (doc #6) • Août 2026 → Inscriptions enfants (doc #4) • <strong>1ᵉʳ Septembre 2026 → Coup d'envoi</strong>.
                  </p>
                </div>
              </div>

              {/* Eau & Élan Project Pack */}
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>💧</span> Projet « Eau & Élan » — Automne 2026
              </h3>
              <div className="bg-gradient-to-br from-blue-50 to-emerald-50 border border-blue-200 rounded-xl p-5 mb-8">
                <p className="text-sm text-gray-700 mb-4">
                  <strong>Projet à double volet</strong> : forage d'eau à <strong>Zanghéra (Lomé)</strong> gestion communautaire + <strong>10 micro-subventions</strong> de 200 000 FCFA (restauration, coiffure, esthétique) à Lomé & Atakpamé. Budget total <strong>≈ 2,8 M FCFA / £3 355</strong>. Execution visée : sept-nov 2026.
                </p>
                <div className="space-y-2">
                  {[
                    { id: "plan", num: "1", label: "Plan détaillé du projet", desc: "11 sections · objectifs · budget · financement · risques", badge: "FONDATEUR", color: "bg-blue-700" },
                    { id: "fund", num: "2", label: "Fundraising Pack (FR + EN)", desc: "Appel à dons · 6 niveaux de contribution (£25 → £1 000+)", badge: "FUNDRAISING", color: "bg-emerald-700" },
                    { id: "app", num: "3", label: "Formulaire de candidature", desc: "Pour les 10 candidatures ouvertes (Lomé + Atakpamé)", badge: "OPÉRATIONNEL", color: "bg-orange-600" },
                    { id: "contract", num: "4", label: "Contrat de micro-subvention", desc: "À signer par chacun des 10 bénéficiaires", badge: "JURIDIQUE", color: "bg-orange-700" },
                    { id: "grid", num: "5", label: "Grille d'évaluation", desc: "100 points · seuils de décision · conflits d'intérêts", badge: "SÉLECTION", color: "bg-indigo-700" },
                    { id: "borehole", num: "6", label: "Convention de forage", desc: "Tripartite SAFUAYE + ECO.BA + propriétaire du terrain", badge: "TRAVAUX", color: "bg-blue-600" },
                    { id: "community", num: "7", label: "Accord communautaire de gestion", desc: "Comité de Gestion de l'Eau · entretien · règles de civilité", badge: "GOUVERNANCE", color: "bg-blue-600" },
                    { id: "report", num: "8", label: "Rapport final (template)", desc: "À remplir avant le 15/12/2026 pour le Board UK", badge: "CLÔTURE", color: "bg-slate-700" },
                  ].map(d => (
                    <div key={d.id} className="bg-white rounded-lg p-3 border border-blue-100">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-sm">{d.num}</span>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2 text-sm">
                              {d.label}
                              <span className={`text-xs ${d.color} text-white px-2 py-0.5 rounded font-semibold`}>{d.badge}</span>
                            </p>
                            <p className="text-xs text-gray-500">{d.desc}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <a href={`/docs/SAFUAYE_Eau_Elan_0${d.num}_${{plan:"Plan",fund:"Fundraising_Pack",app:"Application_Form",contract:"Grant_Contract",grid:"Scoring_Grid",borehole:"Borehole_Convention",community:"Community_Agreement",report:"Final_Report"}[d.id]}.docx`} download data-testid={`eau-elan-${d.id}-word`} className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded font-medium">Word</a>
                          <a href={`/docs/SAFUAYE_Eau_Elan_0${d.num}_${{plan:"Plan",fund:"Fundraising_Pack",app:"Application_Form",contract:"Grant_Contract",grid:"Scoring_Grid",borehole:"Borehole_Convention",community:"Community_Agreement",report:"Final_Report"}[d.id]}.pdf`} download data-testid={`eau-elan-${d.id}-pdf`} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded font-medium">PDF</a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 bg-emerald-50 border-l-4 border-emerald-500 p-3 rounded text-xs text-emerald-900">
                  <strong>🎯 Go / No-go :</strong> projet lancé dès que <strong>70 % du budget (£2 349)</strong> est sécurisé, au plus tard le 15/09/2026. En deçà : plan B à 6 subventions, forage Zanghéra prioritaire.
                </div>
                <div className="mt-2 bg-blue-50 border-l-4 border-blue-500 p-3 rounded text-xs text-blue-900">
                  <strong>📅 Roadmap :</strong> juil-août validation Board + campagne financement · 1-30 sept appel à candidatures · 1-15 oct sélection · 15-30 oct forage · 16-30 nov remise subventions · 1-15 déc rapport & publication page Impact.
                </div>
              </div>

              {/* Togo Documents */}
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>🇹🇬</span> Documents Togo
              </h3>

              {/* 💰 FINANCES SAFUAYE — Tableau de bord général */}
              <FinancesAdminPanel API={API} />

              {/* 🛡️ Checklist Prérequis Gala + 📮 Diffusion officielle documents */}
              <PrerequisitesChecklistPanel API={API} />
              <BroadcastAdminPanel API={API} />

              {/* 👥 Registre des Bénéficiaires SAFUAYE-TOGO — Rencontre 12 sept 2026 */}
              <BeneficiariesAdminPanel API={API} />

              {/* 🎒 Programme Parrainage Scolaire — Public + Admin */}
              <SponsorshipAdminPanel API={API} />

              {/* 🎽 Kit Académie Football & Merchandising 2026-2027 */}
              <div className="bg-gradient-to-br from-fuchsia-50 via-amber-50 to-emerald-50 border-2 border-fuchsia-300 rounded-2xl p-5 mb-6" data-testid="kit-academie-merch-panel">
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-3xl">🎽</span>
                  <div className="flex-1">
                    <h4 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                      Kit Académie Football & Merchandising
                      <span className="text-xs bg-fuchsia-700 text-white px-2 py-0.5 rounded font-semibold">🆕 Fév. 2026</span>
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Feuille de route officielle U8-U13 · Bâche 3m×1m pour le siège d'Atchrimi-Komé · 3 designs t-shirts (Bureau / Académie / Gala) · Brief imprimeur avec spécifications techniques (Kpogan Atakpamé).
                    </p>
                  </div>
                </div>

                {/* Feuille de route académie */}
                <div className="bg-white rounded-lg p-4 border-2 border-emerald-300 mb-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                        📋 Feuille de Route Académie 2026-2027
                        <span className="text-xs bg-emerald-700 text-white px-2 py-0.5 rounded font-semibold">14 pages · P0</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Roadmap officielle U8-U13 · Entraînements DANS les écoles après cours · Équipe 1 Coach Principal + 4 Coaches d'école + 4 Assistants Terrain · Gala 8-9 septembre 2026 · Budget 2,04 M FCFA.
                      </p>
                    </div>
                    <div className="flex flex-col md:flex-row items-end gap-2">
                      <a
                        href="/docs/SAFUAYE_FeuilleDeRoute_Academie_2026-2027.pdf"
                        download
                        data-testid="download-feuille-de-route-academie"
                        className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
                      >
                        <FileText className="w-4 h-4" /> FR · PDF (14 p.)
                      </a>
                      <a
                        href="/docs/SAFUAYE_FeuilleDeRoute_Academie_2026-2027_EN.pdf"
                        download
                        data-testid="download-feuille-de-route-academie-en"
                        className="inline-flex items-center gap-2 bg-fuchsia-700 hover:bg-fuchsia-800 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
                        title="Version anglaise traduite par Gemini IA · pour la Charity Commission UK"
                      >
                        ✨ 🇬🇧 EN
                      </a>
                    </div>
                  </div>
                </div>

                {/* Fiche recrutement coaches */}
                <div className="bg-white rounded-lg p-4 border-2 border-blue-300 mb-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                        📣 Fiche Recrutement — Coaches &amp; Assistants Terrain
                        <span className="text-xs bg-blue-700 text-white px-2 py-0.5 rounded font-semibold">🆕 8 postes</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Appel à candidatures officiel : 4 Coaches d'école + 4 Assistants Terrain · Missions · Profil · Indemnités (12 000 / 6 000 FCFA/mois) · Charte d'engagement moral (9 points) · Formulaire détachable prêt à remplir.
                      </p>
                    </div>
                    <a
                      href="/docs/SAFUAYE_Fiche_Recrutement_Coaches.pdf"
                      download
                      data-testid="download-fiche-recrutement-coaches"
                      className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
                    >
                      <FileText className="w-4 h-4" /> PDF
                    </a>
                  </div>
                </div>

                {/* Convention type école */}
                <div className="bg-white rounded-lg p-4 border-2 border-cyan-300 mb-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                        🤝 Convention type — SAFUAYE ⟷ École partenaire
                        <span className="text-xs bg-cyan-700 text-white px-2 py-0.5 rounded font-semibold">🆕 Prêt à signer</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        12 articles · Mise à disposition cour/terrain 2×/sem après cours 16h-18h · Responsabilités partagées · Assurance RC · Safeguarding · Gratuité · Durée 9 mois renouvelable · Signatures Directeur + Président.
                      </p>
                    </div>
                    <a
                      href="/docs/SAFUAYE_Convention_Type_Ecole.pdf"
                      download
                      data-testid="download-convention-type-ecole"
                      className="inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-800 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
                    >
                      <FileText className="w-4 h-4" /> PDF
                    </a>
                  </div>
                </div>

                {/* PV Réunion Gala 18 août 2026 */}
                <div className="bg-white rounded-lg p-4 border-2 border-orange-300 mb-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                        📝 PV Réunion préparatoire du Gala — 18 août 2026
                        <span className="text-xs bg-orange-600 text-white px-2 py-0.5 rounded font-semibold">🆕 Photos</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Comité d&apos;organisation (Anagonou, Midahin, Assion, Ayetan) · 4 écoles retenues (Migros May · Catho Lom Nava · EPP Agbofon · EPP Lom Nava) · Période : 2 dernières semaines de septembre 2026 · Plan d&apos;actions + signatures · 3 photos de la séance intégrées.
                      </p>
                    </div>
                    <a
                      href="/docs/SAFUAYE_PV_Reunion_Gala_2026-08-18.pdf"
                      download
                      data-testid="download-pv-reunion-gala"
                      className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
                    >
                      <FileText className="w-4 h-4" /> PDF
                    </a>
                  </div>
                </div>

                {/* Budget révisé Gala 2026 — approuvé */}
                <div className="bg-white rounded-lg p-4 border-2 border-yellow-400 mb-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                        💰 Budget Gala 2026 — <span className="text-emerald-700">approuvé Présidents</span>
                        <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded font-semibold">🆕 292 000 FCFA</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Version révisée par les Présidents International &amp; National · Ligne <span className="line-through text-red-600">Réception 60 000 FCFA</span> retirée · Dates <b>26-27 septembre 2026</b> · 4 écoles : EPP Agbo Fon · EPP Petite Mission · Camp Lom-Nava · Micles-Ray · Appel aux Membres d&apos;honneur pour dotations Mairie + agents économiques.
                      </p>
                    </div>
                    <a
                      href="/docs/SAFUAYE_Budget_Gala_2026_Revise.pdf"
                      download
                      data-testid="download-budget-gala-revise"
                      className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-4 py-2 rounded-lg text-sm font-bold shadow"
                    >
                      <FileText className="w-4 h-4" /> PDF
                    </a>
                  </div>
                </div>

                {/* Opérations Académie — pré-remplissage, broadcast, candidatures */}
                <div className="mt-4" data-testid="academy-ops-wrapper">
                  <AcademyOpsPanel API={API} pin="SAFUAYE2024" />
                </div>

                {/* Bâche officielle du siège */}
                <div className="bg-white rounded-lg p-4 border-2 border-amber-300 mb-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <img
                      src="/docs/SAFUAYE_Bache_Siege_Atchrimi_Kome_300x100.png"
                      alt="Bâche officielle SAFUAYE — Siège Atchrimi-Komé"
                      className="w-32 h-auto object-contain border border-amber-200 rounded"
                      data-testid="bache-siege-preview"
                    />
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                        🎪 Bâche Siège Atchrimi-Komé (300 × 100 cm HQ)
                        <span className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded font-semibold">🆕 Impression</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Nouvelle bâche officielle 3 m × 1 m · Fond navy SAFUAYE · Logo + « SAFUAYE-TOGO · Atchrimi-Komé · Atakpamé » · Devise « Héritage · Solidarité · Espoir » · WhatsApp + safuaye.org · Prête à imprimer chez Kpogan (Atakpamé).
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <button
                        onClick={() => setBachePreviewOpen(true)}
                        data-testid="open-bache-preview-3d"
                        className="inline-flex items-center gap-2 bg-fuchsia-700 hover:bg-fuchsia-800 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
                        title="Visualiser la bâche installée sur les murs du siège avant impression"
                      >
                        🎨 Aperçu mural 3D
                      </button>
                      <a
                        href="/docs/SAFUAYE_Bache_Siege_Atchrimi_Kome_300x100.png"
                        download
                        data-testid="download-bache-siege"
                        className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
                      >
                        <FileText className="w-4 h-4" /> PNG HQ
                      </a>
                    </div>
                  </div>
                </div>

                {/* Bâche Académie de Football */}
                <div className="bg-white rounded-lg p-4 border-2 border-emerald-300 mb-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <img
                      src="/docs/SAFUAYE_Bache_Academie_Football_300x100.png"
                      alt="Bâche Académie de Football SAFUAYE"
                      className="w-32 h-auto object-contain border border-emerald-200 rounded"
                      data-testid="bache-academie-preview"
                    />
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                        ⚽ Bâche Académie de Football (300 × 100 cm)
                        <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded font-semibold">🆕 U8-U13</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Fond vert + accents jaunes · &laquo; ACADÉMIE DE FOOTBALL SAFUAYE &raquo; massif · U8·U10·U12 · GRATUIT après les cours 16h-18h · QR d&apos;inscription (tracking bache-academie) · À installer devant chaque école partenaire.
                      </p>
                    </div>
                    <a
                      href="/docs/SAFUAYE_Bache_Academie_Football_300x100.png"
                      download
                      data-testid="download-bache-academie"
                      className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
                    >
                      <FileText className="w-4 h-4" /> PNG HQ
                    </a>
                  </div>
                </div>

                {/* Bâche Hommage Ifodjè FC */}
                <div className="bg-white rounded-lg p-4 border-2 border-red-300 mb-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <img
                      src="/docs/SAFUAYE_Bache_Ifodje_Anciens_300x100.png"
                      alt="Bâche Hommage aux anciens d'Ifodjè FC"
                      className="w-32 h-auto object-contain border border-red-200 rounded"
                      data-testid="bache-ifodje-preview"
                    />
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                        🏆 Bâche Hommage Anciens Ifodjè FC (300 × 100 cm)
                        <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded font-semibold">🆕 Mémoire</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Fond bordeaux + or · &laquo; HOMMAGE AUX ANCIENS D&apos;IFODJÈ FC · ATAKPAMÉ 1984-1990 &raquo; · Solidarité · Mémoire · Fraternité · 3 façons d&apos;aider (Identifiez / Témoignez / Soutenez) · QR de soutien (tracking bache-ifodje).
                      </p>
                    </div>
                    <a
                      href="/docs/SAFUAYE_Bache_Ifodje_Anciens_300x100.png"
                      download
                      data-testid="download-bache-ifodje"
                      className="inline-flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
                    >
                      <FileText className="w-4 h-4" /> PNG HQ
                    </a>
                  </div>
                </div>

                {/* Compteur de scans QR (bâches + t-shirts) */}
                <ScanStatsBox API={API} />


                {/* Brief imprimeur */}
                <div className="bg-white rounded-lg p-4 border-2 border-purple-300 mb-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                        📝 Brief Imprimeur — Spécifications techniques
                        <span className="text-xs bg-purple-700 text-white px-2 py-0.5 rounded font-semibold">Kpogan</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Document à remettre à l'imprimeur · Formats précis · Types de papier · Bâche PVC 500g · T-shirts coton 180g flocage/sérigraphie · Quantités estimées · Budget cible FCFA.
                      </p>
                    </div>
                    <a
                      href="/docs/SAFUAYE_Brief_Imprimeur.pdf"
                      download
                      data-testid="download-brief-imprimeur"
                      className="inline-flex items-center gap-2 bg-purple-700 hover:bg-purple-800 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
                    >
                      <FileText className="w-4 h-4" /> PDF
                    </a>
                  </div>
                </div>

                {/* T-shirts (3 designs) */}
                <div className="bg-white rounded-lg p-4 border-2 border-fuchsia-300">
                  <p className="font-semibold text-gray-900 mb-3 flex items-center flex-wrap gap-2">
                    👕 T-shirts SAFUAYE — 3 designs officiels
                    <span className="text-xs bg-fuchsia-700 text-white px-2 py-0.5 rounded font-semibold">V1 · V2 · V3</span>
                  </p>
                  <div className="grid md:grid-cols-3 gap-3">
                    {[
                      { v: "V1", name: "Bureau", color: "bg-slate-700", desc: "Bureau exécutif · Fond navy · Devise" },
                      { v: "V2", name: "Académie", color: "bg-emerald-700", desc: "Enfants U8-U13 · Ballon + Ifodjè" },
                      { v: "V3", name: "Gala", color: "bg-amber-600", desc: "Édition Gala 8/9 sept. 2026 · Or" },
                    ].map((t) => (
                      <div key={t.v} className="border rounded-lg p-2 bg-slate-50" data-testid={`tshirt-card-${t.v.toLowerCase()}`}>
                        <p className={`text-xs ${t.color} text-white px-2 py-1 rounded font-bold mb-2 text-center`}>
                          T-shirt {t.v} · {t.name}
                        </p>
                        <p className="text-[11px] text-gray-600 mb-2">{t.desc}</p>
                        <div className="grid grid-cols-2 gap-1 mb-2">
                          <img
                            src={`/docs/SAFUAYE_Tshirt_${t.v}_${t.name === "Bureau" ? "Bureau" : t.name === "Académie" ? "Academie" : "Gala"}_recto.png`}
                            alt={`T-shirt ${t.v} recto`}
                            className="w-full h-auto object-contain border border-gray-200 rounded bg-white"
                          />
                          <img
                            src={`/docs/SAFUAYE_Tshirt_${t.v}_${t.name === "Bureau" ? "Bureau" : t.name === "Académie" ? "Academie" : "Gala"}_verso.png`}
                            alt={`T-shirt ${t.v} verso`}
                            className="w-full h-auto object-contain border border-gray-200 rounded bg-white"
                          />
                        </div>
                        <div className="flex gap-1">
                          <a
                            href={`/docs/SAFUAYE_Tshirt_${t.v}_${t.name === "Bureau" ? "Bureau" : t.name === "Académie" ? "Academie" : "Gala"}_recto.png`}
                            download
                            data-testid={`tshirt-${t.v.toLowerCase()}-recto`}
                            className="flex-1 text-center text-[11px] bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800 px-2 py-1 rounded font-medium"
                          >
                            ⬇ Recto
                          </a>
                          <a
                            href={`/docs/SAFUAYE_Tshirt_${t.v}_${t.name === "Bureau" ? "Bureau" : t.name === "Académie" ? "Academie" : "Gala"}_verso.png`}
                            download
                            data-testid={`tshirt-${t.v.toLowerCase()}-verso`}
                            className="flex-1 text-center text-[11px] bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800 px-2 py-1 rounded font-medium"
                          >
                            ⬇ Verso
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Official Statuts + RI */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 mb-6">
                <p className="text-sm text-gray-700 mb-4">
                  <strong>Documents juridiques officiels SAFUAYE Togo</strong> — adoptés le 19/04/2026 par l'AG Constitutive. À déposer à la Préfecture de l'Ogou (Atakpamé) en cinq (5) exemplaires originaux signés.
                </p>
                <div className="space-y-3">
                  {[
                    { id: "academy-needs", file: "SAFUAYE_Academy_Needs_Sheet", pdfOnly: true, title: "⚽ Fiche « Besoins Académie » (à joindre aux réponses partenaires)", badge: "🆕 Outreach", badgeColor: "bg-emerald-700", desc: "Liste détaillée en anglais : ballons tailles 3-4-5, chasubles, plots, mini-buts, kits U9/U11/U13, crampons tailles 28-38, gants gardien, premiers secours · Quantités précises + priorités · Adresse de réception + info dédouanement Lomé · À envoyer à Kit Aid, Boots for Africa, Decathlon Foundation, etc." },
                    { id: "odon-bache", file: "SAFUAYE_OdonItsou_Bache_A0", pdfOnly: true, title: "🎪 Bâche PVC Odôn-Itsou 2026 (A0 — 2 m × 1 m)", badge: "🆕 9 août 2026", badgeColor: "bg-orange-600", desc: "Bâche grand format à imprimer chez Kpogan (Atakpamé) · Portrait Safuayé · 3 blocs ADHÉREZ / ACADÉMIE FOOT / FAITES UN DON · QR safuaye.org · WhatsApp +228 72 78 41 10 · Prête pour la finale coupe Odôn-Itsu" },
                    { id: "portrait-poster", file: "SAFUAYE_Portrait_Poster_A2", pdfOnly: true, title: "🖼️ Portrait Poster A2 — Simon-Joseph Safuayé APEDO", badge: "🆕 Stand", badgeColor: "bg-amber-600", desc: "Bâche A2 (42 × 59 cm) dédiée au portrait grand format · Cadre or épais · Nom + rôle « Président Ifodjè FC 1984-1990 » · Hommage « En sa mémoire, nous continuons son œuvre » · Idéale pour l'arrière du stand ou en fond décoratif" },
                    { id: "photobooth-frame", file: "SAFUAYE_Photobooth_Frame_A3", pdfOnly: true, title: "📸 Cadre selfie A3 « J'honore Safuaye »", badge: "🆕 Stand", badgeColor: "bg-amber-600", desc: "Cadre A3 à imprimer sur carton mousse et découper · Fenêtre centrale pour passer le visage · Titre « J'HONORE SAFUAYE » + hashtag #JHonoreSafuaye · Invite à poster sur WhatsApp / Facebook · Parfait pour créer du buzz réseaux le jour de l'événement" },
                    { id: "social-kit", file: "SAFUAYE_Social_Media_Kit", pdfOnly: true, title: "📱 Kit Médias Sociaux Odôn-Itsou (PDF + 3 visuels)", badge: "🆕 Réseaux", badgeColor: "bg-pink-600", desc: "3 posts prêts-à-publier avec visuels haute résolution : WhatsApp Status (1080×1920), Facebook Post (1200×630), Instagram Post (1080×1080) · Textes complets avec hashtags à copier-coller · Fichiers PNG téléchargeables séparément : SAFUAYE_Social_WhatsApp_Status.png, SAFUAYE_Social_Facebook_Post.png, SAFUAYE_Social_Instagram_Post.png" },
                    { id: "odon-depliant", file: "SAFUAYE_OdonItsou_Depliant", pdfOnly: true, title: "📄 Dépliant Odôn-Itsou 2026 (tri-fold recto-verso — version couleur)", badge: "🆕 9 août 2026", badgeColor: "bg-orange-600", desc: "A4 paysage plié en 3 · Version élégante en couleur (navy, or, orange, vert) · Couverture Odôn-Itsou · Objectifs SAFUAYE · Urgence AVC avec QR de signalement · Programme de la journée · Contacts + adhésion 3 000 FCFA · Idéal impression laser pro (Kpogan)" },
                    { id: "odon-depliant-lisibilite", file: "SAFUAYE_OdonItsou_Depliant_Lisibilite", pdfOnly: true, title: "📄 Dépliant Odôn-Itsou 2026 — HAUTE LISIBILITÉ (photocopie / impression standard)", badge: "🆕 Impression facile", badgeColor: "bg-slate-800", desc: "Version optimisée pour toutes les imprimantes (jet d'encre, photocopieuse noir & blanc, imprimante bureau) · Uniquement noir + navy sur blanc, aucun fond coloré clair, contraste maximal · Mission élargie « personnes vulnérables · plusieurs domaines » · Code F.A.S.T. pour reconnaître un AVC · Contacts Président + relais terrain · Encadré ADHÉREZ 3 000 FCFA très visible · Prêt à tirer en masse sans perte de lisibilité" },
                    { id: "odon-kit", file: "SAFUAYE_OdonItsou_Kit_Stand", pdfOnly: true, title: "🎯 Kit stand + Livre d'or Odôn-Itsou (interne bénévoles)", badge: "🆕 9 août 2026", badgeColor: "bg-orange-600", desc: "3 pages · Check-list matériel (16 items avec cases à cocher) · Script d'accueil FR + éwé + « à éviter » · Livre d'or 20 signatures anciens Ifodjè FC (nom, poste, WhatsApp, statut santé) · À conserver au siège après l'événement" },
                    { id: "briefing-benevoles", file: "SAFUAYE_Briefing_Benevoles_OdonItsou", pdfOnly: true, title: "🎽 Mini-briefing bénévoles Odôn-Itsou (A4 recto-verso)", badge: "🆕 À distribuer", badgeColor: "bg-emerald-600", desc: "Fiche chaleureuse à remettre à chaque bénévole au stand · Recto : SAFUAYE en 3 phrases, posture, phrase d'accroche, 3 messages-clés, objectifs chiffrés du jour (150 signatures, 40 pré-inscriptions, 100 000 FCFA…) · Verso : Q/R fréquentes, ce qu'il faut ÉVITER, contacts référents, hashtags · À imprimer en A4 couleur recto-verso" },
                    { id: "badges-benevoles", file: "SAFUAYE_Badges_Benevoles", pdfOnly: true, title: "🎫 Badges bénévoles à porter au cou (55 × 85 mm)", badge: "🆕 Stand", badgeColor: "bg-emerald-600", desc: "12 badges vierges (6 par page × 2 pages) au format porte-badge standard 55 × 85 mm portrait · Bandeau navy SAFUAYE + « BÉNÉVOLE 2026 » · Champs à remplir au feutre : PRÉNOM et RÔLE (Référent, Trésorier, Accueil, Communication…) · QR code safuaye.org · Trou de cordon indicatif en haut · Pointillés de découpe · À imprimer sur cartonné 250-300 g et insérer dans porte-badges avec cordon" },
                    { id: "fiche-adhesion-eclair", file: "SAFUAYE_Fiche_Adhesion_Eclair", pdfOnly: true, title: "💳 Fiche adhésion éclair 3 000 FCFA (à remplir sur place)", badge: "🆕 Stand", badgeColor: "bg-emerald-600", desc: "4 mini-fiches par A4 (2 pages = 8 fiches) · À découper suivant les pointillés · Champs : Nom & prénom, téléphone/WhatsApp, email, ville, âge/sexe, profession · Case « Cotisation 2026 versée : 3 000 FCFA » (espèces / mobile money / Wave / Mixx by Yas) · Double signature adhérent + trésorier · N° de reçu · Prêt à imprimer en série pour Odôn-Itsou et tout événement de terrain" },
                    { id: "registre-adherents", file: "SAFUAYE_Registre_Adherents", pdfOnly: true, title: "📚 Registre officiel des Adhérents (livret A4 relié)", badge: "🆕 Officiel", badgeColor: "bg-blue-700", desc: "A4 paysage · 16 pages · Page de garde + instructions de tenue + 15 pages tableau de 15 lignes = 225 adhérents · Colonnes : N° SAF-2026-XXX, date, nom, prénom, tél, ville, âge, sexe, profession, cotisation, signature · Signatures Secrétaire Général + Trésorier en bas de chaque page · À imprimer, relier et tenir au siège comme document officiel" },
                    { id: "cartes-qr-a6", file: "SAFUAYE_Cartes_QR_A6", pdfOnly: true, title: "🎴 Cartes visite A6 avec QR safuaye.org (à distribuer)", badge: "🆕 Réseau", badgeColor: "bg-purple-700", desc: "A4 recto-verso · 4 cartes A6 par page (105 × 148 mm) · Recto : logo SAFUAYE, slogan « Une académie · Une famille · Un espoir », QR code safuaye.org, WhatsApp +228 72 78 41 10 · Verso : 3 façons d'aider (Adhère 3 000 FCFA, Académie foot, Don mobile money) · Pointillés de découpe · À imprimer chez Kpogan pour distribution main à main pendant Odôn-Itsou" },
                    { id: "kit-whatsapp", file: "SAFUAYE_Kit_WhatsApp_Benevoles", pdfOnly: true, title: "💬 Kit WhatsApp bénévoles — 5 messages post-festival (PDF + TXT)", badge: "🆕 Relance", badgeColor: "bg-green-600", desc: "5 messages prêts-à-envoyer avec balises {Prénom}/{TonPrénom} à remplir : (1) Remerciement J+1 · (2) Relance adhésion 3 000 FCFA · (3) Signalement AVC / soutien famille · (4) Confirmation pré-inscription académie · (5) Recrutement bénévoles relais · Chaque message : à qui, quand, objectif, bulle verte type WhatsApp · Astuces conversion en fin de kit · Fichier compagnon SAFUAYE_Kit_WhatsApp_Benevoles.txt téléchargeable avec les emojis conservés pour copier-coller directement" },
                    { id: "certificat-adherent", file: "SAFUAYE_Certificat_Adherent", pdfOnly: true, title: "🏅 Certificat d'Adhésion 2026 (modèle à personnaliser)", badge: "🆕 Reconnaissance", badgeColor: "bg-amber-700", desc: "A4 paysage sur fond parchemin · Double bordure navy + or avec ornements dorés aux 4 coins · Ruban « APEDO ABLAVI » (nom à remplacer) · Sceau circulaire SAFUAYE-TOGO au centre · QR code safuaye.org de vérification · Signature Président National AZIAKA KOFFI · Bandeau bas avec coordonnées officielles · À envoyer par mail à chaque nouvel adhérent 2026 comme reconnaissance officielle · Script Python `generate_certificat_adherent.py` permet de générer une version personnalisée (nom, N°, ville, date)" },
                    { id: "certificat-exemple", file: "SAFUAYE_Certificat_Exemple", pdfOnly: true, title: "🏅 Certificat d'Adhésion — Exemple rempli (APEDO Ablavi)", badge: "🆕 Aperçu", badgeColor: "bg-amber-600", desc: "Aperçu du certificat rempli avec les vraies données · Nom : APEDO Ablavi · N° : SAF-2026-001 · Ville : Atakpamé · Date : 27/06/2026 · Sert de référence pour vérifier le rendu avant génération en masse · Signature manuscrite officielle du Président AZIAKA KOFFI directement intégrée (scan haute résolution avec fond transparent)" },
                    { id: "bilan-odon-itsou", file: "SAFUAYE_Bilan_OdonItsou_2026", pdfOnly: true, title: "📊 Bilan Odôn-Itsou 2026 (modèle post-événement)", badge: "🆕 Archives", badgeColor: "bg-blue-600", desc: "4 pages A4 · 8 sections à remplir avec les chiffres réels : (1) Fiche signalétique · (2) Résultats chiffrés vs objectifs · (3) Trésorerie détaillée (recettes / dépenses / résultat net) · (4) Analyse qualitative (points forts, axes d'amélioration, verbatims) · (5) Contacts-clés à recontacter · (6) Couverture média & réseaux · (7) Plan d'actions J+1 à J+30 · (8) Validation Rédacteur + Président + Trésorière · À archiver au siège et publier sur safuaye.org une fois complété" },
                    { id: "post-merci-captions", file: "SAFUAYE_Post_Merci_Captions", pdfOnly: true, title: "📱 Kit « Merci Atakpamé » — Visuels réseaux + 4 captions", badge: "🆕 Post-festival", badgeColor: "bg-pink-600", desc: "Kit complet à publier dans les 48h après le festival · PDF avec 4 captions prêtes à copier-coller (Instagram post, Story, Facebook détaillé, Statut WhatsApp) — chiffres du jour pré-remplis (150 contacts, 24 adhérents, 127 500 FCFA, 42 enfants, 230 photos) · Visuels PNG haute résolution téléchargeables séparément : SAFUAYE_Post_Merci_Atakpame.png (1080×1080 Instagram/Facebook) + SAFUAYE_Story_Merci_Atakpame.png (1080×1920 Story Instagram / statut WhatsApp) · Chiffres modifiables via `python safuaye_docs/generate_post_merci_atakpame.py`" },
                    { id: "depliant", file: "SAFUAYE_Togo_Depliant_AG_2026", pdfOnly: true, title: "📄 Dépliant 3 volets recto-verso (distribution Atakpamé)", badge: "AG 27/06/2026", badgeColor: "bg-orange-600", desc: "Format A4 paysage plié en 3 · 148 Ko · Couverture avec portrait de Safuaye · Présentation des 5 actions · Ordre du jour AG · Bulletin d'adhésion détachable · Contacts Wave/Mixx by Yas · QR code site · Prêt à imprimer chez l'imprimeur local" },
                    { id: "statuts-2026", file: "SAFUAYE_Togo_Statuts_2026", title: "📜 Statuts SAFUAYE-TOGO (révisés 2026)", badge: "AG 27/06/2026", badgeColor: "bg-red-600", desc: "25 articles · Président International (UK) + Bureau Exécutif National Togo (5 membres : Président AZIAKA KOFFI, Vice-Président DOGLO KOMI, Secrétaire Générale APEDO ABLAVI, Trésorière ABOTCHI ÉMILIE DJIFA A., Trésorier Adjoint SMITH K. GNAMIGBEDJRE) + Membres d'Honneur · Conforme Loi togolaise 40-484 / 2018-006" },
                    { id: "ouverture", file: "SAFUAYE_Togo_Message_Ouverture", title: "🎤 Message d'ouverture du Président de séance", badge: "AG 27/06/2026", badgeColor: "bg-red-600", desc: "Discours à prononcer en début d'assemblée — contexte historique, raison d'être de SAFUAYE-TOGO, ordre du jour, appel à la mobilisation" },
                    { id: "pv-ag", file: "SAFUAYE_Togo_PV_AG_Constitutive_27-06-2026_OFFICIEL", title: "📝 PV officiel de l'AG du 27/06/2026", badge: "✅ Adopté à 100%", badgeColor: "bg-green-700", desc: "Procès-verbal officiel finalisé · 25 membres présents · 3 votes à 100% (Statuts, Règlement Intérieur, Bureau) · Bureau élu nominativement · 5 Membres d'Honneur · 15 dons distribués (175 000 FCFA + 10 kits vivres) · Signatures Président National + Secrétaire Générale · Prêt pour dépôt à la Préfecture de l'Ogou" },
                    { id: "bulletins", file: "SAFUAYE_Togo_Bulletins_Vote", title: "🗳 Bulletins de vote (7 bulletins)", badge: "AG 27/06/2026", badgeColor: "bg-red-600", desc: "À photocopier en autant d'exemplaires que de votants. Couvre adoption Statuts, Président International (Kokou APEDO), 5 postes du Bureau National (Aziaka, Doglo, Ablavi, Émilie, Alain), nomination Membres d'Honneur, Convention UK⇔Togo" },
                    { id: "convention", file: "SAFUAYE_Convention_UK_Togo", title: "🤝 Convention de Partenariat UK ⇔ Togo", badge: "AG 27/06/2026", badgeColor: "bg-purple-600", desc: "Accord juridique entre SAFUAYE-UK (CIO) et SAFUAYE-TOGO · 8 articles · financements · redevabilité · gouvernance conjointe · résolution des différends" },
                    { id: "statuts", file: "SAFUAYE_Togo_Statuts", title: "📜 Statuts SAFUAYE Togo (v.1 - archive)", badge: "ARCHIVÉ", badgeColor: "bg-gray-500", desc: "Version initiale avant la révision de juin 2026. Conservée pour traçabilité." },
                    { id: "ri", file: "SAFUAYE_Togo_Reglement_Interieur", title: "📋 Règlement Intérieur SAFUAYE Togo", badge: "OFFICIEL", badgeColor: "bg-emerald-700", desc: "10 chapitres · adhésion · droits & devoirs · démission/radiation · trésorerie · communication · safeguarding · discipline" },
                  ].map((d) => (
                    <div key={d.id} className="bg-white rounded-lg p-4 border border-yellow-100">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 flex items-center flex-wrap gap-2">
                              {d.title}
                              <span className={`text-xs ${d.badgeColor} text-white px-2 py-0.5 rounded font-semibold`}>{d.badge}</span>
                            </p>
                            <p className="text-xs text-gray-500">{d.desc}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {!d.pdfOnly && (
                            <a href={`/docs/${d.file}.docx`} download data-testid={`download-togo-${d.id}-word`} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                              <FileText className="w-4 h-4" /> Word
                            </a>
                          )}
                          <a href={`/docs/${d.file}.pdf`} download data-testid={`download-togo-${d.id}-pdf`} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                            <FileText className="w-4 h-4" /> PDF
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 bg-amber-100 border-l-4 border-amber-600 p-3 rounded">
                  <p className="text-xs text-amber-900">
                    📍 <strong>Bureau Exécutif National (Togo) :</strong> Président <strong>AZIAKA KOFFI</strong> · Vice-Président <strong>DOGLO KOMI</strong> · Secrétaire Générale <strong>APEDO ABLAVI</strong> · Trésorière <strong>ABOTCHI ÉMILIE DJIFA A.</strong> · Trésorier Adjoint <strong>SMITH K. GNAMIGBEDJRE</strong>. <strong>Président International (UK) :</strong> APEDO KOKOU OGNAKOTAN. Les soutiens fidèles sont nommés <strong>Membres d'Honneur</strong> (voix consultative, sans cotisation).
                  </p>
                </div>
              </div>

              {/* Dossier Préfecture complet */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300 rounded-xl p-5 mb-8">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl">📦</span>
                  <div className="flex-1">
                    <p className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                      Dossier de dépôt — Préfecture de l'Ogou
                      <span className="text-xs bg-red-700 text-white px-2 py-0.5 rounded font-semibold">PRÊT-À-DÉPOSER</span>
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Dossier consolidé (≈ 22 pages) regroupant : page de garde · bordereau de dépôt + checklist des pièces à joindre · lettre au Préfet · PV AG Constitutive · liste des fondateurs · Statuts complets · Règlement Intérieur. À imprimer en <strong>5 exemplaires originaux signés</strong>.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-red-200">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="text-sm text-gray-700">
                      <strong>📄 SAFUAYE_Togo_Dossier_Prefecture_COMPLET.pdf</strong>
                      <span className="ml-2 text-xs text-gray-500">— PDF unique consolidé</span>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href="/docs/SAFUAYE_Togo_Dossier_Prefecture_COMPLET.pdf"
                        download
                        data-testid="download-dossier-prefecture-complet"
                        className="inline-flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors shadow"
                      >
                        <FileText className="w-4 h-4" /> Télécharger le dossier COMPLET (PDF)
                      </a>
                    </div>
                  </div>
                </div>
                <details className="mt-3 bg-white/50 rounded-lg p-2">
                  <summary className="text-xs text-gray-700 cursor-pointer font-medium">📑 Télécharger uniquement la partie « Couvertures » (page de garde + bordereau + lettre + PV + liste) si vous voulez ajouter manuellement les Statuts/RI</summary>
                  <div className="flex gap-2 mt-2">
                    <a href="/docs/SAFUAYE_Togo_Dossier_Prefecture_CoverParts.docx" download className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded">Word</a>
                    <a href="/docs/SAFUAYE_Togo_Dossier_Prefecture_CoverParts.pdf" download className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded">PDF</a>
                  </div>
                </details>
                <div className="mt-3 bg-amber-100 border-l-4 border-amber-600 p-3 rounded">
                  <p className="text-xs text-amber-900">
                    ⚠️ <strong>Pièces à apporter le jour du dépôt</strong> (mentionnées dans le bordereau page 2) :
                    photocopies CNI certifiées conformes des 3 trustees · casiers judiciaires (Bulletin n° 3, &lt; 3 mois) · 2 photos d'identité par trustee · justificatif de domicile Présidente · plan de localisation du siège · timbres fiscaux · lettre de mandat de SAFUAYE CIO (UK).
                  </p>
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-3 italic">⚡ Modèles rapides (PDF générés à la volée — utiles pour brouillons ou tests) :</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <button onClick={generateStatutsTogoPDF} className="bg-green-700 text-white p-4 rounded-lg hover:bg-green-800 transition-colors">
                  <FileText className="w-8 h-8 mx-auto mb-2" />
                  <span className="block font-medium">Statuts Togo</span>
                  <span className="text-sm opacity-80">Loi togolaise</span>
                </button>
                <button onClick={generatePVTogoAssembleePDF} className="bg-yellow-600 text-white p-4 rounded-lg hover:bg-yellow-700 transition-colors">
                  <FileText className="w-8 h-8 mx-auto mb-2" />
                  <span className="block font-medium">PV Assemblée</span>
                  <span className="text-sm opacity-80">AG Constitutive</span>
                </button>
                <button onClick={generateDemandePreletPDF} className="bg-red-700 text-white p-4 rounded-lg hover:bg-red-800 transition-colors">
                  <Mail className="w-8 h-8 mx-auto mb-2" />
                  <span className="block font-medium">Demande Préfet</span>
                  <span className="text-sm opacity-80">Préfecture Ogou</span>
                </button>
                <button onClick={generateListeMembresPDF} className="bg-blue-700 text-white p-4 rounded-lg hover:bg-blue-800 transition-colors">
                  <Users className="w-8 h-8 mx-auto mb-2" />
                  <span className="block font-medium">Liste Membres</span>
                  <span className="text-sm opacity-80">Fondateurs & Bureau</span>
                </button>
              </div>
              
              {/* Trustees Info */}
              <div className="bg-gray-100 rounded-lg p-6">
                <h4 className="font-bold text-gray-900 mb-4">Trustees SAFUAYE</h4>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>🇬🇧 <strong>APEDO KOKOU OGNAKOTAN</strong> (Président) - UK</div>
                  <div>🇨🇭 <strong>Yao Blaise Apedo</strong> (Vice-Président) - Suisse</div>
                  <div>🇹🇬 <strong>APEDO ABLAVI</strong> (Secrétaire Générale) - Togo</div>
                </div>
              </div>
            </div>
          )}

          {/* Trustees Tab - Content from Meeting Admin Panel */}
          {adminTab === "trustees" && (
            <div>
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-6">
                👥 Gestion des Trustees & Réunion
              </h3>
              <p className="text-gray-600 mb-6">
                Gérez les informations des trustees, générez les emails de convocation et les procès-verbaux.
              </p>
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 mb-6">
                <p className="text-yellow-800 font-semibold mb-2">🚧 Section en cours de développement</p>
                <p className="text-yellow-700 text-sm">
                  Les fonctionnalités de gestion des trustees (formulaire d'enregistrement, génération email, PV) 
                  sont actuellement disponibles dans la section "Meeting" du site public.
                </p>
                <p className="text-yellow-700 text-sm mt-2">
                  Elles seront bientôt intégrées ici pour une gestion centralisée.
                </p>
              </div>
              <div className="bg-gray-100 rounded-lg p-6">
                <h4 className="font-bold text-gray-900 mb-4">📋 Trustees Actuels</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-2xl mb-2">🇬🇧</p>
                    <p className="font-semibold text-gray-900">APEDO KOKOU OGNAKOTAN</p>
                    <p className="text-sm text-gray-600">Président (Chair)</p>
                    <p className="text-xs text-gray-500 mt-1">Royaume-Uni</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-2xl mb-2">🇨🇭</p>
                    <p className="font-semibold text-gray-900">Yao Blaise Apedo</p>
                    <p className="text-sm text-gray-600">Vice-Président</p>
                    <p className="text-xs text-gray-500 mt-1">Suisse</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-2xl mb-2">🇹🇬</p>
                    <p className="font-semibold text-gray-900">APEDO ABLAVI</p>
                    <p className="text-sm text-gray-600">Secrétaire Générale</p>
                    <p className="text-xs text-gray-500 mt-1">Togo</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Testimonials Tab */}
          {adminTab === "testimonials" && (
            <div className="bg-white rounded-xl p-6 shadow-lg" data-testid="admin-testimonials-panel">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <h4 className="font-semibold text-lg flex items-center gap-2">
                    <Quote className="w-5 h-5 text-blue-600" />
                    Gérer les Témoignages
                    <span className="text-xs text-fuchsia-700 font-normal">✨ IA Gemini</span>
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Approuvez, épinglez et laissez l'IA classer sentiment, thème et priorité.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => analyzeAllTestimonialsAi(false)}
                    data-testid="analyze-testimonials-bulk"
                    className="text-xs bg-fuchsia-700 hover:bg-fuchsia-800 text-white px-3 py-2 rounded-lg font-semibold"
                    title="Analyser tous les témoignages non encore traités"
                  >
                    ✨ Analyser tous (IA)
                  </button>
                  <button
                    onClick={() => analyzeAllTestimonialsAi(true)}
                    className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded-lg"
                    title="Force re-analyse même déjà analysés"
                  >
                    ⟳ Re-analyser tout
                  </button>
                </div>
              </div>
              {adminTestimonials.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Aucun témoignage</p>
              ) : (
                <div className="space-y-4 max-h-[36rem] overflow-y-auto">
                  {[...adminTestimonials].sort((a, b) => {
                    const rank = { haute: 0, moyenne: 1, basse: 2 };
                    return (rank[a.ai_priority] ?? 3) - (rank[b.ai_priority] ?? 3);
                  }).map((testimonial) => {
                    const prio = testimonial.ai_priority;
                    const sent = testimonial.ai_sentiment;
                    const prioColor = prio === "haute" ? "bg-red-100 text-red-800 border-red-300"
                      : prio === "moyenne" ? "bg-amber-100 text-amber-800 border-amber-300"
                      : prio === "basse" ? "bg-gray-100 text-gray-700 border-gray-300"
                      : "bg-slate-100 text-slate-500 border-slate-200";
                    const sentIcon = sent === "positif" ? "😊" : sent === "négatif" ? "😟" : sent === "neutre" ? "😐" : "❓";
                    return (
                      <div key={testimonial.id}
                           data-testid={`admin-testimonial-${testimonial.id}`}
                           className={`p-4 rounded-lg border-2 ${testimonial.featured ? "bg-indigo-50 border-indigo-300" :
                             testimonial.approved ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                        <div className="flex justify-between items-start mb-2 gap-2 flex-wrap">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900">
                              {testimonial.featured && <span title="Épinglé en priorité">📌 </span>}
                              {testimonial.author_name}
                            </p>
                            <p className="text-xs text-gray-500">{testimonial.relationship}</p>
                          </div>
                          <div className="flex flex-wrap gap-1 items-center">
                            {prio ? (
                              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${prioColor}`}
                                    data-testid={`ai-priority-${testimonial.id}`}>
                                🎯 {prio}
                              </span>
                            ) : (
                              <span className="text-[10px] text-fuchsia-600 italic px-2">
                                ✨ Non analysé
                              </span>
                            )}
                            {sent && (
                              <span className="text-[10px] bg-white border px-2 py-0.5 rounded" title={`Sentiment: ${sent}`}>
                                {sentIcon} {sent}
                              </span>
                            )}
                            {testimonial.ai_theme && (
                              <span className="text-[10px] bg-blue-50 border border-blue-200 text-blue-800 px-2 py-0.5 rounded">
                                #{testimonial.ai_theme}
                              </span>
                            )}
                            {testimonial.approved ? (
                              <span className="text-[10px] bg-green-600 text-white px-2 py-0.5 rounded">✓ Approuvé</span>
                            ) : (
                              <span className="text-[10px] bg-yellow-600 text-white px-2 py-0.5 rounded">⏳ En attente</span>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mb-2 whitespace-pre-line">{testimonial.message}</p>
                        {testimonial.ai_summary && (
                          <p className="text-xs italic text-fuchsia-700 mb-2 border-l-2 border-fuchsia-300 pl-2">
                            ✨ IA : {testimonial.ai_summary}
                          </p>
                        )}
                        {testimonial.ai_tags && testimonial.ai_tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {testimonial.ai_tags.map((t, i) => (
                              <span key={i} className="text-[10px] bg-fuchsia-100 text-fuchsia-800 px-1.5 py-0.5 rounded">
                                #{t}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2 pt-1">
                          {!testimonial.approved && (
                            <button
                              onClick={() => approveTestimonial(testimonial.id)}
                              data-testid={`approve-testimonial-${testimonial.id}`}
                              className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                            >
                              ✓ Approuver
                            </button>
                          )}
                          <button
                            onClick={() => featureTestimonial(testimonial.id, !testimonial.featured)}
                            data-testid={`feature-testimonial-${testimonial.id}`}
                            className={`text-xs px-3 py-1 rounded font-medium ${testimonial.featured
                              ? "bg-indigo-600 text-white hover:bg-indigo-700"
                              : "bg-indigo-100 text-indigo-800 hover:bg-indigo-200"}`}
                            title={testimonial.featured ? "Retirer de la priorité" : "Épingler en priorité sur la homepage"}
                          >
                            📌 {testimonial.featured ? "Épinglé" : "Épingler"}
                          </button>
                          <button
                            onClick={() => analyzeTestimonialAi(testimonial.id)}
                            data-testid={`analyze-testimonial-${testimonial.id}`}
                            className="text-xs bg-fuchsia-100 text-fuchsia-800 hover:bg-fuchsia-200 px-3 py-1 rounded font-medium"
                            title="Analyser avec Gemini IA (sentiment, priorité, thème)"
                          >
                            ✨ {testimonial.ai_analyzed_at ? "Re-analyser" : "Analyser IA"}
                          </button>
                          <button
                            onClick={() => deleteTestimonial(testimonial.id)}
                            className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                          >
                            ✕ Supprimer
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* News Tab */}
          {adminTab === "news" && (
            <div>
              {/* Create News Form */}
              <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  Créer une Actualité
                </h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Titre de l'actualité"
                    value={newsForm.title}
                    onChange={(e) => setNewsForm({...newsForm, title: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  <input
                    type="text"
                    placeholder="Résumé court"
                    value={newsForm.summary}
                    onChange={(e) => setNewsForm({...newsForm, summary: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  <textarea
                    placeholder="Contenu complet de l'actualité"
                    value={newsForm.content}
                    onChange={(e) => setNewsForm({...newsForm, content: e.target.value})}
                    rows="4"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    onClick={createNews}
                    className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 font-semibold"
                  >
                    ✓ Publier l'Actualité
                  </button>
                </div>
              </div>

              {/* News List */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-semibold text-lg mb-4">📰 Actualités Publiées</h4>
                {adminNews.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Aucune actualité</p>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {adminNews.map((news) => (
                      <div key={news.id} className="p-4 rounded-lg border-2 bg-blue-50 border-blue-200">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-gray-900">{news.title}</p>
                            <p className="text-xs text-gray-500">{new Date(news.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{news.summary || news.content.substring(0, 100)}...</p>
                        <button
                          onClick={() => deleteNews(news.id)}
                          className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                        >
                          ✕ Supprimer
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Messages & Inscriptions Tab */}
          {adminTab === "messages" && (
            <div>
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-6">
                📬 Messages & Inscriptions des Visiteurs
              </h3>
              
              {/* Contacts Section */}
              <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Messages de Contact ({adminContacts.length})
                </h4>
                {adminContacts.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Aucun message reçu</p>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {adminContacts.map((contact) => (
                      <div key={contact.id} className="p-4 rounded-lg border-2 bg-blue-50 border-blue-200">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-gray-900">{contact.name}</p>
                            <p className="text-sm text-blue-600">{contact.email}</p>
                            <p className="text-xs text-gray-500">{new Date(contact.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mb-3 bg-white p-3 rounded">{contact.message}</p>
                        <div className="flex gap-2">
                          <a
                            href={`mailto:${contact.email}?subject=Re: Votre message à SAFUAYE`}
                            className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                          >
                            ✉️ Répondre
                          </a>
                          <button
                            onClick={() => deleteContact(contact.id)}
                            className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                          >
                            ✕ Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Meeting Registrations Section */}
              <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Inscriptions à la Réunion ({adminMeetingRegs.length})
                </h4>
                {adminMeetingRegs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Aucune inscription</p>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {adminMeetingRegs.map((reg) => (
                      <div key={reg.id} className="p-4 rounded-lg border-2 bg-purple-50 border-purple-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-gray-900">{reg.full_name}</p>
                            <p className="text-sm text-purple-600">{reg.email}</p>
                            <p className="text-sm text-gray-600">Rôle: <span className="font-medium">{reg.role}</span></p>
                            <p className="text-xs text-gray-500 mt-1">{new Date(reg.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Témoignages Non Approuvés */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Quote className="w-5 h-5 text-orange-600" />
                  Témoignages En Attente
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  💡 Astuce: Pour gérer tous les témoignages, allez dans l'onglet "💬 Témoignages"
                </p>
                <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-orange-800">
                    {adminTestimonials.filter(t => !t.approved).length} témoignage(s) en attente d'approbation
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Members Tab */}
          {adminTab === "members" && (
            <div>
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-6">
                👤 Gestion des Membres de l'Association
              </h3>
              
              {/* Add/Edit Member Form */}
              <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  {editingMember ? "Modifier un Membre" : "Ajouter un Nouveau Membre"}
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Prénom *"
                    value={memberForm.first_name}
                    onChange={(e) => setMemberForm({...memberForm, first_name: e.target.value})}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text"
                    placeholder="Nom *"
                    value={memberForm.last_name}
                    onChange={(e) => setMemberForm({...memberForm, last_name: e.target.value})}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    value={memberForm.email}
                    onChange={(e) => setMemberForm({...memberForm, email: e.target.value})}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="tel"
                    placeholder="Téléphone"
                    value={memberForm.phone}
                    onChange={(e) => setMemberForm({...memberForm, phone: e.target.value})}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text"
                    placeholder="Pays *"
                    value={memberForm.country}
                    onChange={(e) => setMemberForm({...memberForm, country: e.target.value})}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text"
                    placeholder="Ville"
                    value={memberForm.city}
                    onChange={(e) => setMemberForm({...memberForm, city: e.target.value})}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <select
                    value={memberForm.role}
                    onChange={(e) => setMemberForm({...memberForm, role: e.target.value})}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="Membre">Membre</option>
                    <option value="Bénévole">Bénévole</option>
                    <option value="Donateur">Donateur</option>
                    <option value="Partenaire">Partenaire</option>
                    <option value="Bienfaiteur">Bienfaiteur</option>
                  </select>
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={createMember}
                    className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 font-semibold"
                  >
                    {editingMember ? "✓ Modifier" : "✓ Ajouter le Membre"}
                  </button>
                  {editingMember && (
                    <button
                      onClick={cancelEdit}
                      className="px-6 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 font-semibold"
                    >
                      Annuler
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">* Champs obligatoires</p>
              </div>

              {/* Members List */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-semibold text-lg mb-4 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    Liste des Membres ({adminMembers.length})
                  </span>
                </h4>
                {adminMembers.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Aucun membre enregistré</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b-2 border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Nom Complet</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Téléphone</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Pays</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Rôle</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Statut</th>
                          <th className="px-4 py-3 text-center font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {adminMembers.map((member) => (
                          <tr key={member.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900">{member.first_name} {member.last_name}</p>
                              <p className="text-xs text-gray-500">
                                Depuis {new Date(member.member_since).toLocaleDateString()}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-gray-700">{member.email}</td>
                            <td className="px-4 py-3 text-gray-700">{member.phone || '-'}</td>
                            <td className="px-4 py-3">
                              <span className="text-gray-700">{member.country}</span>
                              {member.city && <span className="text-xs text-gray-500 block">{member.city}</span>}
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                {member.role}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                                member.status === 'Actif' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {member.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2 justify-center">
                                <button
                                  onClick={() => editMember(member)}
                                  className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                                >
                                  ✏️ Modifier
                                </button>
                                <button
                                  onClick={() => deleteMember(member.id)}
                                  className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                                >
                                  ✕ Supprimer
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* HQ Renovation Photos Tab */}
          {adminTab === "hq" && (
            <div data-testid="admin-hq-panel">
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">
                🏗️ Chantier — Siège d'Atakpamé
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Ajoutez les photos <strong>avant</strong>, <strong>après</strong> ou <strong>en cours</strong> pour chaque étape du chantier. Elles apparaîtront automatiquement dans la section publique <em>"Notre siège"</em>. Taille max 10 Mo — formats JPG / PNG / WEBP.
              </p>

              {/* ============================================================ */}
              {/* Milestone recap — Distribution officielle 12 septembre 2026   */}
              {/* ============================================================ */}
              <div className="bg-gradient-to-br from-orange-50 via-white to-emerald-50 border-2 border-orange-300 rounded-2xl p-5 md:p-6 mb-6 shadow-sm" data-testid="admin-hq-milestone-12sept">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-orange-700 mb-1">
                      🎯 Le siège en action · 12 septembre 2026
                    </p>
                    <h4 className="font-heading text-lg md:text-xl font-bold text-gray-900">
                      Rencontre officielle & distribution aux bénéficiaires
                    </h4>
                  </div>
                  <span className="inline-flex items-center gap-2 self-start md:self-center px-3 py-1.5 rounded-full bg-orange-600 text-white text-xs font-semibold shadow-sm">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                    Atakpamé · Ogou 1
                  </span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  Trois mois après l'AG Constitutive, le siège d'Atchrimi-Komé a accueilli sa première grande action opérationnelle. <strong>20 bénéficiaires signataires</strong> ont reçu un don en espèces tiéré et un kit scolaire pour leurs <strong>25 enfants à charge</strong>. Événement couvert par la presse Togoreport.
                </p>

                {/* KPI stripe (admin compact) */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
                  {[
                    {v: "20", l: "Bénéficiaires signataires", c: "text-orange-700"},
                    {v: "25", l: "Enfants équipés (kits)", c: "text-emerald-700"},
                    {v: "150 000", l: "FCFA distribués (cash)", c: "text-amber-700"},
                    {v: "11", l: "Pièces officielles archivées", c: "text-blue-700"},
                  ].map((k, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 text-center">
                      <p className={`text-xl font-bold ${k.c}`}>{k.v}</p>
                      <p className="text-[10px] text-gray-600 mt-0.5 uppercase tracking-wider leading-tight">{k.l}</p>
                    </div>
                  ))}
                </div>

                {/* Photos preview 5 first */}
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">🖼️ Photos officielles (15)</p>
                  <div className="grid grid-cols-5 md:grid-cols-8 gap-2" data-testid="admin-hq-12sept-photos">
                    {[
                      "IMG-20260912-WA0008.jpg","IMG-20260912-WA0009.jpg","IMG-20260912-WA0010.jpg",
                      "IMG-20260912-WA0011.jpg","IMG-20260912-WA0012.jpg","IMG-20260912-WA0013.jpg",
                      "IMG-20260912-WA0014.jpg","IMG-20260912-WA0015.jpg","IMG-20260912-WA0016.jpg",
                      "IMG-20260912-WA0017.jpg","IMG-20260912-WA0018.jpg","IMG-20260912-WA0019.jpg",
                      "IMG-20260912-WA0020.jpg","IMG-20260912-WA0021.jpg","IMG-20260912-WA0022.jpg",
                    ].map((f, i) => (
                      <a key={i} href={`${API}/media/${f}`} target="_blank" rel="noreferrer" className="relative rounded-md overflow-hidden shadow-sm hover:shadow-lg transition-shadow group aspect-square bg-gray-100" data-testid={`admin-hq-12sept-thumb-${i}`}>
                        <img src={`${API}/media/${f}`} alt={`Photo 12/09/2026 · ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" loading="lazy" />
                      </a>
                    ))}
                  </div>
                </div>

                {/* Doc quick links */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">📎 Documents officiels</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      {file: "SAFUAYE_Registres_Officiels_12sept2026.pdf", label: "📋 Registres (11 pièces)"},
                      {file: "SAFUAYE_PV_Remise_Dons_2026-09-12.pdf", label: "📄 PV Remise de Dons"},
                      {file: "SAFUAYE_Album_Rencontre_12sept2026.pdf", label: "📸 Album photo PDF"},
                    ].map((d, i) => (
                      <a key={i} href={`${API}/docs/${d.file}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border-2 border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 text-sm font-medium text-gray-800 transition-all" data-testid={`admin-hq-12sept-doc-${i}`}>
                        {d.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Upload Form */}
              <div className="bg-white rounded-xl p-6 shadow-lg mb-6 border border-gray-200">
                <h4 className="font-semibold text-lg mb-4">📤 Ajouter une photo</h4>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Étape du chantier</label>
                    <select
                      value={hqUploadForm.stage}
                      onChange={(e) => setHqUploadForm({ ...hqUploadForm, stage: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      data-testid="hq-upload-stage"
                    >
                      <option value="overall">Vue générale / Overall</option>
                      <option value="demolition">Démolition / Demolition</option>
                      <option value="foundations">Fondations / Foundations</option>
                      <option value="walls">Murs / Walls</option>
                      <option value="roof">Toiture / Roof</option>
                      <option value="electrics">Électricité / Electrics</option>
                      <option value="plumbing">Plomberie / Plumbing</option>
                      <option value="joinery">Menuiserie / Joinery</option>
                      <option value="painting">Peinture / Painting</option>
                      <option value="finishing">Finitions / Finishing</option>
                      <option value="furniture">Mobilier / Furniture</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={hqUploadForm.type}
                      onChange={(e) => setHqUploadForm({ ...hqUploadForm, type: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      data-testid="hq-upload-type"
                    >
                      <option value="before">Avant / Before</option>
                      <option value="after">Après / After</option>
                      <option value="progress">En cours / Progress</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Légende (optionnelle)</label>
                    <input
                      type="text"
                      value={hqUploadForm.caption}
                      onChange={(e) => setHqUploadForm({ ...hqUploadForm, caption: e.target.value })}
                      placeholder="Ex : Mur côté rue, fissures visibles avant travaux"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      data-testid="hq-upload-caption"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Photo (JPG / PNG / WEBP, max 10 Mo)</label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => setHqUploadForm({ ...hqUploadForm, file: e.target.files?.[0] || null })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                      data-testid="hq-upload-file"
                    />
                    <p className="text-xs text-gray-500 mt-1">💡 Les photos sont automatiquement compressées avant envoi (max ~800 Ko, 1600px) — pratique sur connexion mobile.</p>
                  </div>
                </div>
                <button
                  onClick={uploadHqPhoto}
                  disabled={hqUploading || !hqUploadForm.file}
                  className="bg-orange-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="hq-upload-btn"
                >
                  {hqUploading ? "⏳ Compression & envoi…" : "📤 Ajouter la photo"}
                </button>
              </div>

              {/* Photos list grouped by stage */}
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                <h4 className="font-semibold text-lg mb-4">🖼️ Photos existantes ({hqPhotos.length})</h4>
                {hqPhotos.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Aucune photo ajoutée pour le moment.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {hqPhotos.map(p => (
                      <div key={p.id} className="relative group border border-gray-200 rounded-lg overflow-hidden" data-testid={`hq-admin-photo-${p.id}`}>
                        <img src={`${API}/hq/photos/${p.id}/file`} alt={p.caption || p.stage} className="w-full aspect-square object-cover" />
                        <div className="p-2 bg-white">
                          <div className="flex items-center gap-1 flex-wrap mb-1">
                            <span className="text-xs font-semibold bg-gray-800 text-white px-1.5 py-0.5 rounded">{p.stage}</span>
                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${p.type === "before" ? "bg-gray-200 text-gray-800" : p.type === "after" ? "bg-orange-500 text-white" : "bg-blue-100 text-blue-800"}`}>
                              {p.type}
                            </span>
                          </div>
                          {p.caption && <p className="text-xs text-gray-600 line-clamp-2">{p.caption}</p>}
                          <button
                            onClick={() => deleteHqPhoto(p.id)}
                            className="mt-1 text-xs bg-red-600 hover:bg-red-700 text-white px-2 py-0.5 rounded w-full"
                            data-testid={`hq-delete-${p.id}`}
                          >
                            ✕ Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Grant Distribution June 2026 Tab */}
          {adminTab === "grants" && (
            <div data-testid="admin-grants-panel">
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                💝 Distribution de Juin 2026 — Premier projet de don
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Suivi en temps réel de la première distribution officielle d'aide aux 7 bénéficiaires identifiés.
                Total prévu : <strong>175 000 FCFA (≈ £210)</strong> · 25 000 FCFA par bénéficiaire · Cash en main propre par Ablavi à Atakpamé.
              </p>

              {/* Stats */}
              {grantStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-4">
                    <p className="text-xs text-emerald-700 font-semibold">Bénéficiaires</p>
                    <p className="text-3xl font-bold text-emerald-900">{grantStats.total_count}</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
                    <p className="text-xs text-blue-700 font-semibold">Total prévu</p>
                    <p className="text-2xl font-bold text-blue-900">{grantStats.total_planned_fcfa.toLocaleString('fr-FR')} FCFA</p>
                    <p className="text-xs text-blue-600">≈ £{Math.round(grantStats.total_planned_fcfa/832)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-4">
                    <p className="text-xs text-amber-700 font-semibold">Total remis</p>
                    <p className="text-2xl font-bold text-amber-900">{grantStats.total_delivered_fcfa.toLocaleString('fr-FR')} FCFA</p>
                    <p className="text-xs text-amber-600">≈ £{Math.round(grantStats.total_delivered_fcfa/832)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
                    <p className="text-xs text-purple-700 font-semibold">Statut</p>
                    <div className="text-xs text-purple-900 space-y-0.5 mt-1">
                      <div>📋 Programmés : <strong>{grantStats.by_status.planned || 0}</strong></div>
                      <div>📨 Notifiés : <strong>{grantStats.by_status.notified || 0}</strong></div>
                      <div>💵 Remis : <strong>{grantStats.by_status.delivered || 0}</strong></div>
                      <div>✅ Confirmés : <strong>{grantStats.by_status.confirmed || 0}</strong></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents kit */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">📦 Kit complet de documents (7 docs · 14 fichiers)</h4>
                <div className="grid md:grid-cols-2 gap-2">
                  {[
                    { id: "plan", label: "1. Plan de Distribution", file: "SAFUAYE_Grant_June2026_DistributionPlan" },
                    { id: "receipt", label: "2. Reçu de Don signé (à imprimer 7×)", file: "SAFUAYE_Grant_June2026_DonationReceipt" },
                    { id: "letter", label: "3. Lettre de Notification", file: "SAFUAYE_Grant_June2026_NotificationLetter" },
                    { id: "comm", label: "4. Kit de Communication (FR + EN)", file: "SAFUAYE_Grant_June2026_CommunicationKit" },
                    { id: "needs", label: "5. Fiche Identification des Besoins", file: "SAFUAYE_Grant_June2026_NeedsAssessmentForm" },
                    { id: "report", label: "6. Rapport Post-Distribution", file: "SAFUAYE_Grant_June2026_PostDistributionReport" },
                    { id: "mission", label: "7. ✉️ Lettre de mission Chair → Ablavi", file: "SAFUAYE_Lettre_Mission_Ablavi_Juin2026" },
                  ].map((d) => (
                    <div key={d.id} className="flex items-center justify-between bg-white rounded-lg p-2 border border-emerald-100">
                      <span className="text-sm text-gray-800 font-medium">{d.label}</span>
                      <div className="flex gap-1">
                        <a href={`/docs/${d.file}.docx`} download data-testid={`grant-doc-${d.id}-word`} className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded">Word</a>
                        <a href={`/docs/${d.file}.pdf`} download data-testid={`grant-doc-${d.id}-pdf`} className="text-xs bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded">PDF</a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Distribution table */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
                  <h4 className="font-bold text-gray-900">📋 Tableau de suivi des remises</h4>
                  <div className="flex gap-2">
                    <button onClick={loadGrantDistributions} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded font-medium">🔄 Actualiser</button>
                    {grantDistributions.length === 0 && (
                      <button onClick={seedGrantDistributions} data-testid="grant-seed-btn" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded font-medium">⚡ Initialiser les 7 bénéficiaires</button>
                    )}
                  </div>
                </div>
                {grantDistributions.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 text-sm">
                    Aucune distribution programmée. Cliquez sur « Initialiser les 7 bénéficiaires » pour démarrer.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Bénéficiaire</th>
                          <th className="px-3 py-2 text-left">Statut</th>
                          <th className="px-3 py-2 text-left">Date prévue</th>
                          <th className="px-3 py-2 text-left">Témoin</th>
                          <th className="px-3 py-2 text-center">Reçu</th>
                          <th className="px-3 py-2 text-center">Photos</th>
                          <th className="px-3 py-2 text-center">Besoins</th>
                          <th className="px-3 py-2 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grantDistributions.map((g, i) => (
                          <tr key={g.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`grant-row-${g.id}`}>
                            <td className="px-3 py-2 text-gray-500">{i+1}</td>
                            <td className="px-3 py-2">
                              <div className="font-semibold text-gray-900">{g.beneficiary_name}</div>
                              <div className="text-xs text-gray-500">{g.beneficiary_age ? g.beneficiary_age+' ans' : ''}{g.beneficiary_situation ? ' · '+g.beneficiary_situation : ''}</div>
                            </td>
                            <td className="px-3 py-2">
                              <select
                                value={g.status}
                                onChange={(e) => updateGrantDistribution(g.id, { status: e.target.value })}
                                data-testid={`grant-status-${g.id}`}
                                className={`text-xs font-semibold rounded px-2 py-1 border ${
                                  g.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                                  g.status === 'delivered' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                                  g.status === 'notified' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                  'bg-gray-100 text-gray-700 border-gray-300'
                                }`}
                              >
                                <option value="planned">📋 Programmé</option>
                                <option value="notified">📨 Notifié</option>
                                <option value="delivered">💵 Remis</option>
                                <option value="confirmed">✅ Confirmé</option>
                              </select>
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="date"
                                value={g.scheduled_date || ''}
                                onChange={(e) => updateGrantDistribution(g.id, { scheduled_date: e.target.value })}
                                className="text-xs border border-gray-300 rounded px-2 py-1 w-36"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                placeholder="Nom du témoin"
                                defaultValue={g.witness_name || ''}
                                onBlur={(e) => e.target.value !== (g.witness_name || '') && updateGrantDistribution(g.id, { witness_name: e.target.value })}
                                className="text-xs border border-gray-300 rounded px-2 py-1 w-32"
                              />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <input type="checkbox" checked={!!g.receipt_signed} onChange={(e) => updateGrantDistribution(g.id, { receipt_signed: e.target.checked })} className="w-4 h-4" />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <input type="checkbox" checked={!!g.photos_taken} onChange={(e) => updateGrantDistribution(g.id, { photos_taken: e.target.checked })} className="w-4 h-4" />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <input type="checkbox" checked={!!g.needs_form_filled} onChange={(e) => updateGrantDistribution(g.id, { needs_form_filled: e.target.checked })} className="w-4 h-4" />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button onClick={() => deleteGrantDistribution(g.id)} className="text-xs bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded">✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="mt-4 bg-blue-50 border-l-4 border-blue-500 p-3 rounded text-xs text-blue-900">
                💡 <strong>Workflow recommandé :</strong> 1) Programmé → 2) Notifié (lettre envoyée) → 3) Remis (cash + reçu signé + photos) → 4) Confirmé (Board UK a reçu le scan complet). Cocher Reçu / Photos / Besoins au fur et à mesure.
              </div>
            </div>
          )}

          {/* Impact Campaigns Tab */}
          {adminTab === "impact" && (
            <ImpactAdminPanel
              API={API}
              impactCampaigns={impactCampaigns}
              impactForm={impactForm} setImpactForm={setImpactForm}
              impactPhotoUpload={impactPhotoUpload} setImpactPhotoUpload={setImpactPhotoUpload}
              createImpactCampaign={createImpactCampaign}
              updateImpactCampaign={updateImpactCampaign}
              deleteImpactCampaign={deleteImpactCampaign}
              uploadImpactPhoto={uploadImpactPhoto}
              deleteImpactPhoto={deleteImpactPhoto}
            />
          )}

          {/* AVC Cases Tab */}
          {adminTab === "avc" && (
            <AvcAdminPanel
              adminAvcCases={adminAvcCases}
              avcCount={avcCount}
              fetchAdminAvcCases={fetchAdminAvcCases}
              deleteAvcCase={deleteAvcCase}
            />
          )}

          {/* Academy Pre-registrations Tab */}
          {adminTab === "academy" && (
            <AcademyAdminPanel
              adminAcademyRecs={adminAcademyRecs}
              acadCount={acadCount}
              fetchAdminAcademy={fetchAdminAcademy}
              deleteAcademyRec={deleteAcademyRec}
            />
          )}

          {/* Mission Expense Reports Tab */}
          {adminTab === "mission" && (
            <MissionAdminPanel
              API={API} pinInput={pinInput}
              adminMissionReports={adminMissionReports} missionSelected={missionSelected} setMissionSelected={setMissionSelected}
              missionUploading={missionUploading}
              missionNewOpen={missionNewOpen} setMissionNewOpen={setMissionNewOpen}
              missionForm={missionForm} setMissionForm={setMissionForm}
              missionEditMode={missionEditMode} setMissionEditMode={setMissionEditMode}
              missionEditItems={missionEditItems} setMissionEditItems={setMissionEditItems}
              signaturePadOpen={signaturePadOpen} setSignaturePadOpen={setSignaturePadOpen}
              signaturePadRef={signaturePadRef}
              fetchAdminMissionReports={fetchAdminMissionReports}
              exportMissionCSV={exportMissionCSV}
              createMissionReport={createMissionReport} deleteMissionReport={deleteMissionReport}
              uploadMissionAttachment={uploadMissionAttachment} deleteMissionAttachment={deleteMissionAttachment}
              updateMissionStatus={updateMissionStatus}
              startMissionEdit={startMissionEdit} saveMissionEdit={saveMissionEdit}
              openSignaturePad={openSignaturePad}
              sigStart={sigStart} sigMove={sigMove} sigEnd={sigEnd} sigClear={sigClear}
              saveSignature={saveSignature}
              MISSION_RATES={MISSION_RATES} toEUR={toEUR} fmtEUR={fmtEUR} fmtFCFA={fmtFCFA}
              missionTotalEUR={missionTotalEUR} missionTotalFCFA={missionTotalFCFA}
            />
          )}

          {adminTab === "togo" && (
            <TogoDossierAdminPanel API={API} pinInput={pinInput} />
          )}
          {adminTab === "charity_uk" && (
            <CharityUkAdminPanel API={API} pinInput={pinInput} />
          )}
          {adminTab === "signed_docs" && (
            <SignedDocsAdminPanel API={API} pinInput={pinInput} />
          )}
          {adminTab === "sport_partners" && (
            <SportPartnersAdminPanel API={API} pinInput={pinInput} />
          )}
          {adminTab === "certificats_bulk" && (
            <CertificatsBulkAdminPanel API={API} pinInput={pinInput} />
          )}
          {adminTab === "twilio_notifs" && (
            <TwilioNotificationsAdminPanel API={API} pinInput={pinInput} />
          )}
          {adminTab === "ai_assistant" && (
            <AiAssistantAdminPanel API={API} pinInput={pinInput} />
          )}

        </div>
      </div>
    </div>
  )}
  <ChatbotWidget />
  <BachePreview3D open={bachePreviewOpen} onClose={() => setBachePreviewOpen(false)} />
</div>
);
}

export default App;
